import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { BarChart, Bar, LineChart, Line, AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { motion, AnimatePresence } from 'motion/react';
import api from './api';
import {
  previewDailyReportClient,
  sendDailyReportClient,
  getSalespersonDayActivity,
} from './utils/discordReportClient';
import { getStoredDiscordWebhookUrl, clearStoredDiscordWebhookUrl } from './utils/discordWebhookStorage';
import {
  mergeWebhookIntoProposalTemplates,
  readDiscordWebhookUrl,
  visibleProposalTemplates,
} from './utils/discordWebhookSettings';
import { findDuplicateCompaniesFromData, buildDuplicateModalState } from './utils/companyDuplicates';
import { formatProperName, normalizeSpocFields } from './utils/formatName';
import { getUserInitials, resolveAvatarColor } from './utils/userAvatar';
import {
  MEETING_TYPE_WALKIN_MET,
  MEETING_TYPE_WALKIN_NO_CONTACT,
  MEETING_TYPE_WALKIN_LEGACY,
  WALKIN_MET_LABEL,
  WALKIN_NO_CONTACT_LABEL,
  WALKIN_MET_SHORT,
  WALKIN_NO_CONTACT_SHORT,
  isNoContactVisit,
  isFieldVisitMeeting,
  meetingTypeLabel,
} from './utils/meetingTypes';
import { citiesMatch, normalizeCityKey } from './utils/cityUtils';
import RemoveUserModal from './components/RemoveUserModal';
import { buildClientTransferPreview, mergeTransferPreview } from './utils/userTransferPreview';
import { buildTopDeveloperEntries, countTopDevelopers, topStarsOf } from './utils/topDevelopers';

// ── Shared animation variants ──────────────────────────────────────────────
const fadeUp   = { hidden:{opacity:0,y:14}, show:{opacity:1,y:0,transition:{duration:.22,ease:[.32,.72,0,1]}} };
const fadeIn   = { hidden:{opacity:0},      show:{opacity:1,transition:{duration:.18}} };
const listContainer = { hidden:{}, show:{ transition:{ staggerChildren:.045, delayChildren:.04 } } };
const listItem = { hidden:{opacity:0,y:8},  show:{opacity:1,y:0,transition:{duration:.2,ease:[.32,.72,0,1]}} };

// Constants
const CITIES = ['Bangalore','Ahmedabad','Mumbai','Pune','Hyderabad','Jaipur','Delhi','Chennai','Kolkata'];
const CNC = ['No answer','Busy','Switched off','Wrong number','Unreachable','Call back later'];
const CCC = ['Not interested','Asked for details','Interested','Meeting requested','Meeting scheduled','Wrong person','Existing vendor','Budget issue','Follow-up later'];
const CVIA = [['Call','📞'],['WhatsApp','💬'],['LinkedIn','💼']];
const meetingTimelineLabel = m => {
  if (isNoContactVisit(m)) return `🚪 ${WALKIN_NO_CONTACT_LABEL} — ${m.stage || 'Logged'}`;
  if (m?.type === MEETING_TYPE_WALKIN_MET) return `🚶 ${WALKIN_MET_LABEL} — ${m.stage || 'Logged'}`;
  return `Meeting #${m.meetingNumber || '?'}` + (m.stage ? ` — ${m.stage}` : '');
};
const DLV = ['Decision maker','Influencer','Gatekeeper','Unknown'];
const NIR = ['No current requirement','Busy right now','Already tied up','Budget constraints','Not relevant','Other'];
const CONC = ['Very Positive','Positive','Neutral','Needs Follow-up','Not Promising','Deal Lost'];
const MSTAGES = ['Tentative','Confirmed','Scheduled','Done','Proposal Sent','Demo','Negotiation','Commercial Discussion','Waiting for Feedback'];
const COLORS = ['#6366F1','#10B981','#F59E0B','#EF4444','#8B5CF6','#EC4899','#06B6D4','#F97316'];
const GRAD_COLORS = [['#6366F1','#818CF8'],['#10B981','#34D399'],['#F59E0B','#FBBF24'],['#EF4444','#F87171'],['#8B5CF6','#A78BFA'],['#EC4899','#F472B6']];

/** Who gets credit for a call/meeting — logged-in rep, not account owner. */
function resolveActivitySalesperson({ user, isAdmin, salespersons = [], fallback = '' }) {
  const name = (user || '').trim();
  if (!name || name === 'admin') return fallback;
  if (!isAdmin) return name;
  if (salespersons.includes(name)) return name;
  return fallback;
}

// Helpers
const today = () => new Date().toISOString().slice(0,10);
const tomorrow = () => new Date(Date.now()+864e5).toISOString().slice(0,10);
const yesterday = () => new Date(Date.now()-864e5).toISOString().slice(0,10); // 24h buffer for late-logged meetings
const fmtDateTime = at => { if(!at) return '—'; try { const d=new Date(at); const p=n=>String(n).padStart(2,'0'); return `${p(d.getDate())}/${p(d.getMonth()+1)}/${d.getFullYear()} · ${p(d.getHours())}:${p(d.getMinutes())}`; } catch { return '—'; } };
const fmtDate = d => { if(!d) return '—'; try { const dt=new Date(d+'T12:00:00'); const dd=String(dt.getDate()).padStart(2,'0'); const mm=String(dt.getMonth()+1).padStart(2,'0'); const yyyy=dt.getFullYear(); return `${dd}/${mm}/${yyyy}`; } catch { return '—'; } };
const fmtINR = v => v ? '₹' + Number(v).toLocaleString('en-IN') : '';
const resolvePhoneType = (phone, phoneType) => {
  if (phoneType === 'landline' || phoneType === 'mobile') return phoneType;
  if (phone && phone.length === 15) return 'landline';
  return 'mobile';
};
const phoneDigitCount = type => (type === 'landline' ? 12 : 10);
const phoneMaxLength = type => 3 + phoneDigitCount(type);
const phoneValid = (p, phoneType = 'mobile') => {
  const type = resolvePhoneType(p, phoneType);
  const digits = phoneDigitCount(type);
  return new RegExp(`^\\+91\\d{${digits}}$`).test(p);
};
const handlePhoneInput = (val, setter, phoneType = 'mobile') => {
  const type = resolvePhoneType(val, phoneType);
  let v = (val || '').replace(/\D/g, '');
  if (v.startsWith('91')) v = v.slice(2);
  v = '+91' + v;
  const maxLen = phoneMaxLength(type);
  if (v.length > maxLen) v = v.slice(0, maxLen);
  setter(v);
};
const handlePhonePaste = (e, setter, phoneType = 'mobile') => {
  e.preventDefault();
  const pasted = e.clipboardData?.getData('text') || '';
  handlePhoneInput(pasted, setter, phoneType);
};
const validatePhone = (v, phoneType = 'mobile') => {
  const type = resolvePhoneType(v, phoneType);
  const maxLen = phoneMaxLength(type);
  if (!v || v === '+91') return '';
  if (!/^\+91/.test(v)) return 'Must start with +91';
  if (v.length > 3 && !/^\+91\d*$/.test(v)) return 'Only digits after +91';
  if (v.length > maxLen) return 'Too many digits';
  if (v.length < maxLen && v.length > 3) {
    const needed = maxLen - v.length;
    return `${needed} more digit${needed > 1 ? 's' : ''} needed`;
  }
  return '';
};
const phoneFormatHint = phoneType => (resolvePhoneType('', phoneType) === 'landline'
  ? 'Format: +91 + STD + number (12 digits)'
  : 'Format: +91 + 10-digit mobile');
const phonePlaceholder = phoneType => (resolvePhoneType('', phoneType) === 'landline'
  ? '+912212345678'
  : '+919845012345');
const phoneErrorMsg = phoneType => (resolvePhoneType('', phoneType) === 'landline'
  ? 'Must be +91 followed by 12 digits'
  : 'Must be +91 followed by 10 digits');
const emailValid = e => !e || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
const numOnly = v => /^\d*$/.test(v);
const daysSince = d => {
  if (!d) return null;
  const start = new Date(d + 'T00:00:00');
  const end = new Date(today() + 'T00:00:00');
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  return Math.max(0, Math.floor((end - start) / 86400000));
};

// Styles — Apple/clean minimal design system
const P='#111827',PL='#F3F4F6',RD='#EF4444',RL='#FFF1F2',GN='#16A34A',GL='#F0FDF4',YL='#D97706',YLL='#FFFBEB',TX='#111827',MU='#6B7280',LT='#9CA3AF',BD='#E5E7EB',BG='#F5F5F7',WH='#FFFFFF';
const APP_VERSION = 'V 1.9';
const SIDEBAR_COLLAPSED_KEY = 'unada_sidebar_collapsed';

const s = {
  btn: (v) => ({ padding:'9px 20px', borderRadius:12, border:'none', cursor:'pointer', fontSize:13, fontWeight:600, display:'inline-flex', alignItems:'center', gap:6, background:v==='r'?'#FFF1F2':v==='s'?WH:v==='g'?'#16A34A':'#111827', color:v==='r'?RD:v==='s'?'#374151':'#fff', transition:'all .18s ease', boxShadow:v==='s'?'0 1px 3px rgba(0,0,0,0.08), inset 0 0 0 1px rgba(0,0,0,0.08)':'0 1px 3px rgba(0,0,0,0.12)', letterSpacing:'-0.01em' }),
  inp: { padding:'8px 12px', borderRadius:10, border:'1px solid #E5E7EB', fontSize:13, width:'100%', outline:'none', boxSizing:'border-box', transition:'all .15s ease', background:WH, color:TX, boxShadow:'none', letterSpacing:'-0.01em' },
  sel: { padding:'0', border:'none', fontSize:13, width:'100%', outline:'none', boxSizing:'border-box', background:'transparent' },
  tag: (bg,fg) => ({ display:'inline-block', padding:'3px 10px', borderRadius:8, fontSize:11, fontWeight:600, background:bg, color:fg, whiteSpace:'nowrap' }),
  th: { padding:'12px 16px', textAlign:'left', fontSize:11, fontWeight:600, color:LT, textTransform:'uppercase', letterSpacing:'.6px', borderBottom:'1px solid #F0F0F5', background:WH },
  td: { padding:'13px 16px', fontSize:13, borderBottom:'1px solid #F7F7F9', color:TX, fontWeight:400, letterSpacing:'-0.01em' },
  card: { background:WH, borderRadius:14, border:'1px solid #EBEBED', padding:22, marginBottom:14 },
  chip: (a,c) => ({ padding:'7px 16px', borderRadius:10, fontSize:12, cursor:'pointer', fontWeight:a?600:400, background:a?(c||PL):WH, color:a?(c?'#fff':(P)):MU, boxShadow:a?'none':'0 1px 3px rgba(0,0,0,0.08), inset 0 0 0 1px rgba(0,0,0,0.06)', whiteSpace:'nowrap', transition:'all .18s ease' }),
  nav: (a) => ({ padding:'7px 12px', borderRadius:9, cursor:'pointer', fontSize:13, fontWeight:a?600:400, color:a?TX:MU, background:a?'#EBEBED':'transparent', marginBottom:2, display:'flex', alignItems:'center', gap:10, transition:'background .12s', letterSpacing:'-0.01em' }),
  tab: (a) => ({ padding:'7px 16px', borderRadius:8, cursor:'pointer', fontSize:13, fontWeight:a?600:400, color:a?TX:MU, background:a?'#EBEBED':'transparent', transition:'all .12s ease', letterSpacing:'-0.01em', whiteSpace:'nowrap' }),
  stat: { background:WH, borderRadius:18, padding:'20px 24px', flex:1, minWidth:130, boxShadow:'0 2px 12px rgba(0,0,0,0.06)' },
  row: { display:'flex', gap:12, flexWrap:'wrap' },
  statRow: { display:'grid', gap:10 },
};

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = e => setMatches(e.matches);
    mq.addEventListener('change', onChange);
    setMatches(mq.matches);
    return () => mq.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

// Default motivational sales quotes
const DEFAULT_QUOTES = [
  "Every call is a chance to change someone's business forever.",
  "Rejection is not the end — it's just redirection to the right deal.",
  "The best salespeople don't sell. They help people buy.",
  "Your pipeline is a reflection of your consistency.",
  "One more call today can make all the difference tomorrow.",
  "Champions keep going when they don't feel like it.",
  "Sales is the art of turning 'no' into 'not yet'.",
  "The harder you work, the luckier your deals get.",
  "A follow-up today is a closed deal tomorrow.",
  "Every 'no' brings you closer to the 'yes' that matters.",
  "Your attitude on the first call sets the tone for the entire deal.",
  "Confidence isn't loud. It's consistent.",
  "Don't count the days — make every call day count.",
  "The pipeline doesn't lie. Fill it with intent.",
  "Great salespeople listen twice as much as they talk.",
  "Momentum in sales is built one conversation at a time.",
  "Every prospect is a relationship waiting to happen.",
  "Be the salesperson you wish had called you.",
  "Energy is contagious — bring yours to every call.",
  "Small daily improvements lead to stunning long-term results.",
  "The difference between good and great is one more follow-up.",
  "You don't lose deals — you learn what to do better next time.",
  "Discipline is choosing your goals over your comfort zone.",
  "Show up prepared. Show up consistent. Show up.",
  "A warm call beats a cold pitch every time.",
  "Build trust first. The deal comes second.",
  "Every conversation plants a seed. Water it with follow-ups.",
  "Your CRM is only as powerful as the effort you put into it.",
  "The best time to make a call was yesterday. Second best is now.",
  "Winning in sales isn't about being the loudest — it's about being the most persistent."
];

// Motivational Quote Ticker
function MotivationalQuote({ quotes, intervalHrs }) {
  const list = (quotes && quotes.length > 0) ? quotes : DEFAULT_QUOTES;
  const hrs = (intervalHrs && intervalHrs > 0) ? intervalHrs : 2;
  const getIdx = () => {
    const slotMs = hrs * 60 * 60 * 1000;
    const slot = Math.floor(new Date().getTime() / slotMs);
    return slot % list.length;
  };
  const [idx, setIdx] = useState(getIdx);
  const [phase, setPhase] = useState('visible'); // 'visible' | 'exit' | 'enter'
  useEffect(() => {
    const id = setInterval(() => {
      const next = getIdx();
      if (next !== idx) {
        setPhase('exit');
        setTimeout(() => { setIdx(next); setPhase('enter'); }, 500);
        setTimeout(() => { setPhase('visible'); }, 900);
      }
    }, 60000);
    return () => clearInterval(id);
  });
  const anim = {
    visible: { opacity: 1, transform: 'translateY(0px) scale(1)', filter: 'blur(0px)' },
    exit:    { opacity: 0, transform: 'translateY(-8px) scale(0.97)', filter: 'blur(3px)' },
    enter:   { opacity: 0, transform: 'translateY(8px) scale(0.97)', filter: 'blur(3px)' },
  };
  return (
    <div className="motivational-quote hide-mobile">
      <div style={{
        display:'flex',alignItems:'center',gap:10,minWidth:0,maxWidth:600,
      }}>
        <div style={{width:2,height:28,borderRadius:2,background:'linear-gradient(180deg,#6366F1,#A78BFA)',flexShrink:0,opacity:0.7}}/>
        <div style={{
          ...anim[phase],
          fontSize:12,
          color:'#6B7280',
          fontStyle:'italic',
          fontWeight:400,
          whiteSpace:'nowrap',
          overflow:'hidden',
          textOverflow:'ellipsis',
          letterSpacing:'0.01em',
          lineHeight:1.5,
          transition:'opacity 0.5s cubic-bezier(.4,0,.2,1), transform 0.5s cubic-bezier(.4,0,.2,1), filter 0.5s cubic-bezier(.4,0,.2,1)',
          minWidth:0,
        }}>{list[idx]}</div>
      </div>
    </div>
  );
}

// Tag component
const stageColors = { New:['#F3F4F6','#374151'], Won:[GL,GN], Dropped:[RL,RD], 'Not interested':[RL,RD], 'Meeting scheduled':['#F0FDFA','#0D9488'], Connected:[GL,GN], Attempted:[YLL,YL], 'Details sent':['#F3F4F6','#374151'], 'Follow-up due':['#F3F4F6','#374151'], 'Very Positive':[GL,GN], Positive:[GL,GN], 'Not Promising':[RL,RD], 'Deal Lost':[RL,RD], 'Decision maker':[GL,GN], Influencer:['#F3F4F6','#374151'], Gatekeeper:[YLL,YL], 'On Hold':[YLL,YL], 'Previously Tapped':['#FEF9C3','#A16207'], 'Proposal Sent':[YLL,YL], Negotiation:[YLL,YL], Scheduled:['#F3F4F6','#374151'], Done:[GL,GN], Demo:[YLL,YL], Tentative:[YLL,YL], Confirmed:[GL,GN] };
function Tag({label}) { if(!label)return null; const[bg,fg]=stageColors[label]||[PL,P]; return <span style={s.tag(bg,fg)}>{label}</span>; }

function MeetingAttribution({ meeting, company }) {
  const conductor = meeting?.salesperson || '';
  const owner = company?.salesperson || '';
  const mismatch = conductor && owner && conductor !== owner;
  return (
    <div style={{ fontSize: 12, color: MU, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
      {conductor
        ? <span>Meeting conducted by <strong style={{ color: TX }}>{conductor}</strong></span>
        : <span style={{ color: LT }}>Meeting conducted by —</span>}
      {owner ? <span>Lead generated by: <strong style={{ color: TX }}>{owner}</strong></span> : null}
      {mismatch ? (
        <span style={{ fontSize: 10, fontWeight: 700, color: '#92400E', background: '#FFFBEB', padding: '2px 8px', borderRadius: 6, border: '1px solid #FDE68A' }}>
          Lead & activity by different reps
        </span>
      ) : null}
    </div>
  );
}

function DeleteIconBtn({ onClick, title = 'Delete', size = 28, style, stopPropagation = false, disabled = false }) {
  return (
    <motion.button
      type="button"
      title={title}
      disabled={disabled}
      whileHover={disabled ? undefined : { scale: 1.05 }}
      whileTap={disabled ? undefined : { scale: 0.95 }}
      onClick={e => { if (disabled) return; if (stopPropagation) e.stopPropagation(); onClick?.(e); }}
      style={{
        width: size,
        height: size,
        padding: 0,
        display: 'inline-flex',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: 10,
        border: '1px solid #FECACA',
        background: RL,
        cursor: disabled ? 'default' : 'pointer',
        color: RD,
        fontSize: size >= 28 ? 12 : 11,
        fontWeight: 600,
        flexShrink: 0,
        lineHeight: 1,
        opacity: disabled ? 0.5 : 1,
        ...style,
      }}
    >✕</motion.button>
  );
}

function Fld({label,children,half,error,hint}) { return <div className="fld" style={{flex:half?'1 1 45%':'1 1 100%',minWidth:half?180:'auto'}}><label style={{fontSize:12,fontWeight:500,color:MU,letterSpacing:'-0.01em'}}>{label}</label>{children}{error&&<div style={{fontSize:11,color:RD,marginTop:2}}>{error}</div>}{hint&&!error&&<div style={{fontSize:11,color:LT,marginTop:2}}>{hint}</div>}</div>; }

function DateInput({ value, onChange, min, error, placeholder = 'DD/MM/YYYY' }) {
  return (
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%' }}>
      <input
        type="date"
        style={{ ...s.inp, width: '100%', color: 'transparent', caretColor: 'transparent', borderColor: error ? RD : BD }}
        value={value || ''}
        onChange={onChange}
        min={min}
      />
      <span style={{ position: 'absolute', left: 12, pointerEvents: 'none', fontSize: 13, fontWeight: value ? 500 : 400, color: value ? TX : LT }}>
        {value ? fmtDate(value) : placeholder}
      </span>
    </div>
  );
}

function UserAvatar({ username, avatarColor, size = 38, active = false, style = {} }) {
  const color = resolveAvatarColor(username, avatarColor);
  return (
    <div
      className="user-avatar"
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: active ? color : `${color}22`,
        color: active ? '#fff' : color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        fontSize: Math.max(10, Math.round(size * 0.34)),
        flexShrink: 0,
        letterSpacing: '-0.02em',
        ...style,
      }}
    >
      {getUserInitials(username)}
    </div>
  );
}

function StatCard({label,value,color,icon,sub}) {
  return <motion.div whileHover={{ y:-1 }} style={{background:WH,borderRadius:14,padding:'16px 18px',flex:1,border:'1px solid #EBEBED',transition:'all .18s'}}>
    <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:14}}>
      <div style={{width:34,height:34,borderRadius:10,background:`linear-gradient(135deg,${color||P},${color||P}CC)`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:16,flexShrink:0,color:'#fff'}}>{icon}</div>
      <div style={{fontSize:10,fontWeight:600,color:MU,textTransform:'uppercase',letterSpacing:'.6px',lineHeight:1.3}}>{label}</div>
    </div>
    <div style={{fontSize:32,fontWeight:700,color:TX,letterSpacing:'-0.04em',lineHeight:1}}>{value}</div>
    {sub&&<div style={{fontSize:11,color:LT,marginTop:5}}>{sub}</div>}
  </motion.div>;
}

// Vertical outcome bar chart with hover tooltip (count + % of total). Scrolls when many.
function OutcomeBars({ items, total }) {
  const [hover, setHover] = useState(null);
  const mx = Math.max(...items.map(o=>o.value), 1);
  const H = 150;
  return <div style={{display:'flex',alignItems:'flex-end',gap:10,overflowX:'auto',paddingBottom:4}}>
    {items.map((o,i)=>{
      const isH = hover===i;
      const pct = total ? Math.round(o.value/total*100) : null;
      return <div key={o.name} onMouseEnter={()=>setHover(i)} onMouseLeave={()=>setHover(null)} style={{flex:'1 0 64px',minWidth:64,display:'flex',flexDirection:'column',alignItems:'center',cursor:'default'}}>
        <div style={{position:'relative',height:H+26,width:'100%',display:'flex',justifyContent:'center',alignItems:'flex-end'}}>
          {isH && <div style={{position:'absolute',top:0,left:'50%',transform:'translateX(-50%)',background:'#111827',color:'#fff',fontSize:11,fontWeight:600,padding:'4px 9px',borderRadius:6,whiteSpace:'nowrap',zIndex:2,boxShadow:'0 4px 14px rgba(0,0,0,0.2)'}}>{o.value}{pct!==null?` · ${pct}%`:''}</div>}
          <div style={{width:28,height:Math.max(o.value/mx*H,3),background:COLORS[i%COLORS.length],borderRadius:'6px 6px 0 0',transition:'height .5s, opacity .15s',opacity:hover===null||isH?1:0.5}}/>
        </div>
        <div style={{fontSize:10,color:isH?TX:MU,fontWeight:isH?700:500,textAlign:'center',marginTop:8,lineHeight:1.25,minHeight:26,wordBreak:'break-word'}}>{o.name}</div>
      </div>;
    })}
  </div>;
}

function GradientCard({label,value,color1,color2,icon,sub,onClick}) {
  return <motion.div
    whileHover={{ y: onClick ? -2 : -1 }}
    onClick={onClick}
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={onClick ? e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(e); } } : undefined}
    style={{background:WH,borderRadius:14,padding:'16px 18px',flex:1,border:'1px solid #EBEBED',transition:'all .18s',cursor:onClick?'pointer':'default'}}
  >
    <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:14}}>
      <div style={{width:34,height:34,borderRadius:10,background:`linear-gradient(135deg,${color1},${color2})`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:16,flexShrink:0}}>{icon}</div>
      <div style={{fontSize:10,fontWeight:600,color:MU,textTransform:'uppercase',letterSpacing:'.6px',lineHeight:1.3}}>{label}</div>
    </div>
    <div style={{fontSize:32,fontWeight:700,color:TX,letterSpacing:'-0.04em',lineHeight:1}}>{value}</div>
    {sub&&<div style={{fontSize:11,color:LT,marginTop:5}}>{sub}</div>}
  </motion.div>;
}

function pickLatestMeeting(meetings = []) {
  return [...meetings].sort((a,b)=>{
    const dateCmp = (b.date||'').localeCompare(a.date||'');
    if (dateCmp !== 0) return dateCmp;
    return new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0);
  })[0];
}

function Modal({open,onClose,title,children,wide,large,fullscreen,preventClose}) { 
  useEffect(() => { if(!open)return; const e=ev=>{if(ev.key==='Escape'&&!preventClose)onClose()}; document.addEventListener('keydown',e); return ()=>document.removeEventListener('keydown',e); }, [open,onClose,preventClose]);
  if(!open) return null; 
  const panelClass = 'app-modal-panel'+(fullscreen?' app-modal-panel--fullscreen':large?' app-modal-panel--large':wide?' app-modal-panel--wide':'');
  const overlayClass = 'app-modal-overlay'+(fullscreen?' app-modal-overlay--fullscreen':large?' app-modal-overlay--large':'');
  const expanded = fullscreen || large;
  return <div className={overlayClass} onClick={preventClose?undefined:onClose}><motion.div initial={{opacity:0,scale:expanded?0.98:0.97,y:expanded?8:12}} animate={{opacity:1,scale:1,y:0}} exit={{opacity:0,scale:expanded?0.98:0.97}} transition={{duration:.2,ease:[.32,.72,0,1]}} className={panelClass} onClick={e=>e.stopPropagation()}>{title&&<div className={large?'app-modal-header':''} style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:large?0:fullscreen?20:24,flexShrink:0,padding:large?'20px 24px 16px':undefined}}><div><h2 style={{fontSize:expanded?20:18,fontWeight:700,margin:0,letterSpacing:'-0.02em'}}>{title}</h2></div><div onClick={onClose} style={{cursor:'pointer',color:MU,fontSize:16,width:30,height:30,display:'flex',alignItems:'center',justifyContent:'center',borderRadius:8,background:'#F5F5F7',transition:'all .15s'}}>✕</div></div>}{children}</motion.div></div>; 
}

function ConfirmDialog({open,onClose,onConfirm,title,message,confirmLabel,confirmKind}) { 
  useEffect(() => { if(!open)return; const e=ev=>{if(ev.key==='Escape')onClose()}; document.addEventListener('keydown',e); return ()=>document.removeEventListener('keydown',e); }, [open,onClose]);
  if(!open)return null; 
  return <div className="app-confirm-overlay" style={{zIndex:110}} onClick={onClose}><motion.div initial={{opacity:0,scale:0.97,y:12}} animate={{opacity:1,scale:1,y:0}} transition={{duration:.18,ease:[.32,.72,0,1]}} className="app-confirm-panel" onClick={e=>e.stopPropagation()}><h3 style={{margin:'0 0 8px',fontSize:16,fontWeight:700,letterSpacing:'-0.02em'}}>{title||'Confirm'}</h3><p style={{color:MU,fontSize:13,margin:'0 0 22px',whiteSpace:'pre-line',lineHeight:1.6}}>{message}</p><div style={{display:'flex',gap:8,justifyContent:'flex-end',flexWrap:'wrap'}}><motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={s.btn('s')} onClick={onClose}>Cancel</motion.button><motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={s.btn(confirmKind || 'r')} onClick={()=>{onConfirm();onClose();}}>{confirmLabel || 'Delete'}</motion.button></div></motion.div></div>; 
}

function NavIcon({ id, active }) {
  const c = active ? TX : MU;
  const icons = {
    overview:       <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>,
    leadbank:       <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 7h16M4 12h16M4 17h10"/></svg>,
    companies:      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>,
    queue:          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/><path d="M9 12h6M9 16h4"/></svg>,
    calllog:        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.79 19.79 0 0 1 11.39 19a19.45 19.45 0 0 1-5-5 19.79 19.79 0 0 1-2.93-8.4A2 2 0 0 1 5.61 3.5h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L9.91 11a16 16 0 0 0 5 5l.94-.94a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 17z"/></svg>,
    followups:      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
    meetings:       <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
    tasks:          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>,
    dropped:        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>,
    targets:        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>,
    visits:         <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13"/><path d="M22 2L15 22 11 13 2 9l20-7z"/></svg>,
    eod:            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><line x1="10" y1="9" x2="8" y2="9"/></svg>,
    sops:           <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>,
    analytics:      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
    approvals:      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>,
    topdevelopers:  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
    probableclients:<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
    settings:       <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>,
  };
  return icons[id] || null;
}

function UnadaLogo({ width=120 }) {
  return <img src="/Unada-Logo.svg" alt="Unada" width={width} style={{display:'block',height:'auto'}}/>;
}

let _ddCloseAll = []; // global registry for all open dropdowns
const DROPDOWN_GAP = 4;
const DROPDOWN_MAX_HEIGHT = 280;

function parseDropdownOptions(children) {
  const opts = [];
  React.Children.toArray(children).forEach(c => {
    if (c.type === 'option') opts.push({ v: c.props.value !== undefined ? c.props.value : c.props.children, l: c.props.children });
    if (c.type === React.Fragment) React.Children.toArray(c.props.children).forEach(cc => {
      if (cc.type === 'option') opts.push({ v: cc.props.value !== undefined ? cc.props.value : cc.props.children, l: cc.props.children });
    });
  });
  return opts;
}

function computeDropdownCoords(triggerRect, panelHeight, panelWidth) {
  const gap = DROPDOWN_GAP;
  const h = Math.min(panelHeight, DROPDOWN_MAX_HEIGHT);
  const spaceBelow = window.innerHeight - triggerRect.bottom - gap;
  const spaceAbove = triggerRect.top - gap;

  let top = triggerRect.bottom + gap;
  let openAbove = false;

  // Flip up only when the panel won't fit below and has room above — anchor bottom of panel to top of trigger
  if (h > spaceBelow && spaceAbove >= Math.min(h, spaceAbove)) {
    top = triggerRect.top - h - gap;
    openAbove = true;
  } else if (top + h > window.innerHeight - gap) {
    top = Math.max(gap, window.innerHeight - h - gap);
  }

  top = Math.max(gap, top);

  // Default: left-align panel to trigger. If it would overflow the right edge,
  // right-align it to the trigger so it stays attached to the button (opens leftward)
  // instead of detaching and floating to the left of it.
  const w = Math.min(panelWidth || triggerRect.width, 320);
  let left = triggerRect.left;
  if (left + w > window.innerWidth - gap) {
    left = Math.max(gap, triggerRect.right - w);
  }

  return { top, left, width: triggerRect.width, openAbove };
}

function Dropdown({ value, onChange, children, style, disabled, plain }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0, openAbove: false });
  const ref = React.useRef();
  const panelRef = React.useRef();
  const openRef = React.useRef(open);
  openRef.current = open;

  const opts = useMemo(() => parseDropdownOptions(children), [children]);
  const selected = opts.find(o => o.v === value) || opts[0];
  const curLabel = selected ? selected.l : '';

  // Close on click outside — use capture:true so modal's stopPropagation doesn't block it
  React.useEffect(()=>{
     const close = e => {
       if(panelRef.current && panelRef.current.contains(e.target)) return;
       if(ref.current && !ref.current.contains(e.target)) {
         setOpen(false);
       }
     };
     const closeOnScroll = (e) => {
       if (!openRef.current) return;
       // Keep open while scrolling inside the dropdown list itself
       if (panelRef.current && (panelRef.current === e.target || panelRef.current.contains(e.target))) return;
       setOpen(false);
     };
     document.addEventListener('mousedown', close, true);
     window.addEventListener('scroll', closeOnScroll, { passive: true, capture: true });
     window.addEventListener('resize', closeOnScroll, { passive: true });
     return () => {
       document.removeEventListener('mousedown', close, true);
       window.removeEventListener('scroll', closeOnScroll, { capture: true });
       window.removeEventListener('resize', closeOnScroll);
     };
  }, []);

  // Register with global close-all registry
  React.useEffect(()=>{
    const fn = () => setOpen(false);
    _ddCloseAll.push(fn);
    return () => { _ddCloseAll = _ddCloseAll.filter(x => x !== fn); };
  }, []);

  // Snap panel to trigger after mount — uses real height, not max-height guess
  useLayoutEffect(() => {
    if (!open || !ref.current || !panelRef.current) return;
    const triggerRect = ref.current.getBoundingClientRect();
    const panelRect = panelRef.current.getBoundingClientRect();
    const next = computeDropdownCoords(triggerRect, panelRect.height, panelRect.width);
    setCoords(prev => (
      Math.abs(prev.top - next.top) < 1 && prev.left === next.left && prev.openAbove === next.openAbove
        ? prev
        : next
    ));
  }, [open, opts.length]);

  const openMe = () => {
    if(disabled) return;
    if(!openRef.current) {
      _ddCloseAll.forEach(fn => fn());
      if(ref.current) {
        const r = ref.current.getBoundingClientRect();
        const estHeight = Math.min(DROPDOWN_MAX_HEIGHT, 8 + opts.length * 40);
        setCoords(computeDropdownCoords(r, estHeight, r.width));
      }
      setOpen(true);
    } else {
      setOpen(false);
    }
  };

  const containerStyle = {...style};
  delete containerStyle.padding; delete containerStyle.border; delete containerStyle.background;

  const panel = open && !disabled ? ReactDOM.createPortal(
    <AnimatePresence>
      <motion.div
        initial={{opacity:0,y:coords.openAbove ? 6 : -6,scale:.97}}
        animate={{opacity:1,y:0,scale:1}}
        exit={{opacity:0,y:coords.openAbove ? 4 : -4,scale:.97}}
        transition={{duration:.15,ease:[.32,.72,0,1]}}
        ref={panelRef}
        className="app-dropdown-panel"
        style={{position:'fixed', top:coords.top, left:coords.left, minWidth:coords.width, width:'max-content', maxWidth:320, background:WH, borderRadius:12, border:'1px solid #D1D5DB', boxShadow:'0 8px 32px rgba(0,0,0,0.14)', zIndex:99999, maxHeight:DROPDOWN_MAX_HEIGHT, padding:'4px'}}
        onClick={e => e.stopPropagation()}
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
      >
        {opts.map((o,i) => (
          <motion.div key={i} initial={{opacity:0,x:-4}} animate={{opacity:1,x:0}} transition={{delay:i*.018,duration:.14}} onMouseDown={(evt)=>{ evt.stopPropagation(); setOpen(false); if(onChange) onChange({target:{value:o.v}}); }} style={{padding:'8px 12px', fontSize:13, borderRadius:8, border: o.v === value ? '1.5px solid '+TX : '1.5px solid transparent', background: o.v === value ? '#F3F4F6' : 'transparent', color: TX, fontWeight: o.v === value ? 600 : 400, cursor:'pointer', letterSpacing:'-0.01em', whiteSpace:'normal', wordBreak:'break-word'}}>
            {o.l}
          </motion.div>
        ))}
      </motion.div>
    </AnimatePresence>,
    document.body
  ) : null;

  return (
    <div ref={ref} style={{position:'relative', cursor: disabled ? 'not-allowed' : 'pointer', padding: plain ? 0 : '8px 12px', borderRadius:10, border: plain ? 'none' : (open ? '1.5px solid '+TX : '1px solid #D1D5DB'), display: plain ? 'inline-flex' : 'flex', alignItems:'center', justifyContent: plain ? 'flex-start' : 'space-between', gap: plain ? 3 : 0, background: plain ? 'transparent' : (disabled ? '#F3F4F6' : WH), color: disabled ? LT : TX, fontWeight:400, fontSize:13, userSelect:'none', transition:'all .15s ease', letterSpacing:'-0.01em', boxShadow: (plain || !open) ? 'none' : '0 0 0 3px rgba(17,24,39,0.07)', opacity: disabled ? 0.6 : 1, ...containerStyle}} onClick={openMe}>
      <span style={{overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flex: plain ? 'none' : 1, fontWeight:500}}>{curLabel}</span>
      <span style={{fontSize:9, opacity: open ? 0.8 : 0.4, transform:open?'rotate(180deg)':'none', transition:'transform .2s', marginLeft: plain ? 0 : 8}}>▼</span>
      {panel}
    </div>
  );
}

function EmpCard({sp,calls,meetings,companies}) {
  const [period,setPeriod] = useState('7d');
  const t = today();
  const ranges = {today:{from:t,to:t},'7d':{from:new Date(Date.now()-7*864e5).toISOString().slice(0,10),to:t},'30d':{from:new Date(Date.now()-30*864e5).toISOString().slice(0,10),to:t},all:{from:'2000-01-01',to:'2099-12-31'}};
  const r = ranges[period];
  const myCalls = calls.filter(c=>c.salesperson===sp);
  const myMtgs = meetings.filter(m=>m.salesperson===sp);
  const myCompanies = companies.filter(c=>c.salesperson===sp);
  const filteredCalls = myCalls.filter(c=>c.date>=r.from&&c.date<=r.to);
  const dateLabel = period==='today'?fmtDate(t):period==='all'?'All time':fmtDate(r.from)+' – '+fmtDate(r.to);
  const tentative=myMtgs.filter(m=>m.stage==='Tentative').length;
  const confirmed=myMtgs.filter(m=>m.stage==='Confirmed').length;
  const completed=myMtgs.filter(m=>m.stage==='Completed'||m.stage==='Done').length;
  return <div style={{background:'#fff',borderRadius:14,padding:18,border:'1px solid #E0E7FF',boxShadow:'0 2px 8px rgba(99,102,241,.06)'}}>
    <div style={{fontSize:15,fontWeight:700,marginBottom:12,color:P}}>{sp}</div>
    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:12}}>
      <div style={{background:PL,borderRadius:8,padding:'8px 10px',textAlign:'center'}}><div style={{fontSize:18,fontWeight:700,color:P}}>{myCompanies.length}</div><div style={{fontSize:10,color:MU}}>Leads Assigned</div></div>
      <div style={{background:GL,borderRadius:8,padding:'8px 10px',textAlign:'center'}}><div style={{fontSize:18,fontWeight:700,color:GN}}>{myCalls.filter(c=>c.connected).length}</div><div style={{fontSize:10,color:MU}}>Connected</div></div>
    </div>
    <div style={{fontSize:12,fontWeight:600,color:MU,marginBottom:6}}>Calls Made</div>
    <Dropdown style={{...s.sel,fontSize:11,padding:'4px 8px',marginBottom:6,width:'100%'}} value={period} onChange={e=>setPeriod(e.target.value)}>
      <option value="today">Today</option><option value="7d">Last 7 Days</option><option value="30d">Last 30 Days</option><option value="all">All Time</option>
    </Dropdown>
    <div style={{background:'#F9FAFB',borderRadius:8,padding:'10px 12px',textAlign:'center',marginBottom:12}}>
      <div style={{fontSize:22,fontWeight:700}}>{filteredCalls.length}</div>
      <div style={{fontSize:10,color:LT}}>{dateLabel}</div>
    </div>
    <div style={{fontSize:12,fontWeight:600,color:MU,marginBottom:6}}>Meetings</div>
    <div style={{display:'flex',gap:6}}>
      <div style={{flex:1,background:YLL,borderRadius:8,padding:'6px 8px',textAlign:'center'}}><div style={{fontSize:14,fontWeight:700,color:YL}}>{tentative}</div><div style={{fontSize:9,color:MU}}>Tentative</div></div>
      <div style={{flex:1,background:GL,borderRadius:8,padding:'6px 8px',textAlign:'center'}}><div style={{fontSize:14,fontWeight:700,color:GN}}>{confirmed}</div><div style={{fontSize:9,color:MU}}>Confirmed</div></div>
      <div style={{flex:1,background:PL,borderRadius:8,padding:'6px 8px',textAlign:'center'}}><div style={{fontSize:14,fontWeight:700,color:P}}>{completed}</div><div style={{fontSize:9,color:MU}}>Done</div></div>
    </div>
  </div>;
}

function LoginScreen({ onLogin }) {
  const [u, setU] = useState('');
  const [p, setP] = useState('');
  const [err, setErr] = useState('');
  const [load, setLoad] = useState(false);
  const [loginUsers, setLoginUsers] = useState([]);

  useEffect(() => {
    api.getLoginUsers().then(setLoginUsers).catch(() => setLoginUsers([]));
  }, []);
  
  const submit = async e => {
    e.preventDefault();
    setLoad(true); setErr('');
    try {
      const res = await api.login({username: u, password: p});
      localStorage.setItem('token', res.token);
      localStorage.setItem('user', JSON.stringify(res.user));
      onLogin(res.user);
    } catch(err) { setErr(err.message); }
    setLoad(false);
  };

  const selectedUser = loginUsers.find(x => x.username === u);

  return <div style={{background:BG, minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'-apple-system,BlinkMacSystemFont,"SF Pro Display",Inter,system-ui,sans-serif', padding:20}}>
    <form onSubmit={submit} className="login-form">
      <div style={{textAlign:'center', marginBottom:32}}>
        <div style={{display:'flex',justifyContent:'center',marginBottom:16}}>
          <UnadaLogo width={140} fill={TX} />
        </div>
        <div style={{fontSize:12, color:MU, fontWeight:500, letterSpacing:'.5px', textTransform:'uppercase'}}>Sales Operating System</div>
      </div>
      {loginUsers.length > 0 && (
        <div style={{marginBottom:20}}>
          <div style={{fontSize:11,fontWeight:600,color:MU,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:10}}>Select account</div>
          <div className="login-user-picker">
            {loginUsers.map(lu => {
              const active = u === lu.username;
              return (
                <button
                  key={lu.username}
                  type="button"
                  className={'login-user-chip'+(active?' login-user-chip--active':'')}
                  onClick={() => setU(lu.username)}
                >
                  <UserAvatar username={lu.username} avatarColor={lu.avatarColor} size={34} active={active} />
                  <span className="login-user-chip__name">{lu.username}</span>
                  <span className="login-user-chip__meta">{(lu.assignedCities||[]).length ? lu.assignedCities.join(', ') : lu.role === 'Admin' ? 'All cities' : 'All cities'}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
      {err && <div style={{color:RD, fontSize:13, marginBottom:16, textAlign:'center', background:RL, borderRadius:10, padding:'10px 14px'}}>{err}</div>}
      <div style={{marginBottom:12}}><input placeholder="Username" style={s.inp} value={u} onChange={e=>setU(e.target.value)} required /></div>
      <div style={{marginBottom:24}}><input placeholder="Password" type="password" style={s.inp} value={p} onChange={e=>setP(e.target.value)} required /></div>
      {selectedUser && (
        <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:16,padding:'10px 12px',background:'#F9FAFB',borderRadius:12,border:'1px solid #E5E7EB'}}>
          <UserAvatar username={selectedUser.username} avatarColor={selectedUser.avatarColor} size={36} />
          <div>
            <div style={{fontSize:13,fontWeight:700,color:TX}}>{selectedUser.username}</div>
            <div style={{fontSize:11,color:MU}}>{selectedUser.role}{(selectedUser.assignedCities||[]).length ? ` · ${selectedUser.assignedCities.join(', ')}` : ''}</div>
          </div>
        </div>
      )}
      <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={{...s.btn(), width:'100%', justifyContent:'center', padding:'12px 20px', borderRadius:12, fontSize:14}} type="submit" disabled={load}>{load ? 'Signing in…' : 'Sign In'}</motion.button>
    </form>
  </div>;
}

const UNCATEGORIZED = '__none__';
const DEFAULT_PROPOSAL_TEMPLATES = [{ name:'IX', url:'' }, { name:'Digital tool', url:'' }, { name:'Experience center', url:'' }];
const PROPOSAL_SENT_VIA = ['Email', 'WhatsApp', 'In-person', 'Other'];
const LEGACY_PROPOSAL_NAMES = ['Standard Proposal', 'Premium Proposal', 'Pilot Proposal'];
const normalizeProposalTemplates = (templates) => {
  const list = visibleProposalTemplates(templates);
  if (!list.length) return DEFAULT_PROPOSAL_TEMPLATES;
  const names = list.map(t => t.name);
  const isLegacy = names.length === LEGACY_PROPOSAL_NAMES.length && LEGACY_PROPOSAL_NAMES.every(n => names.includes(n));
  return isLegacy ? DEFAULT_PROPOSAL_TEMPLATES : list;
};
const getProposalTemplates = cfg => normalizeProposalTemplates(cfg?.proposalTemplates);
const DEFAULT_TARGET_METRICS = { revenue: true, calls: true, connected: true, meetings: true, pipelineValue: true, demos: false };
const TARGET_METRIC_DEFS = [
  { key: 'revenue', label: 'Revenue', icon: '💵', color: '#059669', isCurrency: true },
  { key: 'calls', label: 'Calls', icon: '📞', color: '#6366F1' },
  { key: 'connected', label: 'Connected', icon: '✅', color: '#10B981' },
  { key: 'meetings', label: 'Meetings', icon: '📅', color: '#F59E0B' },
  { key: 'pipelineValue', label: 'Pipeline', icon: '💰', color: '#8B5CF6', isCurrency: true },
  { key: 'demos', label: 'Demos', icon: '🎬', color: '#EC4899' },
];
const getEnabledTargetMetrics = (cfg, period = 'monthly') => {
  const toggles = { ...DEFAULT_TARGET_METRICS, ...(cfg?.targetMetricsEnabled || {}) };
  return TARGET_METRIC_DEFS.filter(m => toggles[m.key] && !(m.key === 'revenue' && period !== 'monthly'));
};
const getTargetMonthRange = (offset) => {
  const d = new Date();
  d.setMonth(d.getMonth() + offset);
  return {
    start: new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10),
    end: new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10),
    label: d.toLocaleString('default', { month: 'long', year: 'numeric' }),
  };
};
const getTargetWeekRange = (offset) => {
  const d = new Date();
  d.setDate(d.getDate() - d.getDay() + 1 + offset * 7);
  const start = d.toISOString().slice(0, 10);
  const end = new Date(d.getTime() + 6 * 864e5).toISOString().slice(0, 10);
  return { start, end, label: `${start} – ${end}` };
};

function TargetMetricTile({ def, target, actual, fmtINR, TX, MU, GN, RD }) {
  const t = Number(target) || 0;
  const a = Number(actual) || 0;
  const pct = t > 0 ? Math.min(Math.round(a / t * 100), 999) : 0;
  const achieved = t > 0 && a >= t;
  const lagging = t > 0 && a < t;
  const gap = t > 0 ? Math.max(t - a, 0) : 0;
  const fmtVal = (n) => {
    const num = Number(n) || 0;
    if (def.isCurrency) return num ? fmtINR(num) : '₹0';
    return String(num);
  };
  return (
    <div className="target-metric-tile">
      <div className="target-metric-tile__head">
        <span className="target-metric-tile__icon">{def.icon}</span>
        <span className="target-metric-tile__label">{def.label}</span>
      </div>
      <div className="target-metric-tile__body">
        <div className="target-metric-tile__col">
          <div className="target-metric-tile__sub">Target</div>
          <div className="target-metric-tile__num">{t > 0 ? fmtVal(t) : '—'}</div>
        </div>
        <div className="target-metric-tile__col">
          <div className="target-metric-tile__sub">Achieved</div>
          <div className="target-metric-tile__num" style={{ color: achieved ? GN : lagging && t > 0 ? RD : TX }}>{fmtVal(a)}</div>
        </div>
      </div>
      {t > 0 ? (
        <>
          <div className="target-metric-tile__bar"><div className="target-metric-tile__fill" style={{ width: `${Math.min(pct, 100)}%`, background: achieved ? GN : def.color }} /></div>
          <div className="target-metric-tile__footer">
            <span style={{ color: achieved ? GN : MU, fontWeight: 600 }}>{pct}%</span>
            {lagging && gap > 0 && <span style={{ color: RD, fontWeight: 600 }}>↓ {def.isCurrency ? (gap ? fmtINR(gap) : '₹0') : gap} behind</span>}
            {achieved && <span style={{ color: GN, fontWeight: 600 }}>✓ On track</span>}
          </div>
        </>
      ) : null}
    </div>
  );
}
const proposalRecipientName = (p, spList) => (p?.sentToName || '').trim() || spList?.find(s => String(s._id) === String(p?.spocId))?.name || '—';
const SOP_CAT_COLORS = {'Email Template':[GL,GN],'WhatsApp Template':['#ECFEFF','#0891B2'],'Calling Script':[RL,RD],'Process':[PL,P],'Checklist':[YLL,YL]};
const SOP_CAT_ICONS = {'Email Template':'✉️','WhatsApp Template':'💬','Calling Script':'📞','Process':'📋','Checklist':'✅'};
const SOP_STAGES = [
  {key:'first-contact',label:'First Contact',icon:'📞',color:'#6366F1',desc:'Cold calls, intros & first impressions'},
  {key:'follow-up',label:'Follow-up',icon:'🔄',color:'#F59E0B',desc:'Nurturing leads & staying top of mind'},
  {key:'meeting',label:'Meeting',icon:'🤝',color:'#10B981',desc:'Before, during & after meetings'},
  {key:'proposal',label:'Proposal',icon:'📄',color:'#8B5CF6',desc:'Quotes, proposals & deal negotiation'},
  {key:'closing',label:'Closing',icon:'🏆',color:'#059669',desc:'Final steps to win the deal'},
  {key:'general',label:'General',icon:'📌',color:'#64748B',desc:'Miscellaneous SOPs & guidelines'},
];
const DUMMY_SOPS = [
  {_id:'_d1',title:'First Introduction Email',category:'Email Template',useCaseKey:'INTRO_EMAIL',tags:['first-contact'],whereToUse:'Send when contacting a prospect for the very first time.',subject:'Introduction from {{salespersonName}}',body:'Hi {{spocName}},\n\nHope this finds you well. I am {{salespersonName}} from Unada and I wanted to briefly introduce our services...\n\nWould love to connect for a quick 15-min call.\n\nBest,\n{{salespersonName}}'},
  {_id:'_d2',title:'Meeting Day Checklist',category:'Checklist',useCaseKey:'MEETING_DAY',tags:['meeting'],whereToUse:'Run through this every morning a meeting is scheduled.',steps:['Check meeting link is active','Send WhatsApp reminder 1 hr before','Prepare company background notes','Keep proposal PDF ready','Test screen-share if demo is planned']},
  {_id:'_d3',title:'Follow-Up WhatsApp',category:'WhatsApp Template',useCaseKey:'FOLLOWUP_WA',tags:['follow-up'],whereToUse:'Send 24 hrs after a meeting to keep momentum.',body:'Hi {{spocName}} 👋, great meeting you today! Sharing a quick recap and next steps below. Let me know if you have any questions.'},
  {_id:'_d4',title:'Not Interested Handling Script',category:'Calling Script',useCaseKey:'NI_SCRIPT',tags:['first-contact','follow-up'],whereToUse:'Use when a prospect says they are not interested.',body:'I completely understand. May I ask what would need to change for this to be relevant? I can note it down and reach back only when it makes sense for you.'},
  {_id:'_d5',title:'Post-Meeting MOM Process',category:'Process',useCaseKey:'POST_MEETING_MOM',tags:['meeting'],whereToUse:'Use immediately after every completed meeting.',steps:['Update the meeting as Completed/Done','Write MOM/Summary in clear bullets','Capture key decisions and blockers','Set next action date before leaving the record','Share summary with the client within 2 hours']},
  {_id:'_d6',title:'Proposal Follow-up Checklist',category:'Checklist',useCaseKey:'PROPOSAL_FOLLOWUP',tags:['proposal'],whereToUse:'After sending a proposal, follow this to close faster.',steps:['Call within 24 hrs to confirm receipt','Address any immediate questions','Schedule a review meeting within 3 days','Prepare comparison sheet if competitor is involved','Send reminder if no response in 48 hrs']},
];

// ══════════════════════════════════════════════
// MAIN APP
// ══════════════════════════════════════════════
function GlobalSearch({ companies, spocs, calls, spocById, companyById, fmtDate, setDetailId, setPage, setDTab, setSearch, s, PL, MU, LT, WH, GN, RD, TX }) {
  const [gq, setGq] = React.useState('');
  const [gOpen, setGOpen] = React.useState(false);
  const gRef = React.useRef(null);
  React.useEffect(()=>{
    const handler = e => { if(gRef.current && !gRef.current.contains(e.target)) setGOpen(false); };
    document.addEventListener('mousedown', handler);
    return ()=>document.removeEventListener('mousedown', handler);
  },[]);
  const q = gq.trim().toLowerCase();
  const matchCos = q.length>=2 ? companies.filter(c=>c.name.toLowerCase().includes(q)||c.city.toLowerCase().includes(q)).slice(0,5) : [];
  const matchSps = q.length>=2 ? spocs.filter(sp=>sp.name.toLowerCase().includes(q)||(sp.phone||'').includes(q)||(sp.email||'').toLowerCase().includes(q)).slice(0,5) : [];
  const matchCls = q.length>=2 ? calls.filter(c=>{ const sp=spocById?.[String(c.spocId)]||spocs.find(x=>x._id===c.spocId),co=companyById?.[String(c.companyId)]||companies.find(x=>x._id===c.companyId); return sp?.name?.toLowerCase().includes(q)||co?.name?.toLowerCase().includes(q)||(c.notes||'').toLowerCase().includes(q); }).slice(0,4) : [];
  const hasResults = matchCos.length||matchSps.length||matchCls.length;
  const go = (fn) => { fn(); setGq(''); setGOpen(false); };
  return <div className="global-search" ref={gRef}>
    <div style={{width:'100%',maxWidth:440,position:'relative',display:'flex',alignItems:'center'}}>
      <svg style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)',pointerEvents:'none',zIndex:1}} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={LT} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
      <input style={{...s.inp,paddingLeft:36,paddingRight:gq?32:12,borderRadius:24,fontSize:13,width:'100%'}} placeholder="Search companies, SPOCs, calls…" value={gq} onChange={e=>{setGq(e.target.value);setGOpen(true);}} onFocus={()=>setGOpen(true)} autoComplete="off"/>
      {gq&&<span style={{position:'absolute',right:12,cursor:'pointer',color:MU,fontSize:18,lineHeight:1,top:'50%',transform:'translateY(-50%)'}} onClick={()=>{setGq('');setGOpen(false);}}>×</span>}
      {gOpen&&q.length>=2&&<div style={{position:'absolute',top:'calc(100% + 8px)',left:0,right:0,background:WH,borderRadius:14,boxShadow:'0 8px 32px rgba(0,0,0,0.14)',border:'1px solid #E5E7EB',zIndex:9999,maxHeight:420,overflowY:'auto',padding:8}}>
        {!hasResults&&<div style={{padding:'18px 14px',textAlign:'center',color:LT,fontSize:13}}>No results for "{gq}"</div>}
        {matchCos.length>0&&<>
          <div style={{fontSize:10,fontWeight:700,color:MU,textTransform:'uppercase',letterSpacing:'.6px',padding:'4px 10px 6px'}}>Companies</div>
          {matchCos.map(co=><div key={co._id} style={{display:'flex',alignItems:'center',gap:10,padding:'9px 12px',borderRadius:10,cursor:'pointer'}} onMouseEnter={e=>e.currentTarget.style.background=PL} onMouseLeave={e=>e.currentTarget.style.background=''} onClick={()=>go(()=>{setDetailId(co._id);setPage('companies');setDTab('overview');})}>
            <span style={{fontSize:16}}>🏢</span>
            <div style={{flex:1}}><div style={{fontSize:13,fontWeight:600}}>{co.name}</div><div style={{fontSize:11,color:MU}}>{co.city} · {co.salesperson||'—'}</div></div>
            <span style={{fontSize:11,background:'#F3F4F6',padding:'2px 7px',borderRadius:6,color:MU}}>{co.dealStatus}</span>
          </div>)}
        </>}
        {matchSps.length>0&&<>
          <div style={{fontSize:10,fontWeight:700,color:MU,textTransform:'uppercase',letterSpacing:'.6px',padding:'8px 10px 6px',borderTop:matchCos.length?'1px solid #F3F4F6':'none'}}>SPOCs</div>
          {matchSps.map(sp=>{ const co=companies.find(c=>String(c._id)===String(sp.companyId?._id||sp.companyId)); return<div key={sp._id} style={{display:'flex',alignItems:'center',gap:10,padding:'9px 12px',borderRadius:10,cursor:'pointer'}} onMouseEnter={e=>e.currentTarget.style.background=PL} onMouseLeave={e=>e.currentTarget.style.background=''} onClick={()=>go(()=>{if(co){setDetailId(co._id);setPage('companies');setDTab('spocs');}})}>
            <span style={{fontSize:16}}>👤</span>
            <div style={{flex:1}}><div style={{fontSize:13,fontWeight:600}}>{sp.name}</div><div style={{fontSize:11,color:MU}}>{sp.designation||'—'} · {co?.name||'—'}</div></div>
            <span style={{fontSize:11,background:'#F3F4F6',padding:'2px 7px',borderRadius:6,color:MU}}>{sp.stage}</span>
          </div>;})}
        </>}
        {matchCls.length>0&&<>
          <div style={{fontSize:10,fontWeight:700,color:MU,textTransform:'uppercase',letterSpacing:'.6px',padding:'8px 10px 6px',borderTop:(matchCos.length||matchSps.length)?'1px solid #F3F4F6':'none'}}>Calls</div>
          {matchCls.map(cl=>{ const sp=spocs.find(x=>x._id===cl.spocId),co=companies.find(x=>x._id===cl.companyId); return<div key={cl._id} style={{display:'flex',alignItems:'center',gap:10,padding:'9px 12px',borderRadius:10,cursor:'pointer'}} onMouseEnter={e=>e.currentTarget.style.background=PL} onMouseLeave={e=>e.currentTarget.style.background=''} onClick={()=>go(()=>{setPage('calllog');setSearch(q);})}>
            <span style={{fontSize:16}}>📞</span>
            <div style={{flex:1}}><div style={{fontSize:13,fontWeight:600}}>{sp?.name||'—'} · {co?.name||'—'}</div><div style={{fontSize:11,color:MU}}>{fmtDate(cl.date)} · {cl.conversationOutcome||cl.outcome||'—'}</div></div>
            <span style={{fontSize:11,color:cl.connected?GN:RD}}>{cl.connected?'Connected':'Not connected'}</span>
          </div>;})}
        </>}
      </div>}
    </div>
  </div>;
}

export default function App() {
  // Data
  const [companies, setCompanies] = useState([]);
  const [spocs, setSpocs] = useState([]);
  const [calls, setCalls] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [leadBank, setLeadBank] = useState([]);
  const [sops, setSops] = useState([]);
  const [sopFilter, setSopFilter] = useState('all');
  const [sopSearch, setSopSearch] = useState('');
  const [sopExpanded, setSopExpanded] = useState({});
  const [sopChecked, setSopChecked] = useState({});
  const [config, setConfig] = useState({ leadSources:[], salespersons:[], segments:[], niReasons:NIR, strikeoutReasons:['Wrong number','Not reachable','Duplicate contact','Left the company','Not relevant'], developerCategories:[], proposalTemplates:DEFAULT_PROPOSAL_TEMPLATES, callTarget:50, recycleDays:90, showTimelineMeetingAge:false, targetMetricsEnabled: DEFAULT_TARGET_METRICS });
  const [dropped, setDropped] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadingQuote, setLoadingQuote] = useState(() =>
    DEFAULT_QUOTES[Math.floor(Math.random() * DEFAULT_QUOTES.length)]
  );
  useEffect(() => {
    if (!loading) return;
    const list = (config.motivationalQuotes?.length) ? config.motivationalQuotes : DEFAULT_QUOTES;
    setLoadingQuote(list[Math.floor(Math.random() * list.length)]);
  }, [loading]);

  // UI state
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1');
  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed(v => {
      const next = !v;
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0');
      return next;
    });
  };
  const isMobile = useMediaQuery('(max-width: 768px)');
  useEffect(() => { if (!isMobile) setMobileNavOpen(false); }, [isMobile]);
  useEffect(() => {
    document.body.style.overflow = mobileNavOpen && isMobile ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileNavOpen, isMobile]);
  const [page, setPage] = useState('overview');
  const [currentUser, setCurrentUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('user')) || null; } catch { return null; }
  });
  const user = currentUser?.username || '';
  const [pTab, setPTab] = useState('all');
  const [search, setSearch] = useState('');
  const [fCity, setFCity] = useState('');
  const [coStageFilter, setCoStageFilter] = useState('');
  const [coSeg, setCoSeg] = useState(''); // clickable overview-tile segment filter
  const [coStars, setCoStars] = useState(''); // '' = all, '1'/'2'/'3' = star rating filter
  const coNoteRef = React.useRef(null); // "Next line of action" notepad
  const [coNoteSaving, setCoNoteSaving] = useState(false);
  const [coNeedSpocSaving, setCoNeedSpocSaving] = useState(false);
  const [coPage, setCoPage] = useState(1);
  const CO_PER_PAGE = 25;
  const [fSrc, setFSrc] = useState('');
  const [fOut, setFOut] = useState('');
  const [dateFrom, setDateFrom] = useState(today());
  const [dateTo, setDateTo] = useState(today());
  const [detailId, setDetailId] = useState(null);
  const [dTab, setDTab] = useState('overview');
  // Open every company detail scrolled to the top (don't inherit the list's scroll position)
  useEffect(() => { if (detailId) window.scrollTo(0, 0); }, [detailId]);
  const [mtgPeriod, setMtgPeriod] = useState('all');
  const [mtgStatus, setMtgStatus] = useState('all');
  const [mtgSearch, setMtgSearch] = useState('');
  const [mtgCity, setMtgCity] = useState('');
  const [mtgConductedBy, setMtgConductedBy] = useState('all');
  const [mtgAccountOwner, setMtgAccountOwner] = useState('');
  const [mtgType, setMtgType] = useState('');
  const [mtgPipeline, setMtgPipeline] = useState('');
  const [mtgShowFilters, setMtgShowFilters] = useState(false);
  const [mtgView, setMtgView] = useState('scheduled');
  const [mtgExpanded, setMtgExpanded] = useState({});
  const [clPage, setClPage] = useState(1);
  const CL_PER_PAGE = 25;
  const [clConn, setClConn] = useState('all');
  const [clOutcome, setClOutcome] = useState('');
  const [clType, setClType] = useState('');
  const [clCity, setClCity] = useState('');
  const [clPeriod, setClPeriod] = useState('today');
  const [clShowFilters, setClShowFilters] = useState(false);

  // Lead bank
  const [bCity, setBCity] = useState(CITIES[0]);
  const [bText, setBText] = useState('');
  const [lbFCity, setLbFCity] = useState('');
  const [lbFStat, setLbFStat] = useState('');
  const [lbFCat, setLbFCat] = useState('');
  const [lbFTop, setLbFTop] = useState(false);
  const [lbSelectMode, setLbSelectMode] = useState(false);
  const [lbSelected, setLbSelected] = useState(new Set());
  const [lbPage, setLbPage] = useState(1);
  const LB_PER_PAGE = 50;
  const [tdCity, setTdCity] = useState('');
  const [tdSearch, setTdSearch] = useState('');
  const [tdStars, setTdStars] = useState(''); // '' = all, '1'/'2'/'3' = star rating filter
  const [anDrill, setAnDrill] = useState(null);
  const [anCovCompany, setAnCovCompany] = useState(''); // coverage drill: selected companyId
  const [covPeriod, setCovPeriod] = useState('all');    // coverage card: local period filter
  const [covPerson, setCovPerson] = useState('all');    // coverage card: local salesperson filter (Everyone)
  const [covCity, setCovCity] = useState('');           // coverage card: local city filter (under More)
  const [covMore, setCovMore] = useState(false);        // coverage card: show advanced (city) filter
  const [tdProspectText, setTdProspectText] = useState('');
  const [tdProspectCity, setTdProspectCity] = useState('');
  const [tdProspectLoading, setTdProspectLoading] = useState(false);
  const [tdProspectResult, setTdProspectResult] = useState(null);


  // Follow-up filters
  const [fuFilter, setFuFilter] = useState('overdue');
  const [fuCity, setFuCity] = useState('');
  const [fuSearch, setFuSearch] = useState('');
  const [fuInsightsOpen, setFuInsightsOpen] = useState(false);
  const [fuInsightsSearch, setFuInsightsSearch] = useState('');
  const [fuInsightsSegment, setFuInsightsSegment] = useState('');
  const [fuInsightsPurpose, setFuInsightsPurpose] = useState('');
  const [fuInsightsStage, setFuInsightsStage] = useState('');
  const [fuInsightsBlocker, setFuInsightsBlocker] = useState('');
  const [fuInsightsDue, setFuInsightsDue] = useState('');
  const [fuInsightsConnect, setFuInsightsConnect] = useState('');

  // Queue config
  const [qShow, setQShow] = useState(false);
  const [qTarget, setQTarget] = useState(50);
  const [qMethod, setQMethod] = useState('balanced');
  const [qBk, setQBk] = useState({overdue:true,today:true,retry:true,hot:true,fresh:true});
  const [qCity, setQCity] = useState('');
  const [qSkip, setQSkip] = useState({});
  const [qSelected, setQSelected] = useState({});

  // Daily queue
  const [dailyQueue, setDailyQueue] = useState([]);
  const [dqDate, setDqDate] = useState(today());
  const [dqCity, setDqCity] = useState(CITIES[0]);
  const [dqTarget, setDqTarget] = useState(30);
  const [dqCarryForward, setDqCarryForward] = useState(true);
  const [dqGenerating, setDqGenerating] = useState(false);
  const [dqSpFilter, setDqSpFilter] = useState('all');
  const [dqMode, setDqMode] = useState('auto');
  const [dqManualView, setDqManualView] = useState('company'); // 'company' | 'spoc'
  const [dqManualCity, setDqManualCity] = useState('');
  const [dqManualSearch, setDqManualSearch] = useState('');
  const [dqManualStage, setDqManualStage] = useState('');
  const [dqManualHot, setDqManualHot] = useState(false);
  const [dqManualTop, setDqManualTop] = useState(false);
  const [dqManualSel, setDqManualSel] = useState(new Set());
  const [dqManualAssign, setDqManualAssign] = useState('');
  const [dqManualQuickN, setDqManualQuickN] = useState('');
  const [selfGenCity, setSelfGenCity] = useState('');
  const [selfGenTarget, setSelfGenTarget] = useState(30);
  const [selfGenCarry, setSelfGenCarry] = useState(true);
  const [selfGenLoading, setSelfGenLoading] = useState(false);

  // EOD state (must be declared before the useEffect below)
  const [eodPreview, setEodPreview] = useState(null);
  const [eodPreviewLoading, setEodPreviewLoading] = useState(false);
  const [eodApiError, setEodApiError] = useState('');
  const [eodNote, setEodNote] = useState('');
  const [eodSubmitting, setEodSubmitting] = useState(false);
  const [eodSubmitted, setEodSubmitted] = useState(false);
  const [eodReports, setEodReports] = useState([]);
  const [eodHistoryLoading, setEodHistoryLoading] = useState(false);
  const [eodDate, setEodDate] = useState(today());
  const [eodHistoryPerson, setEodHistoryPerson] = useState('all');
  const [eodEditing, setEodEditing] = useState(false);
  const [eodEditId, setEodEditId] = useState(null);   // inline-editing a history report
  const [eodEditNote, setEodEditNote] = useState('');
  const [eodRowSaving, setEodRowSaving] = useState(false);
  const [eodTeam, setEodTeam] = useState(null);       // admin: today's team EOD status for eodDate
  const [eodTeamLoading, setEodTeamLoading] = useState(false);
  const [eodAddedView, setEodAddedView] = useState('spocs'); // EOD "Added" card: 'spocs' | 'companies'

  const [waStatus, setWaStatus] = useState(null);
  const [waPreview, setWaPreview] = useState('');
  const [waPreviewDate, setWaPreviewDate] = useState(today());
  const [waLoading, setWaLoading] = useState(false);
  const [discordActionMsg, setDiscordActionMsg] = useState(null);
  const [discordSaveLoading, setDiscordSaveLoading] = useState(false);
  const [settingsTab, setSettingsTab] = useState('integrations');
  const [teamHistory, setTeamHistory] = useState([]);

  // Load daily queue when date changes or page opens
  useEffect(() => {
    if (page === 'queue') {
      api.getQueue({ date: dqDate }).then(setDailyQueue).catch(() => setDailyQueue([]));
    }
  }, [page, dqDate]);

  // ── Tasks (personal action pad) ──
  const [tasks, setTasks] = useState([]);
  const [taskInput, setTaskInput] = useState({ today: '', tomorrow: '' });
  const [taskBusy, setTaskBusy] = useState(false);
  const reloadTasks = () => api.getTasks().then(setTasks).catch(() => {});
  useEffect(() => {
    if (page !== 'tasks') return;
    let alive = true;
    api.getTasks().then(d => { if (alive) setTasks(d); }).catch(() => { if (alive) setTasks([]); });
    return () => { alive = false; };
  }, [page]);
  const addTask = async (bucket) => {
    const text = (taskInput[bucket] || '').trim();
    if (!text || taskBusy) return;
    setTaskBusy(true);
    try { await api.createTask({ text, bucket }); setTaskInput(p => ({ ...p, [bucket]: '' })); await reloadTasks(); }
    catch (e) { alert(e.message); } finally { setTaskBusy(false); }
  };
  const toggleTask = async (t) => {
    try { await api.updateTask(t._id, { status: t.status === 'complete' ? 'in-progress' : 'complete' }); await reloadTasks(); }
    catch (e) { alert(e.message); }
  };
  const moveTask = async (t, bucket) => {
    try { await api.updateTask(t._id, { bucket }); await reloadTasks(); } catch (e) { alert(e.message); }
  };
  const removeTask = async (t) => {
    try { await api.deleteTask(t._id); await reloadTasks(); } catch (e) { alert(e.message); }
  };
  const taskProgressCount = tasks.filter(t => t.bucket === 'today' && t.status !== 'complete').length;

  // ── Follow-up reminders — popup ~5 min before a follow-up's scheduled time ──
  const [fuReminders, setFuReminders] = useState([]);
  const fuRemindedRef = React.useRef(new Set());
  useEffect(() => {
    const admin = currentUser?.role === 'Admin';
    const check = () => {
      const t = today();
      const now = new Date();
      const nowMin = now.getHours() * 60 + now.getMinutes();
      spocs.forEach(sp => {
        if (sp.nextFollowUp !== t || !sp.nextFollowUpTime) return;
        const co = companies.find(c => String(c._id) === String(sp.companyId));
        if (!co) return;
        // Only remind about the current user's own leads (admins see all)
        if (!admin && user && co.salesperson && co.salesperson !== user) return;
        const parts = String(sp.nextFollowUpTime).split(':');
        const fuMin = (+parts[0]) * 60 + (+parts[1]);
        if (isNaN(fuMin)) return;
        const diff = fuMin - nowMin;
        const key = t + ':' + sp._id;
        if (fuRemindedRef.current.has(key)) return;
        try { if (localStorage.getItem('fu_rem_' + key)) { fuRemindedRef.current.add(key); return; } } catch {}
        // Fire once when within 5 minutes before the time (small grace just after)
        if (diff <= 5 && diff >= -1) {
          fuRemindedRef.current.add(key);
          try { localStorage.setItem('fu_rem_' + key, '1'); } catch {}
          setFuReminders(prev => prev.some(r => r.spocId === sp._id) ? prev : [...prev, { spocId: sp._id, name: sp.name, company: co.name || '', time: sp.nextFollowUpTime, reason: sp.followUpReason || '' }]);
          if ('Notification' in window && Notification.permission === 'granted') {
            try { new Notification('Follow-up in 5 min', { body: `${sp.name} · ${co.name || ''} at ${sp.nextFollowUpTime}` }); } catch {}
          }
        }
      });
    };
    check();
    const id = setInterval(check, 30000);
    return () => clearInterval(id);
  }, [spocs, companies, user, currentUser]);
  const dismissReminder = (spocId) => setFuReminders(prev => prev.filter(r => r.spocId !== spocId));

  // Load EOD preview when page opens or date changes
  // Use stable primitives (user, isAdmin) not currentUser object to avoid re-render loop
  const eodUser = currentUser?.username || '';
  const eodIsAdmin = currentUser?.role === 'Admin';
  const buildLocalEodPreview = useCallback(async (date, salesperson) => {
    let dayCalls = calls.filter(c => c.date === date && c.salesperson === salesperson);
    let dayMeetings = meetings.filter(m => m.date === date && m.salesperson === salesperson);
    if (!dayCalls.length && !dayMeetings.length) {
      const [fetchedCalls, fetchedMeetings] = await Promise.all([
        api.getCalls({ date, salesperson }).catch(() => []),
        api.getMeetings({ dateFrom: date, dateTo: date, salesperson }).catch(() => []),
      ]);
      dayCalls = (fetchedCalls || []).filter(c => c.salesperson === salesperson);
      dayMeetings = (fetchedMeetings || []).filter(m => m.salesperson === salesperson);
    }
    return getSalespersonDayActivity(date, salesperson, dayCalls, dayMeetings, companies);
  }, [calls, meetings, companies]);
  useEffect(() => {
    if (page === 'eod' && eodUser) {
      setEodPreviewLoading(true);
      setEodSubmitted(false);
      setEodEditing(false);
      setEodApiError('');
      api.getEodPreview({ date: eodDate, salesperson: eodUser })
        .then(d => { setEodPreview(d); setEodPreviewLoading(false); setEodApiError(''); })
        .catch(async (err) => {
          if (!api.isEodApiAvailable()) {
            setEodApiError('EOD sync unavailable on server — reports save on this device until backend is updated.');
          }
          try {
            const local = await buildLocalEodPreview(eodDate, eodUser);
            setEodPreview(local);
          } catch (e) {
            setEodApiError(e.message || 'Could not load EOD preview');
          } finally {
            setEodPreviewLoading(false);
          }
        });
      setEodHistoryLoading(true);
      const params = eodIsAdmin ? {} : { salesperson: eodUser };
      api.getEodReports(params)
        .then(r => {
          setEodReports(r);
          const existing = r.find(x => x.date === eodDate && x.salesperson === eodUser);
          if (existing?.note) setEodNote(existing.note);
          else setEodNote('');
          setEodHistoryLoading(false);
        })
        .catch(() => { setEodReports([]); setEodHistoryLoading(false); });

      if (eodIsAdmin) {
        setEodTeamLoading(true);
        api.getEodTeam(eodDate)
          .then(t => { setEodTeam(t); setEodTeamLoading(false); })
          .catch(() => { setEodTeam(null); setEodTeamLoading(false); }); // team view is server-only; hide on failure
      } else { setEodTeam(null); }
    }
  }, [page, eodDate, eodUser, eodIsAdmin, buildLocalEodPreview]);

  useEffect(() => {
    if (page === 'settings' && currentUser?.role === 'Admin') {
      api.getDailyReportStatus().then(setWaStatus).catch(() => setWaStatus(null));
    }
  }, [page, currentUser?.role]);

  // Modal + form
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [inlineSps, setInlineSps] = useState([]);
  const [showInlineSp, setShowInlineSp] = useState(false);
  const [inlineSpF, setInlineSpF] = useState({});
  const [callStep, setCallStep] = useState(1);
  const [callSearch, setCallSearch] = useState('');
  const [trailData, setTrailData] = useState([]);
  const [trailSpoc, setTrailSpoc] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [removeUserModal, setRemoveUserModal] = useState(null);
  const [removeUserLoadingId, setRemoveUserLoadingId] = useState(null);
  const [strikeModal, setStrikeModal] = useState(null); // { spoc }
  const [reconcileModal, setReconcileModal] = useState(null); // { exact:[], fuzzy:[], approved: Set }
  const [reconcileSaving, setReconcileSaving] = useState(false);
  const [companyCheck, setCompanyCheck] = useState(null); // { name, city, dupCompanies, prospects, linkLeadId, keepName }
  const [dupModal, setDupModal] = useState(null); // { groups, groupCount, totalDuplicates, selections }
  const [dupMergingGroup, setDupMergingGroup] = useState(null);
  const [dupLoading, setDupLoading] = useState(false);

  // Upload
  const [uploadResult, setUploadResult] = useState(null);

  // Analytics
  const [srcAnalytics, setSrcAnalytics] = useState([]);
  const [personAnalytics, setPersonAnalytics] = useState([]);
  const [funnel, setFunnel] = useState({});
  const [dailyCalls, setDailyCalls] = useState([]);
  const [pipelineData, setPipelineData] = useState({});
  const [cityAnalytics, setCityAnalytics] = useState([]);
  const [conversionRates, setConversionRates] = useState([]);
  const [anFilter, setAnFilter] = useState('all');
  const [anPeriod, setAnPeriod] = useState('all');
  const [anCities, setAnCities] = useState([]);

  // Targets
  const [targetsData, setTargetsData] = useState([]);
  const [targetsActuals, setTargetsActuals] = useState({});
  const [targetPeriod, setTargetPeriod] = useState('monthly');
  const [targetWeekOffset, setTargetWeekOffset] = useState(0);
  const [targetRefresh, setTargetRefresh] = useState(0);
  const [targetView, setTargetView] = useState('current'); // 'current' | 'history'
  const [targetHistory, setTargetHistory] = useState([]);
  const [targetHistoryLoading, setTargetHistoryLoading] = useState(false);

  // Visits
  const [visits, setVisits] = useState([]);
  // Pending drop approvals
  const [pendingDrops, setPendingDrops] = useState([]);
  const [visitDetail, setVisitDetail] = useState(null);
  const [visitDetailData, setVisitDetailData] = useState(null);
  const [visitDetailLoading, setVisitDetailLoading] = useState(false);
  const [probableClients, setProbableClients] = useState([]);
  const [dataCounts, setDataCounts] = useState({ unminedProspects: 0, dropped: 0, pendingDrops: 0 });
  const [loadedSliceList, setLoadedSliceList] = useState([]);
  const loadedSlicesRef = useRef(new Set());
  const activityInflightRef = useRef(null);
  const companiesRef = useRef([]);

  const markSlicesLoaded = useCallback((keys) => {
    keys.forEach(k => loadedSlicesRef.current.add(k));
    setLoadedSliceList(Array.from(loadedSlicesRef.current));
  }, []);

  const ALL_ACTIVITY_SLICES = ['calls', 'meetings', 'leadBank', 'sops', 'dropped', 'proposals', 'probableClients', 'visits', 'pendingDrops'];
  const PAGE_ACTIVITY_SLICES = {
    overview: ['calls', 'meetings'],
    leadbank: ['leadBank', 'calls'],
    companies: ['calls', 'meetings'],
    calllog: ['calls'],
    followups: ['calls', 'meetings'],
    meetings: ['meetings'],
    analytics: ['calls', 'meetings', 'proposals', 'leadBank'],
    sops: ['sops'],
    dropped: ['dropped'],
    approvals: ['pendingDrops'],
    visits: ['visits', 'probableClients'],
    probableclients: ['probableClients'],
    queue: ['calls'],
  };

  const uf = (k,v) => setForm(prev => ({...prev, [k]:v}));
  const touch = k => setTouched(prev => ({...prev,[k]:true}));
  const isAdmin = currentUser?.role === 'Admin';
  const activitySalesperson = useCallback(
    (fallback = '') => resolveActivitySalesperson({ user, isAdmin, salespersons: config.salespersons || [], fallback }),
    [user, isAdmin, config.salespersons]
  );
  const userCities = (!isAdmin && currentUser?.assignedCities?.length) ? currentUser.assignedCities : null;
  const visibleCities = userCities ? userCities : CITIES;
  const anCitySet = useMemo(() => anCities.length ? new Set(anCities.map(normalizeCityKey)) : null, [anCities]);
  const coCityById = useMemo(() => {
    const map = {};
    companies.forEach(c => { map[String(c._id)] = c.city; });
    return map;
  }, [companies]);
  const companyById = useMemo(() => {
    const map = {};
    companies.forEach(c => { map[String(c._id)] = c; });
    return map;
  }, [companies]);
  companiesRef.current = companies;
  const spocById = useMemo(() => {
    const map = {};
    spocs.forEach(sp => { map[String(sp._id)] = sp; });
    return map;
  }, [spocs]);
  const matchesAnCity = useCallback((companyId) => !anCitySet || anCitySet.has(normalizeCityKey(coCityById[String(companyId)])), [anCitySet, coCityById]);
  const toggleAnCity = city => setAnCities(prev => prev.includes(city) ? prev.filter(c => c !== city) : [...prev, city]);

  const applyActivityPayload = useCallback((data) => {
    const loaded = [];
    if (data.calls) { setCalls(data.calls); loaded.push('calls'); }
    if (data.meetings) { setMeetings(data.meetings); loaded.push('meetings'); }
    if (data.leadBank) { setLeadBank(data.leadBank); loaded.push('leadBank'); }
    if (data.sops) { setSops(data.sops); loaded.push('sops'); }
    if (data.dropped) { setDropped(data.dropped); loaded.push('dropped'); }
    if (data.proposals) { setProposals(data.proposals); loaded.push('proposals'); }
    if (data.probableClients) { setProbableClients(data.probableClients); loaded.push('probableClients'); }
    if (data.visits) { setVisits(data.visits); loaded.push('visits'); }
    if (data.pendingDrops) { setPendingDrops(data.pendingDrops); loaded.push('pendingDrops'); }
    if (loaded.length) markSlicesLoaded(loaded);
  }, [markSlicesLoaded]);

  const legacyLoadActivity = useCallback(async (slices, empFilter, isEmp, aCities) => {
    const cityMatch = (city, list) => list && list.some(ac => ac.toLowerCase() === (city || '').toLowerCase());
    const safeLoad = async (fn, fallback) => {
      try { return await fn(); } catch (e) { console.warn('Load skipped:', e.message); return fallback; }
    };
    const loaders = {
      calls: () => safeLoad(() => api.getCalls(), []),
      meetings: () => safeLoad(() => api.getMeetings(), []),
      leadBank: () => safeLoad(() => api.getLeadBank(), []),
      sops: () => safeLoad(() => api.getSops(), []),
      dropped: () => safeLoad(() => api.getDropped(), []),
      proposals: () => safeLoad(() => api.getProposals(), []),
      probableClients: () => safeLoad(() => api.getProbableClients(), []),
      visits: () => safeLoad(() => api.getVisits(), []),
      pendingDrops: () => safeLoad(() => api.getPendingDrops(), []),
    };
    const results = await Promise.all(slices.map(async (slice) => {
      const raw = await loaders[slice]();
      return [slice, raw];
    }));
    const payload = {};
    for (const [slice, raw] of results) {
      if (slice === 'calls' || slice === 'meetings' || slice === 'proposals') payload[slice] = empFilter(raw, 'companyId');
      else if (slice === 'leadBank' || slice === 'dropped') payload[slice] = (isEmp && aCities) ? raw.filter(x => cityMatch(x.city, aCities)) : raw;
      else payload[slice] = raw;
    }
    applyActivityPayload(payload);
  }, [applyActivityPayload]);

  const fetchActivitySlices = useCallback(async (slices, { force = false } = {}) => {
    const token = localStorage.getItem('token');
    if (!token || !slices?.length) return;
    const needed = force ? slices : slices.filter(s => !loadedSlicesRef.current.has(s));
    if (!needed.length) return;
    const runLegacy = async (sliceList) => {
      let activeUser = null;
      try { activeUser = JSON.parse(localStorage.getItem('user')); } catch { activeUser = null; }
      const isEmp = activeUser && activeUser.role === 'Employee';
      const aCities = activeUser?.assignedCities?.length ? activeUser.assignedCities : null;
      const allowedCoIds = (isEmp && aCities)
        ? new Set(companiesRef.current.filter(c => aCities.some(ac => ac.toLowerCase() === (c.city || '').toLowerCase())).map(c => String(c._id)))
        : null;
      const empFilter = (arr, key) => (isEmp && allowedCoIds) ? arr.filter(x => allowedCoIds.has(String(x[key]?._id || x[key]))) : arr;
      await legacyLoadActivity(sliceList, empFilter, isEmp, aCities);
    };
    const run = async () => {
      // Skip bootstrap/activity once we know production doesn't have it — avoids 404 spam
      if (!api.isBootstrapAvailable()) {
        await runLegacy(needed);
        return;
      }
      try {
        const param = force && needed.length >= ALL_ACTIVITY_SLICES.length ? 'all' : needed.join(',');
        const data = await api.getBootstrapActivity({ slices: param });
        applyActivityPayload(data);
      } catch (e) {
        console.warn('Bootstrap activity failed, falling back:', e.message);
        await runLegacy(needed);
      }
    };
    if (activityInflightRef.current) {
      activityInflightRef.current = activityInflightRef.current.then(run).catch(() => run());
    } else {
      activityInflightRef.current = run().finally(() => { activityInflightRef.current = null; });
    }
    await activityInflightRef.current;
  }, [ALL_ACTIVITY_SLICES.length, applyActivityPayload, legacyLoadActivity]);

  // Load all data
  const loadData = useCallback(async ({ fullActivity = true } = {}) => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }
    loadedSlicesRef.current.clear();
    setLoadedSliceList([]);
    const safeLoad = async (fn, fallback) => {
      try { return await fn(); } catch (e) { console.warn('Load skipped:', e.message); return fallback; }
    };
    const cityMatch = (city, list) => list && list.some(ac => ac.toLowerCase() === (city || '').toLowerCase());
    try {
      let boot = null;
      try {
        boot = await api.getBootstrap();
      } catch (bootstrapErr) {
        console.warn('Bootstrap unavailable, using legacy load:', bootstrapErr.message);
      }

      let activeUser = JSON.parse(localStorage.getItem('user') || 'null');
      let co = [];
      let sp = [];
      let cfg = {};

      if (boot) {
        activeUser = boot.me ? { ...activeUser, ...boot.me } : activeUser;
        co = boot.companies || [];
        sp = boot.spocs || [];
        cfg = boot.settings || {};
        setDataCounts(boot.counts || { unminedProspects: 0, dropped: 0, pendingDrops: 0 });
      } else {
        [co, sp, cfg] = await Promise.all([
          safeLoad(() => api.getCompanies(), []),
          safeLoad(() => api.getSpocs(), []),
          safeLoad(() => api.getSettings(), {}),
        ]);
        try {
          const freshUser = await api.getMe();
          activeUser = { ...activeUser, ...freshUser };
        } catch {
          console.log('getMe failed, using localStorage user');
        }
      }

      if (activeUser) {
        localStorage.setItem('user', JSON.stringify(activeUser));
        setCurrentUser(prev => {
          if (!prev) return activeUser;
          return JSON.stringify(prev) === JSON.stringify(activeUser) ? prev : activeUser;
        });
      }

      const isEmp = activeUser && activeUser.role === 'Employee';
      const aCities = activeUser?.assignedCities?.length ? activeUser.assignedCities : null;
      const allowedCoIds = (isEmp && aCities) ? new Set(co.filter(c => cityMatch(c.city, aCities)).map(c => String(c._id))) : null;
      const empFilter = (arr, key) => (isEmp && allowedCoIds) ? arr.filter(x => allowedCoIds.has(String(x[key]?._id || x[key]))) : arr;

      setCompanies(isEmp && aCities ? co.filter(c => cityMatch(c.city, aCities)) : co);
      setSpocs(isEmp && aCities ? sp.filter(s => allowedCoIds.has(String(s.companyId?._id || s.companyId))) : sp);

      let users = [];
      if (boot?.users?.length) {
        users = boot.users;
      } else if (activeUser && activeUser.role === 'Admin') {
        users = await safeLoad(() => api.getUsers(), cfg.salespersons?.map(u => ({ username: u, role: 'Employee' })) || []);
      } else {
        users = [{ username: activeUser?.username, role: activeUser?.role }];
      }
      const discordWebhookUrl = readDiscordWebhookUrl(cfg);
      setConfig({
        ...cfg,
        proposalTemplates: normalizeProposalTemplates(cfg.proposalTemplates),
        salespersons: users.filter(u => u.role !== 'Admin').map(u => u.username) || cfg.salespersons,
        allUsers: users,
        showTimelineMeetingAge: !!cfg.showTimelineMeetingAge,
        targetMetricsEnabled: { ...DEFAULT_TARGET_METRICS, ...(cfg.targetMetricsEnabled || {}) },
        discordWebhookUrl,
      });
      setLoading(false);

      if (fullActivity) {
        await fetchActivitySlices(ALL_ACTIVITY_SLICES, { force: true });
      } else {
        await fetchActivitySlices(['calls', 'meetings'], { force: true });
      }

      setTimeout(() => { api.recycleCheck().catch(() => {}); }, 5000);

      // ── Discord webhook migration (background, non-blocking) ──
      (async () => {
        let webhook = readDiscordWebhookUrl(cfg);
        const legacyWebhook = getStoredDiscordWebhookUrl();
        if (!webhook && legacyWebhook) webhook = legacyWebhook;
        const persistDiscordWebhook = async (url) => {
          const proposalTemplates = mergeWebhookIntoProposalTemplates(cfg.proposalTemplates, url);
          const payload = {
            discordWebhookUrl: url,
            discordReportEnabled: cfg.discordReportEnabled !== false,
            discordReportHour: cfg.discordReportHour ?? 20,
            discordReportMinute: cfg.discordReportMinute ?? 0,
            discordReportTimezone: cfg.discordReportTimezone || 'Asia/Kolkata',
            proposalTemplates,
          };
          try { return await api.updateDiscordSettings(payload); }
          catch { return await api.updateSettings(payload); }
        };
        try {
          if (webhook && !readDiscordWebhookUrl(cfg)) {
            await persistDiscordWebhook(webhook);
            clearStoredDiscordWebhookUrl();
          } else if (legacyWebhook && legacyWebhook !== webhook) {
            await persistDiscordWebhook(legacyWebhook);
            webhook = legacyWebhook;
            clearStoredDiscordWebhookUrl();
          } else { return; }
          setConfig(c => ({ ...c, discordWebhookUrl: webhook }));
        } catch {}
      })();
    } catch(e) { setError(e.message); setLoading(false); }
  }, [fetchActivitySlices]);

  const initialLoadDoneRef = useRef(false);

  useEffect(() => {
    if (!isAdmin && page === 'sops') setPage('overview');
  }, [isAdmin, page]);

  useEffect(() => {
    if (!isAdmin || page !== 'settings' || settingsTab !== 'team') return;
    api.getTeamHistory().then(setTeamHistory).catch(() => setTeamHistory([]));
  }, [isAdmin, page, settingsTab, config.allUsers]);

  // Load data once when user session is available
  useEffect(() => {
    if (!currentUser) {
      initialLoadDoneRef.current = false;
      return;
    }
    if (initialLoadDoneRef.current) return;
    initialLoadDoneRef.current = true;
    loadData({ fullActivity: false });
  }, [currentUser, loadData]);

  // Lazy-load activity data for the current page
  useEffect(() => {
    if (!currentUser || loading) return;
    const slices = [...(PAGE_ACTIVITY_SLICES[page] || [])];
    if (page === 'companies' && (detailId || dTab === 'proposals')) slices.push('proposals');
    if (slices.length) fetchActivitySlices([...new Set(slices)]);
  }, [page, detailId, dTab, currentUser, loading, fetchActivitySlices]);

  // Load analytics when tab opens or filter changes
  useEffect(() => {
    if (page === 'analytics') {
      const params = {};
      if (anFilter !== 'all') params.salesperson = anFilter;
      if (anPeriod !== 'all') {
        const d = new Date(Date.now() - parseInt(anPeriod) * 864e5).toISOString().slice(0,10);
        params.dateFrom = d;
      }
      if (anCities.length) params.cities = anCities.join(',');
      Promise.all([
        api.getSourceAnalytics(params).then(setSrcAnalytics).catch(()=>{}),
        api.getPersonAnalytics(params).then(setPersonAnalytics).catch(()=>{}),
        api.getFunnel(params).then(setFunnel).catch(()=>{}),
        api.getDailyCalls(parseInt(anPeriod)||30, params).then(setDailyCalls).catch(()=>{}),
        api.getPipelineValue(params).then(setPipelineData).catch(()=>{}),
        api.getCityAnalytics(params).then(setCityAnalytics).catch(()=>{}),
        api.getConversionRates(params).then(d=>setConversionRates(d.stages||[])).catch(()=>{}),
      ]);
    }
  }, [page, anFilter, anPeriod, anCities]);

  // Load targets when page opens or period/offset changes
  useEffect(() => {
    if (page === 'targets') {
      const range = targetPeriod === 'weekly' ? getTargetWeekRange(targetWeekOffset) : getTargetMonthRange(targetWeekOffset);
      const spList = isAdmin ? (config.allUsers?.filter(u => u.role !== 'Admin').map(u => u.username) || []) : [user === 'admin' ? '' : user].filter(Boolean);
      (async () => {
        try {
          const t = await api.getTargets({ period: targetPeriod, startDate: range.start });
          setTargetsData(t);
          if (spList.length) {
            const actualsMap = await api.getTargetActualsBatch({ startDate: range.start, endDate: range.end, salespeople: spList.join(',') });
            setTargetsActuals(actualsMap);
          } else {
            setTargetsActuals({});
          }
        } catch {}
      })();
    }
  }, [page, targetPeriod, targetWeekOffset, targetRefresh, isAdmin, user, config.allUsers]);

  // Helpers
  const getCo = sp => companies.find(c => String(c._id) === String(sp?.companyId?._id || sp?.companyId));
  const goTo = p => { setMobileNavOpen(false); setPage(p); setDetailId(null); setSearch(''); setFCity(''); setFSrc(''); setFOut(''); setCoStageFilter(''); setPTab('all'); setDateFrom(today()); setDateTo(today()); setFuFilter('overdue'); setFuCity(''); setFuSearch(''); setFuInsightsOpen(false); setFuInsightsSearch(''); setFuInsightsSegment(''); setFuInsightsPurpose(''); setFuInsightsStage(''); setFuInsightsBlocker(''); setFuInsightsDue(''); setFuInsightsConnect(''); setClPage(1); setClConn('all'); setClOutcome(''); setClType(''); setClCity(''); setClPeriod('today'); setClShowFilters(false); setMtgStatus('all'); setMtgPeriod('all'); setMtgSearch(''); setMtgCity(''); setMtgConductedBy('all'); setMtgAccountOwner(''); setMtgType(''); setMtgShowFilters(false); setMtgView('scheduled'); setMtgExpanded({}); setCoPage(1); setCoSeg(''); setCoStars(''); setLbFCity(''); setLbFStat(''); setLbFCat(''); setLbFTop(false); setLbPage(1); window.scrollTo(0,0); };
  const navigateFromOverview = target => {
    setMobileNavOpen(false);
    setDetailId(null);
    window.scrollTo(0, 0);
    if (target === 'companies') {
      setPage('companies');
      setSearch(''); setFCity(''); setCoPage(1); setCoStageFilter('');
    } else if (target === 'calls-today') {
      setPage('calllog');
      setClPeriod('today'); setClPage(1); setClConn('all'); setClOutcome(''); setClType(''); setClCity(''); setPTab('all');
      setDateFrom(today()); setDateTo(today());
    } else if (target === 'overdue') {
      setPage('followups');
      setFuFilter('overdue'); setFuCity(''); setFuSearch(''); setPTab('all');
    } else if (target === 'hot') {
      setPage('followups');
      setFuFilter('hot'); setFuCity(''); setFuSearch(''); setPTab('all');
    } else if (target === 'meetings') {
      setPage('meetings');
      setMtgView('scheduled'); setMtgStatus('all'); setMtgExpanded({});
    } else if (target.startsWith('stage:')) {
      const stage = target.slice(6);
      if (stage === 'Meeting scheduled') {
        setPage('meetings');
        setMtgView('scheduled'); setMtgStatus('all'); setMtgExpanded({});
      } else if (stage === 'Connected') {
        setPage('calllog');
        setClPeriod('all'); setClPage(1); setClConn('yes'); setClOutcome(''); setClType(''); setClCity(''); setPTab('all');
        setDateFrom('2000-01-01'); setDateTo(today());
      } else if (stage === 'Attempted') {
        setPage('queue');
      } else {
        setPage('companies');
        setCoStageFilter(stage);
        setSearch(''); setFCity(''); setCoPage(1);
      }
    }
  };
  const slideIdFromUrl = url => (url || '').match(/\/presentation\/d\/([^/]+)/)?.[1] || '';
  const slideExportUrl = url => {
    const id = slideIdFromUrl(url);
    return id ? `https://docs.google.com/presentation/d/${id}/export/pdf` : '';
  };

  // Computed
  const stats = useMemo(() => {
    const t = today();
    // Compute unified follow-ups for accurate global counts
    const fuMap = {};
    // SPOC follow-ups always take priority
    spocs.forEach(sp => {
      if(!sp.nextFollowUp || ['Won','Dropped','Invalid data'].includes(sp.stage)) return;
      const coId = String(sp.companyId?._id || sp.companyId);
      if(!fuMap[coId] || sp.nextFollowUp > fuMap[coId].d) fuMap[coId] = { d: sp.nextFollowUp, src: 'spoc' };
    });
    // Meeting follow-ups only for companies with NO spoc follow-up
    meetings.forEach(m => {
      if(!m.nextActionDate || ['Completed','Cancelled'].includes(m.stage)) return;
      const coId = String(m.companyId?._id || m.companyId);
      if(!fuMap[coId]) fuMap[coId] = { d: m.nextActionDate, src: 'mtg' };
    });
    const fuDates = Object.values(fuMap).map(x => x.d);
    
    return {
      od: fuDates.filter(d => d < t),
      tf: fuDates.filter(d => d === t),
      hot: spocs.filter(x => x.hot),
      ct: calls.filter(c => c.date === t),
    };
  }, [spocs, meetings, calls]);

  const queue = useMemo(() => {
    const t = today(), q = [], seen = {};
    const coMap = {}; companies.forEach(c => { coMap[c._id] = c; });
    const ok = x => !qSkip[x._id] && (!qCity || citiesMatch(coMap[x.companyId]?.city, qCity));
    const add = (list, pri, pn) => list.filter(ok).forEach(x => { if (!seen[x._id]) { seen[x._id]=1; q.push({...x, pri, pn}); }});
    if(qBk.overdue) add(spocs.filter(x => x.nextFollowUp && x.nextFollowUp < t && !['Won','Dropped','Not interested'].includes(x.stage)), 'P1-Overdue', 1);
    if(qBk.today) add(spocs.filter(x => x.nextFollowUp === t), 'P2-Today', 2);
    if(qBk.retry) add(spocs.filter(x => x.stage === 'Attempted' && !x.nextFollowUp), 'P3-Retry', 3);
    if(qBk.hot) add(spocs.filter(x => x.hot && !x.nextFollowUp && !['Won','Dropped','Not interested','Meeting scheduled'].includes(x.stage)), 'P4-Hot', 4);
    if(qBk.fresh) spocs.filter(x => x.stage === 'New' && ok(x) && !seen[x._id]).forEach(x => { seen[x._id]=1; q.push({...x, pri:'P5-Fresh', pn:5}); });
    q.sort((a,b) => a.pn - b.pn);
    return q.slice(0, qTarget);
  }, [spocs, companies, qBk, qMethod, qTarget, qCity, qSkip]);

  const sopGroups = useMemo(() => {
    const hasMeeting = item => [item.title, item.useCaseKey, item.whereToUse, ...(item.tags || [])].join(' ').toLowerCase().includes('meeting');
    const byName = (a,b) => (a.title || '').localeCompare(b.title || '');
    const isChecklist = item => ['Process','Checklist'].includes(item.category);
    const meetingChecklists = sops.filter(item => isChecklist(item) && hasMeeting(item)).sort(byName);
    return {
      meetingChecklists,
      checklists: sops.filter(item => isChecklist(item) && !hasMeeting(item)).sort(byName),
      emailTemplates: sops.filter(item => item.category === 'Email Template').sort(byName),
      whatsappTemplates: sops.filter(item => item.category === 'WhatsApp Template').sort(byName),
      callingScripts: sops.filter(item => item.category === 'Calling Script').sort(byName),
    };
  }, [sops]);

  // ── MODAL HELPERS ──
  const closeModal = () => { setModal(null); setForm({}); setFormErrors({}); setTouched({}); setInlineSps([]); setShowInlineSp(false); setInlineSpF({}); setCallStep(1); setCallSearch(''); setUploadResult(null); };

  // Helper: find salesperson assigned to a city
  const getSalespersonForCity = (city) => {
    if (!city || !config.allUsers) return '';
    const matched = config.allUsers.find(u => 
      u.role === 'Employee' && 
      u.assignedCities?.length > 0 && 
      u.assignedCities.includes(city)
    );
    return matched?.username || '';
  };

  const openCompanyForm = (edit, prefill) => {
    setModal({ type:'company', edit:!!edit, editId:edit?._id });
    const city = prefill?.city || CITIES[0];
    // Auto-assign salesperson based on city if not already provided
    const autoSalesperson = prefill?.salesperson || getSalespersonForCity(city);
    setForm(edit ? {...edit} : { name:prefill?.name||'', city, segment:'Tier 1', source:'', salesperson:autoSalesperson, potential:0, generatedFromLeadBankId:prefill?.generatedFromLeadBankId||null, generatedFromLeadBankName:prefill?.generatedFromLeadBankName||'' });
    setInlineSps([]); setFormErrors({}); setTouched({});
  };

  const openSpocForm = (edit, preCoId) => {
    setModal({ type:'spoc', edit:!!edit, editId:edit?._id });
    setForm(edit ? {
      ...edit,
      phoneType: edit.phoneType || resolvePhoneType(edit.phone, edit.phoneType),
    } : { name:'', companyId:preCoId||'', designation:'', phone:'+91', phoneType:'mobile', email:'', decisionLevel:'Unknown', stage:'New', leadSource:'' });
    setFormErrors({}); setTouched({}); setSpocPhoneDup(null); setSpocDupOk('');
  };

  const openCallForm = (spoc, editCall) => {
    if (editCall) {
      const sp = spoc || spocs.find(x => String(x._id) === String(editCall.spocId?._id || editCall.spocId));
      setModal({ type: 'call', edit: true, editId: editCall._id, hasSpoc: true });
      setForm({
        ...editCall,
        spocId: String(editCall.spocId?._id || editCall.spocId || ''),
        companyId: String(editCall.companyId?._id || editCall.companyId || sp?.companyId || ''),
        salesperson: editCall.salesperson || activitySalesperson(),
        outcome: editCall.outcome || '',
        conversationOutcome: editCall.conversationOutcome || editCall.outcome || '',
        callType: editCall.callType || 'follow-up',
        connected: !!editCall.connected,
        confidence: editCall.confidence ?? 5,
      });
      setCallStep(2);
      setCallSearch('');
      setFormErrors({});
      setTouched({});
      return;
    }
    const hist = spoc ? calls.filter(c => c.spocId === spoc._id) : [];
    setModal({ type:'call', hasSpoc:!!spoc });
    setForm({ date:today(), spocId:spoc?._id||'', companyId:spoc?.companyId||'', salesperson:activitySalesperson(), callType:hist.length?'follow-up':'first call', connected:false, outcome:'', conversationOutcome:'', detailsSent:false, sentWhere:'', followUpDate:'', meetingDate:'', notInterestedReason:'', notes:'', confidence:5 });
    setCallStep(spoc ? 2 : 1); setCallSearch(''); setFormErrors({}); setTouched({});
  };

  const openMeetingForm = (edit, prefill) => {
    setModal({ type:'meeting', edit:!!edit, editId:edit?._id });
    const defaultSalesperson = activitySalesperson();
    setForm(edit ? {
      ...edit,
      type: edit.type === MEETING_TYPE_WALKIN_LEGACY ? MEETING_TYPE_WALKIN_NO_CONTACT : edit.type,
    } : {
      date:today(), type:'online', companyId:detailId||'', agenda:'', summary:'', stage:'Tentative',
      nextAction:'', nextActionDate:'', estimatedValue:'', closingProbability:50, keyDecisions:'', blockers:'',
      location:'',
      salesperson:defaultSalesperson,
      ...prefill,
      salesperson: prefill?.salesperson || defaultSalesperson,
    });
  };

  const openProposalForm = (edit, preCoId) => {
    if (!api.isProposalsAvailable()) {
      alert('Proposals are not available on the live server yet. Backend needs an update to enable saving proposals.');
      return;
    }
    const coId = String(preCoId || detailId || edit?.companyId?._id || edit?.companyId || '');
    const co = companies.find(c => String(c._id) === coId);
    const defaultSalesperson = activitySalesperson();
    const coSps = spocs.filter(s => String(s.companyId?._id || s.companyId) === coId && !s.isStrikedOut);
    const templates = getProposalTemplates(config);
    const templateNames = templates.map(t => t.name);
    const pickTemplate = name => (name && templateNames.includes(name) ? name : (templates[0]?.name || 'IX'));
    const editSp = edit ? coSps.find(s => String(s._id) === String(edit.spocId?._id || edit.spocId)) : null;
    setModal({ type: 'proposal', edit: !!edit, editId: edit?._id });
    setForm(edit ? {
      ...edit,
      companyId: coId,
      spocId: edit.spocId ? String(edit.spocId?._id || edit.spocId) : '',
      sentToName: edit.sentToName || editSp?.name || '',
      templateName: pickTemplate(edit.templateName),
      sentVia: edit.sentVia || 'Email',
      nextFollowUpDate: edit.nextFollowUpDate || '',
      salesperson: edit.salesperson || defaultSalesperson,
    } : {
      companyId: coId,
      spocId: '',
      sentToName: '',
      date: today(),
      templateName: templates[0]?.name || 'IX',
      sentVia: 'Email',
      estimatedValue: '',
      nextFollowUpDate: '',
      notes: '',
      salesperson: defaultSalesperson,
    });
    setFormErrors({});
  };

  const openSopForm = (edit) => {
    setModal({ type:'sop', edit:!!edit, editId:edit?._id });
    setForm(edit ? {
      ...edit,
      stepsText: (edit.steps || []).join('\n'),
      tagsText: (edit.tags || []).join(', ')
    } : {
      title:'',
      category:'Process',
      useCaseKey:'',
      whereToUse:'',
      subject:'',
      body:'',
      stepsText:'',
      notes:'',
      tagsText:''
    });
    setFormErrors({});
  };

  const openConcludeForm = (company) => {
    setModal({ type:'conclude', companyId:company._id, companyName:company.name });
    setForm({ status:'won', reason:'', reconnectDate:'' });
  };

  const openTrail = async (spoc) => {
    setTrailSpoc(spoc);
    try { const trail = await api.getSpocTrail(spoc._id); setTrailData(trail); } catch { setTrailData(spoc.trail || []); }
    setModal({ type:'trail' });
  };

  const openQueueSpocDetail = (spoc) => {
    setModal({ type: 'queueSpocDetail', spoc });
  };

  const openUpload = (type) => {
    setModal({ type:'upload', uploadType:type });
    setUploadResult(null); setFormErrors({});
  };

  // ── REAL-TIME VALIDATION ──
  const validateEmail = (v) => { if(!v) return ''; if(!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return 'Invalid email format'; return ''; };
  const validateAmount = (v) => { if(!v) return ''; if(!/^\d+$/.test(v)) return 'Must be a number'; return ''; };

  // ── SAVE HANDLERS ──
  const [companySaving, setCompanySaving] = useState(false);
  const creatingRef = React.useRef(false); // synchronous lock against double-submit
  // Actually create the company. opts: { linkLeadId, unifyLeadName, nameOverride }
  const performCreateCompany = async (opts = {}) => {
    if (creatingRef.current) return; // block rapid double-clicks before state updates
    creatingRef.current = true;
    setCompanySaving(true);
    try {
      const formattedName = formatProperName(opts.nameOverride || form.name || '');
      const payload = {
        ...form,
        name: formattedName,
        spocs: inlineSps.map(sp => normalizeSpocFields(sp)),
      };
      if (opts.nameOverride) payload.name = formattedName;
      const linkLeadId = opts.linkLeadId || modal?.leadBankId || undefined;
      if (linkLeadId) payload.linkLeadId = linkLeadId;
      if (opts.unifyLeadName !== undefined) payload.unifyLeadName = opts.unifyLeadName;
      if (opts.allowDuplicate) payload.allowDuplicate = true;
      const result = await api.createCompany(payload);
      await loadData(); closeModal(); setCompanyCheck(null);
      return result;
    } catch(e) { setFormErrors({ api: e.message }); }
    finally { creatingRef.current = false; setCompanySaving(false); }
  };

  const saveCompany = async () => {
    if (companySaving) return;
    const errs = {};
    if (!form.name?.trim()) errs.name = 'Required';
    for (const sp of inlineSps) {
      const spType = resolvePhoneType(sp.phone, sp.phoneType);
      if (sp.phone && !phoneValid(sp.phone, spType)) { errs.spocPhone = phoneErrorMsg(spType); break; }
      if (sp.email && !emailValid(sp.email)) { errs.spocEmail = 'Invalid email'; break; }
    }
    if (Object.keys(errs).length) { setFormErrors(errs); return; }

    // Edit path — unchanged
    if (modal.edit) {
      setCompanySaving(true);
      try {
        const result = await api.updateCompany(modal.editId, { ...form, name: formatProperName(form.name || '') });
        await loadData(); closeModal();
        return result;
      } catch(e) { setFormErrors({ api: e.message }); }
      finally { setCompanySaving(false); }
      return;
    }

    const formattedName = formatProperName(form.name.trim());
    if (formattedName !== form.name) uf('name', formattedName);

    // Creating FROM a prospect — link directly, no duplicate prompt needed
    if (modal?.leadBankId) return await performCreateCompany();

    // New company — check for duplicate companies & matching prospects first
    try {
      const check = await api.checkCompanyBeforeCreate(formattedName, form.city || '');
      const dupExact = check.duplicateCompanies?.exact || [];
      const dupFuzzy = check.duplicateCompanies?.fuzzy || [];
      const proExact = check.matchingProspects?.exact || [];
      const proFuzzy = check.matchingProspects?.fuzzy || [];
      const dupCompanies = [...dupExact, ...dupFuzzy];
      const prospects = [...proExact, ...proFuzzy];
      if (dupCompanies.length || prospects.length) {
        setCompanyCheck({
          name: formattedName,
          city: form.city || '',
          dupCompanies, dupExact, dupFuzzy,
          prospects, proExact, proFuzzy,
          linkLeadId: prospects[0]?.id || null,
          keepName: 'company',
        });
        return; // wait for user decision in modal
      }
    } catch (e) { /* check failed — fall through and create normally */ }

    return await performCreateCompany();
  };

  // Live duplicate-number check while the SPOC form is open. Fires once the
  // number is complete, so the user is warned before they hit Save.
  const [spocPhoneDup, setSpocPhoneDup] = useState(null);
  const [spocPhoneChecking, setSpocPhoneChecking] = useState(false);
  // Holds the exact number the user approved via "Add anyway". Storing the value
  // (not a boolean) means editing the number afterwards revokes the override.
  const [spocDupOk, setSpocDupOk] = useState('');
  const spocPhone = form?.phone;
  const spocPhoneTypeVal = form?.phoneType;
  useEffect(() => {
    if (modal?.type !== 'spoc') { setSpocPhoneDup(null); return; }
    const type = resolvePhoneType(spocPhone, spocPhoneTypeVal);
    if (!spocPhone || !phoneValid(spocPhone, type)) { setSpocPhoneDup(null); return; }
    let cancelled = false;
    setSpocPhoneChecking(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.checkSpocPhone(spocPhone, modal?.editId);
        if (!cancelled) setSpocPhoneDup(r?.duplicate ? { ...r.duplicate, message: r.message } : null);
      } catch { if (!cancelled) setSpocPhoneDup(null); } // check is advisory — the server still blocks on save
      if (!cancelled) setSpocPhoneChecking(false);
    }, 350);
    return () => { cancelled = true; clearTimeout(t); setSpocPhoneChecking(false); };
  }, [modal?.type, modal?.editId, spocPhone, spocPhoneTypeVal]);

  const spocDupOverridden = !!spocPhoneDup && !!spocDupOk && spocDupOk === spocPhone;

  const [spocSaving, setSpocSaving] = useState(false);
  const saveSpoc = async () => {
    if (spocSaving) return;
    const errs = {};
    if (!form.name?.trim()) errs.name = 'Required';
    if (!form.companyId) errs.companyId = 'Required';
    const spPhoneType = resolvePhoneType(form.phone, form.phoneType);
    if (form.phone && !phoneValid(form.phone, spPhoneType)) errs.phone = phoneErrorMsg(spPhoneType);
    if (form.email && !emailValid(form.email)) errs.email = 'Invalid email format';
    if (!form.phone && !form.email) errs.phone = 'Phone or email required';
    if (spocPhoneDup && !spocDupOverridden) errs.phone = 'This number already belongs to another SPOC';
    if (Object.keys(errs).length) { setFormErrors(errs); return; }
    setSpocSaving(true);
    try {
      const payload = normalizeSpocFields({ ...form, phoneType: resolvePhoneType(form.phone, form.phoneType) });
      if (spocDupOverridden) payload.allowDuplicate = true;
      if (modal.edit) { await api.updateSpoc(modal.editId, payload); }
      else { await api.createSpoc(payload); }
      await loadData(); closeModal();
    } catch(e) {
      // 409 = another user saved this number while the form was open.
      if (e.status === 409 && e.body?.duplicate) {
        setSpocPhoneDup({ ...e.body.duplicate, message: e.body.error });
        setSpocDupOk(''); // newly-surfaced duplicate — make them approve this one explicitly
        setFormErrors({ phone: 'This number already belongs to another SPOC' });
      } else setFormErrors({ api: e.message });
    }
    setSpocSaving(false);
  };

  const [callSaving, setCallSaving] = useState(false);
  const saveCall = async () => {
    if (callSaving) return;
    if (!form.spocId) return;
    if (!form.connected && !form.outcome) return;
    if (form.connected && !form.conversationOutcome) return;
    if (form.connected && (!form.notes || form.notes.trim().length < 10)) { setFormErrors({ notes:'Min 10 characters' }); return; }
    if (form.conversationOutcome === 'Not interested' && !form.notInterestedReason) { setFormErrors({ ni:'Select reason' }); return; }
    if (['Follow-up later', 'Meeting requested'].includes(form.conversationOutcome) && !form.followUpDate) { setFormErrors({ followUpDate: 'Follow-up Date is required' }); return; }
    if (form.conversationOutcome === 'Meeting scheduled') {
      if (!form.followUpDate) { setFormErrors({ followUpDate: 'Follow-up Date is required' }); return; }
      if (!form.meetingDate) { setFormErrors({ meetingDate: 'Meeting Date is required' }); return; }
    }
    setCallSaving(true);
    try {
      const outcome = form.outcome || '';
      const spocId = form.spocId;
      const salesperson = form.salesperson || activitySalesperson();
      if (!salesperson) {
        setFormErrors({ api: 'Call done by is required — select who made this call.' });
        setCallSaving(false);
        return;
      }
      const payload = { ...form, salesperson };
      if (modal?.edit) {
        await api.updateCall(modal.editId, payload);
      } else {
        await api.createCall(payload);
        const qItem = dailyQueue.find(q => q._id === spocId && q.dailyAssignedDate === dqDate);
        if (qItem) { try { await api.updateQueueStatus(spocId, { status: 'done' }); } catch {} }
      }
      await loadData();
      api.getQueue({ date: dqDate }).then(setDailyQueue).catch(() => {});
      closeModal();
      if (!modal?.edit && ['Wrong number','Unreachable'].includes(outcome)) {
        setConfirmDel({
          title: 'Strike Down SPOC?',
          msg: `The call outcome was "${outcome}". Do you want to strike down this SPOC?`,
          confirmLabel: 'Strike Down',
          confirmKind: 'r',
          fn: async () => { await api.strikeoutSpoc(spocId, outcome); await loadData(); api.getQueue({ date: dqDate }).then(setDailyQueue).catch(() => {}); }
        });
      }
    } catch(e) { setFormErrors({ api: e.message }); } finally { setCallSaving(false); }
  };

  const saveMeeting = async () => {
    if (!modal.edit && !form.companyId) {
      setFormErrors({ api: 'Company is required.' });
      return;
    }
    const isFieldVisit = form.type === MEETING_TYPE_WALKIN_MET || isNoContactVisit({ type: form.type });
    // Field visits allow any past date; regular meetings get a 24h buffer (yesterday) for late logging
    if (!modal.edit && !isFieldVisit && form.date && form.date < yesterday()) {
      setFormErrors({ api: 'Meeting date cannot be earlier than yesterday.' });
      return;
    }
    if (form.estimatedValue && validateAmount(form.estimatedValue)) {
      setFormErrors({ api: 'Estimated Value must be a valid number.' });
      touch('estimatedValue');
      return;
    }
    if (form.meetingNumber && (!/^[1-9]\d*$/.test(String(form.meetingNumber)))) {
      setFormErrors({ api: 'Meeting # must be a positive whole number.' });
      return;
    }
    if (!form.salesperson) {
      setFormErrors({ api: 'Meeting conducted by is required — select who ran this meeting.' });
      return;
    }
    if (isFieldVisit && !(form.location || '').trim()) {
      setFormErrors({ api: 'Location is required for site walk-ins.' });
      return;
    }
    try {
      const payload = { ...form, salesperson: form.salesperson || activitySalesperson() };
      delete payload._id;
      delete payload.__v;
      delete payload.createdAt;
      delete payload.updatedAt;
      // Empty string breaks Mongo ObjectId cast — omit or null
      if (!payload.spocId) payload.spocId = null;
      if (!isFieldVisit) payload.location = '';
      else payload.location = (payload.location || '').trim();
      if (payload.estimatedValue) payload.estimatedValue = Number(payload.estimatedValue);
      if (payload.meetingNumber) payload.meetingNumber = Number(payload.meetingNumber);
      if (modal.edit) { await api.updateMeeting(modal.editId, payload); }
      else { await api.createMeeting(payload); }
      await loadData(); closeModal();
    } catch(e) { setFormErrors({ api: e.message }); }
  };

  const saveProposal = async () => {
    const errs = {};
    if (!form.companyId) errs.companyId = 'Required';
    if (!(form.sentToName || '').trim()) errs.sentToName = 'Required';
    if (!form.date) errs.date = 'Required';
    if (form.estimatedValue && validateAmount(form.estimatedValue)) errs.estimatedValue = 'Must be a number';
    if (Object.keys(errs).length) { setFormErrors(errs); return; }
    try {
      const payload = {
        companyId: form.companyId,
        spocId: form.spocId || null,
        sentToName: form.sentToName.trim(),
        date: form.date,
        templateName: form.templateName || '',
        sentVia: form.sentVia || 'Email',
        estimatedValue: form.estimatedValue || '',
        nextFollowUpDate: form.nextFollowUpDate || '',
        notes: form.notes || '',
        salesperson: form.salesperson || activitySalesperson(),
      };
      if (modal.edit) await api.updateProposal(modal.editId, payload);
      else await api.createProposal(payload);
      await loadData(); closeModal();
    } catch (e) { setFormErrors({ api: e.message }); }
  };

  const saveSop = async () => {
    const errs = {};
    if (!form.title?.trim()) errs.title = 'Title is required';
    if (!form.useCaseKey?.trim()) errs.useCaseKey = 'Key is required';
    if (Object.keys(errs).length) { setFormErrors(errs); return; }
    const payload = {
      title: form.title.trim(),
      category: form.category || 'Process',
      useCaseKey: form.useCaseKey.trim(),
      whereToUse: form.whereToUse || '',
      subject: form.subject || '',
      body: form.body || '',
      steps: (form.stepsText || '').split('\n').map(x => x.trim()).filter(Boolean),
      notes: form.notes || '',
      tags: (form.tagsText || '').split(',').map(x => x.trim()).filter(Boolean),
      active: form.active !== false
    };
    try {
      if (modal.edit) await api.updateSop(modal.editId, payload);
      else await api.createSop(payload);
      await loadData(); closeModal();
    } catch(e) { setFormErrors({ api: e.message }); }
  };

  const concludeDeal = async () => {
    try {
      await api.concludeDeal(modal.companyId, form);
      await loadData(); closeModal();
    } catch(e) { setFormErrors({ api: e.message }); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if(!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const fn = modal.uploadType === 'leads' ? api.uploadLeads : modal.uploadType === 'companies-with-spocs' ? api.uploadCompaniesWithSpocs : api.uploadCompanies;
      const result = await fn(fd);
      setUploadResult(result);
      await loadData();
    } catch(err) { setFormErrors({ api: err.message }); }
  };

  const downloadTemplate = (type) => {
    let csv, filename;
    if(type==='leads') {
      csv = 'name,city,assignedTo,notes\nDLF Homes,Delhi,Vikram,New lead\nBrigade Group,Bangalore,,';
      filename = 'lead_bank_template.csv';
    } else if(type==='companies-with-spocs') {
      csv = 'Company Name,City,Source,Spoc Name,Spoc Designation,Spoc Contact,Spoc Email Id,Spoc Lead Source\nDLF Homes,Delhi,LinkedIn,Rahul Sharma,CFO,9876543210,rahul@dlf.com,Reference\nDLF Homes,Delhi,LinkedIn,Priya Mehta,Director Sales,9123456780,priya@dlf.com,Website\nBrigade Group,Bangalore,Cold Call,Suresh Kumar,VP Sales,9000011111,suresh@brigade.com,Justdial\nBrigade Group,Bangalore,Cold Call,Meena Reddy,Marketing Head,9000022222,meena@brigade.com,Event';
      filename = 'company_with_spocs_template.csv';
    } else {
      csv = 'name,city,segment,source,salesperson,potential\nDLF Homes,Delhi,Tier 1,LinkedIn,Vikram,8\nBrigade Group,Bangalore,Tier 2,Cold Call,Srinivas,6';
      filename = 'company_template.csv';
    }
    const blob = new Blob([csv], {type:'text/csv'});
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
  };

  // Lead bank handlers
  const addToBank = async () => {
    const rawNames = bText.split(/[\r\n]+/)
      .map(n => n.trim().replace(/^["',;]+|["',;]+$/g, '').trim())
      .filter(Boolean);
    
    // Validate each name
    const invalidNames = rawNames.filter(n => !/[a-zA-Z]/.test(n));
    if (invalidNames.length > 0) {
      alert(`Invalid entries (must contain letters):\n${invalidNames.join('\n')}`);
      return;
    }
    
    // Filter out very short names
    const shortNames = rawNames.filter(n => n.length <= 1);
    if (shortNames.length > 0) {
      alert(`These names are too short:\n${shortNames.join('\n')}`);
      return;
    }
    
    const names = rawNames;
    if (!names.length) { alert('Please enter at least one valid developer name.'); return; }
    
    // Check for duplicates in existing lead bank
    const existingLeads = leadBank.filter(lb => 
      citiesMatch(lb.city, bCity) &&
      names.some(n => n.toLowerCase() === lb.name.toLowerCase())
    );
    
    if (existingLeads.length > 0) {
      const dupes = existingLeads.map(l => l.name).join(', ');
      const shouldContinue = window.confirm(
        `${existingLeads.length} developer(s) already exist in ${bCity}:\n${dupes}\n\n` +
        `Click OK to skip these and add the rest, or Cancel to edit the list.`
      );
      if (!shouldContinue) return;
    }
    
    const newNames = names.filter(n => 
      !existingLeads.some(el => el.name.toLowerCase() === n.toLowerCase())
    );
    
    if (!newNames.length) {
      alert('All developers already exist in the Lead Bank.');
      return;
    }
    
    const preview = newNames.slice(0, 5).join(', ');
    const extra = newNames.length > 5 ? ` and ${newNames.length - 5} more` : '';
    setConfirmDel({
      title: 'Confirm Bulk Add',
      msg: `Add ${newNames.length} developer${newNames.length > 1 ? 's' : ''} to ${bCity}?\n\n${preview}${extra}${existingLeads.length ? `\n\n(${existingLeads.length} duplicate${existingLeads.length > 1 ? 's' : ''} will be skipped)` : ''}`,
      confirmLabel: 'Add',
      confirmKind: undefined,
      fn: async () => {
        try { await api.bulkAddLeads({ names: newNames, city: bCity }); setBText(''); await loadData(); }
        catch(e) { alert(e.message); }
      }
    });
  };

  const promoteLead = (item) => {
    openCompanyForm(null, {
      name: item.name,
      city: item.city,
      salesperson: item.assignedTo || '',
      generatedFromLeadBankId: item._id,
      generatedFromLeadBankName: item.name,
    });
    setModal(prev => ({ ...prev, leadBankId: item._id }));
  };

  // Prospects and companies are both keyed name+city — the same developer exists
  // once per city, so a name match alone resolves to whichever city sorts first.
  const resolveProspectCompanyId = (item) => {
    if (item.generatedCompanyId) return String(item.generatedCompanyId);
    if (item.status !== 'done') return null; // not converted — nothing to link to
    const byNameAndCity = n => n && companies.find(c =>
      (c.name || '').toLowerCase().trim() === n.toLowerCase().trim() && citiesMatch(c.city, item.city));
    const co = byNameAndCity(item.name) || byNameAndCity(item.generatedCompanyName);
    return co ? String(co._id) : null;
  };

  const openProspectCompany = (item) => {
    const coId = resolveProspectCompanyId(item);
    if (!coId) {
      alert('No linked company found for this prospect.');
      return;
    }
    setDetailId(coId);
    setPage('companies');
    setDTab('overview');
  };

  // Nav items
  const unminedBadge = loadedSliceList.includes('leadBank')
    ? leadBank.filter(x => x.status === 'unmined').length
    : (dataCounts.unminedProspects || 0);
  const droppedBadge = loadedSliceList.includes('dropped') ? dropped.length : (dataCounts.dropped || 0);
  const pendingBadge = loadedSliceList.includes('pendingDrops') ? pendingDrops.length : (dataCounts.pendingDrops || 0);
  const navItems = [
    { id:'overview', icon:'📊', label:'Overview' },
    { id:'leadbank', icon:'🏗️', label:'Prospects', badge: unminedBadge },
    { id:'companies', icon:'🏢', label:'Companies' },
    { id:'queue', icon:'📋', label:'Daily Call Targets', badge: isAdmin ? dailyQueue.filter(q=>q.dailyAssignedDate===today()).length : dailyQueue.filter(q=>q.dailyAssignedTo===user&&(!q.dailyStatus||q.dailyStatus==='pending')).length },
    { id:'calllog', icon:'📞', label:'Call History' },
    { id:'followups', icon:'⏰', label:'Follow-ups', badge: stats.od.length, badgeColor: RD },
    { id:'meetings', icon:'📅', label:'Meetings' },
    { id:'tasks', icon:'🗒️', label:'Tasks', badge: taskProgressCount, badgeColor: '#F59E0B' },
    { id:'visits', icon:'✈️', label:'City Visits', separator:true },
    { id:'eod', icon:'📝', label:'EOD Report' },
    { id:'targets', icon:'🎯', label:'Sales Targets' },
    { id:'dropped', icon:'📁', label:'Dropped Leads', badge: droppedBadge, separator:true },
    ...(isAdmin ? [
      { id:'sops', icon:'SOP', label:'SOPs', variant:'sops', sectionLabel:'SOP Library' },
      { id:'analytics', icon:'📈', label:'Analytics', sectionLabel:'Admin Section' },
      { id:'approvals', icon:'✅', label:'Approvals', badge: pendingBadge, badgeColor: RD },
      { id:'topdevelopers', icon:'⭐', label:'Top Developers' },
      { id:'probableclients', icon:'👥', label:'Probable Clients' },
      { id:'settings', icon:'⚙️', label:'Settings' },
    ] : []),
  ];

  // Detail data
  const detailCo = detailId ? companies.find(c => String(c._id) === String(detailId)) : null;
  const detailSps = detailId ? spocs.filter(x => String(x.companyId) === String(detailId)) : [];
  const detailCls = detailId ? calls.filter(c => String(c.companyId) === String(detailId)).sort((a,b) => (b.date||'').localeCompare(a.date||'')) : [];
  const detailMts = detailId ? meetings.filter(m => String(m.companyId) === String(detailId)).sort((a,b) => a.meetingNumber - b.meetingNumber) : [];
  const detailProps = detailId ? proposals.filter(p => String(p.companyId) === String(detailId)).sort((a,b) => (b.date||'').localeCompare(a.date||'')) : [];
  const lastProposal = detailProps[0] || null;

  if (!currentUser) return <LoginScreen onLogin={setCurrentUser} />;
  if (loading) return <div style={{display:'flex',alignItems:'center',justifyContent:'center',minHeight:'100vh',fontFamily:'-apple-system,BlinkMacSystemFont,"SF Pro Display",Inter,system-ui,sans-serif',background:BG,padding:24}}>
    <motion.div initial={{opacity:0,y:8}} animate={{opacity:1,y:0}} transition={{duration:.4,ease:[.32,.72,0,1]}} style={{textAlign:'center',maxWidth:440}}>
      <motion.div animate={{rotate:360}} transition={{repeat:Infinity,duration:.9,ease:'linear'}} style={{width:34,height:34,borderRadius:'50%',border:'3px solid '+BD,borderTopColor:P,margin:'0 auto 26px'}}/>
      <div style={{fontSize:11,fontWeight:600,letterSpacing:'.18em',textTransform:'uppercase',color:LT,marginBottom:18}}>Unada Sales OS</div>
      <motion.div key={loadingQuote} initial={{opacity:0}} animate={{opacity:1}} transition={{duration:.6,delay:.15}} style={{fontSize:16,lineHeight:1.6,color:TX,fontWeight:500,letterSpacing:'-0.01em',fontStyle:'italic'}}>“{loadingQuote}”</motion.div>
    </motion.div>
  </div>;
  if (error) return <div style={{display:'flex',alignItems:'center',justifyContent:'center',minHeight:'100vh',color:RD,flexDirection:'column',gap:14,fontFamily:'-apple-system,BlinkMacSystemFont,"SF Pro Display",Inter,system-ui,sans-serif',background:BG}}><div style={{...s.card,maxWidth:400,textAlign:'center',padding:40}}><div style={{fontSize:36,marginBottom:12}}>⚠️</div><h2 style={{margin:'0 0 8px',fontSize:18,fontWeight:700,letterSpacing:'-0.02em',color:TX}}>Connection Error</h2><p style={{color:MU,fontSize:13,margin:'0 0 6px'}}>{error}</p><p style={{fontSize:12,color:LT,margin:'0 0 20px'}}>Make sure backend is running and MongoDB is connected</p><motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={s.btn()} onClick={()=>{setError(null);setLoading(true);loadData();}}>Retry</motion.button></div></div>;

  // ══════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════
  return (
    <div style={{background:BG, minHeight:'100vh', color:TX, fontFamily:'-apple-system,BlinkMacSystemFont,"SF Pro Display",Inter,system-ui,sans-serif'}}>

      {/* ── TOP BAR ── */}
      <div className="app-topbar">
        <button type="button" className="mobile-menu-btn show-mobile-only" aria-label="Open menu" onClick={() => setMobileNavOpen(v => !v)}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
        </button>
        {/* Logo */}
        <div className="app-logo-slot">
          <UnadaLogo width={88} fill={TX}/>
        </div>
        {/* Global Search */}
        <GlobalSearch companies={companies} spocs={spocs} calls={calls} spocById={spocById} companyById={companyById} fmtDate={fmtDate} setDetailId={setDetailId} setPage={setPage} setDTab={setDTab} setSearch={setSearch} s={s} PL={PL} MU={MU} LT={LT} WH={WH} GN={GN} RD={RD} TX={TX}/>
        {/* Motivational Quote */}
        <MotivationalQuote quotes={config.motivationalQuotes} intervalHrs={config.quoteIntervalHrs}/>
        {/* User Avatar */}
        <div className="app-user-menu">
          <div style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',padding:'4px 8px',borderRadius:10,transition:'background .12s'}} onClick={()=>{localStorage.clear();window.location.reload();}}>
            <UserAvatar username={user} avatarColor={currentUser?.avatarColor} size={30} />
            <div className="app-user-menu__text">
              <div style={{fontSize:12,fontWeight:600,color:TX,letterSpacing:'-0.01em',whiteSpace:'nowrap'}}>{user}</div>
              <div style={{fontSize:10,color:LT,whiteSpace:'nowrap'}}>{currentUser?.role} · Sign out</div>
            </div>
          </div>
        </div>
      </div>

      <div className={'sidebar-backdrop'+(mobileNavOpen?' is-visible':'')} onClick={()=>setMobileNavOpen(false)}/>

      {/* ── SIDEBAR ── */}
      <div className={'app-sidebar'+(mobileNavOpen?' is-open':'')+(sidebarCollapsed?' app-sidebar--collapsed':'')}>
        <div className="sidebar-nav">
          {navItems.map(n => (
            <React.Fragment key={n.id}>
              {n.separator && !sidebarCollapsed && <div className="sidebar-separator"/>}
              {n.sectionLabel && !sidebarCollapsed && (<>
                {n.sectionLabel === 'Admin Section' && <div className="sidebar-separator" style={{marginTop:8}}/>}
                <div className="sidebar-section-label">{n.sectionLabel}</div>
              </>)}
              <div className={'sidebar-nav-item'+(page===n.id && !detailId?' is-active':'')} title={sidebarCollapsed?n.label:undefined} onClick={() => goTo(n.id)}>
                <span className="sidebar-nav-item__icon"><NavIcon id={n.id} active={page===n.id && !detailId}/></span>
                {!sidebarCollapsed && <span className="sidebar-nav-item__label">{n.label}</span>}
                {n.badge > 0 && (
                  <span className={'sidebar-nav-item__badge'+(sidebarCollapsed?' sidebar-nav-item__badge--dot':'')}>{sidebarCollapsed?'':n.badge}</span>
                )}
              </div>
            </React.Fragment>
          ))}
        </div>
        <div className="sidebar-footer">
          {!sidebarCollapsed && (
            <div className="sidebar-version">Unada Sales OS &nbsp;·&nbsp; <span>{APP_VERSION}</span></div>
          )}
          <button type="button" className="sidebar-toggle hide-mobile" onClick={toggleSidebarCollapsed} title={sidebarCollapsed?'Expand sidebar':'Collapse sidebar'} aria-label={sidebarCollapsed?'Expand sidebar':'Collapse sidebar'}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{transform:sidebarCollapsed?'rotate(180deg)':'none',transition:'transform .2s ease'}}>
              <polyline points="15 18 9 12 15 6"/>
            </svg>
            {!sidebarCollapsed && <span>Collapse</span>}
          </button>
          {sidebarCollapsed && <div className="sidebar-version sidebar-version--mini">{APP_VERSION}</div>}
        </div>
      </div>

      {/* ── MAIN ── */}
      <div className={'app-main'+(sidebarCollapsed?' app-main--sidebar-collapsed':'')}>
        <AnimatePresence mode="wait">
        <motion.div key={page+(detailId||'')} variants={fadeUp} initial="hidden" animate="show" exit={{opacity:0,y:-6,transition:{duration:.12}}}>

        {/* ═══ OVERVIEW ═══ */}
        {page==='overview' && !detailId && <>
          <div style={{marginBottom:24}}>
            <div style={{fontSize:13,color:MU,marginBottom:4}}>Good {new Date().getHours()<12?'morning':new Date().getHours()<17?'afternoon':'evening'}, <strong style={{color:TX}}>{user}</strong></div>
            <h1 style={{fontSize:28,fontWeight:700,margin:0,letterSpacing:'-0.04em',color:TX}}>Overview</h1>
          </div>
          <motion.div className="stat-row" variants={listContainer} initial="hidden" animate="show">
            <motion.div variants={listItem} style={{flex:1}}><GradientCard label="Companies" value={companies.length} color1={GRAD_COLORS[0][0]} color2={GRAD_COLORS[0][1]} onClick={()=>navigateFromOverview('companies')} icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>}/></motion.div>
            <motion.div variants={listItem} style={{flex:1}}><GradientCard label="SPOCs" value={spocs.length} color1={GRAD_COLORS[1][0]} color2={GRAD_COLORS[1][1]} onClick={()=>navigateFromOverview('companies')} icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>}/></motion.div>
            <motion.div variants={listItem} style={{flex:1}}><GradientCard label="Calls Today" value={stats.ct.length} color1={GRAD_COLORS[2][0]} color2={GRAD_COLORS[2][1]} onClick={()=>navigateFromOverview('calls-today')} icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.79 19.79 0 0 1 11.39 19a19.45 19.45 0 0 1-5-5A19.79 19.79 0 0 1 3.46 5.18 2 2 0 0 1 5.44 3h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L9.91 11a16 16 0 0 0 5 5l.94-.94a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 17z"/></svg>}/></motion.div>
            <motion.div variants={listItem} style={{flex:1}}><GradientCard label="Overdue" value={stats.od.length} color1={GRAD_COLORS[3][0]} color2={GRAD_COLORS[3][1]} onClick={()=>navigateFromOverview('overdue')} icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>}/></motion.div>
            <motion.div variants={listItem} style={{flex:1}}><GradientCard label="Hot Leads" value={stats.hot.length} color1={GRAD_COLORS[4][0]} color2={GRAD_COLORS[4][1]} onClick={()=>navigateFromOverview('hot')} icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>}/></motion.div>
            <motion.div variants={listItem} style={{flex:1}}><GradientCard label="Meetings" value={meetings.length} color1={GRAD_COLORS[5][0]} color2={GRAD_COLORS[5][1]} onClick={()=>navigateFromOverview('meetings')} icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>}/></motion.div>
          </motion.div>
          <div style={{...s.card, marginTop:4}}>
            <div style={{fontSize:14, fontWeight:600, marginBottom:16, letterSpacing:'-0.01em', color:MU}}>Pipeline Summary</div>
            <div className="pipeline-summary">
              {['New','Attempted','Connected','Details sent','Meeting scheduled','Proposal stage','Won'].map((stage,i) => {
                const cnt = spocs.filter(sp => sp.stage === stage).length;
                const isLast = i === 6;
                return <div key={stage} role="button" tabIndex={0} className="pipeline-summary__cell" onClick={()=>navigateFromOverview('stage:'+stage)} onKeyDown={e=>{if(e.key==='Enter'||e.key===' '){e.preventDefault();navigateFromOverview('stage:'+stage);}}} style={{borderRight:isLast?'none':'1px solid #D1D5DB'}} onMouseEnter={e=>{e.currentTarget.style.background='#F9FAFB'}} onMouseLeave={e=>{e.currentTarget.style.background='transparent'}}>
                  <div style={{fontSize:26, fontWeight:700, color:cnt?COLORS[i%COLORS.length]:LT, letterSpacing:'-0.03em', lineHeight:1}}>{cnt}</div>
                  <div style={{fontSize:11, color:MU, marginTop:6, fontWeight:400}}>{stage}</div>
                </div>;
              })}
            </div>
          </div>
        </>}

        {/* ═══ LEAD BANK ═══ */}
        {page==='leadbank' && !detailId && <>
          <div className="page-header-row" style={{alignItems:'center',marginBottom:20}}>
            <h1 style={{fontSize:26, fontWeight:700, margin:0, letterSpacing:'-0.03em'}}>Prospects</h1>
            <div className="page-header-actions">
              {isAdmin&&<motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={s.btn('s')} onClick={async()=>{try{const r=await api.reconcilePreview();if(!r.exact.length&&!r.fuzzy.length){alert('No matching prospects found — everything is already in sync!');return;}setReconcileModal({exact:r.exact,fuzzy:r.fuzzy,approved:new Set(r.fuzzy.map((_,i)=>i))});}catch(e){alert('Error: '+e.message);}}}>🔄 Sync Prospects</motion.button>}
              {isAdmin&&<motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={s.btn('s')} onClick={()=>setConfirmDel({title:'Clean Up Junk Entries',msg:'This will:\n• Strip leading/trailing quotes (" \' etc.) from all names\n• Delete entries with empty or invalid names\n\nContinue?',confirmLabel:'Clean Up',fn:async()=>{const r=await api.cleanupJunkLeads();await loadData();alert(`${r.cleaned||0} names cleaned, ${r.deleted||0} junk entries deleted.`);}})}>🧹 Clean Junk</motion.button>}
              <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={s.btn()} onClick={()=>openUpload('leads')}>📤 Upload CSV</motion.button>
            </div>
          </div>
          {(()=>{
            const lbUnmined = leadBank.filter(x=>x.status==='unmined').length;
            const lbInProgress = leadBank.filter(x=>x.status==='in-progress').length;
            const lbDone = leadBank.filter(x=>x.status==='done').length;
            const lbTotal = leadBank.length;
            const lbConvRate = lbTotal ? Math.round(lbDone/lbTotal*100) : 0;
            const lbCities = new Set(leadBank.map(x=>x.city).filter(Boolean)).size;
            const lbTopDevs = countTopDevelopers(leadBank, companies);
            return (
              <div style={{...s.card,padding:0,overflow:'hidden',marginBottom:16,border:'1px solid #E5E7EB'}}>
                <div style={{padding:'14px 16px',borderBottom:'1px solid #F3F4F6',display:'flex',justifyContent:'space-between',gap:12,alignItems:'center',flexWrap:'wrap'}}>
                  <div>
                    <div style={{fontSize:14,fontWeight:700,color:TX,letterSpacing:'-0.02em'}}>Prospect Overview</div>
                    <div style={{fontSize:12,color:MU,marginTop:2}}>{lbTotal} prospect{lbTotal!==1?'s':''} · {lbCities} cit{lbCities===1?'y':'ies'} · {lbConvRate}% converted</div>
                  </div>
                  {lbTopDevs>0&&<span style={{fontSize:11,fontWeight:600,color:'#D97706',background:'#FFFBEB',border:'1px solid #FDE68A',padding:'4px 9px',borderRadius:7}}>⭐ {lbTopDevs} top developer{lbTopDevs!==1?'s':''}</span>}
                </div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))',gap:1,background:'#F3F4F6'}}>
                  {[
                    {label:'Unmined',value:lbUnmined,sub:lbUnmined?'Awaiting assignment':'All assigned',accent:'#7C3AED'},
                    {label:'In Progress',value:lbInProgress,sub:lbInProgress?'Assigned to team':'None active',accent:P},
                    {label:'Done',value:lbDone,sub:`${lbConvRate}% conversion rate`,accent:GN},
                    {label:'Total',value:lbTotal,sub:lbTopDevs?`${lbTopDevs} top developers (prospects + companies)`:'Full lead bank',accent:TX},
                  ].map(({label,value,sub,accent})=>
                    <div key={label} style={{background:WH,padding:'14px 12px',minHeight:86}}>
                      <div style={{fontSize:22,fontWeight:800,color:TX,letterSpacing:'-0.04em',lineHeight:1}}>{value}</div>
                      <div style={{fontSize:11,fontWeight:700,color:MU,marginTop:7}}>{label}</div>
                      <div style={{fontSize:10,color:accent,marginTop:4,fontWeight:600,lineHeight:1.35}}>{sub}</div>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
          {/* ── City Breakdown ── */}
          {(()=>{
            // Group by normalized key so "Hyderabad" / "hyderabad" / " Hyderabad " are one row
            const cities = Object.values(leadBank.reduce((m,x)=>{const k=normalizeCityKey(x.city);if(k&&!m[k])m[k]=x.city.trim();return m;},{})).sort();
            if(!cities.length) return null;
            return <div style={{...s.card, marginTop:16, padding:0, overflow:'hidden'}}>
              <div className="prospect-section-header" style={{borderBottom:'1px solid '+BD}}>
                <div style={{fontSize:14, fontWeight:700, color:TX}}>🏙 City-wise Prospect Breakdown</div>
                <div style={{fontSize:11, color:MU}}>Prospects · companies · call coverage</div>
              </div>
              <div className="table-wrap table-wrap--city">
                <table style={{width:'100%', borderCollapse:'collapse', fontSize:12}}>
                  <thead>
                    <tr style={{background:'#F9FAFB'}}>
                      {['City','Total Prospects','Unmined','In Progress','Converted','Companies','Called','Connected','Connect %','Top Devs'].map(h=><th key={h} style={{...s.th, fontSize:11, padding:'8px 14px', whiteSpace:'nowrap'}}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {cities.map((city, ci)=>{
                      const cityLeads = leadBank.filter(x=>citiesMatch(x.city,city));
                      const unmined = cityLeads.filter(x=>x.status==='unmined').length;
                      const inProg = cityLeads.filter(x=>x.status==='in-progress').length;
                      const done = cityLeads.filter(x=>x.status==='done').length;
                      const topDevs = countTopDevelopers(leadBank, companies, { city });
                      const cityComps = companies.filter(c=>citiesMatch(c.city,city)).length;
                      // calls for companies in this city
                      const cityCalls = calls.filter(c=>{ const co=companies.find(x=>String(x._id)===String(c.companyId)); return citiesMatch(co?.city,city); });
                      const cityConn = cityCalls.filter(c=>c.connected).length;
                      const connPct = cityCalls.length ? Math.round(cityConn/cityCalls.length*100) : 0;
                      const convRate = cityLeads.length ? Math.round(done/cityLeads.length*100) : 0;
                      return <tr key={city} style={{background:ci%2===0?WH:'#FAFAFA', borderBottom:'1px solid '+BD}}>
                        <td style={{...s.td, fontWeight:700, padding:'8px 14px'}}>{city}</td>
                        <td style={{...s.td, padding:'8px 14px'}}><span style={{fontWeight:700, color:'#7C3AED'}}>{cityLeads.length}</span></td>
                        <td style={{...s.td, padding:'8px 14px', color:MU}}>{unmined}</td>
                        <td style={{...s.td, padding:'8px 14px', color:P}}>{inProg}</td>
                        <td style={{...s.td, padding:'8px 14px'}}>
                          <div style={{display:'flex', alignItems:'center', gap:6}}>
                            <span style={{color:GN, fontWeight:600}}>{done}</span>
                            {convRate>0&&<span style={{fontSize:10, color:convRate>=30?GN:convRate>=10?YL:MU, background:convRate>=30?GL:convRate>=10?YLL:'#F3F4F6', borderRadius:5, padding:'1px 5px'}}>{convRate}%</span>}
                          </div>
                        </td>
                        <td style={{...s.td, padding:'8px 14px', fontWeight:600}}>{cityComps}</td>
                        <td style={{...s.td, padding:'8px 14px'}}>{cityCalls.length}</td>
                        <td style={{...s.td, padding:'8px 14px', color:GN, fontWeight:600}}>{cityConn}</td>
                        <td style={{...s.td, padding:'8px 14px'}}>
                          {cityCalls.length>0
                            ? <div style={{display:'flex', alignItems:'center', gap:6}}>
                                <div style={{width:50, height:5, background:'#E5E7EB', borderRadius:99, overflow:'hidden'}}>
                                  <div style={{height:'100%', width:connPct+'%', background:connPct>=50?GN:connPct>=25?YL:RD, borderRadius:99}}/>
                                </div>
                                <span style={{fontWeight:700, color:connPct>=50?GN:connPct>=25?YL:RD}}>{connPct}%</span>
                              </div>
                            : <span style={{color:LT}}>—</span>}
                        </td>
                        <td style={{...s.td, padding:'8px 14px'}}>{topDevs>0?<span style={{color:'#D97706', fontWeight:700}}>⭐ {topDevs}</span>:<span style={{color:LT}}>—</span>}</td>
                      </tr>;
                    })}
                    {/* Totals row */}
                    {(()=>{
                      const allCalls=calls.length;
                      const allConn=calls.filter(c=>c.connected).length;
                      const allPct=allCalls?Math.round(allConn/allCalls*100):0;
                      return <tr style={{background:'#F3F4F6', fontWeight:700, borderTop:'2px solid '+BD}}>
                        <td style={{...s.td, padding:'8px 14px', fontWeight:700}}>Total</td>
                        <td style={{...s.td, padding:'8px 14px', color:'#7C3AED', fontWeight:700}}>{leadBank.length}</td>
                        <td style={{...s.td, padding:'8px 14px'}}>{leadBank.filter(x=>x.status==='unmined').length}</td>
                        <td style={{...s.td, padding:'8px 14px', color:P}}>{leadBank.filter(x=>x.status==='in-progress').length}</td>
                        <td style={{...s.td, padding:'8px 14px', color:GN}}>{leadBank.filter(x=>x.status==='done').length}</td>
                        <td style={{...s.td, padding:'8px 14px'}}>{companies.length}</td>
                        <td style={{...s.td, padding:'8px 14px'}}>{allCalls}</td>
                        <td style={{...s.td, padding:'8px 14px', color:GN}}>{allConn}</td>
                        <td style={{...s.td, padding:'8px 14px', fontWeight:700, color:allPct>=50?GN:allPct>=25?YL:RD}}>{allPct}%</td>
                        <td style={{...s.td, padding:'8px 14px', color:'#D97706'}}>{countTopDevelopers(leadBank, companies)} ⭐</td>
                      </tr>;
                    })()}
                  </tbody>
                </table>
              </div>
            </div>;
          })()}

          {/* Bulk add */}
          <div className="prospect-bulk-card" style={{...s.card, marginTop:16}}>
            <div style={{fontSize:14, fontWeight:700, marginBottom:12, color:TX, letterSpacing:'-0.02em'}}>⚡ Bulk Add Developers</div>
            <div style={{marginBottom:8}}><Fld label="City"><Dropdown style={{...s.sel,maxWidth:250,width:'100%'}} value={bCity} onChange={e=>setBCity(e.target.value)}>{CITIES.map(c=><option key={c}>{c}</option>)}</Dropdown></Fld></div>
            <Fld label="Developer names (one per line)"><textarea style={{...s.inp,minHeight:80,width:'100%'}} value={bText} onChange={e=>setBText(e.target.value)} placeholder={"DLF Homes\nEmbassy Group"}/></Fld>
            <div className="prospect-bulk-footer">
              {(() => {
                const lines = bText.split(/[\r\n]+/).map(l => l.trim().replace(/^["',;]+|["',;]+$/g, '').trim()).filter(Boolean);
                const invalid = lines.filter(n => !/[a-zA-Z]/.test(n));
                const valid = lines.filter(n => /[a-zA-Z]/.test(n) && n.length > 1);
                return (
                  <span style={{fontSize:12,color: valid.length ? MU : RD}}>
                    {valid.length} valid name{valid.length !== 1 ? 's' : ''}
                    {invalid.length > 0 && ` · ${invalid.length} invalid`}
                  </span>
                );
              })()}
              <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={s.btn()} onClick={addToBank}>Add to Bank</motion.button>
            </div>
          </div>
          {/* Filters + Table */}
          <div className="prospects-filters">
            <input style={{...s.inp,width:180}} placeholder="Search..." value={search} onChange={e=>{setSearch(e.target.value);setLbPage(1);}}/>
            <Dropdown style={{...s.sel,width:140}} value={lbFCity} onChange={e=>{setLbFCity(e.target.value);setLbPage(1);}}><option value="">All Cities</option>{visibleCities.map(c=><option key={c}>{c}</option>)}</Dropdown>
            <Dropdown style={{...s.sel,width:140}} value={lbFStat} onChange={e=>{setLbFStat(e.target.value);setLbPage(1);}}><option value="">All Status</option><option value="unmined">Unmined</option><option value="in-progress">In Progress</option><option value="done">Done</option></Dropdown>
            <Dropdown style={{...s.sel,width:180}} value={lbFCat} onChange={e=>{setLbFCat(e.target.value);setLbPage(1);}}><option value="">All Categories</option><option value="__none__">Uncategorized</option>{(config.developerCategories||[]).map(c=><option key={c}>{c}</option>)}</Dropdown>
            <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={{...s.btn(lbFTop?undefined:'s'),padding:'6px 12px',fontSize:12,background:lbFTop?'#F59E0B':undefined,border:lbFTop?'none':undefined}} onClick={()=>{setLbFTop(v=>!v);setLbPage(1);}}>⭐ Top Only</motion.button>
            <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={{...s.btn(lbSelectMode?undefined:'s'),padding:'6px 12px',fontSize:12,background:lbSelectMode?P:undefined,color:lbSelectMode?WH:undefined,border:lbSelectMode?'none':undefined}} onClick={()=>{setLbSelectMode(v=>!v);setLbSelected(new Set());}}>☑ Select</motion.button>
            <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} className="prospects-filters__template" style={{...s.btn('s'),marginLeft:'auto',fontSize:11}} onClick={()=>downloadTemplate('leads')}>📥 Download Template</motion.button>
          </div>
          {/* Filtered count + selection bar */}
          {(()=>{const fCount=leadBank.filter(x=>{if(lbFCity&&!citiesMatch(x.city,lbFCity))return false;if(lbFStat&&x.status!==lbFStat)return false;if(lbFCat===UNCATEGORIZED&&x.developerCategory)return false;if(lbFCat&&lbFCat!==UNCATEGORIZED&&x.developerCategory!==lbFCat)return false;if(lbFTop&&!x.isTopDeveloper)return false;if(search&&!x.name.toLowerCase().includes(search.toLowerCase()))return false;return true;}).length;const hasFilter=lbFCity||lbFStat||lbFCat||lbFTop||search;return <div className="prospect-bar-row" style={{padding:'10px 16px',background:hasFilter?'linear-gradient(135deg,#EEF2FF,#F5F3FF)':'#F9FAFB',borderRadius:10,marginBottom:12,border:'1px solid '+(hasFilter?'#C7D2FE':BD)}}><div style={{fontSize:13,fontWeight:600,color:hasFilter?'#4F46E5':MU}}>📋 Showing <span style={{color:TX,fontSize:15,fontWeight:700}}>{fCount}</span> of <span style={{fontWeight:600}}>{leadBank.length}</span> developers{hasFilter?' (filtered)':''}</div>{hasFilter&&<motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={{...s.btn('s'),padding:'4px 12px',fontSize:11}} onClick={()=>{setSearch('');setLbFCity('');setLbFStat('');setLbFCat('');setLbFTop(false);}}>Clear Filters</motion.button>}</div>;})()}
          {lbSelectMode&&lbSelected.size>0&&<div className="prospect-bar-row" style={{padding:'10px 16px',background:'linear-gradient(135deg,#EEF2FF,#E0E7FF)',borderRadius:10,marginBottom:12,border:'1px solid #A5B4FC'}}>
            <div style={{fontSize:13,fontWeight:700,color:'#4338CA'}}>{lbSelected.size} selected</div>
            <div style={{display:'flex',gap:8}}>
              <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn('s'),padding:'5px 14px',fontSize:12}} onClick={()=>setLbSelected(new Set())}>Deselect All</motion.button>
              {isAdmin&&<motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn('r'),padding:'5px 14px',fontSize:12}} onClick={()=>setConfirmDel({title:'Bulk Delete',msg:`Delete ${lbSelected.size} selected developer${lbSelected.size>1?'s':''} from Lead Bank? This cannot be undone.`,confirmLabel:'Delete All',fn:async()=>{await Promise.all([...lbSelected].map(id=>api.deleteLead(id)));setLbSelected(new Set());loadData();}})}>✕ Delete Selected</motion.button>}
            </div>
          </div>}
          <div className="table-wrap table-wrap--prospects">
          <table style={{width:'100%', borderCollapse:'collapse', ...s.card, padding:0, overflow:'hidden', marginTop:0}}>
            <thead><tr>{[...(lbSelectMode?[(()=>{
              const filteredIds=leadBank.filter(x=>{if(lbFCity&&!citiesMatch(x.city,lbFCity))return false;if(lbFStat&&x.status!==lbFStat)return false;if(lbFCat===UNCATEGORIZED&&x.developerCategory)return false;if(lbFCat&&lbFCat!==UNCATEGORIZED&&x.developerCategory!==lbFCat)return false;if(lbFTop&&!x.isTopDeveloper)return false;if(search&&!x.name.toLowerCase().includes(search.toLowerCase()))return false;return true;}).map(x=>x._id);
              const allSelected=filteredIds.length>0&&filteredIds.every(id=>lbSelected.has(id));
              return <th key="sel" style={{...s.th,width:36}}><input type="checkbox" checked={allSelected} onChange={()=>{if(allSelected)setLbSelected(new Set());else setLbSelected(new Set(filteredIds));}}/></th>;
            })()]:[]),'Developer','City','Category','Status','Assigned','Generated Lead','Notes','Actions'].map(h=>typeof h==='string'?<th key={h} style={s.th}>{h}</th>:h)}</tr></thead>
            <tbody>{(()=>{
              const filtered=leadBank.filter(x=>{
                if(lbFCity&&!citiesMatch(x.city,lbFCity))return false;
                if(lbFStat&&x.status!==lbFStat)return false;
                if(lbFCat===UNCATEGORIZED&&x.developerCategory)return false;
                if(lbFCat&&lbFCat!==UNCATEGORIZED&&x.developerCategory!==lbFCat)return false;
                if(lbFTop&&!x.isTopDeveloper)return false;
                if(search&&!x.name.toLowerCase().includes(search.toLowerCase()))return false;
                return true;
              });
              if(!filtered.length)return <tr><td colSpan={lbSelectMode?9:8} style={{...s.td,textAlign:'center',padding:40,color:MU}}>No data</td></tr>;
              const catColorMap=new Map((config.developerCategories||[]).map((c,i)=>[c,COLORS[i%COLORS.length]]));
              const totalPages=Math.ceil(filtered.length/LB_PER_PAGE);
              const paged=filtered.slice((lbPage-1)*LB_PER_PAGE,lbPage*LB_PER_PAGE);
              return paged.map(item=>{
              const sc={unmined:['#EDE9FE','#7C3AED'],'in-progress':[PL,P],done:[GL,GN]};const[bg,fg]=sc[item.status]||[PL,P];
              return <tr key={item._id} style={{background:lbSelected.has(item._id)?'#EEF2FF':item.isTopDeveloper?'#FFFBEB':undefined}}>
                {lbSelectMode&&<td style={{...s.td,width:36}}><input type="checkbox" checked={lbSelected.has(item._id)} onChange={()=>{const s2=new Set(lbSelected);if(s2.has(item._id))s2.delete(item._id);else s2.add(item._id);setLbSelected(s2);}}/></td>}
                <td style={{...s.td,fontWeight:600}}>
                  <div style={{display:'flex',flexDirection:'column',gap:3}}>
                    {item.status==='done' ? (
                      <span style={{cursor:'pointer',color:P,textDecoration:'underline',textUnderlineOffset:2}} onClick={()=>openProspectCompany(item)} title="View company details">{item.name}</span>
                    ) : (
                      <span>{item.name}</span>
                    )}
                    {item.isTopDeveloper&&<span style={{display:'inline-flex',alignItems:'center',gap:3,fontSize:10,fontWeight:700,color:'#92400E',background:'#FEF3C7',border:'1px solid #FCD34D',borderRadius:6,padding:'1px 7px',width:'fit-content'}}>⭐ Top Developer</span>}
                  </div>
                </td>
                <td style={s.td}>{item.city}</td>
                <td style={s.td}>
                  <Dropdown style={{...s.sel,padding:'4px 8px',fontSize:11,width:'auto'}} value={item.developerCategory||''} onChange={async e=>{try{await api.updateLead(item._id,{developerCategory:e.target.value});loadData();}catch{}}}>
                    <option value="">—</option>
                    {(config.developerCategories?.length?config.developerCategories:['Cat A Developer','Strong Mid Developer','Villa / Plot Focus','Budget Housing','Commercial Focus','Township Developer']).map(c=><option key={c} style={{color:catColorMap.get(c)}}>{c}</option>)}
                  </Dropdown>
                </td>
                <td style={s.td}><Dropdown style={{...s.sel,padding:'4px 8px',fontSize:12,width:'auto',background:bg,color:fg,fontWeight:600,borderRadius:20}} value={item.status} onChange={async e=>{try{const newStat=e.target.value;if(newStat==='done'&&!item.assignedTo&&!item.generatedCompanyId){alert('Cannot mark as Done without assigning to a salesperson or generating a company first.');return;}const updates={status:newStat};if(newStat==='unmined'){updates.assignedTo='';updates.developerCategory='';updates.notes='';updates.generatedCompanyId='';updates.generatedCompanyName='';}await api.updateLead(item._id,updates);loadData();}catch{}}}><option value="unmined">Unmined</option><option value="in-progress">In Progress</option><option value="done">Done</option></Dropdown></td>
                <td style={s.td}><Dropdown style={{...s.sel,padding:'4px 8px',fontSize:12,width:'auto'}} value={item.assignedTo||''} onChange={async e=>{try{await api.updateLead(item._id,{assignedTo:e.target.value});loadData();}catch{}}}><option value="">—</option>{config.salespersons?.map(sp=><option key={sp}>{sp}</option>)}</Dropdown></td>
                <td style={s.td}>{resolveProspectCompanyId(item) ? <button style={{...s.btn('s'),padding:'4px 10px',fontSize:11}} onClick={()=>openProspectCompany(item)}>{item.generatedCompanyName||item.name||'View company'}</button> : <span style={{color:LT}}>-</span>}{item.generatedAt&&<div style={{fontSize:10,color:LT,marginTop:3}}>{fmtDate(item.generatedAt)}</div>}</td>
                <td style={s.td}><input style={{...s.inp,padding:'6px 12px',fontSize:12,width:180}} defaultValue={item.notes||''} onBlur={async e=>{try{await api.updateLead(item._id,{notes:e.target.value});loadData();}catch{}}} placeholder="Notes..."/></td>
                <td style={{...s.td,whiteSpace:'nowrap'}}><div style={{display:'flex',gap:6,alignItems:'center',justifyContent:'flex-end'}}>
                  {item.status!=='done'&&<motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn('s'),padding:'5px 12px',fontSize:12,height:28}} onClick={()=>promoteLead(item)}>→ Company</motion.button>}
                  {isAdmin&&<DeleteIconBtn onClick={()=>setConfirmDel({fn:async()=>{await api.deleteLead(item._id);loadData();},msg:`Delete "${item.name}" from Lead Bank?`})}/>}
                </div></td>
              </tr>;
            })})()}</tbody>
          </table>
          </div>
          {(()=>{const filtered=leadBank.filter(x=>{const nm=(x.name||'').replace(/^["',;\s]+|["',;\s]+$/g,'').trim();if(nm.length<=1)return false;if(lbFCity&&!citiesMatch(x.city,lbFCity))return false;if(lbFStat&&x.status!==lbFStat)return false;if(lbFCat===UNCATEGORIZED&&x.developerCategory)return false;if(lbFCat&&lbFCat!==UNCATEGORIZED&&x.developerCategory!==lbFCat)return false;if(lbFTop&&!x.isTopDeveloper)return false;if(search&&!x.name.toLowerCase().includes(search.toLowerCase()))return false;return true;});const totalPages=Math.ceil(filtered.length/LB_PER_PAGE);if(totalPages<=1)return null;return <div style={{display:'flex',justifyContent:'center',alignItems:'center',gap:12,padding:'16px 0',flexWrap:'wrap'}}>
            <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn('s'),padding:'6px 14px',fontSize:12,opacity:lbPage<=1?.5:1}} disabled={lbPage<=1} onClick={()=>setLbPage(p=>p-1)}>← Prev</motion.button>
            <span style={{fontSize:13,fontWeight:600,color:MU}}>Page {lbPage} of {totalPages}</span>
            <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn('s'),padding:'6px 14px',fontSize:12,opacity:lbPage>=totalPages?.5:1}} disabled={lbPage>=totalPages} onClick={()=>setLbPage(p=>p+1)}>Next →</motion.button>
          </div>;})()}
        </>}

        {/* ═══ COMPANIES ═══ */}
        {page==='companies' && !detailId && (()=>{
          const todayStr = today();
          const coFiltered=companies.filter(c=>{
            if(fCity&&!citiesMatch(c.city,fCity))return false;
            if(search&&!c.name.toLowerCase().includes(search.toLowerCase()))return false;
            if(coStageFilter&&!spocs.some(sp=>String(sp.companyId)===String(c._id)&&sp.stage===coStageFilter))return false;
            if(coStars&&topStarsOf(c)!==Number(coStars))return false;
            return true;
          });
          const isFiltered = !!(fCity||search||coStageFilter||coSeg);
          const coIdSet = new Set(coFiltered.map(c => String(c._id)));
          const coScopedSpocs = spocs.filter(sp => coIdSet.has(String(sp.companyId?._id || sp.companyId)));
          const coActiveSpocs = coScopedSpocs.filter(sp => !sp.isStrikedOut);
          const coActive = coFiltered.filter(c => c.dealStatus === 'active').length;
          const coWon = coFiltered.filter(c => c.dealStatus === 'won').length;
          const coDropped = coFiltered.filter(c => c.dealStatus === 'dropped').length;
          const coOnHold = coFiltered.filter(c => c.dealStatus === 'on-hold').length;
          const coTopDev = coFiltered.filter(c => c.isTopDeveloper).length;
          const coUnassigned = coFiltered.filter(c => !c.salesperson?.trim()).length;
          const coHighPotential = coFiltered.filter(c => (c.potential || 0) >= 7).length;
          const coPendingDrop = coFiltered.filter(c => c.pendingDrop).length;
          const coWithSpocs = new Set(coActiveSpocs.map(sp => String(sp.companyId?._id || sp.companyId))).size;
          const coNoSpocs = Math.max(0, coFiltered.length - coWithSpocs);
          const coHotCompanies = new Set(coActiveSpocs.filter(sp => sp.hot).map(sp => String(sp.companyId?._id || sp.companyId))).size;
          const coHotSpocs = coActiveSpocs.filter(sp => sp.hot).length;
          const coWithFollowUp = new Set(coActiveSpocs.filter(sp =>
            sp.nextFollowUp && !['Won', 'Dropped', 'Invalid data'].includes(sp.stage)
          ).map(sp => String(sp.companyId?._id || sp.companyId))).size;
          const coOverdueFollowUp = new Set(coActiveSpocs.filter(sp =>
            sp.nextFollowUp && sp.nextFollowUp < todayStr && !['Won', 'Dropped', 'Invalid data'].includes(sp.stage)
          ).map(sp => String(sp.companyId?._id || sp.companyId))).size;
          const coUniqueCities = new Set(coFiltered.map(c => c.city).filter(Boolean)).size;
          const coAvgPotential = coFiltered.length
            ? Math.round(coFiltered.reduce((sum, c) => sum + (Number(c.potential) || 0), 0) / coFiltered.length * 10) / 10
            : 0;
          const coTotalSpocs = coActiveSpocs.length;
          const coAvgSpocs = coFiltered.length ? Math.round(coTotalSpocs / coFiltered.length * 10) / 10 : 0;
          const coActiveFilters = [fCity, search, coStageFilter, coSeg, coStars].filter(Boolean).length;
          const clearCoFilters = () => { setFCity(''); setSearch(''); setCoStageFilter(''); setCoSeg(''); setCoStars(''); setCoPage(1); };
          // Clickable overview-tile segments → filter the company list below
          const coWithSpocsSet = new Set(coActiveSpocs.map(sp => String(sp.companyId?._id || sp.companyId)));
          const coHotCompaniesSet = new Set(coActiveSpocs.filter(sp => sp.hot).map(sp => String(sp.companyId?._id || sp.companyId)));
          // Per-company: has an active follow-up, latest call date, active SPOC count (for "Needs Attention")
          const coFollowUpSet = new Set();
          const coLastCall = {};
          const coSpocCount = {};
          coActiveSpocs.forEach(sp => {
            const cid = String(sp.companyId?._id || sp.companyId);
            coSpocCount[cid] = (coSpocCount[cid] || 0) + 1;
            if (sp.nextFollowUp) coFollowUpSet.add(cid);
            if (sp.lastCallDate && (!coLastCall[cid] || sp.lastCallDate > coLastCall[cid])) coLastCall[cid] = sp.lastCallDate;
          });
          const tenDaysAgo = new Date(Date.now() - 10 * 864e5).toISOString().slice(0, 10);
          const needsAttentionPred = c => {
            const cid = String(c._id);
            if (c.dealStatus !== 'active') return false;   // only live deals
            if (!coSpocCount[cid]) return false;           // must have contacts (else "Without SPOCs" covers it)
            if (coFollowUpSet.has(cid)) return false;      // a follow-up is marked → ignore
            const last = coLastCall[cid];
            return !last || last <= tenDaysAgo;            // never called, or last call 10+ days ago
          };
          // New today — SPOCs and companies created on today's date
          const newSpocsToday = spocs.filter(sp => (sp.createdAt||'').slice(0,10) === todayStr);
          const newSpocCoIds = new Set(newSpocsToday.map(sp => String(sp.companyId?._id || sp.companyId || '')));
          const coNewSpocsCount = newSpocsToday.length;
          const coNewSpocCompanies = coFiltered.filter(c => newSpocCoIds.has(String(c._id))).length;
          const coNewCompanies = coFiltered.filter(c => (c.createdAt||'').slice(0,10) === todayStr).length;
          const segPredicates = {
            active: c => c.dealStatus === 'active',
            withspocs: c => coWithSpocsSet.has(String(c._id)),
            nospocs: c => !coWithSpocsSet.has(String(c._id)),
            newspocs: c => newSpocCoIds.has(String(c._id)),
            newco: c => (c.createdAt||'').slice(0,10) === todayStr,
            hot: c => coHotCompaniesSet.has(String(c._id)),
            highpotential: c => (c.potential || 0) >= 7,
            topdev: c => !!c.isTopDeveloper,
            onhold: c => c.dealStatus === 'on-hold',
            unassigned: c => !c.salesperson?.trim(),
            droppending: c => !!c.pendingDrop,
            needsattention: needsAttentionPred,
            needspoc: c => !!c.needNewSpoc,
          };
          const coNeedsAttention = coFiltered.filter(needsAttentionPred).length;
          const coNeedNewSpoc = coFiltered.filter(c => c.needNewSpoc).length;
          const coListFiltered = coSeg && segPredicates[coSeg] ? coFiltered.filter(segPredicates[coSeg]) : coFiltered;
          const coTotalPages = Math.ceil(coListFiltered.length / CO_PER_PAGE);
          const coPaged = coListFiltered.slice((coPage-1)*CO_PER_PAGE, coPage*CO_PER_PAGE);
          const segLabels = { active:'Active Pipeline', withspocs:'With SPOCs', nospocs:'Without SPOCs', newspocs:'New SPOCs Today', newco:'New Companies Today', hot:'Hot Leads', highpotential:'High Potential', topdev:'Top Developers', onhold:'On Hold', unassigned:'Unassigned', droppending:'Drop Pending', needsattention:'Needs Attention', needspoc:'Need New SPOC' };
          const toggleSeg = seg => { setCoSeg(prev => prev === seg ? '' : seg); setCoPage(1); };
          return <>
          <div className="page-header-row" style={{alignItems:'flex-start',marginBottom:20}}>
            <div>
              <h1 style={{fontSize:26,fontWeight:700,margin:'0 0 4px',letterSpacing:'-0.03em'}}>Companies</h1>
              <div style={{fontSize:13,color:MU}}>
                {coFiltered.length} total{isFiltered&&coFiltered.length!==companies.length?` of ${companies.length}`:''} · {coActive} active · {coWithSpocs} with SPOCs · {coHotCompanies} hot{coUnassigned>0?` · ${coUnassigned} unassigned`:''}
              </div>
            </div>
            <div className="page-header-actions">
              <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={s.btn('s')} onClick={()=>openUpload('companies-with-spocs')}>📋 Upload with SPOCs</motion.button>
              {isAdmin&&<motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={s.btn('s')} disabled={dupLoading} onClick={async()=>{setDupLoading(true);try{const r=findDuplicateCompaniesFromData(companies,spocs,fCity||undefined);if(!r.groupCount){alert('No duplicate companies found'+(fCity?' in '+fCity:'')+'!');return;}setDupModal(buildDuplicateModalState(r));}catch(e){alert('Error: '+e.message);}finally{setDupLoading(false);}}}>{dupLoading?'Scanning…':'🔍 Find Duplicates'}</motion.button>}
              <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={s.btn()} onClick={()=>openCompanyForm()}>+ Add Company</motion.button>
            </div>
          </div>

          {/* Compact filters */}
          <div style={{...s.card,padding:'12px 14px',marginBottom:16}}>
            <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
              <input style={{...s.inp,flex:'1 1 160px',minWidth:140,padding:'8px 12px',fontSize:12}} placeholder="Search company…" value={search} onChange={e=>{setSearch(e.target.value);setCoPage(1);}}/>
              <Dropdown style={{...s.sel,width:130,fontSize:12}} value={fCity} onChange={e=>{setFCity(e.target.value);setCoPage(1);}}>
                <option value="">All cities</option>{visibleCities.map(c=><option key={c} value={c}>{c}</option>)}
              </Dropdown>
              {coStageFilter&&<motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn('s'),padding:'8px 12px',fontSize:12}} onClick={()=>{setCoStageFilter('');setCoPage(1);}}>✕ Stage: {coStageFilter}</motion.button>}
              {coSeg&&<motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn('s'),padding:'8px 12px',fontSize:12,color:P}} onClick={()=>{setCoSeg('');setCoPage(1);}}>✕ {segLabels[coSeg]||coSeg}</motion.button>}
              {coActiveFilters>0&&<motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn('s'),padding:'8px 12px',fontSize:12,color:RD}} onClick={clearCoFilters}>Clear</motion.button>}
            </div>
          </div>

          {/* Company Overview */}
          <div style={{...s.card,padding:0,overflow:'hidden',marginBottom:16,border:'1px solid #E5E7EB'}}>
            <div style={{padding:'14px 16px',borderBottom:'1px solid #F3F4F6',display:'flex',justifyContent:'space-between',gap:12,alignItems:'center',flexWrap:'wrap'}}>
              <div>
                <div style={{fontSize:14,fontWeight:700,color:TX,letterSpacing:'-0.02em'}}>Company Overview</div>
                <div style={{fontSize:12,color:MU,marginTop:2}}>{isFiltered ? 'Filtered snapshot' : 'Full portfolio'} · {coUniqueCities} cit{coUniqueCities===1?'y':'ies'} · avg {coAvgSpocs} SPOCs per company</div>
              </div>
              <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                {coNoSpocs>0&&<span onClick={()=>toggleSeg('nospocs')} title="Show companies without SPOCs" style={{fontSize:11,fontWeight:700,color:RD,background:RL,border:'1px solid '+(coSeg==='nospocs'?RD:'#FECACA'),padding:'4px 9px',borderRadius:7,cursor:'pointer',boxShadow:coSeg==='nospocs'?'0 0 0 2px '+RL:'none'}}>{coNoSpocs} without SPOCs{coSeg==='nospocs'?' ✕':''}</span>}
                {coUnassigned>0&&<span onClick={()=>toggleSeg('unassigned')} title="Show unassigned companies" style={{fontSize:11,fontWeight:600,color:YL,background:YLL,border:'1px solid '+(coSeg==='unassigned'?YL:'#FDE68A'),padding:'4px 9px',borderRadius:7,cursor:'pointer',boxShadow:coSeg==='unassigned'?'0 0 0 2px '+YLL:'none'}}>{coUnassigned} unassigned{coSeg==='unassigned'?' ✕':''}</span>}
                {coPendingDrop>0&&<span onClick={()=>toggleSeg('droppending')} title="Show companies with a pending drop" style={{fontSize:11,fontWeight:600,color:'#7C3AED',background:'#F5F3FF',border:'1px solid '+(coSeg==='droppending'?'#7C3AED':'#DDD6FE'),padding:'4px 9px',borderRadius:7,cursor:'pointer',boxShadow:coSeg==='droppending'?'0 0 0 2px #F5F3FF':'none'}}>{coPendingDrop} drop pending{coSeg==='droppending'?' ✕':''}</span>}
              </div>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))',gap:1,background:'#F3F4F6'}}>
              {[
                {label:'Total Companies',value:coFiltered.length,sub:`${coActive} active · ${coWon} won`,accent:TX,seg:''},
                {label:'Active Pipeline',value:coActive,sub:coWithFollowUp?`${coWithFollowUp} with follow-ups${coOverdueFollowUp?` · ${coOverdueFollowUp} overdue`:''}`:'No follow-ups scheduled',accent:coActive?GN:MU,seg:'active'},
                {label:'With SPOCs',value:coWithSpocs,sub:coNoSpocs?`${coNoSpocs} need contacts`:`${coTotalSpocs} active contacts`,accent:coNoSpocs?RD:GN,seg:'withspocs'},
                {label:'Hot Leads',value:coHotCompanies,sub:coHotSpocs?`${coHotSpocs} hot SPOC${coHotSpocs!==1?'s':''}`:'None marked hot',accent:coHotCompanies?'#EC4899':MU,seg:'hot'},
                {label:'High Potential',value:coHighPotential,sub:`avg ${coAvgPotential}/10 score`,accent:coHighPotential>=1?GN:MU,seg:'highpotential'},
                {label:'Top Developers',value:coTopDev,sub:coTopDev?'Starred accounts':'None flagged',accent:coTopDev?'#F59E0B':MU,seg:'topdev'},
                {label:'On Hold',value:coOnHold,sub:coDropped?`${coDropped} dropped`:'Paused deals',accent:coOnHold?YL:MU,seg:'onhold'},
                {label:'Unassigned',value:coUnassigned,sub:coUnassigned?'Needs owner':'All assigned',accent:coUnassigned?RD:GN,seg:'unassigned'},
                {label:'Needs Attention',value:coNeedsAttention,sub:coNeedsAttention?'No call 10d+ · no follow-up':'All followed up',accent:coNeedsAttention?RD:GN,seg:'needsattention'},
                {label:'Need New SPOC',value:coNeedNewSpoc,sub:coNeedNewSpoc?'Flagged for new contacts':'None flagged',accent:coNeedNewSpoc?'#7C3AED':MU,seg:'needspoc'},
                {label:'New SPOCs · Today',value:coNewSpocsCount,sub:coNewSpocCompanies?`${coNewSpocCompanies} compan${coNewSpocCompanies===1?'y':'ies'}`:'None today',accent:coNewSpocsCount?'#4338CA':MU,seg:'newspocs'},
                {label:'New Companies · Today',value:coNewCompanies,sub:coNewCompanies?'Added today':'None today',accent:coNewCompanies?GN:MU,seg:'newco'},
              ].map(({label,value,sub,accent,seg})=>{
                const isTotal = seg === '';
                const active = !isTotal && coSeg === seg;
                const clickable = isTotal ? !!coSeg : true;
                const isTopDevTile = seg === 'topdev';
                const tileActive = active || (isTopDevTile && !!coStars);
                return <div key={label} onClick={()=>{ if(isTotal){ if(coSeg){ setCoSeg(''); setCoPage(1); } } else if(isTopDevTile){ setCoSeg(coSeg==='topdev'?'':'topdev'); setCoStars(''); setCoPage(1); } else { toggleSeg(seg); } }} title={isTotal?(coSeg?'Show all companies':undefined):`Show ${label}`} style={{position:'relative',background:tileActive?'#EEF2FF':WH,padding:'14px 12px',minHeight:86,cursor:clickable?'pointer':'default',boxShadow:tileActive?'inset 0 0 0 2px '+P:'none',transition:'background .15s'}}>
                  <div style={{fontSize:22,fontWeight:800,color:TX,letterSpacing:'-0.04em',lineHeight:1}}>{value}</div>
                  <div style={{fontSize:11,fontWeight:700,color:tileActive?P:MU,marginTop:7}}>{label}{active?' ▾':''}</div>
                  {isTopDevTile
                    ? <div onClick={e=>e.stopPropagation()} style={{marginTop:4}}>
                        <Dropdown plain style={{fontSize:10,color:'#F59E0B',fontWeight:600}}
                          value={coStars || (coSeg==='topdev'?'top':'')}
                          onChange={e=>{const v=e.target.value; if(v===''){setCoSeg('');setCoStars('');} else if(v==='top'){setCoSeg('topdev');setCoStars('');} else {setCoSeg('topdev');setCoStars(v);} setCoPage(1);}}>
                          <option value="">All companies</option>
                          <option value="top">★ Top — any rating</option>
                          <option value="1">★ 1 star</option>
                          <option value="2">★★ 2 star</option>
                          <option value="3">★★★ 3 star</option>
                        </Dropdown>
                      </div>
                    : <div style={{fontSize:10,color:accent,marginTop:4,fontWeight:600,lineHeight:1.35}}>{sub}</div>}
                </div>;
              })}
            </div>
          </div>

          {(()=>{
            const filtered=coPaged;
            if(!coListFiltered.length)return <div style={{textAlign:'center',padding:40,color:MU,...s.card}}>{coSeg?`No companies under “${segLabels[coSeg]||coSeg}”`:'No data'}</div>;
            return <motion.div variants={listContainer} initial="hidden" animate="show">{filtered.map(co=>{
            const coSps = spocs.filter(x=>x.companyId===co._id);
            return <motion.div key={co._id} variants={listItem} whileHover={{x:2}} style={{...s.card,padding:0,overflow:'hidden',marginBottom:8}}>
              <div className="company-card-row" onClick={()=>{setDetailId(co._id);setDTab('overview');}}>
                <div>
                  <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                    {topStarsOf(co)>0&&<span title={`${topStarsOf(co)}-star Top Developer`} style={{fontSize:14,color:'#F59E0B',letterSpacing:-1,whiteSpace:'nowrap'}}>{'★'.repeat(topStarsOf(co))}</span>}
                    <span style={{fontSize:15,fontWeight:700}}>{co.name}</span>
                    {co.previouslyTapped && <Tag label="Previously Tapped"/>}
                    {co.dealStatus==='on-hold' && <Tag label="On Hold"/>}
                    {co.source && <span style={s.tag(PL,P)}>{co.source}</span>}
                  </div>
                  <div style={{fontSize:12,color:MU,marginTop:3}}>{co.city} · {co.salesperson||'Unassigned'} · Potential: <span style={{fontWeight:600,color:co.potential>=7?GN:co.potential>=4?YL:co.potential>0?RD:MU}}>{co.potential||'—'}/10</span></div>
                </div>
                <div className="company-card-row__aside">
                  <div style={{textAlign:'right'}}>
                    <div style={{fontSize:18,fontWeight:700,color:P}}>{coSps.length}</div>
                    <div style={{fontSize:11,color:MU}}>SPOCs</div>
                  </div>
                  {isAdmin&&<DeleteIconBtn stopPropagation onClick={()=>setConfirmDel({fn:async()=>{await api.deleteCompany(co._id);loadData();},msg:`Delete "${co.name}" and all its data?`})}/>}
                </div>
              </div>
            </motion.div>;
          })}</motion.div>})()}
          {coTotalPages>1&&<div style={{display:'flex',justifyContent:'center',alignItems:'center',gap:12,padding:'16px 0'}}>
            <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn('s'),padding:'6px 14px',fontSize:12,opacity:coPage<=1?.5:1}} disabled={coPage<=1} onClick={()=>setCoPage(p=>p-1)}>← Prev</motion.button>
            <span style={{fontSize:13,fontWeight:600,color:MU}}>Page {coPage} of {coTotalPages} · {coListFiltered.length} companies</span>
            <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn('s'),padding:'6px 14px',fontSize:12,opacity:coPage>=coTotalPages?.5:1}} disabled={coPage>=coTotalPages} onClick={()=>setCoPage(p=>p+1)}>Next →</motion.button>
          </div>}
        </>;})()}

        {/* ═══ COMPANY DETAIL ═══ */}
        {detailId && !detailCo && <div style={{...s.card,textAlign:'center',padding:40}}>
          <div style={{fontSize:16,fontWeight:600,color:MU,marginBottom:8}}>Company not found</div>
          <div style={{fontSize:13,color:LT,marginBottom:16}}>This company may have been deleted or is no longer accessible.</div>
          <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={s.btn('s')} onClick={()=>setDetailId(null)}>← Back to list</motion.button>
        </div>}
        {detailId && detailCo && <>
          <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:20}}>
            <div onClick={()=>setDetailId(null)} style={{cursor:'pointer',width:34,height:34,borderRadius:10,background:WH,boxShadow:'0 1px 4px rgba(0,0,0,.08)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:16,flexShrink:0}}>←</div>
            <div><h1 style={{fontSize:20,fontWeight:700,margin:0,letterSpacing:'-0.02em'}}>{detailCo.name} {detailCo.previouslyTapped&&<Tag label="Previously Tapped"/>}</h1><div style={{fontSize:12,color:MU}}>{detailCo.city} · {detailCo.salesperson||'Unassigned'}{detailCo.source?' · '+detailCo.source:''}</div></div>
            <div style={{marginLeft:'auto',display:'flex',gap:6}}>
              <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={s.btn('s')} onClick={()=>openCompanyForm(detailCo)}>Edit</motion.button>
              <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={s.btn()} onClick={()=>openSpocForm(null,detailId)}>+ SPOC</motion.button>
              <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={s.btn()} onClick={()=>openCallForm(null)}>+ Log Call</motion.button>
              <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={s.btn()} onClick={()=>openMeetingForm()}>+ Meeting</motion.button>
              <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={{...s.btn(),background:'#7C3AED'}} onClick={()=>openProposalForm(null, detailId)}>+ Proposal</motion.button>
              <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={detailCo.needNewSpoc?{...s.btn(),background:'#7C3AED'}:s.btn('s')} disabled={coNeedSpocSaving} title={detailCo.needNewSpoc?`Flagged by ${detailCo.needNewSpocBy||'—'}${detailCo.needNewSpocDate?' on '+fmtDate(detailCo.needNewSpocDate):''} — click to clear`:'Flag: previous SPOCs unresponsive, find new contacts'} onClick={async()=>{const on=!detailCo.needNewSpoc;setCoNeedSpocSaving(true);try{await api.updateCompany(detailCo._id,{needNewSpoc:on,needNewSpocBy:on?user:'',needNewSpocDate:on?today():''});await loadData();}catch(e){alert(e.message);}finally{setCoNeedSpocSaving(false);}}}>{coNeedSpocSaving?'…':(detailCo.needNewSpoc?'✓ New SPOC Needed':'🔎 Need New SPOC')}</motion.button>
              {detailCo.dealStatus==='active'&&isAdmin&&<motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={{...s.btn('r')}} onClick={()=>openConcludeForm(detailCo)}>Conclude</motion.button>}
              {detailCo.dealStatus==='active'&&!isAdmin&&!detailCo.pendingDrop&&<motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={{...s.btn('r')}} onClick={()=>{setModal({type:'requestDrop',companyId:detailCo._id,companyName:detailCo.name});setForm({reason:'',reconnectDate:''});}}>Request Drop</motion.button>}
              {detailCo.dealStatus==='active'&&!isAdmin&&detailCo.pendingDrop&&<span style={{...s.tag('#FEF3C7','#D97706'),fontSize:12}}>⏳ Drop Pending Approval</span>}
            </div>
          </div>
          <div className="tab-row" style={{marginBottom:16}}>{['overview','spocs','hierarchy','calls','meetings','proposals','timeline'].map(t=><div key={t} style={s.tab(dTab===t)} onClick={()=>setDTab(t)}>{t==='hierarchy'?'Hierarchy':t.charAt(0).toUpperCase()+t.slice(1)}</div>)}</div>

          {dTab==='overview'&&<>
            <motion.div variants={listContainer} initial="hidden" animate="show" style={s.card}>{[['Segment',detailCo.segment],['Source',detailCo.source||'—'],['Salesperson',detailCo.salesperson||'—'],['Potential',(detailCo.potential||5)+'/10'],['Status',detailCo.dealStatus],['Generated by entry',detailCo.generatedFromLeadBankName||'—'],['Active SPOCs',detailSps.filter(x=>!x.isStrikedOut).length+' of '+detailSps.length],['Meetings',detailMts.length],['Proposals sent',detailProps.length],['Last proposal',lastProposal ? `${fmtDate(lastProposal.date)} → ${proposalRecipientName(lastProposal, detailSps)} · ${lastProposal.templateName||'Proposal'}${lastProposal.nextFollowUpDate?` · FU ${fmtDate(lastProposal.nextFollowUpDate)}`:''}` : '—']].map(([l,v])=><motion.div key={l} variants={listItem} style={{display:'flex',padding:'10px 0',borderBottom:'1px solid #F3F4F6',fontSize:13}}><div style={{width:140,color:MU,fontWeight:500}}>{l}</div><div style={{fontWeight:500}}>{v}</div></motion.div>)}</motion.div>
            {(()=>{
              // Note log — newest first. Legacy single-field note (if any) shown as the oldest entry.
              const saved = Array.isArray(detailCo.nextActionNotes) ? detailCo.nextActionNotes : [];
              const legacy = detailCo.nextActionNote?.trim() ? [{ text: detailCo.nextActionNote, by: '', at: '', legacy: true }] : [];
              const all = [...legacy, ...saved];
              const clean = arr => arr.filter(n=>!n.legacy).map(n=>({ text:n.text, by:n.by||'', at:n.at||'' }));
              const persist = async next => { await api.updateCompany(detailCo._id, { nextActionNotes: clean(next) }); await loadData(); };
              return <div style={{...s.card,marginTop:14}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
                  <div style={{fontSize:14,fontWeight:700,letterSpacing:'-0.01em'}}>📝 Next Line of Action</div>
                  <span style={{fontSize:11,color:LT}}>Notes to prep for the next meet</span>
                </div>
                <textarea key={detailId} ref={coNoteRef} placeholder="e.g. Share revised quote, then push for a site visit next week…" style={{...s.inp,minHeight:80,resize:'vertical',lineHeight:1.5}}/>
                <div style={{display:'flex',justifyContent:'flex-end',marginTop:8}}>
                  <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn(),opacity:coNoteSaving?.6:1}} disabled={coNoteSaving} onClick={async()=>{
                    const text=(coNoteRef.current?.value||'').trim();
                    if(!text) return;
                    setCoNoteSaving(true);
                    try{ await persist([...all, { text, by:user, at:new Date().toISOString() }]); if(coNoteRef.current) coNoteRef.current.value=''; }
                    catch(e){ alert(e.message); } finally { setCoNoteSaving(false); }
                  }}>{coNoteSaving?'Saving…':'Save Note'}</motion.button>
                </div>
                {all.length>0&&<div style={{marginTop:14,borderTop:'1px solid #F3F4F6',paddingTop:12,display:'flex',flexDirection:'column',gap:8}}>
                  <div style={{fontSize:11,fontWeight:700,color:MU}}>{all.length} note{all.length>1?'s':''}</div>
                  {all.map((n,i)=>(
                    <div key={i} style={{display:'flex',gap:10,alignItems:'flex-start',padding:'10px 12px',borderRadius:10,border:'1px solid '+BD,background:'#FAFBFC'}}>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:13,color:TX,whiteSpace:'pre-wrap',wordBreak:'break-word',lineHeight:1.5}}>{n.text}</div>
                        <div style={{fontSize:10,color:LT,marginTop:4}}>{n.legacy?'Earlier note':`${n.by||'—'} · ${fmtDateTime(n.at)}`}</div>
                      </div>
                      <button type="button" title="Delete note" onClick={()=>setConfirmDel({msg:'Delete this note?',fn:async()=>{try{ if(n.legacy){ await api.updateCompany(detailCo._id,{nextActionNote:''}); await loadData(); } else { await persist(all.filter((_,j)=>j!==i)); } }catch(e){alert(e.message);}}})} style={{border:'none',background:'transparent',cursor:'pointer',color:RD,fontSize:15,lineHeight:1,padding:'0 2px',flexShrink:0}}>×</button>
                    </div>
                  )).reverse()}
                </div>}
              </div>;
            })()}
          </>}

          {dTab==='spocs'&&<>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10}}>
              <div style={{fontSize:13,color:MU}}>
                <span style={{fontWeight:700,color:TX}}>{detailSps.filter(x=>!x.isStrikedOut).length}</span> active SPOC{detailSps.filter(x=>!x.isStrikedOut).length!==1?'s':''}
                {detailSps.filter(x=>x.isStrikedOut).length>0&&<span style={{marginLeft:8,color:LT}}>· {detailSps.filter(x=>x.isStrikedOut).length} struck out</span>}
              </div>
            </div>
            <table style={{width:'100%',borderCollapse:'collapse',...s.card,padding:0,overflow:'hidden'}}>
              <thead><tr>{['SPOC','Designation','Phone','Level','Stage','Hot','DNP','Trail','Actions'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
              <tbody>{detailSps.map(sp=>{
                const struck=!!sp.isStrikedOut;
                const rowStyle={opacity:struck?0.45:1,background:struck?'#F9FAFB':''};
                return <tr key={sp._id} style={rowStyle}>
                  <td style={{...s.td,fontWeight:600}}>
                    <span style={{textDecoration:struck?'line-through':'underline',color:struck?MU:P,cursor:'pointer',textUnderlineOffset:2}} onClick={()=>openSpocForm(sp)} title="Edit SPOC">{sp.name}</span>
                    {struck&&<span style={{marginLeft:6,fontSize:10,fontWeight:600,padding:'1px 6px',borderRadius:10,background:'#FEE2E2',color:RD}}>✕ {sp.strikeoutReason||'struck out'}</span>}
                  </td>
                  <td style={{...s.td,fontSize:12,color:MU,textDecoration:struck?'line-through':'none'}}>{sp.designation||'—'}</td>
                  <td style={{...s.td,fontSize:12,textDecoration:struck?'line-through':'none'}}>{sp.phone||'—'}</td>
                  <td style={s.td}>{!struck&&<Tag label={sp.decisionLevel}/>}</td>
                  <td style={s.td}>{!struck&&<Tag label={sp.stage}/>}</td>
                  <td style={s.td}>{!struck&&<motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={{...s.btn(sp.hot?undefined:'s'),padding:'3px 10px',fontSize:11,background:sp.hot?'#F59E0B':undefined,border:sp.hot?'none':undefined}} onClick={async()=>{await api.toggleHot(sp._id);loadData();}}>{sp.hot?'🔥 Hot':'Mark Hot'}</motion.button>}</td>
                  <td style={s.td}>{!struck&&(()=>{const dnp=calls.filter(c=>c.spocId===sp._id&&c.outcome==='No answer').length;return <span title="Did Not Pick — 'No answer' calls" style={{fontWeight:700,fontSize:13,color:dnp>=3?RD:dnp>0?YL:MU}}>{dnp}</span>;})()}</td>
                  <td style={s.td}>{!struck&&<motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={{...s.btn('s'),padding:'3px 10px',fontSize:11}} onClick={()=>openTrail(sp)}>Trail ({calls.filter(c=>c.spocId===sp._id).length})</motion.button>}</td>
                  <td style={s.td}><div style={{display:'flex',gap:4}}>
                    {!struck&&<motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={{...s.btn(),padding:'3px 10px'}} onClick={()=>openCallForm(sp)}>Call</motion.button>}
                    {!struck&&<motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={{...s.btn('s'),padding:'3px 10px'}} onClick={()=>openSpocForm(sp)}>Edit</motion.button>}
                    {struck
                      ? <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.97}}
                          style={{padding:'3px 10px',fontSize:11,borderRadius:8,border:'1.5px solid #6366F1',background:PL,color:P,cursor:'pointer',fontWeight:600}}
                          onClick={async()=>{await api.restoreSpoc(sp._id);loadData();}}>↩ Restore</motion.button>
                      : <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.97}}
                          style={{padding:'3px 10px',fontSize:11,borderRadius:8,border:'1.5px solid #EF4444',background:'#FFF5F5',color:RD,cursor:'pointer',fontWeight:600}}
                          onClick={()=>setStrikeModal({spoc:sp})}>Strike</motion.button>
                    }
                    {isAdmin&&<DeleteIconBtn onClick={()=>setConfirmDel({fn:async()=>{await api.deleteSpoc(sp._id);loadData();},msg:`Delete SPOC "${sp.name}"?`})}/>}
                  </div></td>
                </tr>;
              })}
              {!detailSps.length&&<tr><td colSpan={9} style={{...s.td,textAlign:'center',color:LT,padding:30}}>No SPOCs yet</td></tr>}
              </tbody>
            </table>
          </>}

          {dTab==='hierarchy'&&(()=>{
            const activeSps = detailSps.filter(sp => !sp.isStrikedOut);
            const idSet = new Set(activeSps.map(sp => String(sp._id)));
            const rtOf = sp => { const r = sp.reportsTo?._id || sp.reportsTo; return r ? String(r) : ''; };
            const childrenOf = pid => activeSps.filter(sp => rtOf(sp) === String(pid));
            // Descendants of a spoc (to prevent choosing a manager that would create a cycle)
            const descendants = (pid, acc = new Set()) => { childrenOf(pid).forEach(c => { if (!acc.has(String(c._id))) { acc.add(String(c._id)); descendants(c._id, acc); } }); return acc; };
            const roots = activeSps.filter(sp => { const r = rtOf(sp); return !r || !idSet.has(r); });
            const setManager = async (spId, mgrId) => { try { await api.updateSpoc(spId, { reportsTo: mgrId || null }); await loadData(); } catch(e){ alert(e.message); } };
            const renderNode = (sp, seen = new Set()) => {
              if (seen.has(String(sp._id))) return null; // cycle guard
              const seen2 = new Set(seen); seen2.add(String(sp._id));
              const kids = childrenOf(sp._id);
              return <li key={sp._id}>
                <div className="org-node" onClick={()=>openSpocForm(sp)} title="Edit SPOC">
                  <div className="org-node__name">{sp.hot?'🔥 ':''}{sp.name}</div>
                  <div className="org-node__role">{sp.designation||'—'}</div>
                  {sp.phone&&<div className="org-node__meta">{sp.phone}</div>}
                </div>
                {kids.length>0&&<ul>{kids.map(k=>renderNode(k, seen2))}</ul>}
              </li>;
            };
            return <>
              {!activeSps.length&&<div style={{...s.card,textAlign:'center',color:LT,padding:30}}>No active SPOCs to map. Add SPOCs first.</div>}
              {activeSps.length>0&&<>
                <div style={{...s.card,overflowX:'auto',paddingBottom:24}}>
                  <div style={{fontSize:14,fontWeight:700,marginBottom:4}}>Company Hierarchy</div>
                  <div style={{fontSize:12,color:MU,marginBottom:8}}>Who reports to whom · click a card to edit the SPOC</div>
                  <div className="org-tree"><ul>{roots.map(r=>renderNode(r))}</ul></div>
                </div>
                <div style={{...s.card,marginTop:14}}>
                  <div style={{fontSize:14,fontWeight:700,marginBottom:2}}>Reporting Lines</div>
                  <div style={{fontSize:12,color:MU,marginBottom:12}}>Set each SPOC's manager to build the tree above.</div>
                  <div style={{display:'flex',flexDirection:'column',gap:8}}>
                    {activeSps.map(sp=>{
                      const blocked = new Set([String(sp._id), ...descendants(sp._id)]); // self + reports can't be its manager
                      const options = activeSps.filter(m => !blocked.has(String(m._id)));
                      return <div key={sp._id} style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
                        <div style={{minWidth:180,flex:'0 0 auto',fontSize:13,fontWeight:600}}>{sp.name} <span style={{fontWeight:400,color:MU,fontSize:11}}>· {sp.designation||'—'}</span></div>
                        <span style={{fontSize:12,color:LT}}>reports to</span>
                        <Dropdown style={{...s.sel,minWidth:200,flex:'1 1 200px'}} value={rtOf(sp)} onChange={e=>setManager(sp._id, e.target.value)}>
                          <option value="">— None (top level) —</option>
                          {options.map(m=><option key={m._id} value={String(m._id)}>{m.name}{m.designation?` (${m.designation})`:''}</option>)}
                        </Dropdown>
                      </div>;
                    })}
                  </div>
                </div>
              </>}
            </>;
          })()}

          {dTab==='calls'&&<table style={{width:'100%',borderCollapse:'collapse',...s.card,padding:0,overflow:'hidden'}}><thead><tr>{['Date','SPOC','Outcome','Confidence','Notes','Actions'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead><tbody>{detailCls.map(c=>{const sp=spocs.find(x=>x._id===c.spocId);return<tr key={c._id}><td style={s.td}>{fmtDate(c.date)}</td><td style={{...s.td,fontWeight:500}}>{sp?.name||'—'}</td><td style={s.td}><Tag label={c.outcome||c.conversationOutcome}/></td><td style={s.td}>{c.confidence}/10</td><td style={{...s.td,maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{c.notes||'—'}</td><td style={s.td}><div style={{display:'flex',gap:4}}><motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn('s'),padding:'3px 10px',fontSize:11}} onClick={()=>openCallForm(sp,c)}>Edit</motion.button>{isAdmin&&<DeleteIconBtn onClick={()=>setConfirmDel({fn:async()=>{await api.deleteCall(c._id);loadData();},msg:'Delete this call entry?'})}/>}</div></td></tr>})}{!detailCls.length&&<tr><td colSpan={6} style={{...s.td,textAlign:'center',color:LT,padding:30}}>No calls</td></tr>}</tbody></table>}

          {dTab==='meetings'&&<><motion.div variants={listContainer} initial="hidden" animate="show">{detailMts.map(m=><motion.div key={m._id} variants={listItem} whileHover={{x:2}} style={{...s.card,borderLeft:'4px solid '+P,marginBottom:8}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div><strong>Meeting #{m.meetingNumber}</strong> · {fmtDate(m.date)} · <Tag label={m.stage}/></div>
              <div style={{display:'flex',gap:4,alignItems:'center'}}>{m.estimatedValue&&<span style={{fontWeight:700,color:GN}}>{fmtINR(m.estimatedValue)}</span>}<motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={{...s.btn('s'),padding:'3px 10px'}} onClick={()=>openMeetingForm(m)}>Edit</motion.button>{isAdmin&&<DeleteIconBtn onClick={()=>setConfirmDel({fn:async()=>{await api.deleteMeeting(m._id);loadData();},msg:`Delete Meeting #${m.meetingNumber}?`})}/>}</div>
            </div>
            {m.agenda&&<div style={{fontSize:12,color:MU,marginTop:4}}>Agenda: {m.agenda}</div>}
            {m.location&&<div style={{fontSize:12,color:MU,marginTop:4}}>📍 Location: {m.location}</div>}
            {m.summary&&<div style={{fontSize:12,marginTop:4}}>{m.summary}</div>}
            {m.keyDecisions&&<div style={{fontSize:12,color:GN,marginTop:4}}>Decisions: {m.keyDecisions}</div>}
            {m.nextAction&&<div style={{fontSize:12,color:P,marginTop:4}}>Next: {m.nextAction} {m.nextActionDate?'by '+fmtDate(m.nextActionDate):''}</div>}
            <div style={{height:4,background:BG,borderRadius:2,marginTop:8}}><div style={{height:'100%',width:(m.closingProbability||0)+'%',background:(m.closingProbability||0)>=60?GN:(m.closingProbability||0)>=30?YL:RD,borderRadius:2}}/></div>
          </motion.div>)}</motion.div>{!detailMts.length&&<div style={{textAlign:'center',color:LT,padding:30}}>No meetings yet</div>}</>}

          {dTab==='proposals'&&<>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
              <div style={{fontSize:13,color:MU}}><span style={{fontWeight:700,color:TX}}>{detailProps.length}</span> proposal{detailProps.length!==1?'s':''} logged</div>
              <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn(),background:'#7C3AED'}} onClick={()=>openProposalForm(null, detailId)}>+ Log Proposal</motion.button>
            </div>
            <table style={{width:'100%',borderCollapse:'collapse',...s.card,padding:0,overflow:'hidden'}}>
              <thead><tr>{['Date sent','Sent to','Proposal','Via','Next follow-up','Sent by','Notes','Actions'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
              <tbody>{detailProps.map(p=>{
                const sp=detailSps.find(s=>String(s._id)===String(p.spocId));
                const recipient=proposalRecipientName(p, detailSps);
                return <tr key={p._id}>
                  <td style={s.td}>{fmtDate(p.date)}</td>
                  <td style={{...s.td,fontWeight:600}}>{recipient}{sp?.designation?` · ${sp.designation}`:''}</td>
                  <td style={s.td}>{p.templateName||'—'}</td>
                  <td style={s.td}>{p.sentVia||'—'}</td>
                  <td style={{...s.td,fontWeight:p.nextFollowUpDate?600:400,color:p.nextFollowUpDate?P:MU}}>{p.nextFollowUpDate?fmtDate(p.nextFollowUpDate):'—'}</td>
                  <td style={s.td}>{p.salesperson||'—'}</td>
                  <td style={{...s.td,maxWidth:180,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{p.notes||'—'}</td>
                  <td style={s.td}><div style={{display:'flex',gap:4}}>
                    <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn('s'),padding:'3px 10px',fontSize:11}} onClick={()=>openProposalForm(p, detailId)}>Edit</motion.button>
                    {isAdmin&&<DeleteIconBtn onClick={()=>setConfirmDel({fn:async()=>{await api.deleteProposal(p._id);loadData();},msg:'Delete this proposal log?'})}/>}
                  </div></td>
                </tr>;
              })}{!detailProps.length&&<tr><td colSpan={8} style={{...s.td,textAlign:'center',color:LT,padding:30}}>No proposals logged yet. Use + Log Proposal to track whom you sent proposals to.</td></tr>}</tbody>
            </table>
          </>}

          {dTab==='timeline'&&<div style={s.card}>
            <h3 style={{marginBottom:16}}>Company Timeline</h3>
            {config.showTimelineMeetingAge && (() => {
              const firstMeeting = [...detailMts].sort((a,b)=>(a.date||'').localeCompare(b.date||''))[0];
              const totalDays = daysSince(firstMeeting?.date);
              if (!firstMeeting || totalDays === null) return null;
              return <div style={{display:'inline-flex',alignItems:'center',gap:8,marginBottom:16,padding:'8px 12px',borderRadius:10,background:'linear-gradient(135deg,#FFF7ED,#FFEDD5)',border:'1px solid #FDBA74',fontSize:13,fontWeight:600,color:'#C2410C'}}>
                <span>Timeline Age</span>
                <span>{totalDays} day{totalDays!==1?'s':''} since first meeting</span>
                <span style={{fontWeight:500,color:MU}}>from {fmtDate(firstMeeting.date)}</span>
              </div>;
            })()}
            {[...detailCls.map(c=>({date:c.date,type:'call',label:(c.conversationOutcome||c.outcome||'Call'),notes:c.notes,person:c.salesperson,isWalkin:false})),
              ...detailMts.map(m=>({date:m.date,type:'meeting',label:meetingTimelineLabel(m),notes:m.summary||m.agenda,person:m.salesperson,isWalkin:m.type===MEETING_TYPE_WALKIN_MET,isNoContact:isNoContactVisit(m)})),
              ...detailProps.map(p=>{
                const recipient=proposalRecipientName(p, detailSps);
                const noteParts = [p.sentVia, p.nextFollowUpDate ? `Follow-up ${fmtDate(p.nextFollowUpDate)}` : '', p.notes].filter(Boolean);
                return {date:p.date,type:'proposal',label:`Proposal sent → ${recipient} · ${p.templateName||'Proposal'}`,notes:noteParts.join(' · '),person:p.salesperson,isWalkin:false};
              })].sort((a,b)=>(a.date||'').localeCompare(b.date||'')).map((ev,i)=>{
              const borderColor=ev.type==='proposal'?'#7C3AED':ev.isNoContact?'#D97706':ev.isWalkin?'#F59E0B':ev.type==='meeting'?P:GN;
              return <motion.div key={i} initial={{opacity:0,x:-8}} animate={{opacity:1,x:0}} transition={{delay:i*.04,duration:.18,ease:[.32,.72,0,1]}} style={{display:'flex',gap:12,paddingBottom:16,borderLeft:'2px solid '+borderColor,marginLeft:8,paddingLeft:16}}>
                <div style={{width:8,height:8,borderRadius:4,background:borderColor,marginTop:4,marginLeft:-21}}/>
                <div>
                  <div style={{fontSize:12,color:MU,display:'flex',alignItems:'center',gap:6}}>
                    {fmtDate(ev.date)}
                    {ev.person&&<span style={{display:'inline-flex',alignItems:'center',gap:3,padding:'1px 7px',borderRadius:10,background:ev.type==='proposal'?'#F5F3FF':ev.isNoContact?'#FFEDD5':ev.isWalkin?'#FEF3C7':PL,color:ev.type==='proposal'?'#6D28D9':ev.isNoContact?'#C2410C':ev.isWalkin?'#92400E':P,fontWeight:600,fontSize:11}}>👤 {ev.person}</span>}
                  </div>
                  <div style={{fontSize:13,fontWeight:500,marginTop:2}}><Tag label={ev.label}/></div>
                  {ev.notes&&<div style={{fontSize:12,color:MU,marginTop:2}}>{ev.notes}</div>}
                </div>
              </motion.div>;
            })}
            {!detailCls.length&&!detailMts.length&&!detailProps.length&&<div style={{color:LT}}>No activity yet</div>}
          </div>}
        </>}

        {/* ═══ DAILY QUEUE ═══ */}
        {page==='queue' && !detailId && (()=>{
          const filtered=dailyQueue.filter(q=>{
            if(dqSpFilter!=='all'&&q.dailyAssignedTo!==dqSpFilter)return false;
            if(!isAdmin&&q.dailyAssignedTo!==user)return false;
            return true;
          });
          const qDone=filtered.filter(q=>q.dailyStatus==='done').length;
          const qSkipped=filtered.filter(q=>q.dailyStatus==='skipped').length;
          const qPending=filtered.filter(q=>!q.dailyStatus||q.dailyStatus==='pending').length;
          const dqEmployees = config.allUsers?.filter(u => u.role === 'Employee') || [];
          const dqSpCount = username => dailyQueue.filter(q => q.dailyAssignedTo === username).length;
          const dqSpInitials = name => (name || '').split(/\s+/).filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?';
          const dqSpColor = sp => {
            const u = config.allUsers?.find(x => x.username === sp);
            return resolveAvatarColor(sp, u?.avatarColor);
          };
          const queueCols=isAdmin?['#','SPOC','Mobile','Company','City','Assigned To','Status','Last Call','Action']:['#','SPOC','Mobile','Company','City','Status','Last Call','Action'];
          return <>
          <div className="page-header-row" style={{marginBottom:14,flexWrap:'wrap',gap:12}}>
            <div>
              <h1 style={{fontSize:26,fontWeight:700,margin:0,letterSpacing:'-0.03em'}}>Daily Call Targets</h1>
              <div style={{fontSize:12,color:MU,marginTop:2}}>{filtered.length} assigned · {qDone} done · {qSkipped} skipped</div>
            </div>
            <div className="page-header-actions">
              <div style={{position:'relative',display:'inline-flex',alignItems:'center'}}>
                <input type="date" style={{...s.inp,width:140,padding:'8px 12px',color:'transparent',caretColor:'transparent'}} value={dqDate} onChange={e=>setDqDate(e.target.value)} />
                <span style={{position:'absolute',left:12,pointerEvents:'none',fontSize:13,fontWeight:500,color:TX}}>{fmtDate(dqDate)}</span>
              </div>
              {isAdmin&&<motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={s.btn()} onClick={()=>setModal({type:'generateQueue'})}>⚡ Generate Queue</motion.button>}
              {!isAdmin&&<motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={s.btn()} onClick={()=>{setSelfGenCity(currentUser?.assignedCities?.[0]||CITIES[0]);setSelfGenTarget(30);setSelfGenCarry(true);setDqMode('auto');setDqManualSel(new Set());setDqManualCity('');setDqManualSearch('');setDqManualStage('');setDqManualHot(false);setDqManualTop(false);setFormErrors({});setModal({type:'selfGenQueue'});}}>⚡ Generate My List</motion.button>}
            </div>
          </div>

          {isAdmin&&dqEmployees.length>0&&(
            <div className="fu-person-filter-card">
              <div className="fu-person-filter-card__head">
                <div>
                  <div className="fu-person-filter-card__title">Team member</div>
                  <div className="fu-person-filter-card__sub">Filter daily assignments{dqSpFilter!=='all' ? ` · showing ${dqSpFilter}` : ''}</div>
                </div>
                {dqSpFilter!=='all' && (
                  <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn('s'),padding:'6px 12px',fontSize:12}} onClick={()=>setDqSpFilter('all')}>Clear</motion.button>
                )}
              </div>
              <div className="fu-person-filter">
                <button type="button" className={'fu-person-chip'+(dqSpFilter==='all'?' fu-person-chip--active':'')} onClick={()=>setDqSpFilter('all')}>
                  <span className="fu-person-chip__avatar fu-person-chip__avatar--all">All</span>
                  <span className="fu-person-chip__label">Everyone</span>
                  <span className="fu-person-chip__count">{dailyQueue.length}</span>
                </button>
                {dqEmployees.map(u=>{
                  const name = u.username;
                  const count = dqSpCount(name);
                  const active = dqSpFilter === name;
                  const color = dqSpColor(name);
                  return (
                    <button key={u._id} type="button" className={'fu-person-chip'+(active?' fu-person-chip--active':'')} onClick={()=>setDqSpFilter(name)}>
                      <span className="fu-person-chip__avatar" style={{background:active?color:`${color}18`,color:active?'#fff':color}}>{dqSpInitials(name)}</span>
                      <span className="fu-person-chip__label">{u.isTopDeveloper?'⭐ ':''}{name}</span>
                      <span className="fu-person-chip__count">{count}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="queue-stats">
            {[
              {label:'Assigned',value:filtered.length,accent:'neutral'},
              {label:'Done',value:qDone,accent:'positive'},
              {label:'Skipped',value:qSkipped,accent:'warning'},
              {label:'Pending',value:qPending,accent:'negative'},
            ].map(({label,value,accent})=>
              <div key={label} className="queue-stat">
                <div className={`queue-stat__value queue-stat__value--${accent}`}>{value}</div>
                <div className="queue-stat__label">{label}</div>
              </div>
            )}
          </div>

          <div className="table-wrap table-wrap--queue" style={{borderRadius:14,border:'1px solid #EBEBED',background:WH}}>
          <table style={{width:'100%',borderCollapse:'collapse'}}>
            <thead><tr>{queueCols.map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
            <tbody>{(()=>{
              if(!filtered.length)return <tr><td colSpan={queueCols.length} style={{...s.td,textAlign:'center',padding:40,color:MU}}>No daily assignments for {fmtDate(dqDate)}{!isAdmin?'. Use Generate My List to build your queue.':''}</td></tr>;
              return filtered.map((q,i)=>{
                const statusColor={done:GN,skipped:YL,pending:P,'':P}[q.dailyStatus||'pending'];
                const statusBg={done:GL,skipped:YLL,pending:PL,'':PL}[q.dailyStatus||'pending'];
                return<motion.tr key={q._id} initial={{opacity:0,y:4}} animate={{opacity:1,y:0}} transition={{delay:i*.03,duration:.15}}>
                <td style={{...s.td,width:36,color:LT,padding:'13px 12px'}}>{i+1}</td>
                <td style={{...s.td,fontWeight:600,maxWidth:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                  <span style={{cursor:'pointer',color:P,textDecoration:'underline',textUnderlineOffset:2}} onClick={()=>openQueueSpocDetail(q)} title="View SPOC & company details">{q.hot?'🔥 ':''}{q.name}</span>
                </td>
                <td style={{...s.td,fontSize:12,whiteSpace:'nowrap'}}>{q.phone||'—'}</td>
                <td style={{...s.td,maxWidth:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}} title={q.company?.name||''}>{q.company?.name||'—'}</td>
                <td style={{...s.td,whiteSpace:'nowrap'}}>{q.company?.city||'—'}</td>
                {isAdmin&&<td style={{...s.td,whiteSpace:'nowrap'}}>{q.dailyAssignedTo||'—'}</td>}
                <td style={{...s.td,whiteSpace:'nowrap'}}><span style={{...s.tag(statusBg,statusColor),fontSize:11}}>{q.dailyStatus==='done'?'✅ Done':q.dailyStatus==='skipped'?'⏭️ Skipped':'⏳ Pending'}</span></td>
                <td style={{...s.td,whiteSpace:'nowrap'}}><span style={{fontSize:11,color:q.lastCallDate?MU:LT}}>{q.lastCallDate?fmtDate(q.lastCallDate):'Never'}</span></td>
                <td style={{...s.td,padding:'10px 12px'}}>
                  <div style={{display:'flex',gap:6,flexWrap:'nowrap',justifyContent:'flex-start'}}>
                    {q.dailyStatus!=='done'&&<motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} className="hover-btn" style={{...s.btn(),padding:'5px 10px',fontSize:11}} onClick={()=>openCallForm(q)}>Call</motion.button>}
                    {(!q.dailyStatus||q.dailyStatus==='pending')&&<motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} className="hover-btn" style={{...s.btn('s'),padding:'5px 10px',fontSize:11}} onClick={async()=>{await api.updateQueueStatus(q._id,{status:'skipped'});api.getQueue({date:dqDate}).then(setDailyQueue).catch(()=>{});}}>Skip</motion.button>}
                    {(!q.dailyStatus||q.dailyStatus==='pending')&&<motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} className="hover-btn" style={{...s.btn('g'),padding:'5px 10px',fontSize:11,background:GN,color:'#fff'}} onClick={async()=>{await api.updateQueueStatus(q._id,{status:'done'});api.getQueue({date:dqDate}).then(setDailyQueue).catch(()=>{});}}>Done</motion.button>}
                    {q.dailyStatus==='skipped'&&<motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} className="hover-btn" style={{...s.btn(),padding:'5px 10px',fontSize:11}} onClick={async()=>{await api.updateQueueStatus(q._id,{status:'pending'});api.getQueue({date:dqDate}).then(setDailyQueue).catch(()=>{});}}>Unskip</motion.button>}
                    {q.isStrikedOut&&<span style={{padding:'3px 8px',borderRadius:8,background:'#FEE2E2',color:RD,fontSize:10,fontWeight:600,whiteSpace:'nowrap'}}>✕ {q.strikeoutReason||'struck'}</span>}
                  </div>
                </td>
              </motion.tr>})})()}</tbody>
          </table>
          </div>
        </>})()}

        {/* ═══ CALL LOG ═══ */}
        {page==='calllog' && !detailId && (()=>{
          // Compute date range from period preset
          const clDateFrom = clPeriod==='today'?today():clPeriod==='yesterday'?(()=>{const d=new Date();d.setDate(d.getDate()-1);return d.toISOString().slice(0,10)})():clPeriod==='week'?new Date(Date.now()-7*864e5).toISOString().slice(0,10):clPeriod==='month'?new Date(Date.now()-30*864e5).toISOString().slice(0,10):clPeriod==='custom'?dateFrom:'';
          const clDateTo = clPeriod==='yesterday'?clDateFrom:clPeriod==='custom'?dateTo:today();

          // Filter calls
          const clFiltered = calls.filter(c=>{
            if(clDateFrom && c.date<clDateFrom) return false;
            if(clDateTo && c.date>clDateTo) return false;
            if(pTab!=='all' && c.salesperson!==pTab) return false;
            if(clConn==='yes' && !c.connected) return false;
            if(clConn==='no' && c.connected) return false;
            if(clOutcome && (c.outcome||c.conversationOutcome)!==clOutcome) return false;
            if(clType && c.callType!==clType) return false;
            if(clCity){const co=companies.find(x=>x._id===c.companyId);if(!citiesMatch(co?.city,clCity)) return false;}
            if(search){
              const sp=spocs.find(x=>x._id===c.spocId),co=companies.find(x=>x._id===c.companyId);
              const q=search.toLowerCase();
              if(!sp?.name?.toLowerCase().includes(q)&&!co?.name?.toLowerCase().includes(q)&&!(c.notes||'').toLowerCase().includes(q)&&!(c.salesperson||'').toLowerCase().includes(q)) return false;
            }
            return true;
          });

          // Stats
          const clTotal=clFiltered.length;
          const clConnected=clFiltered.filter(c=>c.connected).length;
          const clNotConn=clTotal-clConnected;
          const clRate=clTotal?Math.round(clConnected/clTotal*100):0;
          const clMeetings=clFiltered.filter(c=>c.conversationOutcome==='Meeting scheduled'||c.conversationOutcome==='Meeting requested').length;
          const clNI=clFiltered.filter(c=>c.conversationOutcome==='Not interested').length;
          const clInterested=clFiltered.filter(c=>c.conversationOutcome==='Interested'||c.conversationOutcome==='Asked for details').length;

          // Unique outcomes for dropdown
          const allOutcomes=[...new Set(calls.map(c=>c.outcome||c.conversationOutcome).filter(Boolean))].sort();

          // Pagination
          const totalPages=Math.ceil(clFiltered.length/CL_PER_PAGE);
          const paged=clFiltered.slice((clPage-1)*CL_PER_PAGE,clPage*CL_PER_PAGE);

          // Active filter count (for clear button)
          const activeFilters=[clPeriod!=='today',clConn!=='all',clOutcome,clType,clCity,search,pTab!=='all'].filter(Boolean).length;
          const clAdvancedFilters=[clConn!=='all',clOutcome,clType,clCity].filter(Boolean).length;
          const clearClFilters=()=>{setClPeriod('today');setClConn('all');setClOutcome('');setClType('');setClCity('');setSearch('');setPTab('all');setClShowFilters(false);setClPage(1);};

          return <>
          <div className="page-header-row" style={{marginBottom:20,flexWrap:'wrap',gap:8}}>
            <div>
              <h1 style={{fontSize:26,fontWeight:700,margin:0,letterSpacing:'-0.03em'}}>Call History</h1>
              <div style={{fontSize:12,color:MU}}>{clTotal} calls{clDateFrom?' · '+fmtDate(clDateFrom)+(clDateTo&&clDateTo!==clDateFrom?' → '+fmtDate(clDateTo):' '):''}</div>
            </div>
            <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={s.btn()} onClick={()=>openCallForm(null)}>+ Log Call</motion.button>
          </div>

          {/* Compact filters */}
          <div style={{...s.card,padding:'12px 14px',marginBottom:14}}>
            <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
              <input style={{...s.inp,flex:'1 1 160px',minWidth:140,padding:'8px 12px',fontSize:12}} placeholder="Search..." value={search} onChange={e=>{setSearch(e.target.value);setClPage(1);}}/>
              <Dropdown style={{...s.sel,width:118,fontSize:12}} value={clPeriod} onChange={e=>{setClPeriod(e.target.value);setClPage(1);}}>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="week">7 days</option>
                <option value="month">30 days</option>
                <option value="all">All time</option>
                <option value="custom">Custom</option>
              </Dropdown>
              <Dropdown style={{...s.sel,width:120,fontSize:12}} value={pTab} onChange={e=>{setPTab(e.target.value);setClPage(1);}}>
                <option value="all">Everyone</option>
                {config.salespersons?.map(sp=><option key={sp} value={sp}>{sp}</option>)}
              </Dropdown>
              <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn('s'),padding:'8px 12px',fontSize:12,whiteSpace:'nowrap'}} onClick={()=>setClShowFilters(v=>!v)}>
                {clShowFilters?'▾ Less':'▸ More'}{clAdvancedFilters>0?` · ${clAdvancedFilters}`:''}
              </motion.button>
              {activeFilters>0&&<motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={{...s.btn('s'),padding:'8px 12px',fontSize:12,color:RD}} onClick={clearClFilters}>Clear</motion.button>}
            </div>
            {(clShowFilters||clPeriod==='custom')&&<div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center',marginTop:10,paddingTop:10,borderTop:'1px solid #F3F4F6'}}>
              {clPeriod==='custom'&&<>
                <input type="date" style={{...s.inp,width:130,padding:'7px 10px',fontSize:12}} value={dateFrom} onChange={e=>{setDateFrom(e.target.value);setClPage(1);}}/>
                <span style={{fontSize:12,color:MU}}>to</span>
                <input type="date" style={{...s.inp,width:130,padding:'7px 10px',fontSize:12}} value={dateTo} onChange={e=>{setDateTo(e.target.value);setClPage(1);}}/>
              </>}
              <Dropdown style={{...s.sel,width:110,fontSize:12}} value={clConn} onChange={e=>{setClConn(e.target.value);setClPage(1);}}>
                <option value="all">All status</option>
                <option value="yes">Connected</option>
                <option value="no">Not connected</option>
              </Dropdown>
              <Dropdown style={{...s.sel,width:140,fontSize:12}} value={clOutcome} onChange={e=>{setClOutcome(e.target.value);setClPage(1);}}>
                <option value="">All outcomes</option>
                {allOutcomes.map(o=><option key={o} value={o}>{o}</option>)}
              </Dropdown>
              <Dropdown style={{...s.sel,width:110,fontSize:12}} value={clType} onChange={e=>{setClType(e.target.value);setClPage(1);}}>
                <option value="">All types</option>
                {['first call','follow-up','reminder','warm reconnect'].map(t=><option key={t} value={t}>{t}</option>)}
              </Dropdown>
              <Dropdown style={{...s.sel,width:110,fontSize:12}} value={clCity} onChange={e=>{setClCity(e.target.value);setClPage(1);}}>
                <option value="">All cities</option>
                {visibleCities.map(c=><option key={c} value={c}>{c}</option>)}
              </Dropdown>
            </div>}
          </div>

          {/* Quick Stats */}
          <div className="call-stats-grid">
            {[
              {label:'Total',value:clTotal,accent:'neutral'},
              {label:'Connected',value:clConnected,accent:'positive'},
              {label:'Not Connected',value:clNotConn,accent:'negative'},
              {label:'Connect Rate',value:clRate+'%',accent:clRate>=50?'positive':clRate>=25?'warning':'negative'},
              {label:'Meeting Leads',value:clMeetings,accent:'purple'},
              {label:'Interested',value:clInterested,accent:'positive'},
              {label:'Not Interested',value:clNI,accent:'negative'},
            ].map(({label,value,accent})=>
              <div key={label} className="call-stat-tile">
                <div className={`call-stat-tile__value call-stat-tile__value--${accent}`}>{value}</div>
                <div className="call-stat-tile__label">{label}</div>
              </div>
            )}
          </div>


          {/* Table */}
          <div className="table-wrap" style={{borderRadius:14,border:'1px solid #EBEBED',marginBottom:14}}>
          <table style={{width:'100%',minWidth:960,borderCollapse:'collapse',background:WH}}>
            <thead><tr>{['Date','SPOC','Company','City','Salesperson','Type','Status','Outcome','Conf','Notes','Actions'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
            <tbody>{(()=>{
              if(!paged.length)return <tr><td colSpan={11} style={{...s.td,textAlign:'center',color:MU,padding:40}}>No calls match your filters</td></tr>;
              return paged.map(c=>{const sp=spocs.find(x=>x._id===c.spocId),co=companies.find(x=>x._id===c.companyId);return<tr key={c._id}>
                <td style={s.td}>{fmtDate(c.date)}</td>
                <td style={{...s.td,fontWeight:500,cursor:'pointer',color:P}} onClick={()=>{if(co)goTo('companies');setDetailId(co?._id);}}>{sp?.name||'—'}</td>
                <td style={s.td}>{co?.name||'—'}</td>
                <td style={{...s.td,fontSize:11,color:MU}}>{co?.city||'—'}</td>
                <td style={{...s.td,fontSize:12}}>{c.salesperson||'—'}</td>
                <td style={{...s.td,fontSize:11}}><span style={{...s.tag(PL,P),fontSize:10}}>{c.callType||'—'}</span></td>
                <td style={s.td}><span style={{fontSize:11,fontWeight:600,color:c.connected?GN:RD}}>{c.connected?'✅ Conn':'❌ NC'}</span></td>
                <td style={s.td}><Tag label={c.outcome||c.conversationOutcome}/></td>
                <td style={s.td}><span style={{fontWeight:600,color:(c.confidence||0)>=7?GN:(c.confidence||0)>=4?YL:RD}}>{c.confidence||0}/10</span></td>
                <td style={{...s.td,maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',fontSize:12,color:MU}} title={c.notes||''}>{c.notes||'—'}</td>
                <td style={s.td}><div style={{display:'flex',gap:4}}><motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn('s'),padding:'3px 10px',fontSize:11}} onClick={()=>openCallForm(sp,c)}>Edit</motion.button>{isAdmin&&<DeleteIconBtn onClick={()=>setConfirmDel({fn:async()=>{await api.deleteCall(c._id);loadData();},msg:'Delete this call?'})}/>}</div></td>
              </tr>});
            })()}</tbody>
          </table>
          </div>

          {/* Pagination */}
          {totalPages>1&&<div style={{display:'flex',justifyContent:'center',alignItems:'center',gap:8,marginTop:14}}>
            <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} className="hover-btn" style={{...s.btn('s'),padding:'6px 12px',fontSize:12}} disabled={clPage<=1} onClick={()=>setClPage(p=>Math.max(1,p-1))}>← Prev</motion.button>
            <span style={{fontSize:13,color:MU}}>Page {clPage} of {totalPages} <span style={{color:LT}}>({clFiltered.length} records)</span></span>
            <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} className="hover-btn" style={{...s.btn('s'),padding:'6px 12px',fontSize:12}} disabled={clPage>=totalPages} onClick={()=>setClPage(p=>Math.min(totalPages,p+1))}>Next →</motion.button>
          </div>}
        </>;})()}

        {/* ═══ FOLLOW-UPS ═══ */}
        {page==='followups' && !detailId && (()=>{
          const todayStr = today();
          const normOwner = v => (v || '').trim().toLowerCase();
          const fuMatchesPerson = (owner, person) => person === 'all' || normOwner(owner) === normOwner(person);

          // ── Build unified follow-up map (city/search only — person filter applied after) ──
          const fuMap = {};
          spocs.filter(sp => {
            if (!sp.nextFollowUp || ['Won','Dropped','Invalid data'].includes(sp.stage)) return false;
            const co = getCo(sp);
            if (fuCity && !citiesMatch(co?.city, fuCity)) return false;
            if (fuSearch && !sp.name.toLowerCase().includes(fuSearch.toLowerCase()) && !co?.name?.toLowerCase().includes(fuSearch.toLowerCase())) return false;
            return true;
          }).forEach(sp => {
            const coId = String(sp.companyId?._id || sp.companyId);
            const co = getCo(sp);
            if (!fuMap[coId] || sp.nextFollowUp > fuMap[coId].date) {
              fuMap[coId] = { type:'call', date:sp.nextFollowUp, sp, co, coId, hot:sp.hot, stage:sp.stage, trailLen:calls.filter(c=>c.spocId===sp._id).length, info:sp.followUpReason||'' };
            }
          });
          meetings.filter(m => {
            if (!m.nextActionDate || ['Completed','Cancelled'].includes(m.stage)) return false;
            const co = companies.find(c => String(c._id) === String(m.companyId?._id || m.companyId));
            if (fuCity && !citiesMatch(co?.city, fuCity)) return false;
            if (fuSearch && !co?.name?.toLowerCase().includes(fuSearch.toLowerCase())) return false;
            return true;
          }).forEach(m => {
            const coId = String(m.companyId?._id || m.companyId);
            if (fuMap[coId]) return;
            const co = companies.find(c => String(c._id) === coId);
            const mSpoc = spocs.find(ss => String(ss._id) === String(m.spocId));
            fuMap[coId] = { type:'post-meeting', date:m.nextActionDate, sp:mSpoc||{name:'—'}, co, coId, hot:false, stage:m.stage||'Meeting', trailLen:0, info:m.nextAction||'', meeting:m };
          });

          // ── Derive purpose category from stage/type ──
          const getPurpose = e => {
            if (e.type === 'post-meeting') {
              // Show meeting-specific status instead of generic "After Meeting"
              const mStage = e.stage || '';
              if (mStage === 'Confirmed') return 'meeting-confirmed';
              if (mStage === 'Rescheduled') return 'meeting-rescheduled';
              if (mStage === 'Scheduled' || mStage === 'Tentative') return 'meeting-pending';
              return 'after-meeting';
            }
            const st = e.stage || '';
            if (st === 'Meeting scheduled') return 'confirm-meeting';
            if (st === 'Meeting requested' || st === 'Interested') return 'ask-meeting';
            if (st === 'Details sent') return 'follow-details';
            return 'general';
          };
          const PURPOSE_META = {
            'after-meeting':     { label:'After Meeting',      hint:'Check next steps, send proposal',  color:'#7C3AED', bg:'#F5F3FF' },
            'meeting-confirmed': { label:'Confirmed Meeting',  hint:'Meeting is confirmed - prepare',   color:'#059669', bg:'#ECFDF5' },
            'meeting-rescheduled':{label:'Rescheduled',       hint:'Meeting was rescheduled',          color:'#D97706', bg:'#FFFBEB' },
            'meeting-pending':   { label:'Pending Meeting',    hint:'Meeting not yet confirmed',        color:'#0369A1', bg:'#E0F2FE' },
            'confirm-meeting':   { label:'Confirm Meeting',    hint:'Confirm the meeting is still on',   color:'#0369A1', bg:'#E0F2FE' },
            'ask-meeting':       { label:'Ask for Meeting',    hint:'Push to schedule a meeting',        color:'#059669', bg:'#ECFDF5' },
            'follow-details':    { label:'Follow Up on Details',hint:'They received info — follow up',  color:'#D97706', bg:'#FFFBEB' },
            'general':           { label:'General Follow-up',  hint:'Regular check-in or retry',         color:'#6B7280', bg:'#F3F4F6' },
          };

          // Tag each entry with its purpose (for display on cards)
          const diffFuDays = (from, to) => {
            if (!from || !to) return 0;
            return Math.max(0, Math.round((new Date(to + 'T12:00:00') - new Date(from + 'T12:00:00')) / 86400000));
          };
          const enrichFuEntry = e => {
            const coSpocs = spocs.filter(sp => String(sp.companyId?._id || sp.companyId) === e.coId && !sp.isStrikedOut);
            const coCalls = calls.filter(c => coSpocs.some(sp => String(sp._id) === String(c.spocId?._id || c.spocId)));
            const followUpCalls = coCalls.filter(c =>
              c.followUpDate || c.conversationOutcome === 'Follow-up later' || c.callType === 'follow-up'
            );
            const touchDates = [
              ...coSpocs.flatMap(sp => (sp.trail || []).map(t => t.date)),
              ...coCalls.map(c => c.date),
            ].filter(Boolean).sort();
            const firstTouch = touchDates[0] || e.co?.createdAt?.slice?.(0, 10) || e.date;
            const daysInPipeline = diffFuDays(firstTouch, todayStr);
            const daysOverdue = e.date < todayStr ? diffFuDays(e.date, todayStr) : 0;
            const activeFuSpocs = coSpocs.filter(sp => sp.nextFollowUp && !['Won', 'Dropped', 'Invalid data'].includes(sp.stage)).length;
            const latestCall = [...coCalls].sort((a, b) => (b.date || '').localeCompare(a.date || ''))[0];
            const notConnectedCalls = coCalls.filter(c => !c.connected);
            const blockerCounts = {};
            notConnectedCalls.forEach(c => {
              const o = c.outcome || 'Unknown';
              blockerCounts[o] = (blockerCounts[o] || 0) + 1;
            });
            const topBlocker = Object.entries(blockerCounts).sort((a, b) => b[1] - a[1])[0];
            const connectedCalls = coCalls.filter(c => c.connected).length;
            const spCalls = calls.filter(c => String(c.spocId?._id || c.spocId) === String(e.sp?._id))
              .sort((a, b) => (b.date || '').localeCompare(a.date || ''));
            const coCallOwnerCounts = {};
            coCalls.forEach(c => {
              const name = c.salesperson?.trim();
              if (name) coCallOwnerCounts[name] = (coCallOwnerCounts[name] || 0) + 1;
            });
            const topCoCallOwner = Object.entries(coCallOwnerCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || '';
            const owner = e.type === 'post-meeting'
              ? (e.meeting?.salesperson || e.co?.salesperson || topCoCallOwner || '')
              : (e.sp?.dailyAssignedTo || e.co?.salesperson || spCalls[0]?.salesperson || topCoCallOwner || latestCall?.salesperson || '');
            return {
              ...e,
              owner,
              daysInPipeline,
              daysOverdue,
              totalTouches: coCalls.length,
              followUpTouches: Math.max(followUpCalls.length, e.trailLen || 0),
              activeFuSpocs,
              firstTouchDate: firstTouch,
              isRepeat: followUpCalls.length >= 2 || coCalls.length >= 3 || daysInPipeline >= 14,
              segment: e.co?.segment || 'Unknown',
              connectRate: coCalls.length ? Math.round(connectedCalls / coCalls.length * 100) : 0,
              connectedCalls,
              notConnectedCalls: notConnectedCalls.length,
              lastOutcome: latestCall?.conversationOutcome || latestCall?.outcome || e.sp?.lastOutcome || '',
              lastBlocker: topBlocker?.[0] || '',
              lastBlockerCount: topBlocker?.[1] || 0,
              blockerCounts,
              hasWrongNumber: !!(blockerCounts['Wrong number'] || (latestCall?.outcome === 'Wrong number')),
              meetingInterest: coCalls.some(c => ['Meeting requested', 'Meeting scheduled', 'Interested', 'Asked for details'].includes(c.conversationOutcome)),
            };
          };

          const allWithPurpose = Object.values(fuMap).map(e => enrichFuEntry({ ...e, purpose: getPurpose(e) }))
            .sort((a,b) => (a.date||'').localeCompare(b.date||''));

          const allSortedAll = allWithPurpose;
          const allSorted = pTab === 'all' ? allSortedAll : allSortedAll.filter(e => fuMatchesPerson(e.owner, pTab));
          const overdueFu  = allSorted.filter(e => e.date < todayStr);
          const todayFu    = allSorted.filter(e => e.date === todayStr);
          const upcomingFu = allSorted.filter(e => e.date > todayStr);
          const hotFu = allSorted.filter(e => e.hot);
          const callFu = allSorted.filter(e => e.type === 'call');
          const meetingFu = allSorted.filter(e => e.type === 'post-meeting');
          const repeatFu = allSorted.filter(e => e.isRepeat);
          const staleFu = overdueFu.filter(e => e.daysOverdue >= 7);
          const fuActiveFilters = [fuCity, fuSearch, pTab !== 'all'].filter(Boolean).length;
          const clearFuFilters = () => { setFuCity(''); setFuSearch(''); setPTab('all'); };
          const fuSpCount = sp => allSortedAll.filter(e => fuMatchesPerson(e.owner, sp)).length;
          const fuSpInitials = name => (name || '').split(/\s+/).filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?';
          const fuSpColor = sp => {
            const u = config.allUsers?.find(x => x.username === sp);
            return resolveAvatarColor(sp, u?.avatarColor);
          };
          const avgDaysInPipeline = allSorted.length
            ? Math.round(allSorted.reduce((sum, e) => sum + e.daysInPipeline, 0) / allSorted.length)
            : 0;
          const topTouchFu = [...allSorted].sort((a, b) => b.totalTouches - a.totalTouches || b.daysInPipeline - a.daysInPipeline);
          const fuCoIdSet = new Set(allSorted.map(e => e.coId));
          const fuCalls = calls.filter(c => {
            const sp = spocs.find(s => String(s._id) === String(c.spocId?._id || c.spocId));
            return sp && fuCoIdSet.has(String(sp.companyId?._id || sp.companyId));
          });
          const fuConnected = fuCalls.filter(c => c.connected).length;
          const fuConnectRate = fuCalls.length ? Math.round(fuConnected / fuCalls.length * 100) : 0;
          const blockerTotals = {};
          fuCalls.filter(c => !c.connected).forEach(c => {
            const o = c.outcome || 'Unknown';
            blockerTotals[o] = (blockerTotals[o] || 0) + 1;
          });
          const blockerRows = Object.entries(blockerTotals).sort((a, b) => b[1] - a[1]);
          const wrongNumberCos = allSorted.filter(e => e.hasWrongNumber);
          const noConnectHeavy = allSorted.filter(e => e.totalTouches >= 3 && e.connectRate <= 25);
          const segmentStats = Object.entries(allSorted.reduce((acc, e) => {
            const seg = e.segment || 'Unknown';
            if (!acc[seg]) acc[seg] = { count: 0, days: 0, overdue: 0, touches: 0, connect: 0 };
            acc[seg].count += 1;
            acc[seg].days += e.daysInPipeline;
            acc[seg].overdue += e.daysOverdue > 0 ? 1 : 0;
            acc[seg].touches += e.totalTouches;
            acc[seg].connect += e.connectRate;
            return acc;
          }, {})).map(([seg, s]) => ({
            seg,
            count: s.count,
            avgDays: Math.round(s.days / s.count),
            overduePct: Math.round(s.overdue / s.count * 100),
            avgTouches: Math.round((s.touches / s.count) * 10) / 10,
            avgConnect: Math.round(s.connect / s.count),
          })).sort((a, b) => b.avgDays - a.avgDays || b.overduePct - a.overduePct);
          const purposeStats = Object.entries(allSorted.reduce((acc, e) => {
            const label = (PURPOSE_META[e.purpose] || PURPOSE_META.general).label;
            acc[label] = (acc[label] || 0) + 1;
            return acc;
          }, {})).sort((a, b) => b[1] - a[1]);
          const stageStats = Object.entries(allSorted.reduce((acc, e) => {
            const st = e.stage || 'Unknown';
            acc[st] = (acc[st] || 0) + 1;
            return acc;
          }, {})).sort((a, b) => b[1] - a[1]);
          const avgTouches = allSorted.length ? Math.round(allSorted.reduce((s, e) => s + e.totalTouches, 0) / allSorted.length * 10) / 10 : 0;
          const avgOverdueDays = overdueFu.length ? Math.round(overdueFu.reduce((s, e) => s + e.daysOverdue, 0) / overdueFu.length) : 0;

          const insightsSegmentOptions = [...new Set(allSorted.map(e => e.segment || 'Unknown'))].sort();
          const insightsPurposeOptions = purposeStats.map(([label]) => label);
          const insightsStageOptions = stageStats.map(([label]) => label);
          const insightsBlockerOptions = [...new Set(allSorted.map(e => e.lastBlocker).filter(Boolean))].sort();
          const insightsActiveFilters = [fuInsightsSearch, fuInsightsSegment, fuInsightsPurpose, fuInsightsStage, fuInsightsBlocker, fuInsightsDue, fuInsightsConnect].filter(Boolean).length;
          const clearFuInsightsFilters = () => {
            setFuInsightsSearch('');
            setFuInsightsSegment('');
            setFuInsightsPurpose('');
            setFuInsightsStage('');
            setFuInsightsBlocker('');
            setFuInsightsDue('');
            setFuInsightsConnect('');
          };
          const insightsFiltered = allSorted.filter(e => {
            if (fuInsightsSearch) {
              const q = fuInsightsSearch.toLowerCase();
              if (!e.co?.name?.toLowerCase().includes(q) && !(e.co?.city || '').toLowerCase().includes(q) && !(e.sp?.name || '').toLowerCase().includes(q)) return false;
            }
            if (fuInsightsSegment && (e.segment || 'Unknown') !== fuInsightsSegment) return false;
            if (fuInsightsPurpose) {
              const label = (PURPOSE_META[e.purpose] || PURPOSE_META.general).label;
              if (label !== fuInsightsPurpose) return false;
            }
            if (fuInsightsStage && (e.stage || 'Unknown') !== fuInsightsStage) return false;
            if (fuInsightsBlocker && e.lastBlocker !== fuInsightsBlocker) return false;
            if (fuInsightsDue === 'overdue' && !(e.date < todayStr)) return false;
            if (fuInsightsDue === 'today' && e.date !== todayStr) return false;
            if (fuInsightsDue === 'upcoming' && !(e.date > todayStr)) return false;
            if (fuInsightsConnect === 'low' && !(e.connectRate <= 25)) return false;
            if (fuInsightsConnect === 'medium' && !(e.connectRate > 25 && e.connectRate < 40)) return false;
            if (fuInsightsConnect === 'high' && !(e.connectRate >= 40)) return false;
            return true;
          });
          const insightsFilteredSorted = [...insightsFiltered].sort((a, b) => b.totalTouches - a.totalTouches || b.daysInPipeline - a.daysInPipeline);
          const insightsSegmentStats = Object.entries(insightsFiltered.reduce((acc, e) => {
            const seg = e.segment || 'Unknown';
            if (!acc[seg]) acc[seg] = { count: 0, days: 0, overdue: 0, touches: 0, connect: 0 };
            acc[seg].count += 1;
            acc[seg].days += e.daysInPipeline;
            acc[seg].overdue += e.daysOverdue > 0 ? 1 : 0;
            acc[seg].touches += e.totalTouches;
            acc[seg].connect += e.connectRate;
            return acc;
          }, {})).map(([seg, s]) => ({
            seg,
            count: s.count,
            avgDays: Math.round(s.days / s.count),
            overduePct: Math.round(s.overdue / s.count * 100),
            avgTouches: Math.round((s.touches / s.count) * 10) / 10,
            avgConnect: Math.round(s.connect / s.count),
          })).sort((a, b) => b.avgDays - a.avgDays || b.overduePct - a.overduePct);
          const insightsBlockerTotals = {};
          insightsFiltered.forEach(e => {
            Object.entries(e.blockerCounts || {}).forEach(([o, n]) => { insightsBlockerTotals[o] = (insightsBlockerTotals[o] || 0) + n; });
          });
          const insightsBlockerRows = Object.entries(insightsBlockerTotals).sort((a, b) => b[1] - a[1]);
          const insightsPurposeStats = Object.entries(insightsFiltered.reduce((acc, e) => {
            const label = (PURPOSE_META[e.purpose] || PURPOSE_META.general).label;
            acc[label] = (acc[label] || 0) + 1;
            return acc;
          }, {})).sort((a, b) => b[1] - a[1]);
          const insightsStageStats = Object.entries(insightsFiltered.reduce((acc, e) => {
            const st = e.stage || 'Unknown';
            acc[st] = (acc[st] || 0) + 1;
            return acc;
          }, {})).sort((a, b) => b[1] - a[1]);
          const insightsWrongNumber = insightsFiltered.filter(e => e.hasWrongNumber);
          const insightsLowConnect = insightsFiltered.filter(e => e.totalTouches >= 3 && e.connectRate <= 25);
          const insightsAvgDays = insightsFiltered.length ? Math.round(insightsFiltered.reduce((sum, e) => sum + e.daysInPipeline, 0) / insightsFiltered.length) : 0;

          const visibleEntries = fuFilter === 'overdue' ? overdueFu
            : fuFilter === 'today' ? todayFu
            : fuFilter === 'upcoming' ? upcomingFu
            : hotFu;

          // ── Card component ──
          const FuCard = ({e}) => {
            const od = e.date < todayStr;
            const isToday = e.date === todayStr;
            const accent = od ? RD : isToday ? P : GN;
            const pm = PURPOSE_META[e.purpose] || PURPOSE_META['general'];
            return (
              <motion.div initial={{opacity:0,y:6}} animate={{opacity:1,y:0}} transition={{duration:.18,ease:[.32,.72,0,1]}} whileHover={{y:-1,boxShadow:'0 4px 14px rgba(0,0,0,.06)'}} className="followup-card" style={{background:WH,borderRadius:12,border:'1px solid #E5E7EB',borderLeft:'4px solid '+accent,padding:'16px 18px'}}>
                <div className="followup-card__main">
                  {/* Company + Purpose in one line */}
                  <div style={{fontSize:15,fontWeight:700,color:TX,marginBottom:4,cursor:'pointer'}} onClick={()=>{setDetailId(e.coId);setDTab('timeline');}}>
                    {e.co?.name||'—'}
                    {e.co?.city && <span style={{fontSize:12,color:LT,fontWeight:400,marginLeft:6}}>{e.co.city}</span>}
                  </div>

                  {/* What to do - clear action */}
                  <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap',marginBottom:6}}>
                    <span style={{fontSize:13,color:pm.color,fontWeight:500}}>{pm.label}</span>
                    {e.hot && <span>🔥</span>}
                    {e.stage && <span style={{fontSize:10,color:MU,background:'#F3F4F6',padding:'2px 8px',borderRadius:4,fontWeight:500}}>{e.stage}</span>}
                  </div>

                  {/* Contact */}
                  <div style={{fontSize:12,color:MU}}>
                    {e.sp?.name}
                    {e.sp?.designation && <span style={{color:LT}}> · {e.sp.designation}</span>}
                    {isAdmin && e.owner && <span style={{color:LT}}> · {e.owner}</span>}
                  </div>
                  <div style={{fontSize:11,color:LT,marginTop:4}}>
                    In pipeline {e.daysInPipeline}d · {e.segment}
                    {e.totalTouches > 0 && ` · ${e.totalTouches} call${e.totalTouches !== 1 ? 's' : ''} · ${e.connectRate}% conn`}
                    {e.isRepeat && <span style={{color:YL,fontWeight:600}}> · repeat</span>}
                    {e.daysOverdue > 0 && <span style={{color:RD,fontWeight:600}}> · {e.daysOverdue}d overdue</span>}
                    {e.lastBlocker && <span style={{color:e.lastBlocker==='Wrong number'?RD:YL,fontWeight:600}}> · {e.lastBlocker}</span>}
                  </div>
                </div>

                {/* Right side - date and actions */}
                <div className="followup-card__side">
                  <div style={{fontSize:13,fontWeight:600,color:accent}}>{fmtDate(e.date)}</div>

                  <div style={{display:'flex',gap:8,alignItems:'center'}}>
                    {e.type==='call' && e.trailLen>0 && (
                      <span style={{fontSize:11,color:MU,cursor:'pointer'}} onClick={()=>openTrail(e.sp)}>
                        {e.trailLen} calls
                      </span>
                    )}
                    {e.type==='call'
                      ? <button style={{...s.btn(),padding:'7px 16px',fontSize:12,borderRadius:8,cursor:'pointer'}} onClick={()=>openCallForm(e.sp)}>Call</button>
                      : <button style={{...s.btn('s'),padding:'7px 14px',fontSize:12,borderRadius:8,cursor:'pointer'}} onClick={()=>openMeetingForm(e.meeting)}>Update</button>
                    }
                  </div>
                </div>
              </motion.div>
            );
          };

          const fuTabEmptyMsg = {
            overdue: 'No overdue follow-ups match your filters',
            today: 'Nothing due today — nice work',
            hot: 'No hot leads in follow-up right now',
            upcoming: 'No upcoming follow-ups scheduled',
          };

          const EmptyState = () => (
            <div style={{...s.card,textAlign:'center',padding:52,color:LT,marginTop:8}}>
              <div style={{fontSize:30,marginBottom:10}}>{fuFilter==='overdue'?'✅':fuFilter==='hot'?'🔥':'📅'}</div>
              <div style={{fontSize:14,fontWeight:600,color:MU}}>{allSorted.length===0?'All caught up!':'Nothing here'}</div>
              <div style={{fontSize:12,marginTop:4}}>{allSorted.length===0?'No active follow-ups':fuTabEmptyMsg[fuFilter]||'No follow-ups match this filter'}</div>
              {fuActiveFilters>0&&<motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn('s'),marginTop:16}} onClick={clearFuFilters}>Clear filters</motion.button>}
            </div>
          );

          return <>
            {/* Header */}
            <div className="page-header-row" style={{alignItems:'flex-start',marginBottom:20}}>
              <div>
                <h1 style={{fontSize:26,fontWeight:700,margin:'0 0 4px',letterSpacing:'-0.03em'}}>Follow-ups</h1>
                <div style={{fontSize:13,color:MU}}>{allSorted.length} active · {overdueFu.length} overdue · {todayFu.length} today · {upcomingFu.length} upcoming{hotFu.length>0?` · ${hotFu.length} hot`:''}</div>
              </div>
              <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={s.btn()} onClick={()=>openCallForm(null)}>+ Log Call</motion.button>
            </div>

            {/* Compact filters */}
            <div style={{...s.card,padding:'12px 14px',marginBottom:16}}>
              <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
                <input style={{...s.inp,flex:'1 1 160px',minWidth:140,padding:'8px 12px',fontSize:12}} placeholder="Search company or contact…" value={fuSearch} onChange={e=>setFuSearch(e.target.value)}/>
                <Dropdown style={{...s.sel,width:130,fontSize:12}} value={fuCity} onChange={e=>setFuCity(e.target.value)}>
                  <option value="">All cities</option>{visibleCities.map(c=><option key={c} value={c}>{c}</option>)}
                </Dropdown>
                {fuActiveFilters>0&&<motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn('s'),padding:'8px 12px',fontSize:12,color:RD}} onClick={clearFuFilters}>Clear</motion.button>}
              </div>
            </div>

            {/* Follow-up Overview */}
            <div style={{...s.card,padding:0,overflow:'hidden',marginBottom:16,border:'1px solid #E5E7EB'}}>
              <div style={{padding:'14px 16px',borderBottom:'1px solid #F3F4F6',display:'flex',justifyContent:'space-between',gap:12,alignItems:'center',flexWrap:'wrap'}}>
                <div>
                  <div style={{fontSize:14,fontWeight:700,color:TX,letterSpacing:'-0.02em'}}>Follow-up Overview</div>
                  <div style={{fontSize:12,color:MU,marginTop:2}}>{allSorted.length} active compan{allSorted.length===1?'y':'ies'} · avg {avgDaysInPipeline} days in pipeline</div>
                </div>
                <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
                  {staleFu.length>0&&<span style={{fontSize:11,fontWeight:700,color:RD,background:RL,border:'1px solid #FECACA',padding:'4px 9px',borderRadius:7}}>{staleFu.length} stale 7+ days</span>}
                  {repeatFu.length>0&&<span style={{fontSize:11,fontWeight:600,color:YL,background:YLL,border:'1px solid #FDE68A',padding:'4px 9px',borderRadius:7}}>{repeatFu.length} repeat touch</span>}
                  <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn('s'),padding:'4px 10px',fontSize:11}} onClick={()=>{clearFuInsightsFilters();setFuInsightsOpen(true);}}>
                    ▸ View all details
                  </motion.button>
                </div>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))',gap:1,background:'#F3F4F6'}}>
                {[
                  {label:'Total Follow-ups',value:allSorted.length,sub:`${callFu.length} from calls · ${meetingFu.length} post-meeting`,accent:TX},
                  {label:'Overdue',value:overdueFu.length,sub:staleFu.length?`${staleFu.length} overdue 7+ days · avg ${avgOverdueDays}d`:'Needs immediate action',accent:overdueFu.length?RD:GN},
                  {label:'Due Today',value:todayFu.length,sub:'Scheduled for today',accent:P},
                  {label:'Upcoming',value:upcomingFu.length,sub:'Future follow-ups',accent:GN},
                  {label:'Hot Leads',value:hotFu.length,sub:'Marked hot in pipeline',accent:'#EC4899'},
                  {label:'Repeat Companies',value:repeatFu.length,sub:'2+ follow-ups or 14+ days',accent:repeatFu.length?YL:MU},
                  {label:'Avg Calls / Co',value:avgTouches,sub:`${fuCalls.length} total calls logged`,accent:'#6366F1'},
                  {label:'Connect Rate',value:fuConnectRate+'%',sub:`${fuConnected} connected of ${fuCalls.length}`,accent:fuConnectRate>=40?GN:fuConnectRate>=20?YL:RD},
                ].map(({label,value,sub,accent})=>
                  <div key={label} style={{background:WH,padding:'14px 12px',minHeight:86}}>
                    <div style={{fontSize:22,fontWeight:800,color:TX,letterSpacing:'-0.04em',lineHeight:1}}>{value}</div>
                    <div style={{fontSize:11,fontWeight:700,color:MU,marginTop:7}}>{label}</div>
                    <div style={{fontSize:10,color:accent,marginTop:4,fontWeight:600,lineHeight:1.35}}>{sub}</div>
                  </div>
                )}
              </div>
            </div>

            <Modal open={fuInsightsOpen} onClose={()=>setFuInsightsOpen(false)} title="Follow-up details" large>
              <div className="fu-insights-filters">
                <div style={{fontSize:12,color:MU,marginBottom:12}}>
                  {insightsFiltered.length} of {allSorted.length} companies
                  {insightsActiveFilters ? ` · filtered` : ` · avg ${insightsAvgDays} days in pipeline`}
                </div>
                <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
                  <input style={{...s.inp,flex:'1 1 180px',minWidth:160,padding:'8px 12px',fontSize:12,background:'#F9FAFB'}} placeholder="Search company, city, contact…" value={fuInsightsSearch} onChange={e=>setFuInsightsSearch(e.target.value)}/>
                  <Dropdown style={{...s.sel,width:120,fontSize:12,background:'#F9FAFB'}} value={fuInsightsSegment} onChange={e=>setFuInsightsSegment(e.target.value)}>
                    <option value="">All segments</option>
                    {insightsSegmentOptions.map(seg=><option key={seg} value={seg}>{seg}</option>)}
                  </Dropdown>
                  <Dropdown style={{...s.sel,width:150,fontSize:12,background:'#F9FAFB'}} value={fuInsightsPurpose} onChange={e=>setFuInsightsPurpose(e.target.value)}>
                    <option value="">All purposes</option>
                    {insightsPurposeOptions.map(p=><option key={p} value={p}>{p}</option>)}
                  </Dropdown>
                  <Dropdown style={{...s.sel,width:130,fontSize:12,background:'#F9FAFB'}} value={fuInsightsStage} onChange={e=>setFuInsightsStage(e.target.value)}>
                    <option value="">All stages</option>
                    {insightsStageOptions.map(st=><option key={st} value={st}>{st}</option>)}
                  </Dropdown>
                  <Dropdown style={{...s.sel,width:130,fontSize:12,background:'#F9FAFB'}} value={fuInsightsBlocker} onChange={e=>setFuInsightsBlocker(e.target.value)}>
                    <option value="">All blockers</option>
                    {insightsBlockerOptions.map(b=><option key={b} value={b}>{b}</option>)}
                  </Dropdown>
                  <Dropdown style={{...s.sel,width:110,fontSize:12,background:'#F9FAFB'}} value={fuInsightsDue} onChange={e=>setFuInsightsDue(e.target.value)}>
                    <option value="">All due dates</option>
                    <option value="overdue">Overdue</option>
                    <option value="today">Due today</option>
                    <option value="upcoming">Upcoming</option>
                  </Dropdown>
                  <Dropdown style={{...s.sel,width:120,fontSize:12,background:'#F9FAFB'}} value={fuInsightsConnect} onChange={e=>setFuInsightsConnect(e.target.value)}>
                    <option value="">All connect</option>
                    <option value="low">Low (≤25%)</option>
                    <option value="medium">Medium (26–39%)</option>
                    <option value="high">High (≥40%)</option>
                  </Dropdown>
                  {insightsActiveFilters>0&&<motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn('s'),padding:'8px 12px',fontSize:12,color:RD}} onClick={clearFuInsightsFilters}>Clear</motion.button>}
                </div>
              </div>

              <div className="fu-insights-body">
                {/* Segment + blockers */}
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))',gap:12,marginBottom:12}}>
                  <div className="fu-insights-section">
                    <div style={{fontSize:11,fontWeight:700,color:MU,textTransform:'uppercase',letterSpacing:'.5px',marginBottom:10}}>By segment — who's stalling?</div>
                    {insightsSegmentStats.length ? insightsSegmentStats.map(row=>(
                      <div key={row.seg} style={{display:'grid',gridTemplateColumns:'1fr auto',gap:8,padding:'8px 0',borderBottom:'1px solid #F3F4F6',fontSize:12}}>
                        <div>
                          <div style={{fontWeight:700,color:TX}}>{row.seg}</div>
                          <div style={{fontSize:11,color:LT,marginTop:2}}>{row.count} companies · avg {row.avgTouches} calls · {row.avgConnect}% connect</div>
                        </div>
                        <div style={{textAlign:'right'}}>
                          <div style={{fontWeight:700,color:row.overduePct>=30?RD:row.avgDays>=20?YL:TX}}>{row.avgDays}d avg</div>
                          <div style={{fontSize:10,color:row.overduePct?RD:LT,marginTop:2}}>{row.overduePct}% overdue</div>
                        </div>
                      </div>
                    )) : <div style={{fontSize:12,color:LT}}>No companies match filters</div>}
                  </div>
                  <div className="fu-insights-section fu-insights-section--muted">
                    <div style={{fontSize:11,fontWeight:700,color:MU,textTransform:'uppercase',letterSpacing:'.5px',marginBottom:10}}>Call blockers (not connected)</div>
                    {insightsBlockerRows.length ? insightsBlockerRows.slice(0, 8).map(([label, count])=>(
                      <div key={label} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'7px 0',borderBottom:'1px solid #ECEEF1',fontSize:12}}>
                        <span style={{color:label==='Wrong number'?RD:label==='No answer'?YL:TX,fontWeight:600}}>{label}</span>
                        <span style={{fontWeight:700,color:MU}}>{count}</span>
                      </div>
                    )) : <div style={{fontSize:12,color:LT}}>No blocked calls logged</div>}
                    <div style={{display:'flex',gap:8,flexWrap:'wrap',marginTop:12}}>
                      {insightsWrongNumber.length>0&&<span style={{fontSize:10,fontWeight:700,color:RD,background:RL,padding:'4px 8px',borderRadius:6}}>{insightsWrongNumber.length} cos · wrong number</span>}
                      {insightsLowConnect.length>0&&<span style={{fontSize:10,fontWeight:700,color:YL,background:YLL,padding:'4px 8px',borderRadius:6}}>{insightsLowConnect.length} cos · low connect (≤25%)</span>}
                    </div>
                  </div>
                </div>

                {/* Purpose + stage chips */}
                <div className="fu-insights-section" style={{marginBottom:12}}>
                  <div style={{fontSize:11,fontWeight:700,color:MU,textTransform:'uppercase',letterSpacing:'.5px',marginBottom:8}}>Follow-up purpose mix</div>
                  <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:12}}>
                    {insightsPurposeStats.length ? insightsPurposeStats.map(([label,count])=>(
                      <span key={label} style={{fontSize:11,fontWeight:600,color:TX,background:'#F3F4F6',border:'1px solid #E5E7EB',padding:'4px 10px',borderRadius:999}}>{label} · <strong>{count}</strong></span>
                    )) : <span style={{fontSize:12,color:LT}}>—</span>}
                  </div>
                  <div style={{fontSize:11,fontWeight:700,color:MU,textTransform:'uppercase',letterSpacing:'.5px',marginBottom:8}}>Pipeline stage</div>
                  <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                    {insightsStageStats.length ? insightsStageStats.slice(0, 10).map(([label,count])=>(
                      <span key={label} style={{fontSize:11,fontWeight:600,color:MU,background:'#F9FAFB',border:'1px solid #E5E7EB',padding:'4px 10px',borderRadius:999}}>{label} · {count}</span>
                    )) : <span style={{fontSize:12,color:LT}}>—</span>}
                  </div>
                </div>

                {/* Full company table */}
                <div className="fu-insights-section">
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:12,marginBottom:10,flexWrap:'wrap'}}>
                    <div style={{fontSize:11,fontWeight:700,color:MU,textTransform:'uppercase',letterSpacing:'.5px'}}>All companies — ranked by follow-up activity</div>
                    <span style={{fontSize:11,color:MU}}>{insightsFilteredSorted.length} shown</span>
                  </div>
                  <div style={{overflowX:'auto',borderRadius:8,border:'1px solid #ECEEF1'}}>
                    <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                      <thead>
                        <tr style={{background:'#F3F4F6'}}>
                          {['Company','Segment','Purpose','Days','Calls','Connect','Last outcome','Blocker','Due','Status'].map(h=>(
                            <th key={h} style={{...s.th,fontSize:10,padding:'8px 10px',whiteSpace:'nowrap',background:'#F3F4F6'}}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {insightsFilteredSorted.length ? insightsFilteredSorted.map((e, i)=>{
                          const pm = PURPOSE_META[e.purpose] || PURPOSE_META.general;
                          const dueLabel = e.daysOverdue>0 ? `${e.daysOverdue}d overdue` : e.date===todayStr ? 'Today' : fmtDate(e.date);
                          return (
                            <tr key={e.coId} style={{borderBottom:'1px solid #F3F4F6',cursor:'pointer',background:i%2===0?'#fff':'#FAFBFC'}} onClick={()=>{setFuInsightsOpen(false);setDetailId(e.coId);setDTab('timeline');}} onMouseEnter={ev=>{ev.currentTarget.style.background='#EEF2FF';}} onMouseLeave={ev=>{ev.currentTarget.style.background=i%2===0?'#fff':'#FAFBFC';}}>
                              <td style={{...s.td,padding:'8px 10px',fontWeight:700,whiteSpace:'nowrap'}}>{e.co?.name||'—'}<div style={{fontSize:10,color:LT,fontWeight:400}}>{e.co?.city||''}</div></td>
                              <td style={{...s.td,padding:'8px 10px'}}>{e.segment}</td>
                              <td style={{...s.td,padding:'8px 10px',color:pm.color,fontWeight:600,whiteSpace:'nowrap'}}>{pm.label}</td>
                              <td style={{...s.td,padding:'8px 10px',fontWeight:700}}>{e.daysInPipeline}d</td>
                              <td style={{...s.td,padding:'8px 10px'}}>{e.totalTouches}</td>
                              <td style={{...s.td,padding:'8px 10px',color:e.connectRate>=40?GN:e.connectRate>=20?YL:RD,fontWeight:600}}>{e.connectRate}%</td>
                              <td style={{...s.td,padding:'8px 10px',maxWidth:140,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{e.lastOutcome||'—'}</td>
                              <td style={{...s.td,padding:'8px 10px',color:e.lastBlocker==='Wrong number'?RD:e.lastBlocker?YL:LT,whiteSpace:'nowrap'}}>{e.lastBlocker ? `${e.lastBlocker}${e.lastBlockerCount>1?` (${e.lastBlockerCount})`:''}` : '—'}</td>
                              <td style={{...s.td,padding:'8px 10px',color:e.daysOverdue?RD:TX,whiteSpace:'nowrap'}}>{dueLabel}</td>
                              <td style={{...s.td,padding:'8px 10px'}}><Tag label={e.stage}/></td>
                            </tr>
                          );
                        }) : (
                          <tr><td colSpan={10} style={{...s.td,padding:'24px 10px',textAlign:'center',color:LT}}>No companies match your filters</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </Modal>

            {/* Person filter (admin only) */}
            {isAdmin && config.salespersons?.length>0 && (
              <div className="fu-person-filter-card">
                <div className="fu-person-filter-card__head">
                  <div>
                    <div className="fu-person-filter-card__title">Team member</div>
                    <div className="fu-person-filter-card__sub">Filter follow-ups by owner{pTab!=='all' ? ` · showing ${pTab}` : ''}</div>
                  </div>
                  {pTab!=='all' && (
                    <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn('s'),padding:'6px 12px',fontSize:12}} onClick={()=>setPTab('all')}>Clear</motion.button>
                  )}
                </div>
                <div className="fu-person-filter">
                  <button type="button" className={'fu-person-chip'+(pTab==='all'?' fu-person-chip--active':'')} onClick={()=>setPTab('all')}>
                    <span className="fu-person-chip__avatar fu-person-chip__avatar--all">All</span>
                    <span className="fu-person-chip__label">Everyone</span>
                    <span className="fu-person-chip__count">{allSortedAll.length}</span>
                  </button>
                  {config.salespersons.map(sp=>{
                    const count = fuSpCount(sp);
                    const active = pTab === sp;
                    const color = fuSpColor(sp);
                    return (
                      <button key={sp} type="button" className={'fu-person-chip'+(active?' fu-person-chip--active':'')} onClick={()=>setPTab(sp)}>
                        <span className="fu-person-chip__avatar" style={{background:active?color:`${color}18`,color:active?'#fff':color}}>{fuSpInitials(sp)}</span>
                        <span className="fu-person-chip__label">{sp}</span>
                        <span className="fu-person-chip__count">{count}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Tabs */}
            <div style={{display:'flex',gap:0,borderBottom:'1px solid #E5E7EB',marginBottom:14}}>
              {[['overdue','Overdue',overdueFu.length],['today','Today',todayFu.length],['hot','Hot',hotFu.length],['upcoming','Upcoming',upcomingFu.length]].map(([k,l,cnt])=>
                <div key={k} onClick={()=>setFuFilter(k)} style={{padding:'10px 20px',fontSize:13,fontWeight:fuFilter===k?700:400,color:fuFilter===k?TX:MU,borderBottom:fuFilter===k?'2px solid '+TX:'2px solid transparent',cursor:'pointer',transition:'all .15s',marginBottom:-1,display:'flex',alignItems:'center',gap:6}}>
                  {k==='hot'&&<span>🔥</span>}{l}
                  <span style={{minWidth:20,height:20,borderRadius:6,background:fuFilter===k?TX:'#F3F4F6',color:fuFilter===k?'#fff':MU,fontSize:11,fontWeight:700,display:'inline-flex',alignItems:'center',justifyContent:'center',padding:'0 5px'}}>{cnt}</span>
                </div>
              )}
            </div>

            {/* Cards */}
            {visibleEntries.length ? (
              <motion.div variants={listContainer} initial="hidden" animate="show" style={{display:'flex',flexDirection:'column',gap:8}}>
                {visibleEntries.map(e=><FuCard key={e.coId} e={e}/>)}
              </motion.div>
            ) : (
              <EmptyState/>
            )}
          </>;
        })()}

        {/* ═══ MEETINGS ═══ */}
        {page==='meetings' && !detailId && (()=>{
          const SCHED_STAGES=['Tentative','Confirmed','Scheduled','Rescheduled'];
          const DONE_STAGES=['Completed','Done','Cancelled'];
          const getCoForMeeting = m => companies.find(c => String(c._id) === String(m.companyId));
          const mtgPassesPersonFilters = m => {
            const co = getCoForMeeting(m);
            if (mtgConductedBy !== 'all' && (m.salesperson || '') !== mtgConductedBy) return false;
            if (mtgAccountOwner && (co?.salesperson || '') !== mtgAccountOwner) return false;
            return true;
          };
          const mtgDateMin=mtgPeriod==='today'?today():mtgPeriod==='week'?new Date(Date.now()-7*864e5).toISOString().slice(0,10):mtgPeriod==='month'?new Date(Date.now()-30*864e5).toISOString().slice(0,10):mtgPeriod==='custom'?dateFrom:'';
          const mtgDateMax=mtgPeriod==='yesterday'?(()=>{const d=new Date();d.setDate(d.getDate()-1);return d.toISOString().slice(0,10);})():mtgPeriod==='custom'?dateTo:mtgPeriod==='today'||mtgPeriod==='week'||mtgPeriod==='month'?today():'';
          const todayStr=today();
          const mtgValue=m=>Number(m.estimatedValue)||0;
          const hasVisiblePipeline=m=>!isFieldVisitMeeting(m)&&m.stage!=='Cancelled'&&mtgValue(m)>0;
          const hasOverdueNextAction=m=>m.stage!=='Cancelled'&&m.nextActionDate&&m.nextActionDate<todayStr;
          const isFruitfulWalkin=m=>m.type===MEETING_TYPE_WALKIN_MET&&m.stage!=='Cancelled'&&!!(
            m.summary?.trim()||m.nextAction?.trim()||m.keyDecisions?.trim()||m.proposalSent||mtgValue(m)>0||Number(m.closingProbability)>=50
          );
          const mtgPassesFilters=m=>{
            if(!mtgPassesPersonFilters(m))return false;
            if(mtgDateMin&&m.date<mtgDateMin)return false;
            if(mtgDateMax&&m.date>mtgDateMax)return false;
            const co=getCoForMeeting(m);
            if(mtgCity&&!citiesMatch(co?.city, mtgCity))return false;
            if(mtgType==='walk-in'&&m.type!==MEETING_TYPE_WALKIN_MET)return false;
            if(mtgType==='no-contact'&&!isNoContactVisit(m))return false;
            if(mtgType==='field'&&!isFieldVisitMeeting(m))return false;
            if(mtgType&&['online','in-person','over a call'].includes(mtgType)&&m.type!==mtgType)return false;
            if(mtgStatus!=='all'){
              if(mtgStatus==='missing-summary'&&(!DONE_STAGES.includes(m.stage)||m.summary?.trim()||m.stage==='Cancelled'))return false;
              else if(mtgStatus!=='missing-summary'&&m.stage!==mtgStatus)return false;
            }
            if(mtgPipeline==='visible'&&!hasVisiblePipeline(m))return false;
            if(mtgPipeline==='missing'&&(isFieldVisitMeeting(m)||m.stage==='Cancelled'||hasVisiblePipeline(m)))return false;
            if(mtgPipeline==='overdue-next'&&!hasOverdueNextAction(m))return false;
            if(mtgPipeline==='proposal-sent'&&!m.proposalSent)return false;
            if(mtgSearch){
              const q=mtgSearch.toLowerCase();
              const mSpoc=spocs.find(ss=>ss._id===m.spocId);
              if(!co?.name?.toLowerCase().includes(q)&&!mSpoc?.name?.toLowerCase().includes(q)&&!(m.agenda||'').toLowerCase().includes(q)&&!(m.summary||'').toLowerCase().includes(q)&&!(m.salesperson||'').toLowerCase().includes(q)&&!(m.location||'').toLowerCase().includes(q))return false;
            }
            return true;
          };
          const overviewMeetings=meetings.filter(mtgPassesFilters);
          const scheduled=meetings.filter(m=>mtgPassesFilters(m)&&!isFieldVisitMeeting(m)&&SCHED_STAGES.includes(m.stage)).sort((a,b)=>(a.date||'').localeCompare(b.date||''));
          const completed=meetings.filter(m=>mtgPassesFilters(m)&&DONE_STAGES.includes(m.stage)).sort((a,b)=>(b.date||'').localeCompare(a.date||''));
          const missingMOM=completed.filter(m=>!isFieldVisitMeeting(m)&&!m.summary?.trim()&&m.stage!=='Cancelled');
          const mtgFilteredTotal=scheduled.length+completed.length;
          const mtgWalkins=overviewMeetings.filter(m=>m.type===MEETING_TYPE_WALKIN_MET);
          const mtgFruitfulWalkins=mtgWalkins.filter(isFruitfulWalkin);
          const mtgNoContact=overviewMeetings.filter(isNoContactVisit);
          const mtgFieldVisits=overviewMeetings.filter(isFieldVisitMeeting);
          const mtgPipelineVisible=overviewMeetings.filter(hasVisiblePipeline);
          const mtgPipelineValue=mtgPipelineVisible.reduce((sum,m)=>sum+mtgValue(m),0);
          const mtgPipelineMissing=overviewMeetings.filter(m=>!isFieldVisitMeeting(m)&&m.stage!=='Cancelled'&&!hasVisiblePipeline(m)).length;
          const mtgProposalSent=overviewMeetings.filter(m=>m.proposalSent).length;
          const mtgMomShared=overviewMeetings.filter(m=>!isFieldVisitMeeting(m)&&m.momShared).length;
          const mtgOverdueNext=overviewMeetings.filter(hasOverdueNextAction).length;
          const mtgUniqueCompanies=new Set(overviewMeetings.map(m=>String(m.companyId)).filter(Boolean)).size;
          const mtgPipelineEligible=overviewMeetings.filter(m=>!isFieldVisitMeeting(m)&&m.stage!=='Cancelled').length;
          const mtgPipelineCoverage=mtgPipelineEligible?Math.round(mtgPipelineVisible.length/mtgPipelineEligible*100):0;
          const mtgActiveFilters=[mtgPeriod!=='all',mtgStatus!=='all',mtgCity,mtgType,mtgPipeline,mtgSearch,mtgConductedBy!=='all',mtgAccountOwner].filter(Boolean).length;
          const mtgAdvancedFilters=[mtgStatus!=='all',mtgCity,mtgType,mtgPipeline,mtgAccountOwner].filter(Boolean).length;
          const clearMtgFilters=()=>{setMtgPeriod('all');setMtgStatus('all');setMtgCity('');setMtgConductedBy('all');setMtgAccountOwner('');setMtgType('');setMtgPipeline('');setMtgSearch('');setMtgShowFilters(false);};
          const borderColor=st=>st==='Tentative'?YL:st==='Confirmed'?GN:st==='Completed'||st==='Done'?TX:st==='Rescheduled'?'#F97316':st==='Cancelled'?RD:'#E5E7EB';
          return <>
            {/* Header */}
            <div className="page-header-row" style={{marginBottom:20}}>
              <div>
                <h1 style={{fontSize:26,fontWeight:700,margin:'0 0 2px',letterSpacing:'-0.03em'}}>Meetings</h1>
                <div style={{fontSize:13,color:MU}}>{mtgFilteredTotal} shown · {scheduled.length} upcoming · {completed.length} completed{missingMOM.length>0?<span style={{color:RD,fontWeight:600}}> · {missingMOM.length} missing summary</span>:''}</div>
              </div>
              <div className="page-header-actions">
                <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={s.btn()} onClick={()=>openMeetingForm()}>+ Schedule Meeting</motion.button>
              </div>
            </div>

            {/* Compact filters */}
            <div style={{...s.card,padding:'12px 14px',marginBottom:16}}>
              <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
                <input style={{...s.inp,flex:'1 1 160px',minWidth:140,padding:'8px 12px',fontSize:12}} placeholder="Search..." value={mtgSearch} onChange={e=>setMtgSearch(e.target.value)}/>
                <Dropdown style={{...s.sel,width:118,fontSize:12}} value={mtgPeriod} onChange={e=>setMtgPeriod(e.target.value)}>
                  <option value="today">Today</option>
                  <option value="week">7 days</option>
                  <option value="month">30 days</option>
                  <option value="all">All time</option>
                  <option value="custom">Custom</option>
                </Dropdown>
                <Dropdown style={{...s.sel,width:155,fontSize:12}} value={mtgConductedBy} onChange={e=>setMtgConductedBy(e.target.value)} title="Who conducted the meeting">
                  <option value="all">Meeting conducted by: all</option>
                  {config.salespersons?.map(sp=><option key={sp} value={sp}>{sp}</option>)}
                </Dropdown>
                <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn('s'),padding:'8px 12px',fontSize:12,whiteSpace:'nowrap'}} onClick={()=>setMtgShowFilters(v=>!v)}>
                  {mtgShowFilters?'▾ Less':'▸ More'}{mtgAdvancedFilters>0?` · ${mtgAdvancedFilters}`:''}
                </motion.button>
                {mtgActiveFilters>0&&<motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn('s'),padding:'8px 12px',fontSize:12,color:RD}} onClick={clearMtgFilters}>Clear</motion.button>}
              </div>
              {(mtgShowFilters||mtgPeriod==='custom')&&<div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center',marginTop:10,paddingTop:10,borderTop:'1px solid #F3F4F6'}}>
                {mtgPeriod==='custom'&&<>
                  <input type="date" style={{...s.inp,width:130,padding:'7px 10px',fontSize:12}} value={dateFrom} onChange={e=>setDateFrom(e.target.value)}/>
                  <span style={{fontSize:12,color:MU}}>to</span>
                  <input type="date" style={{...s.inp,width:130,padding:'7px 10px',fontSize:12}} value={dateTo} onChange={e=>setDateTo(e.target.value)}/>
                </>}
                <Dropdown style={{...s.sel,width:110,fontSize:12}} value={mtgCity} onChange={e=>setMtgCity(e.target.value)}>
                  <option value="">All cities</option>
                  {visibleCities.map(c=><option key={c} value={c}>{c}</option>)}
                </Dropdown>
                <Dropdown style={{...s.sel,width:160,fontSize:12}} value={mtgAccountOwner} onChange={e=>setMtgAccountOwner(e.target.value)} title="Who generated or owns the lead">
                  <option value="">Lead generated by: all</option>
                  {config.salespersons?.map(sp=><option key={sp} value={sp}>{sp}</option>)}
                </Dropdown>
                <Dropdown style={{...s.sel,width:130,fontSize:12}} value={mtgType} onChange={e=>setMtgType(e.target.value)}>
                  <option value="">All types</option>
                  <option value="online">Online</option>
                  <option value="in-person">In-person</option>
                  <option value="over a call">Over a call</option>
                  <option value="field">All site walk-ins</option>
                  <option value="walk-in">{WALKIN_MET_LABEL}</option>
                  <option value="no-contact">{WALKIN_NO_CONTACT_LABEL}</option>
                </Dropdown>
                <Dropdown style={{...s.sel,width:130,fontSize:12}} value={mtgStatus} onChange={e=>setMtgStatus(e.target.value)}>
                  <option value="all">All stages</option>
                  <option value="Tentative">Tentative</option>
                  <option value="Confirmed">Confirmed</option>
                  <option value="Scheduled">Scheduled</option>
                  <option value="Rescheduled">Rescheduled</option>
                  <option value="Completed">Completed</option>
                  <option value="Done">Done</option>
                  <option value="Cancelled">Cancelled</option>
                  <option value="missing-summary">Missing summary</option>
                </Dropdown>
                <Dropdown style={{...s.sel,width:145,fontSize:12}} value={mtgPipeline} onChange={e=>setMtgPipeline(e.target.value)}>
                  <option value="">Pipeline: all</option>
                  <option value="visible">Pipeline visible</option>
                  <option value="missing">Pipeline missing</option>
                  <option value="proposal-sent">Proposal sent</option>
                  <option value="overdue-next">Overdue next action</option>
                </Dropdown>
              </div>}
            </div>

            {/* Meeting Overview */}
            <div style={{...s.card,padding:0,overflow:'hidden',marginBottom:16,border:'1px solid #E5E7EB'}}>
              <div style={{padding:'14px 16px',borderBottom:'1px solid #F3F4F6',display:'flex',justifyContent:'space-between',gap:12,alignItems:'center',flexWrap:'wrap'}}>
                <div>
                  <div style={{fontSize:14,fontWeight:700,color:TX,letterSpacing:'-0.02em'}}>Meeting Overview</div>
                  <div style={{fontSize:12,color:MU,marginTop:2}}>Filtered snapshot across {mtgUniqueCompanies} compan{mtgUniqueCompanies===1?'y':'ies'}</div>
                </div>
                <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                  <span style={{fontSize:11,fontWeight:600,color:mtgPipelineMissing?YL:GN,background:mtgPipelineMissing?YLL:GL,border:'1px solid '+(mtgPipelineMissing?'#FDE68A':'#BBF7D0'),padding:'4px 9px',borderRadius:7}}>{mtgPipelineCoverage}% pipeline visibility</span>
                  {mtgOverdueNext>0&&<span style={{fontSize:11,fontWeight:700,color:RD,background:RL,border:'1px solid #FECACA',padding:'4px 9px',borderRadius:7}}>{mtgOverdueNext} overdue next action{mtgOverdueNext!==1?'s':''}</span>}
                </div>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))',gap:1,background:'#F3F4F6'}}>
                {[
                  {label:'Total Meetings',value:overviewMeetings.length,sub:`${scheduled.length} upcoming · ${completed.length} closed`,accent:TX},
                  {label:'Completed',value:completed.length,sub:`${missingMOM.length} missing MOM`,accent:missingMOM.length?RD:GN},
                  {label:'Site walk-ins',value:mtgFieldVisits.length,sub:`${mtgWalkins.length} met contact · ${mtgNoContact.length} no contact${mtgWalkins.length?` · ${mtgFruitfulWalkins.length} fruitful`:''}`,accent:GN},
                  {label:'Pipeline Visible',value:mtgPipelineVisible.length,sub:mtgPipelineValue?fmtINR(mtgPipelineValue):`${mtgPipelineMissing} missing value`,accent:mtgPipelineMissing?YL:GN},
                  {label:'Proposals Sent',value:mtgProposalSent,sub:'From meeting records',accent:'#7C3AED'},
                  {label:'MOM Shared',value:mtgMomShared,sub:`${missingMOM.length} pending summary`,accent:missingMOM.length?RD:GN},
                ].map(({label,value,sub,accent})=>
                  <div key={label} style={{background:WH,padding:'14px 12px',minHeight:86}}>
                    <div style={{fontSize:22,fontWeight:800,color:TX,letterSpacing:'-0.04em',lineHeight:1}}>{value}</div>
                    <div style={{fontSize:11,fontWeight:700,color:MU,marginTop:7}}>{label}</div>
                    <div style={{fontSize:10,color:accent,marginTop:4,fontWeight:600,lineHeight:1.35}}>{sub}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Tabs */}
            <div style={{display:'flex',gap:0,borderBottom:'1px solid #E5E7EB',marginBottom:14}}>
              {[['scheduled','Scheduled',scheduled.length,TX],['completed','Completed',completed.length,MU]].map(([k,l,cnt,col])=>
                <div key={k} onClick={()=>setMtgView(k)} style={{padding:'10px 20px',fontSize:13,fontWeight:mtgView===k?700:400,color:mtgView===k?TX:MU,borderBottom:mtgView===k?'2px solid '+TX:'2px solid transparent',cursor:'pointer',transition:'all .15s',marginBottom:-1,display:'flex',alignItems:'center',gap:6}}>
                  {l}
                  <span style={{minWidth:20,height:20,borderRadius:6,background:mtgView===k?TX:'#F3F4F6',color:mtgView===k?'#fff':MU,fontSize:11,fontWeight:700,display:'inline-flex',alignItems:'center',justifyContent:'center',padding:'0 5px'}}>{cnt}</span>
                  {k==='completed'&&missingMOM.length>0&&<span style={{minWidth:18,height:18,borderRadius:5,background:RD,color:'#fff',fontSize:10,fontWeight:700,display:'inline-flex',alignItems:'center',justifyContent:'center',padding:'0 4px'}}>{missingMOM.length}</span>}
                </div>
              )}
            </div>

            {/* ── TAB 1: SCHEDULED ── */}
            {mtgView==='scheduled'&&(()=>{
              if(!scheduled.length) return <div style={{...s.card,textAlign:'center',padding:48,color:MU}}>
                <div style={{fontSize:32,marginBottom:12}}>📅</div>
                <div style={{fontSize:15,fontWeight:600,color:TX,marginBottom:6}}>No upcoming meetings{mtgActiveFilters>0?' match filters':''}</div>
                <div style={{fontSize:13,color:LT,marginBottom:20}}>{mtgActiveFilters>0?'Try clearing filters or switching tabs':'Schedule your first meeting to track it here'}</div>
                {mtgActiveFilters>0
                  ? <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={s.btn('s')} onClick={clearMtgFilters}>Clear filters</motion.button>
                  : <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={s.btn()} onClick={()=>openMeetingForm()}>+ Schedule Meeting</motion.button>}
              </div>;
              return <motion.div variants={listContainer} initial="hidden" animate="show" style={{display:'flex',flexDirection:'column',gap:10}}>
                {scheduled.map(m=>{
                  const co=getCoForMeeting(m);
                  const mSpoc=spocs.find(ss=>ss._id===m.spocId);
                  const isToday=m.date===todayStr;
                  const isOverdue=m.date<todayStr;
                  return <motion.div key={m._id} variants={listItem} whileHover={{x:2}} style={{...s.card,padding:0,overflow:'hidden',borderLeft:'4px solid '+borderColor(m.stage),marginBottom:0}}>
                    <div className="meeting-card-row">
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4,flexWrap:'wrap'}}>
                          <span style={{fontWeight:700,fontSize:14}}>{co?.name||'—'}</span>
                          <Tag label={m.stage}/>
                          {isToday&&<span style={{fontSize:11,fontWeight:700,color:GN,background:GL,padding:'2px 8px',borderRadius:6}}>Today</span>}
                          {isOverdue&&<span style={{fontSize:11,fontWeight:700,color:RD,background:RL,padding:'2px 8px',borderRadius:6}}>Overdue</span>}
                          <span style={{fontSize:11,color:MU,background:'#F3F4F6',padding:'2px 8px',borderRadius:6}}>{meetingTypeLabel(m.type) || 'online'}</span>
                        </div>
                        <div style={{fontSize:12,color:MU,display:'flex',gap:12,flexWrap:'wrap',marginBottom:4}}>
                          <span>📅 {fmtDate(m.date)}</span>
                          {co?.city&&<span>🏙️ {co.city}</span>}
                          {m.location&&<span>📍 {m.location}</span>}
                          {mSpoc&&<span>🤝 {mSpoc.name}{mSpoc.designation?` (${mSpoc.designation})`:''}</span>}
                        </div>
                        <MeetingAttribution meeting={m} company={co} />
                        {m.agenda&&<div style={{fontSize:12,color:TX,marginTop:6,fontStyle:'italic'}}>"{m.agenda}"</div>}
                      </div>
                      <div className="meeting-card-actions">
                        {m.estimatedValue&&<span style={{fontWeight:700,color:GN,fontSize:13}}>{fmtINR(m.estimatedValue)}</span>}
                        {m.stage==='Tentative'&&<motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn(),padding:'6px 12px',fontSize:12,background:GN}} onClick={async()=>{await api.updateMeeting(m._id,{stage:'Confirmed'});loadData();}}>Confirm</motion.button>}
                        <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn(),padding:'6px 12px',fontSize:12}} onClick={()=>openMeetingForm({...m,stage:'Completed'})}>Mark Done</motion.button>
                        <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn('s'),padding:'6px 12px',fontSize:12}} onClick={()=>openMeetingForm(m)}>Edit</motion.button>
                        {isAdmin&&<DeleteIconBtn onClick={()=>setConfirmDel({fn:async()=>{await api.deleteMeeting(m._id);loadData();},msg:'Delete this meeting?'})}/>}
                      </div>
                    </div>
                    {/* Progress bar */}
                    <div style={{height:3,background:'#F3F4F6'}}><div style={{height:'100%',width:(m.closingProbability||0)+'%',background:(m.closingProbability||0)>=60?GN:(m.closingProbability||0)>=30?YL:RD,borderRadius:2,transition:'width .3s'}}/></div>
                  </motion.div>;
                })}
              </motion.div>;
            })()}

            {/* ── TAB 2: COMPLETED (includes walk-ins + no-contact visits) ── */}
            {(mtgView==='completed'||mtgView==='field')&&(()=>{
              if(!completed.length) return <div style={{...s.card,textAlign:'center',padding:48,color:MU}}>
                <div style={{fontSize:32,marginBottom:12}}>🏁</div>
                <div style={{fontSize:15,fontWeight:600,color:TX,marginBottom:6}}>No completed meetings{mtgActiveFilters>0?' match filters':''}</div>
                <div style={{fontSize:13,color:LT}}>{mtgActiveFilters>0?'Adjust filters or clear them to see more':'Mark scheduled meetings as Done, or log a site walk-in via + Schedule Meeting'}</div>
              </div>;
              return <>
                {missingMOM.length>0&&<div style={{background:RL,border:'1px solid #FECACA',borderRadius:12,padding:'12px 18px',marginBottom:16,display:'flex',alignItems:'center',gap:10}}>
                  <span style={{fontSize:16}}>⚠️</span>
                  <div><strong style={{color:RD}}>{missingMOM.length} meeting{missingMOM.length!==1?'s':''} missing post-meeting summary</strong><span style={{fontSize:12,color:MU,marginLeft:8}}>Click "Add Summary" to fill in the MOM</span></div>
                </div>}
                <motion.div variants={listContainer} initial="hidden" animate="show" style={{display:'flex',flexDirection:'column',gap:10}}>
                {completed.map(m=>{
                  const co=getCoForMeeting(m);
                  const mSpoc=spocs.find(ss=>ss._id===m.spocId);
                  const isWalkIn=m.type===MEETING_TYPE_WALKIN_MET;
                  const isNoContact=isNoContactVisit(m);
                  const isFieldVisit=isFieldVisitMeeting(m);
                  const noSummary=!isFieldVisit&&!m.summary?.trim()&&m.stage!=='Cancelled';
                  const borderCol=isWalkIn?'#059669':isNoContact?'#F59E0B':noSummary?RD:GN;
                  return <motion.div key={m._id} variants={listItem} whileHover={{x:2}} style={{...s.card,padding:0,overflow:'hidden',borderLeft:'4px solid '+borderCol,marginBottom:0}}>
                    <div className="meeting-card-row meeting-card-row--completed">
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4,flexWrap:'wrap'}}>
                          <span style={{fontWeight:700,fontSize:14}}>{co?.name||'—'}</span>
                          {isWalkIn&&<span style={{fontSize:11,fontWeight:700,padding:'2px 8px',borderRadius:6,background:'#D1FAE5',color:'#047857'}}>🚶 {WALKIN_MET_SHORT}</span>}
                          {isNoContact&&<span style={{fontSize:11,fontWeight:700,padding:'2px 8px',borderRadius:6,background:'#FEF3C7',color:'#92400E'}}>🚪 {WALKIN_NO_CONTACT_SHORT}</span>}
                          <Tag label={m.stage}/>
                          {!isFieldVisit&&noSummary&&<span style={{fontSize:11,fontWeight:700,color:RD,background:RL,padding:'2px 8px',borderRadius:6}}>Summary missing</span>}
                          {!isFieldVisit&&!noSummary&&<span style={{fontSize:11,fontWeight:600,color:GN,background:GL,padding:'2px 8px',borderRadius:6}}>Summary added</span>}
                          {!isFieldVisit&&<span style={{fontSize:11,color:MU,background:'#F3F4F6',padding:'2px 8px',borderRadius:6}}>{meetingTypeLabel(m.type) || 'online'}</span>}
                        </div>
                        <div style={{fontSize:12,color:MU,display:'flex',gap:12,flexWrap:'wrap',marginBottom:4}}>
                          <span>📅 {fmtDate(m.date)}</span>
                          {co?.city&&<span>🏙️ {co.city}</span>}
                          {m.location&&<span>📍 {m.location}</span>}
                          {mSpoc&&<span>🤝 {mSpoc.name}</span>}
                        </div>
                        <MeetingAttribution meeting={m} company={co} />
                        {!isFieldVisit&&m.agenda&&<div style={{fontSize:12,color:MU,fontStyle:'italic',marginBottom:4}}>Agenda: "{m.agenda}"</div>}
                        {isFieldVisit?(m.summary||m.agenda)&&<div style={{fontSize:12,color:TX,background:'#F9FAFB',padding:'8px 12px',borderRadius:8,marginTop:4,lineHeight:1.5}}>{m.summary||m.agenda}</div>
                          :m.summary?<div style={{fontSize:13,color:TX,background:'#F9FAFB',padding:'8px 12px',borderRadius:8,marginTop:4,lineHeight:1.5}}>{m.summary}</div>:!isFieldVisit&&<div style={{fontSize:12,color:LT,fontStyle:'italic'}}>No summary yet — add post-meeting notes</div>}
                        {!isFieldVisit&&m.keyDecisions&&<div style={{fontSize:12,color:MU,marginTop:6}}><strong>Key Decisions:</strong> {m.keyDecisions}</div>}
                        {!isFieldVisit&&m.nextAction&&<div style={{fontSize:12,color:TX,marginTop:4,background:'#F3F4F6',padding:'4px 10px',borderRadius:6,display:'inline-block'}}><strong>Next:</strong> {m.nextAction}{m.nextActionDate?' by '+fmtDate(m.nextActionDate):''}</div>}
                      </div>
                      <div className="meeting-card-actions meeting-card-actions--stacked">
                        {m.estimatedValue&&<span style={{fontWeight:700,color:GN,fontSize:13}}>{fmtINR(m.estimatedValue)}</span>}
                        {!isFieldVisit&&m.closingProbability!=null&&<span style={{fontSize:12,color:MU}}>{m.closingProbability}% close</span>}
                        <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                          {noSummary&&<motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn(),padding:'6px 12px',fontSize:12,background:RD}} onClick={()=>openMeetingForm(m)}>Add Summary</motion.button>}
                          <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn('s'),padding:'6px 12px',fontSize:12}} onClick={()=>openMeetingForm(m)}>Edit</motion.button>
                          {isAdmin&&<DeleteIconBtn onClick={()=>setConfirmDel({fn:async()=>{await api.deleteMeeting(m._id);loadData();},msg:isFieldVisit?'Delete this site walk-in?':'Delete this meeting?'})}/>}
                        </div>
                      </div>
                    </div>
                  </motion.div>;
                })}
                </motion.div>
              </>;
            })()}
          </>;})()} 

        {/* ═══ TASKS (personal action pad) ═══ */}
        {page==='tasks' && !detailId && (()=>{
          const renderCol = (bucket, title, sub) => {
            const items = tasks.filter(t => t.bucket === bucket);
            const prog = items.filter(t => t.status !== 'complete');
            const done = items.filter(t => t.status === 'complete');
            const ordered = [...prog, ...done];
            const other = bucket === 'today' ? 'tomorrow' : 'today';
            return <div style={{flex:'1 1 340px', minWidth:300}}>
              <div style={{...s.card, marginBottom:0}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',marginBottom:2}}>
                  <h2 style={{fontSize:17,fontWeight:700,margin:0,letterSpacing:'-0.02em'}}>{title}</h2>
                  <span style={{fontSize:12,color:MU}}>{fmtDate(bucket==='today'?today():tomorrow())}</span>
                </div>
                <div style={{fontSize:12,color:LT,marginBottom:14}}>{sub}</div>
                <div style={{display:'flex',gap:8,marginBottom:14}}>
                  <input style={s.inp} placeholder="Write a task and press Enter…" value={taskInput[bucket]} onChange={e=>setTaskInput(p=>({...p,[bucket]:e.target.value}))} onKeyDown={e=>{if(e.key==='Enter')addTask(bucket);}}/>
                  <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn(),whiteSpace:'nowrap',opacity:taskBusy?.6:1}} disabled={taskBusy} onClick={()=>addTask(bucket)}>+ Add</motion.button>
                </div>
                {items.length>0 && <div style={{fontSize:11,color:MU,marginBottom:10,display:'flex',gap:14}}>
                  <span>🟡 {prog.length} In Progress</span><span>✅ {done.length} Complete</span>
                </div>}
                {!items.length && <div style={{textAlign:'center',color:LT,padding:'28px 0',fontSize:13}}>Nothing here yet — start writing above.</div>}
                <div style={{display:'flex',flexDirection:'column',gap:8}}>
                  {ordered.map(t=>{
                    const isDone = t.status==='complete';
                    return <div key={t._id} style={{display:'flex',alignItems:'flex-start',gap:10,padding:'10px 12px',borderRadius:10,border:'1px solid '+BD,background:isDone?'#F9FAFB':WH}}>
                      <input type="checkbox" checked={isDone} onChange={()=>toggleTask(t)} style={{marginTop:2,cursor:'pointer',width:16,height:16,flexShrink:0}}/>
                      <div style={{flex:1,minWidth:0,fontSize:13,color:isDone?LT:TX,textDecoration:isDone?'line-through':'none',wordBreak:'break-word',whiteSpace:'pre-wrap'}}>{t.text}</div>
                      <div style={{display:'flex',gap:6,alignItems:'center',flexShrink:0}}>
                        <span style={{fontSize:10,fontWeight:600,padding:'2px 8px',borderRadius:20,background:isDone?GL:'#FEF3C7',color:isDone?GN:'#B45309',whiteSpace:'nowrap'}}>{isDone?'Complete':'In Progress'}</span>
                        <button type="button" title={'Move to '+(other==='today'?'Today':'Tomorrow')} onClick={()=>moveTask(t,other)} style={{border:'none',background:'transparent',cursor:'pointer',color:MU,fontSize:14,padding:'0 2px'}}>{bucket==='today'?'→':'←'}</button>
                        <button type="button" title="Delete" onClick={()=>removeTask(t)} style={{border:'none',background:'transparent',cursor:'pointer',color:RD,fontSize:16,lineHeight:1,padding:'0 2px'}}>×</button>
                      </div>
                    </div>;
                  })}
                </div>
              </div>
            </div>;
          };
          return <>
            <h1 style={{fontSize:26,fontWeight:700,margin:'0 0 6px',letterSpacing:'-0.03em'}}>Tasks</h1>
            <div style={{fontSize:13,color:MU,marginBottom:20}}>Your personal action pad — jot today's tasks and tomorrow's line of action &amp; follow-ups. Only you can see these.</div>
            <div style={{display:'flex',gap:16,flexWrap:'wrap',alignItems:'flex-start'}}>
              {renderCol('today', "Today's Tasks", 'What needs to get done today')}
              {renderCol('tomorrow', 'Tomorrow — Line of Action', 'Plan & follow-ups for tomorrow')}
            </div>
          </>;
        })()}

        {/* ═══ CITY VISITS ═══ */}
        {page==='visits' && !detailId && (()=>{
          const VSTATUS_COLOR = {planning:'#6B7280',active:GN,completed:TX,cancelled:RD};
          const VSTATUS_BG = {planning:'#F3F4F6',active:GL,completed:'#F3F4F6',cancelled:RL};
          const dayLabel = d => {const days=['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];return days[new Date(d+'T12:00:00').getDay()];};
          const diffDays = (a,b) => Math.round((new Date(b)-new Date(a))/(1000*60*60*24))+1;

          // Visit detail view
          if (visitDetail) {
            const v = visitDetail;
            const d = visitDetailData;
            const days = d ? Object.entries(d.stats.days).sort((a,b)=>a[0].localeCompare(b[0])) : [];
            const minDay = days.length ? Math.min(...days.map(([,x])=>x.meetings)) : 0;
            const needsAttention = days.filter(([,x])=>x.meetings===0||x.confirmedMeetings===0);
            return <>
              <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:20}}>
                <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn('s'),padding:'6px 12px',fontSize:12}} onClick={()=>{setVisitDetail(null);setVisitDetailData(null);}}>← Back</motion.button>
                <div style={{flex:1}}>
                  <h1 style={{fontSize:24,fontWeight:700,margin:0,letterSpacing:'-0.03em'}}>{v.title}</h1>
                  <div style={{fontSize:13,color:MU,marginTop:2}}>📍 {v.city} · {fmtDate(v.visitFrom)} – {fmtDate(v.visitTo)} · {diffDays(v.visitFrom,v.visitTo)} days</div>
                </div>
                <span style={{...s.tag(VSTATUS_BG[v.status],VSTATUS_COLOR[v.status]),fontSize:12}}>{v.status}</span>
                {isAdmin&&<motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn('s'),padding:'5px 12px',fontSize:11}} onClick={()=>{setForm({...v,targetCompanies:v.targetCompanies||[]});setModal({type:'visit',edit:true,editId:v._id});}}>✏️ Edit</motion.button>}
                {isAdmin&&<DeleteIconBtn onClick={()=>setConfirmDel({fn:async()=>{await api.deleteVisit(v._id);setVisitDetail(null);api.getVisits().then(setVisits);},msg:'Delete this visit?'})}/>}
              </div>

              {visitDetailLoading&&<div style={{textAlign:'center',padding:40,color:MU}}>Loading visit data…</div>}

              {d&&<>
                {/* KPI row */}
                <div className="grid-4" style={{marginBottom:20}}>
                  {[
                    {label:'Total Calls',value:d.stats.totalCalls,color:'#3B82F6',bg:'#EFF6FF'},
                    {label:'Meetings Set',value:d.stats.totalMeetings,color:GN,bg:GL},
                    {label:'Confirmed',value:d.stats.confirmedMeetings,color:TX,bg:'#F3F4F6'},
                    {label:'Companies Reached',value:d.stats.companiesContacted,color:'#8B5CF6',bg:'#F5F3FF'},
                  ].map(k=><div key={k.label} style={{...s.card,padding:'16px 20px',marginBottom:0,borderLeft:'4px solid '+k.color}}>
                    <div style={{fontSize:26,fontWeight:700,color:k.color}}>{k.value}</div>
                    <div style={{fontSize:12,color:MU,marginTop:2}}>{k.label}</div>
                  </div>)}
                </div>

                {/* Attention alert */}
                {needsAttention.length>0&&<div style={{background:RL,border:'1px solid #FECACA',borderRadius:12,padding:'12px 18px',marginBottom:16,display:'flex',alignItems:'center',gap:10}}>
                  <span style={{fontSize:15}}>⚠️</span>
                  <div><strong style={{color:RD}}>{needsAttention.map(([d])=>dayLabel(d)+' '+fmtDate(d)).join(', ')}</strong><span style={{fontSize:12,color:MU,marginLeft:8}}>— no confirmed meetings yet. Push the team!</span></div>
                </div>}

                {/* Per-day breakdown */}
                <div style={{...s.card,marginBottom:16}}>
                  <div style={{fontSize:14,fontWeight:700,marginBottom:14,letterSpacing:'-0.02em'}}>Day-by-Day Breakdown</div>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(130px,1fr))',gap:10}}>
                    {days.map(([date,stat])=>{
                      const isGreen=stat.confirmedMeetings>0;
                      const isWarn=stat.meetings===0;
                      return <div key={date} style={{borderRadius:12,padding:'12px 14px',background:isGreen?GL:isWarn?RL:'#F9FAFB',border:'1px solid '+(isGreen?GN:isWarn?'#FECACA':'#E5E7EB'),textAlign:'center'}}>
                        <div style={{fontSize:11,fontWeight:700,color:isGreen?GN:isWarn?RD:MU,marginBottom:4}}>{dayLabel(date)}</div>
                        <div style={{fontSize:11,color:MU,marginBottom:8}}>{fmtDate(date)}</div>
                        <div style={{fontSize:20,fontWeight:700,color:isGreen?GN:isWarn?RD:TX}}>{stat.confirmedMeetings}</div>
                        <div style={{fontSize:10,color:MU}}>confirmed</div>
                        <div style={{fontSize:11,color:MU,marginTop:6,borderTop:'1px solid '+(isGreen?GN+'44':'#E5E7EB'),paddingTop:6}}>{stat.calls} calls · {stat.meetings} mtgs</div>
                      </div>;
                    })}
                  </div>
                </div>

                {/* Target companies */}
                {v.targetCompanies?.length>0&&<div style={{...s.card,marginBottom:16}}>
                  <div style={{fontSize:14,fontWeight:700,marginBottom:14,letterSpacing:'-0.02em'}}>Target Companies <span style={{fontSize:12,color:MU,fontWeight:400}}>({v.targetCompanies.length})</span></div>
                  {[
                    {key:'high', label:'High Priority', dot:RD, bg:'#FEF2F2', border:'#FECACA', headerBg:'#FEE2E2', headerColor:RD},
                    {key:'medium', label:'Medium Priority', dot:YL, bg:'#FFFBEB', border:'#FDE68A', headerBg:'#FEF3C7', headerColor:'#D97706'},
                    {key:'low', label:'Low Priority', dot:GN, bg:GL, border:'#BBF7D0', headerBg:'#DCFCE7', headerColor:'#15803D'},
                  ].map(({key,label,dot,bg,border,headerBg,headerColor})=>{
                    const group=v.targetCompanies.filter(tc=>tc.priority===key);
                    if(!group.length) return null;
                    return <div key={key} style={{marginBottom:12,borderRadius:12,border:'1px solid '+border,overflow:'hidden'}}>
                      <div style={{background:headerBg,padding:'6px 14px',display:'flex',alignItems:'center',gap:8}}>
                        <div style={{width:8,height:8,borderRadius:'50%',background:dot}}/>
                        <span style={{fontSize:11,fontWeight:700,color:headerColor,textTransform:'uppercase',letterSpacing:'.5px'}}>{label}</span>
                        <span style={{fontSize:11,color:headerColor,opacity:.7}}>({group.length})</span>
                      </div>
                      <div style={{display:'flex',flexDirection:'column',gap:0}}>
                        {group.map((tc,i)=>{
                          const coMeetings=d.meetings.filter(m=>String(m.companyId)===String(tc.companyId));
                          const coCalls=d.calls.filter(c=>String(c.companyId)===String(tc.companyId));
                          const hasMeeting=coMeetings.length>0;
                          const tcSpoc=spocs.find(s=>String(s.companyId?._id||s.companyId)===String(tc.companyId));
                          return <div key={tc._id} style={{display:'flex',alignItems:'center',gap:12,padding:'10px 14px',background:hasMeeting?GL:bg,borderTop:i>0?'1px solid '+border:'none'}}>
                            <div style={{flex:1}}>
                              <div style={{fontWeight:600,fontSize:13}}>{tc.companyName}</div>
                              {tc.notes&&<div style={{fontSize:11,color:MU,marginTop:1}}>{tc.notes}</div>}
                            </div>
                            <div style={{display:'flex',gap:6,alignItems:'center',flexShrink:0}}>
                              {coCalls.length>0&&<span style={{fontSize:11,color:MU}}>{coCalls.length} call{coCalls.length!==1?'s':''}</span>}
                              {hasMeeting
                                ?<span style={{...s.tag(GL,GN),fontSize:10}}>Meeting set ✓</span>
                                :<span style={{...s.tag('#F3F4F6',MU),fontSize:10}}>No meeting yet</span>}
                              <motion.button whileHover={{scale:1.02}} whileTap={{scale:0.98}} style={{...s.btn('s'),padding:'3px 9px',fontSize:11}} onClick={()=>{if(tcSpoc)openCallForm(tcSpoc);else{setDetailId(tc.companyId);setPage('companies');}}}>📞 Call</motion.button>
                              <motion.button whileHover={{scale:1.02}} whileTap={{scale:0.98}} style={{...s.btn('s'),padding:'3px 9px',fontSize:11}} onClick={()=>{setDetailId(tc.companyId);openMeetingForm();}}>📅 Meeting</motion.button>
                            </div>
                          </div>;
                        })}
                      </div>
                    </div>;
                  })}
                </div>}

                {/* Meetings list */}
                {d.meetings.length>0&&<div style={{...s.card,marginBottom:16}}>
                  <div style={{fontSize:14,fontWeight:700,marginBottom:12,letterSpacing:'-0.02em'}}>Meetings During This Visit <span style={{fontSize:12,color:MU,fontWeight:400}}>({d.meetings.length})</span></div>
                  {d.meetings.sort((a,b)=>(a.date||'').localeCompare(b.date||'')).map(m=>{
                    const co=companies.find(c=>String(c._id)===String(m.companyId));
                    return <div key={m._id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 0',borderBottom:'1px solid #F3F4F6'}}>
                      <div>
                        <div style={{fontWeight:600,fontSize:13}}>{co?.name||'—'} <Tag label={m.stage}/></div>
                        <div style={{fontSize:12,color:MU,marginTop:2}}>{fmtDate(m.date)}{m.agenda?` · ${m.agenda}`:''}</div>
                      </div>
                      <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn('s'),padding:'4px 10px',fontSize:11}} onClick={()=>{setDetailId(m.companyId);setDTab('timeline');}}>Trail</motion.button>
                    </div>;
                  })}
                </div>}

                {/* Probable Clients List */}
                <div style={{...s.card,marginBottom:16}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
                    <div style={{fontSize:14,fontWeight:700,letterSpacing:'-0.02em'}}>Probable Clients / Leads Met <span style={{fontSize:12,color:MU,fontWeight:400}}>({(v.probableClients||[]).length})</span></div>
                    <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn('s'),padding:'4px 10px',fontSize:11}} onClick={()=>{setForm({name:'',companyName:'',meetingTrail:'',remarks:''});setModal({type:'probableClient'});}}>+ Add Client</motion.button>
                  </div>
                  {!(v.probableClients?.length) && <div style={{fontSize:13,color:MU,padding:'10px 0'}}>No probable clients recorded for this visit yet.</div>}
                  {(v.probableClients||[]).map((pc, idx) => (
                    <div key={idx} style={{padding:'12px 0',borderBottom:idx<v.probableClients.length-1?'1px solid #F3F4F6':'none'}}>
                      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                        <div>
                          <div style={{fontWeight:700,fontSize:14,color:TX}}>{pc.name} <span style={{fontWeight:500,fontSize:13,color:MU}}>{pc.companyName?`@ ${pc.companyName}`:''}</span></div>
                          {pc.meetingTrail && <div style={{fontSize:13,color:TX,marginTop:4}}><b>Meeting Trail:</b> {pc.meetingTrail}</div>}
                          {pc.remarks && <div style={{fontSize:12,color:MU,marginTop:4}}><b>Remarks:</b> {pc.remarks}</div>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {v.notes&&<div style={{...s.card,marginBottom:16}}><div style={{fontSize:12,fontWeight:600,color:MU,marginBottom:6}}>Visit Notes</div><div style={{fontSize:13,color:TX}}>{v.notes}</div></div>}
              </>}
            </>;
          }

          // Visit list view
          const openVisitDetail = async (v) => {
            setVisitDetail(v);
            setVisitDetailLoading(true);
            try { const d = await api.getVisit(v._id); setVisitDetailData(d); }
            catch(e) { setVisitDetailData(null); }
            setVisitDetailLoading(false);
          };

          return <>
            <div className="page-header-row" style={{alignItems:'center',marginBottom:20}}>
              <div>
                <h1 style={{fontSize:26,fontWeight:700,margin:'0 0 2px',letterSpacing:'-0.03em'}}>City Visits</h1>
                <div style={{fontSize:13,color:MU}}>{visits.length} visit{visits.length!==1?'s':''} · Plan and track your management city trips</div>
              </div>
              {isAdmin&&<motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={s.btn()} onClick={()=>{setForm({visitFrom:today(),visitTo:today(),status:'planning',targetCompanies:[]});setModal({type:'visit'});}}>+ Plan Visit</motion.button>}
            </div>

            {!visits.length&&<div style={{...s.card,textAlign:'center',padding:48}}>
              <div style={{fontSize:32,marginBottom:12}}>✈️</div>
              <div style={{fontSize:15,fontWeight:600,color:TX,marginBottom:6}}>No city visits planned yet</div>
              <div style={{fontSize:13,color:LT,marginBottom:20}}>Create a visit to track calls and meetings your team lines up for your trip</div>
              {isAdmin&&<motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={s.btn()} onClick={()=>{setForm({visitFrom:today(),visitTo:today(),status:'planning',targetCompanies:[]});setModal({type:'visit'});}}>+ Plan Your First Visit</motion.button>}
            </div>}

            <div style={{display:'flex',flexDirection:'column',gap:12}}>
              {[...visits].filter(v=>isAdmin||!v.assignedTo?.length||v.assignedTo.includes(user)).sort((a,b)=>(b.visitFrom||'').localeCompare(a.visitFrom||'')).map(v=>{
                const totalDays=diffDays(v.visitFrom,v.visitTo);
                const isPast=v.visitTo<today();
                const isActive=v.visitFrom<=today()&&v.visitTo>=today();
                return <div key={v._id} style={{...s.card,padding:0,overflow:'hidden',cursor:'pointer',borderLeft:'4px solid '+(isActive?GN:isPast?'#D1D5DB':'#6B7280')}} onClick={()=>openVisitDetail(v)}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'16px 20px'}}>
                    <div style={{flex:1}}>
                      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
                        <span style={{fontSize:15,fontWeight:700}}>{v.title}</span>
                        <span style={{...s.tag(VSTATUS_BG[v.status],VSTATUS_COLOR[v.status]),fontSize:11}}>{v.status}</span>
                        {isActive&&<span style={{...s.tag(GL,GN),fontSize:11,fontWeight:700}}>● Active now</span>}
                      </div>
                      <div style={{fontSize:12,color:MU,display:'flex',gap:14,flexWrap:'wrap'}}>
                        <span>📍 {v.city}</span>
                        <span>📅 {fmtDate(v.visitFrom)} – {fmtDate(v.visitTo)} ({totalDays}d)</span>
                        {v.assignedTo?.length>0&&<span>👤 {v.assignedTo.join(', ')}</span>}
                        {v.targetMeetings>0&&<span>🎯 Target: {v.targetMeetings} meetings</span>}
                      </div>
                      {v.targetCompanies?.length>0&&<div style={{fontSize:11,color:MU,marginTop:4}}>{v.targetCompanies.length} target companies</div>}
                    </div>
                    <div style={{display:'flex',gap:8,alignItems:'center',flexShrink:0}}>
                      <span style={{fontSize:11,color:MU}}>View →</span>
                      {isAdmin&&<DeleteIconBtn size={26} stopPropagation onClick={()=>setConfirmDel({fn:async()=>{await api.deleteVisit(v._id);api.getVisits().then(setVisits);},msg:'Delete this visit?'})}/>}
                    </div>
                  </div>
                </div>;
              })}
            </div>
          </>;
        })()}

        {/* ═══ EOD REPORT ═══ */}
        {page==='eod' && !detailId && (()=>{
          const todayStr = today();
          const isToday = eodDate === todayStr;
          const p = eodPreview || {};
          // Admin's own activity here is empty; show the team total (sum of all reps) instead.
          const teamTotals = (isAdmin && eodTeam?.members?.length) ? eodTeam.members.reduce((t,m)=>{
            const a = m.snapshot || m.activity || {};
            t.callsTotal+=a.callsTotal||0; t.callsConnected+=a.callsConnected||0; t.callsNotConnected+=a.callsNotConnected||0;
            t.meetingsTotal+=a.meetingsTotal||0; t.walkins+=a.walkins||0; t.siteVisits+=a.siteVisits||0;
            t.siteVisitsTotal+=a.siteVisitsTotal||0; t.followupsScheduled+=a.followupsScheduled||0; t.companiesAdded+=a.companiesAdded||0;
            Object.entries(a.outcomes||{}).forEach(([k,v])=>{ t.outcomes[k]=(t.outcomes[k]||0)+v; });
            return t;
          }, {callsTotal:0,callsConnected:0,callsNotConnected:0,meetingsTotal:0,walkins:0,siteVisits:0,siteVisitsTotal:0,followupsScheduled:0,companiesAdded:0,outcomes:{}}) : null;
          const act = teamTotals || p; // tiles reflect team totals for admin, personal preview otherwise
          const outcomeEntries = Object.entries(act.outcomes||{});
          const alreadySubmitted = eodReports.find(r => r.date === eodDate && r.salesperson === user);
          const showSubmitted = (eodSubmitted || alreadySubmitted) && !eodEditing;

          const reloadEodReports = async () => {
            const params = isAdmin ? {} : { salesperson: user };
            const reports = await api.getEodReports(params).catch(() => []);
            setEodReports((reports || []).sort((a, b) => (b.date || '').localeCompare(a.date || '') || (a.salesperson || '').localeCompare(b.salesperson || '')));
          };

          const submitEod = async () => {
            setEodSubmitting(true);
            setEodApiError('');
            try {
              const snapshot = {
                callsTotal: p.callsTotal||0, callsConnected: p.callsConnected||0,
                callsNotConnected: p.callsNotConnected||0, meetingsTotal: p.meetingsTotal||0,
                walkins: p.walkins||0, siteVisits: p.siteVisits||0, siteVisitsTotal: p.siteVisitsTotal||0,
                followupsScheduled: p.followupsScheduled||0,
                companiesAdded: p.companiesAdded||0, outcomes: p.outcomes||{}
              };
              const saved = await api.submitEod({ date: eodDate, salesperson: user, note: eodNote, snapshot });
              setEodSubmitted(true);
              setEodEditing(false);
              setEodApiError(
                saved?._localOnly
                  ? 'Saved on this device — EOD API is not on the server yet. Redeploy backend to sync team-wide.'
                  : !api.isEodApiAvailable()
                    ? 'Saved on this device — server EOD route unavailable.'
                    : ''
              );
              const params = isAdmin ? {} : { salesperson: user };
              const reports = await api.getEodReports(params).catch(() => []);
              setEodReports(prev => {
                const merged = [...reports];
                if (saved && !merged.some(r => r.date === saved.date && r.salesperson === saved.salesperson)) {
                  merged.unshift(saved);
                }
                return merged.sort((a, b) => (b.date || '').localeCompare(a.date || '') || (a.salesperson || '').localeCompare(b.salesperson || ''));
              });
            } catch(e) {
              console.error('EOD submission failed:', e);
              setEodApiError(e.message || 'Could not save EOD report');
            }
            finally { setEodSubmitting(false); }
          };

          return <>
            {/* Header */}
            <div className="page-header-row" style={{alignItems:'flex-start',marginBottom:28,flexWrap:'wrap',gap:12}}>
              <div>
                <h1 style={{fontSize:26,fontWeight:700,margin:'0 0 4px',letterSpacing:'-0.03em',color:TX}}>EOD Report</h1>
                <div style={{fontSize:13,color:LT,fontWeight:400}}>End-of-day summary · auto-pulled from your activity</div>
              </div>
              <div style={{display:'flex',gap:8,alignItems:'center'}}>
                <div style={{position:'relative',display:'inline-flex',alignItems:'center'}}>
                  <input type="date" max={todayStr} style={{...s.inp,width:140,padding:'8px 12px',color:'transparent',caretColor:'transparent'}} value={eodDate} onChange={e=>{setEodDate(e.target.value);setEodSubmitted(false);setEodEditing(false);setEodNote('');}}/>
                  <span style={{position:'absolute',left:12,pointerEvents:'none',fontSize:13,fontWeight:500,color:TX}}>{fmtDate(eodDate)}</span>
                </div>
                <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn('s'),padding:'8px 12px',fontSize:12}} onClick={()=>{setEodPreviewLoading(true);setEodApiError('');api.getEodPreview({date:eodDate,salesperson:user}).then(d=>{setEodPreview(d);setEodPreviewLoading(false);}).catch(async()=>{try{const local=await buildLocalEodPreview(eodDate,user);setEodPreview(local);}finally{setEodPreviewLoading(false);}});}}>↺ Refresh</motion.button>
              </div>
            </div>

            {eodApiError && (
              <div style={{
                ...s.card,marginBottom:16,fontSize:13,padding:'12px 16px',
                background: eodApiError.includes('this device only') ? '#FFFBEB' : '#FEF2F2',
                border: eodApiError.includes('this device only') ? '1px solid #FDE68A' : '1px solid #FECACA',
                color: eodApiError.includes('this device only') ? '#92400E' : '#B91C1C',
              }}>
                {eodApiError.includes('this device only') ? 'ℹ️' : '⚠️'} {eodApiError}
              </div>
            )}

            {/* Activity Summary */}
            <div style={{...s.card,marginBottom:16,border:'1px solid #E5E7EB',background:WH}}>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:18}}>
                <div style={{fontSize:13,fontWeight:600,color:TX,letterSpacing:'-0.01em'}}>{fmtDate(eodDate)} · {teamTotals?'Team Activity':'Activity'}{teamTotals?<span style={{fontSize:11,color:LT,fontWeight:500}}> · all reps combined</span>:''}</div>
                {alreadySubmitted && !eodSubmitted && !eodEditing && <span style={{fontSize:11,background:'#F0FDF4',color:'#16A34A',padding:'3px 10px',borderRadius:6,fontWeight:600,border:'1px solid #BBF7D0'}}>✓ Submitted</span>}
              </div>
              {eodPreviewLoading ? <div style={{textAlign:'center',padding:28,color:LT,fontSize:13}}>Loading…</div> :
              <motion.div variants={listContainer} initial="hidden" animate="show" style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(110px,1fr))',gap:1,background:'#F3F4F6',borderRadius:10,overflow:'hidden',border:'1px solid #E5E7EB'}}>
                {[
                  {label:'Calls Made',value:act.callsTotal||0},
                  {label:'Connected',value:act.callsConnected||0},
                  {label:'Not Connected',value:act.callsNotConnected||0},
                  {label:'Meetings',value:act.meetingsTotal||0},
                  {label:'Walk-ins · Met contact',value:act.walkins||0},
                  {label:'Walk-ins · No contact',value:act.siteVisits||0},
                  {label:'Site walk-ins total',value:act.siteVisitsTotal||0},
                  {label:'Follow-ups Set',value:act.followupsScheduled||0},
                  {label:'Companies Added',value:act.companiesAdded||0},
                ].map(({label,value})=>
                  <motion.div key={label} variants={listItem} style={{background:WH,padding:'16px 12px',textAlign:'center'}}>
                    <div style={{fontSize:24,fontWeight:700,color:TX,letterSpacing:'-0.04em',lineHeight:1}}>{value}</div>
                    <div style={{fontSize:10,color:LT,fontWeight:500,marginTop:5,lineHeight:1.3}}>{label}</div>
                  </motion.div>
                )}
              </motion.div>}
              {outcomeEntries.length>0&&<div style={{marginTop:16,paddingTop:16,borderTop:'1px solid #F3F4F6'}}>
                <div style={{fontSize:11,fontWeight:600,color:LT,marginBottom:8,textTransform:'uppercase',letterSpacing:'.6px'}}>Call Outcomes</div>
                <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
                  {outcomeEntries.map(([k,v])=><span key={k} style={{padding:'4px 12px',borderRadius:6,background:'#F9FAFB',border:'1px solid #E5E7EB',fontSize:12,color:TX,fontWeight:500}}>{k} · <strong>{v}</strong></span>)}
                </div>
              </div>}
            </div>

            {/* Added today — SPOCs (by company) or new companies, created on this date */}
            {(()=>{
              const isCoView = eodAddedView==='companies';
              // SPOCs added, grouped by company
              const added = spocs.filter(sp => (sp.createdAt||'').slice(0,10) === eodDate);
              const byCo = {};
              added.forEach(sp=>{
                const coId = String(sp.companyId?._id || sp.companyId || '');
                const co = companyById[coId];
                const key = coId || `__${sp.name||'unknown'}`;
                if(!byCo[key]) byCo[key] = { name: co?.name || 'Unknown company', city: co?.city || '', count:0 };
                byCo[key].count++;
              });
              const groups = Object.values(byCo).sort((a,b)=>b.count-a.count || a.name.localeCompare(b.name));
              // Companies added this day
              const cosAdded = companies.filter(c => (c.createdAt||'').slice(0,10) === eodDate)
                .slice().sort((a,b)=>(a.name||'').localeCompare(b.name||''));
              const total = isCoView ? cosAdded.length : added.length;
              return <div style={{...s.card,marginBottom:16,border:'1px solid #E5E7EB',background:WH}}>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16,flexWrap:'wrap',gap:12}}>
                  <div>
                    <div style={{fontSize:13,fontWeight:600,color:TX,letterSpacing:'-0.01em'}}>{fmtDate(eodDate)} · {isCoView?'Companies Added':'SPOCs Added'}</div>
                    <div style={{fontSize:12,color:LT,marginTop:2}}>{isCoView?'New companies inserted this day':'New contacts inserted this day, by company'}</div>
                  </div>
                  <div style={{display:'flex',alignItems:'center',gap:14}}>
                    <Dropdown style={{...s.sel,width:160,fontSize:12}} value={eodAddedView} onChange={e=>setEodAddedView(e.target.value)}>
                      <option value="spocs">SPOCs added</option>
                      <option value="companies">New companies</option>
                    </Dropdown>
                    <div style={{display:'flex',alignItems:'baseline',gap:6}}>
                      <span style={{fontSize:26,fontWeight:700,color:TX,letterSpacing:'-0.04em',lineHeight:1}}>{total}</span>
                      <span style={{fontSize:11,color:LT,fontWeight:500}}>{isCoView?`compan${total===1?'y':'ies'}`:`total · ${groups.length} compan${groups.length===1?'y':'ies'}`}</span>
                    </div>
                  </div>
                </div>
                {isCoView
                  ? (cosAdded.length===0
                      ? <div style={{textAlign:'center',padding:24,color:LT,fontSize:13}}>No new companies added on this day</div>
                      : <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(230px,1fr))',gap:10,maxHeight:340,overflowY:'auto'}}>
                          {cosAdded.map(c=>(
                            <div key={c._id} style={{display:'flex',alignItems:'center',gap:12,border:'1px solid #EEF0F2',borderRadius:10,padding:'10px 12px',background:'#FAFBFC'}}>
                              <div style={{width:38,height:38,borderRadius:10,background:'#F0FDF4',color:'#16A34A',display:'flex',alignItems:'center',justifyContent:'center',fontSize:16,flexShrink:0}}>🏢</div>
                              <div style={{minWidth:0}}>
                                <div style={{fontSize:13,fontWeight:600,color:TX,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{c.name}</div>
                                <div style={{fontSize:11,color:LT}}>{c.city||'—'}{c.salesperson?` · ${c.salesperson}`:''}</div>
                              </div>
                            </div>
                          ))}
                        </div>)
                  : (groups.length===0
                      ? <div style={{textAlign:'center',padding:24,color:LT,fontSize:13}}>No SPOCs added on this day</div>
                      : <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(230px,1fr))',gap:10,maxHeight:340,overflowY:'auto'}}>
                          {groups.map((g,i)=>(
                            <div key={i} style={{display:'flex',alignItems:'center',gap:12,border:'1px solid #EEF0F2',borderRadius:10,padding:'10px 12px',background:'#FAFBFC'}}>
                              <div style={{width:38,height:38,borderRadius:10,background:'#EEF2FF',color:'#4338CA',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:16,flexShrink:0}}>{g.count}</div>
                              <div style={{minWidth:0}}>
                                <div style={{fontSize:13,fontWeight:600,color:TX,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{g.name}</div>
                                <div style={{fontSize:11,color:LT}}>{g.city||'—'} · {g.count} SPOC{g.count!==1?'s':''}</div>
                              </div>
                            </div>
                          ))}
                        </div>)}
              </div>;
            })()}

            {/* EOD Note + Submit — hidden for admin (admin sees the team view, not a personal EOD) */}
            {!isAdmin && <div style={{...s.card,marginBottom:16,border:'1px solid #E5E7EB',background:WH}}>
              <div style={{fontSize:13,fontWeight:600,color:TX,marginBottom:4}}>Note</div>
              <div style={{fontSize:12,color:LT,marginBottom:12}}>Wins, blockers, plans for tomorrow — anything worth logging</div>
              {showSubmitted ?
                <div style={{background:'#F0FDF4',border:'1px solid #BBF7D0',borderRadius:10,padding:'14px 18px'}}>
                  <div style={{display:'flex',alignItems:'flex-start',gap:12}}>
                    <div style={{width:32,height:32,borderRadius:'50%',background:'#16A34A',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:14,flexShrink:0}}>✓</div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:13,fontWeight:600,color:'#15803D'}}>EOD submitted for {fmtDate(eodDate)}</div>
                      <div style={{fontSize:12,color:'#16A34A',marginTop:2,fontWeight:400}}>Report saved. Check history below.</div>
                      {(alreadySubmitted?.note || eodNote) && (
                        <div style={{fontSize:12,color:'#166534',marginTop:10,lineHeight:1.6,whiteSpace:'pre-wrap',borderLeft:'2px solid #BBF7D0',paddingLeft:10}}>{alreadySubmitted?.note || eodNote}</div>
                      )}
                    </div>
                  </div>
                  {!eodSubmitted && alreadySubmitted && (
                    <div style={{display:'flex',justifyContent:'flex-end',marginTop:12}}>
                      <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn('s'),padding:'6px 14px',fontSize:12}} onClick={()=>{setEodEditing(true);setEodNote(alreadySubmitted.note||'– ');}}>Edit & resubmit</motion.button>
                    </div>
                  )}
                </div>
              : <>
                <textarea
                  style={{...s.inp,minHeight:100,width:'100%',resize:'vertical',fontSize:13,lineHeight:1.8,fontFamily:'inherit'}}
                  value={eodNote}
                  onChange={e=>setEodNote(e.target.value)}
                  onKeyDown={e=>{
                    if(e.key==='Enter'){
                      e.preventDefault();
                      const ta=e.target;
                      const start=ta.selectionStart, end=ta.selectionEnd;
                      const val=ta.value;
                      const newVal=val.substring(0,start)+'\n– '+val.substring(end);
                      setEodNote(newVal);
                      setTimeout(()=>{ta.selectionStart=ta.selectionEnd=start+3;},0);
                    }
                  }}
                  onFocus={e=>{if(!eodNote){setEodNote('– ');}}}
                  placeholder={'– Met DLF team, asked for proposal\n– Rescheduled Godrej to next week\n– Plan to close 2 follow-ups tomorrow'}
                />
                <div style={{display:'flex',justifyContent:'flex-end',marginTop:12}}>
                  <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} disabled={eodSubmitting} style={{padding:'9px 24px',borderRadius:9,border:'none',background:TX,color:WH,cursor:'pointer',fontSize:13,fontWeight:600,letterSpacing:'-0.01em',opacity:eodSubmitting?0.5:1}} onClick={submitEod}>
                    {eodSubmitting ? 'Saving…' : 'Submit EOD'}
                  </motion.button>
                </div>
              </>}
            </div>}

            {/* Team status (admin) — who submitted EOD for this date */}
            {isAdmin && (eodTeamLoading || eodTeam) && <div style={{...s.card,marginBottom:16,border:'1px solid #E5E7EB',background:WH}}>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16,flexWrap:'wrap',gap:8}}>
                <div style={{fontSize:13,fontWeight:600,color:TX}}>{fmtDate(eodDate)} · Team EOD</div>
                {eodTeam && <span style={{fontSize:11,color:LT,fontWeight:500}}>{eodTeam.submitted} of {eodTeam.teamSize} submitted{eodTeam.missingNotes?` · ${eodTeam.missingNotes} missing note`:''}</span>}
              </div>
              {eodTeamLoading ? <div style={{textAlign:'center',padding:24,color:LT,fontSize:13}}>Loading…</div> :
              !eodTeam?.members?.length ? <div style={{textAlign:'center',padding:24,color:LT,fontSize:13}}>No team members configured</div> :
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))',gap:10}}>
                {eodTeam.members.map(m=>{
                  const a=m.snapshot||m.activity||{};
                  return <div key={m.salesperson} style={{border:'1px solid #E5E7EB',borderRadius:10,padding:'12px 14px',background:m.eodSubmitted?'#F0FDF4':'#FFFBEB'}}>
                    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:8,marginBottom:8}}>
                      <div style={{fontSize:13,fontWeight:600,color:TX}}>{m.salesperson}</div>
                      <span style={{fontSize:10,fontWeight:600,padding:'3px 8px',borderRadius:6,border:'1px solid',...(m.eodSubmitted?{background:'#F0FDF4',color:'#16A34A',borderColor:'#BBF7D0'}:{background:'#FEF2F2',color:'#B91C1C',borderColor:'#FECACA'})}}>{m.eodSubmitted?'✓ Submitted':'Not submitted'}</span>
                    </div>
                    <div style={{fontSize:11,color:MU,fontWeight:500}}>{a.callsTotal||0} calls · {a.callsConnected||0} conn · {a.meetingsTotal||0} mtgs · {a.siteVisitsTotal||0} walk-ins · {a.followupsScheduled||0} f/u</div>
                    {m.eodSubmitted && (m.note ? <div style={{fontSize:11,color:LT,marginTop:6,whiteSpace:'pre-wrap'}}>{m.note}</div> : <div style={{fontSize:11,color:'#B45309',marginTop:6,fontWeight:500}}>Note missing</div>)}
                  </div>;
                })}
              </div>}
            </div>}

            {/* History */}
            <div style={{...s.card,border:'1px solid #E5E7EB',background:WH}}>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16,flexWrap:'wrap',gap:8}}>
                <div style={{fontSize:13,fontWeight:600,color:TX}}>History</div>
                {isAdmin && <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
                  <div style={s.tab(eodHistoryPerson==='all')} onClick={()=>setEodHistoryPerson('all')}>All</div>
                  {(config.salespersons||[]).map(sp=><div key={sp} style={s.tab(eodHistoryPerson===sp)} onClick={()=>setEodHistoryPerson(sp)}>{sp}</div>)}
                </div>}
              </div>
              {eodHistoryLoading ? <div style={{textAlign:'center',padding:24,color:LT,fontSize:13}}>Loading…</div> :
              (() => {
                const filtered = eodReports.filter(r => eodHistoryPerson==='all' || r.salesperson===eodHistoryPerson);
                if (!filtered.length) return <div style={{textAlign:'center',padding:36,color:LT}}>
                  <div style={{fontSize:13,fontWeight:500,color:MU}}>No reports yet</div>
                  <div style={{fontSize:12,marginTop:4}}>Submitted reports will appear here</div>
                </div>;
                return <div style={{display:'flex',flexDirection:'column',gap:12}}>
                  {filtered.map((r) => {
                    const sn = r.snapshot||{};
                    const canManage = isAdmin || r.salesperson === user;   // own report, or admin
                    const rowEditing = eodEditId === r._id;
                    const stats = [['Calls',sn.callsTotal],['Connected',sn.callsConnected],['Meetings',sn.meetingsTotal],['Walk-ins · Met',sn.walkins],['Walk-ins · No contact',sn.siteVisits],['Site walk-ins total',sn.siteVisitsTotal],['Follow-ups',sn.followupsScheduled]].filter(([,v])=>v>0);
                    return <motion.div key={r._id} whileHover={{y:-1}} style={{border:'1px solid #E5E7EB',borderRadius:12,padding:'14px 16px',background:WH,boxShadow:'0 1px 2px rgba(0,0,0,0.03)'}}>
                      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:stats.length||r.note||rowEditing?12:0,flexWrap:'wrap'}}>
                        <div style={{fontSize:14,fontWeight:700,color:TX,letterSpacing:'-0.01em'}}>{fmtDate(r.date)}</div>
                        <span style={{fontSize:11,color:MU,background:'#F3F4F6',padding:'2px 9px',borderRadius:6,fontWeight:600}}>{r.salesperson}</span>
                        {r._localOnly&&<span style={{fontSize:10,color:'#92400E',background:'#FFFBEB',padding:'2px 7px',borderRadius:5,fontWeight:600,border:'1px solid #FDE68A'}}>This device</span>}
                        {r.date===todayStr&&<span style={{fontSize:10,color:'#16A34A',background:'#F0FDF4',padding:'2px 7px',borderRadius:5,fontWeight:600,border:'1px solid #BBF7D0'}}>Today</span>}
                        {canManage&&!rowEditing&&<div style={{marginLeft:'auto',display:'flex',gap:6}}>
                          <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn('s'),padding:'3px 10px',fontSize:11}} onClick={()=>{setEodEditId(r._id);setEodEditNote(r.note||'– ');}}>Edit</motion.button>
                          <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn('r'),padding:'3px 10px',fontSize:11}} onClick={()=>setConfirmDel({title:'Delete EOD Report',msg:`Delete the EOD report for ${r.salesperson} on ${fmtDate(r.date)}? This cannot be undone.`,confirmLabel:'Delete',fn:async()=>{try{await api.deleteEod(r._id);if(r.date===eodDate&&r.salesperson===user){setEodSubmitted(false);setEodEditing(false);setEodNote('');}await reloadEodReports();}catch(e){alert(e.message);}}})}>Delete</motion.button>
                        </div>}
                      </div>
                      {stats.length>0&&<div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:(r.note||rowEditing)?12:0}}>
                        {stats.map(([lbl,val])=>
                          <div key={lbl} style={{background:'#F9FAFB',border:'1px solid #EEF0F2',borderRadius:8,padding:'5px 11px',fontSize:12,color:MU}}><strong style={{color:TX,fontWeight:700,fontSize:13}}>{val}</strong> {lbl}</div>
                        )}
                      </div>}
                      {rowEditing
                        ? <div>
                            <textarea style={{...s.inp,minHeight:90,resize:'vertical',fontSize:12,lineHeight:1.7,fontFamily:'inherit'}} value={eodEditNote} onChange={e=>setEodEditNote(e.target.value)} onKeyDown={e=>{if(e.key==='Enter'){e.preventDefault();const ta=e.target;const st=ta.selectionStart,en=ta.selectionEnd;setEodEditNote(ta.value.substring(0,st)+'\n– '+ta.value.substring(en));setTimeout(()=>{ta.selectionStart=ta.selectionEnd=st+3;},0);}}} placeholder={'– Met DLF team, asked for proposal'}/>
                            <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:8}}>
                              <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn('s'),padding:'5px 12px',fontSize:12}} onClick={()=>setEodEditId(null)}>Cancel</motion.button>
                              <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn(),padding:'5px 12px',fontSize:12,opacity:eodRowSaving?.6:1}} disabled={eodRowSaving} onClick={async()=>{setEodRowSaving(true);try{await api.submitEod({date:r.date,salesperson:r.salesperson,note:eodEditNote,snapshot:r.snapshot||{}});setEodEditId(null);await reloadEodReports();}catch(e){alert(e.message);}finally{setEodRowSaving(false);}}}>{eodRowSaving?'Saving…':'Save'}</motion.button>
                            </div>
                          </div>
                        : r.note&&<div style={{fontSize:12.5,color:'#4B5563',lineHeight:1.65,background:'#F9FAFB',border:'1px solid #F0F1F3',borderLeft:'3px solid #D1D5DB',borderRadius:8,padding:'10px 12px',whiteSpace:'pre-wrap'}}>{r.note}</div>}
                    </motion.div>;
                  })}
                </div>;
              })()}
            </div>
          </>;
        })()}

        {/* ═══ APPROVALS ═══ */}
        {page==='approvals' && isAdmin && <>
          <div className="page-header-row" style={{alignItems:'center',marginBottom:20}}>
            <div>
              <h1 style={{fontSize:26,fontWeight:700,margin:0,letterSpacing:'-0.03em'}}>Approvals</h1>
              <div style={{fontSize:13,color:MU,marginTop:2}}>Review and action pending drop requests from your team</div>
            </div>
            <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={s.btn('s')} onClick={()=>api.getPendingDrops().then(setPendingDrops)}>🔄 Refresh</motion.button>
          </div>

          {!pendingDrops.length&&<div style={{...s.card,textAlign:'center',padding:48}}>
            <div style={{fontSize:32,marginBottom:12}}>✅</div>
            <div style={{fontSize:15,fontWeight:600,color:TX,marginBottom:6}}>No pending approvals</div>
            <div style={{fontSize:13,color:LT}}>All drop requests have been actioned</div>
          </div>}

          {pendingDrops.length>0&&<div style={{display:'flex',flexDirection:'column',gap:12}}>
            {pendingDrops.map(co=>(
              <div key={co._id} style={{...s.card,borderLeft:'4px solid '+YL,padding:20}}>
                <div style={{display:'flex',alignItems:'flex-start',gap:16}}>
                  <div style={{flex:1}}>
                    <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:6}}>
                      <span style={{fontSize:16,fontWeight:700,color:TX}}>{co.name}</span>
                      <span style={{...s.tag('#FEF3C7','#D97706'),fontSize:11}}>⏳ Pending Drop</span>
                    </div>
                    <div style={{fontSize:12,color:MU,marginBottom:8}}>📍 {co.city} · Assigned to {co.salesperson||'—'}</div>
                    <div style={{background:'#FEF9EE',border:'1px solid #FDE68A',borderRadius:10,padding:'10px 14px',marginBottom:8}}>
                      <div style={{fontSize:11,fontWeight:600,color:'#92400E',marginBottom:4,textTransform:'uppercase',letterSpacing:'.5px'}}>Drop Reason</div>
                      <div style={{fontSize:13,color:TX}}>{co.dropRequestReason||'—'}</div>
                    </div>
                    <div style={{display:'flex',gap:16,fontSize:12,color:MU}}>
                      <span>👤 Requested by <strong>{co.dropRequestBy||'—'}</strong></span>
                      <span>📅 On {fmtDate(co.dropRequestDate)}</span>
                      {co.dropRequestReconnectDate&&<span>🔁 Reconnect after {fmtDate(co.dropRequestReconnectDate)}</span>}
                    </div>
                  </div>
                  <div style={{display:'flex',flexDirection:'column',gap:8,flexShrink:0,minWidth:130}}>
                    <motion.button whileHover={{scale:1.02}} whileTap={{scale:0.98}} style={{...s.btn(),background:GN,borderColor:GN,padding:'8px 20px'}} onClick={async()=>{
                      try{await api.approveDrop(co._id);await loadData();}catch(e){alert(e.message);}
                    }}>✅ Approve Drop</motion.button>
                    <motion.button whileHover={{scale:1.02}} whileTap={{scale:0.98}} style={{...s.btn('r'),padding:'8px 20px'}} onClick={async()=>{
                      try{await api.rejectDrop(co._id);await loadData();}catch(e){alert(e.message);}
                    }}>❌ Reject</motion.button>
                    <motion.button whileHover={{scale:1.02}} whileTap={{scale:0.98}} style={{...s.btn('s'),padding:'6px 20px',fontSize:12}} onClick={()=>{setDetailId(co._id);setPage('companies');}}>View Company →</motion.button>
                  </div>
                </div>
              </div>
            ))}
          </div>}
        </>}

        {/* ═══ DROPPED ═══ */}
        {page==='dropped' && !detailId && <>
          <h1 style={{fontSize:26,fontWeight:700,margin:'0 0 20px',letterSpacing:'-0.03em'}}>Dropped Leads <span style={{fontSize:18,color:MU,fontWeight:400}}>({dropped.length})</span></h1>
          {!dropped.length&&<div style={{textAlign:'center',color:LT,padding:40}}>No dropped companies</div>}
          <motion.div variants={listContainer} initial="hidden" animate="show" style={{display:'flex',flexDirection:'column',gap:12}}>
          {dropped.map((co,_di)=><motion.div key={co._id} variants={listItem} whileHover={{x:2}} style={{...s.card,borderLeft:'4px solid '+RD,marginBottom:0}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div><strong>{co.name}</strong> · {co.city}{co.previouslyTapped&&<span> · <Tag label="Previously Tapped"/></span>}<div style={{fontSize:12,color:MU,marginTop:3}}>Dropped: {fmtDate(co.dropDate)} · Reason: {co.dropReason||'—'} · Reconnect: {co.reconnectDate?fmtDate(co.reconnectDate):'Not set'}</div></div>
              <div style={{display:'flex',gap:6}}>
                <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={s.btn()} onClick={async()=>{try{await api.reactivateCompany(co._id);loadData();}catch(e){alert(e.message);}}}>Reactivate</motion.button>
                {isAdmin&&<DeleteIconBtn onClick={()=>setConfirmDel({fn:async()=>{await api.deleteCompany(co._id);loadData();},msg:`Permanently delete "${co.name}"?`})}/>}
              </div>
            </div>
          </motion.div>)}
          </motion.div>
        </>
        }

        {/* ═══ TARGETS ═══ */}
        {page==='targets' && !detailId && (()=>{
          const range = targetPeriod === 'weekly' ? getTargetWeekRange(targetWeekOffset) : getTargetMonthRange(targetWeekOffset);
          const spList = isAdmin ? (config.allUsers?.filter(u => u.role !== 'Admin').map(u => u.username) || []) : [user === 'admin' ? '' : user].filter(Boolean);
          const enabledMetrics = getEnabledTargetMetrics(config, targetPeriod);

          const loadTargets = async () => {
            try {
              const t = await api.getTargets({ period: targetPeriod, startDate: range.start });
              setTargetsData(t);
              if (spList.length) {
                const actualsMap = await api.getTargetActualsBatch({ startDate: range.start, endDate: range.end, salespeople: spList.join(',') });
                setTargetsActuals(actualsMap);
              }
            } catch {}
          };

          const openTargetForm = (sp, tgt = {}, overrides = {}) => {
            setModal({ type: 'target' });
            setForm({
              salesperson: sp || '',
              isEdit: !!sp,
              period: overrides.period || targetPeriod,
              startDate: overrides.startDate || range.start,
              endDate: overrides.endDate || range.end,
              calls: tgt.calls || '',
              connected: tgt.connected || '',
              meetings: tgt.meetings || '',
              pipelineValue: tgt.pipelineValue || '',
              revenue: tgt.revenue || '',
              demos: tgt.demos || '',
            });
          };

          const calcOverallPct = (tgt, actual) => {
            const parts = enabledMetrics.map(m => {
              const t = Number(tgt[m.key]) || 0;
              if (t <= 0) return null;
              return Math.min((Number(actual[m.key]) || 0) / t, 1);
            }).filter(x => x !== null);
            return parts.length ? Math.round(parts.reduce((a, b) => a + b, 0) / parts.length * 100) : 0;
          };

          const loadHistory = async () => {
            setTargetHistoryLoading(true);
            try {
              const periods = await Promise.all(
                [...Array(12)].map(async (_, i) => {
                  const pastRange = targetPeriod === 'weekly' ? getTargetWeekRange(-(i + 1)) : getTargetMonthRange(-(i + 1));
                  const targets = await api.getTargets({ period: targetPeriod, startDate: pastRange.start });
                  return { period: pastRange, targets };
                })
              );
              const actualsPromises = [];
              for (const { period, targets } of periods) {
                for (const t of targets) {
                  actualsPromises.push(
                    api.getTargetActuals({ salesperson: t.salesperson, startDate: period.start, endDate: period.end })
                      .then(actual => ({ t, period, actual }))
                      .catch(() => ({ t, period, actual: null }))
                  );
                }
              }
              const results = await Promise.all(actualsPromises);
              const history = results.map(({ t, period, actual }) => ({
                ...t,
                actuals: actual,
                periodLabel: targetPeriod === 'weekly'
                  ? `${fmtDate(period.start)} – ${fmtDate(period.end)}`
                  : new Date(period.start).toLocaleString('default', { month: 'long', year: 'numeric' }),
                startDate: period.start,
                endDate: period.end,
              }));
              setTargetHistory(history);
            } catch {}
            setTargetHistoryLoading(false);
          };

          const showRevenue = enabledMetrics.some(m => m.key === 'revenue');
          let teamRevenueTarget = 0;
          let teamRevenueActual = 0;
          if (isAdmin && showRevenue) {
            spList.forEach(sp => {
              const target = targetsData.find(t => t.salesperson === sp && t.startDate === range.start);
              const actual = targetsActuals[sp] || {};
              teamRevenueTarget += Number(target?.targets?.revenue) || 0;
              teamRevenueActual += Number(actual.revenue) || 0;
            });
          }

          const periodTitle = targetPeriod === 'monthly' ? (range.label || getTargetMonthRange(targetWeekOffset).label) : `${fmtDate(range.start)} — ${fmtDate(range.end)}`;

          return <>
            <div className="targets-page-header">
              <div className="targets-page-header__title">
                <h1>Sales Targets</h1>
                <p>{isAdmin ? 'Set monthly goals and track team performance' : 'Your targets vs what you have achieved this period'}</p>
              </div>
              <div className="page-header-actions targets-toolbar">
                <div style={{ display: 'flex', background: '#EFEFEF', borderRadius: 10, padding: 3, gap: 2 }}>
                  {[{ k: 'current', l: 'Current' }, { k: 'history', l: 'History' }].map(({ k, l }) =>
                    <div key={k} style={{ padding: '6px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', background: targetView === k ? '#fff' : 'transparent', color: targetView === k ? TX : MU, boxShadow: targetView === k ? '0 1px 3px rgba(0,0,0,.10)' : 'none', transition: 'all .15s' }} onClick={() => { setTargetView(k); if (k === 'history') loadHistory(); }}>{l}</div>
                  )}
                </div>
                {targetView === 'current' && <>
                  {isAdmin && (
                    <div style={{ display: 'flex', background: '#EFEFEF', borderRadius: 10, padding: 3, gap: 2 }}>
                      {['monthly', 'weekly'].map(p => <div key={p} style={{ padding: '6px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', background: targetPeriod === p ? '#fff' : 'transparent', color: targetPeriod === p ? TX : MU, boxShadow: targetPeriod === p ? '0 1px 3px rgba(0,0,0,.10)' : 'none', textTransform: 'capitalize' }} onClick={() => { setTargetPeriod(p); setTargetWeekOffset(0); }}>{p}</div>)}
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: '4px 8px' }}>
                    <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} style={{ width: 28, height: 28, borderRadius: 7, border: 'none', background: '#F3F4F6', cursor: 'pointer', fontSize: 14, color: TX }} onClick={() => setTargetWeekOffset(o => o - 1)}>‹</motion.button>
                    <div style={{ fontSize: 12, fontWeight: 600, minWidth: 170, textAlign: 'center', color: TX }}>
                      {periodTitle}
                      {targetWeekOffset === 0 && <span style={{ marginLeft: 5, fontSize: 10, background: '#EEF2FF', color: '#6366F1', padding: '2px 7px', borderRadius: 20, fontWeight: 700 }}>Current</span>}
                    </div>
                    <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} style={{ width: 28, height: 28, borderRadius: 7, border: 'none', background: '#F3F4F6', cursor: 'pointer', fontSize: 14, color: TX }} onClick={() => setTargetWeekOffset(o => o + 1)} disabled={targetWeekOffset >= 0}>›</motion.button>
                  </div>
                  <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={{ ...s.btn('s'), padding: '7px 14px', fontSize: 12 }} onClick={loadTargets}>↻ Refresh</motion.button>
                  {isAdmin && <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={{ padding: '7px 16px', borderRadius: 9, border: 'none', background: '#10B981', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600 }} onClick={() => { const m = getTargetMonthRange(targetWeekOffset); openTargetForm('', {}, { period: 'monthly', startDate: m.start, endDate: m.end }); }}>+ Set Monthly Target</motion.button>}
                </>}
              </div>
            </div>

            {targetView === 'current' && isAdmin && showRevenue && targetPeriod === 'monthly' && (
              <div className="targets-revenue-banner">
                <div>
                  <div className="targets-revenue-banner__label">Team Revenue — {periodTitle}</div>
                  <div className="targets-revenue-banner__nums">
                    <span style={{ color: teamRevenueTarget > 0 && teamRevenueActual >= teamRevenueTarget ? GN : TX }}>{teamRevenueActual ? fmtINR(teamRevenueActual) : '₹0'}</span>
                    <span className="targets-revenue-banner__sep">achieved of</span>
                    <span>{teamRevenueTarget > 0 ? fmtINR(teamRevenueTarget) : '₹0'}</span>
                    <span className="targets-revenue-banner__sep">target</span>
                  </div>
                  {teamRevenueTarget <= 0 && (
                    <div style={{ fontSize: 12, color: '#047857', marginTop: 6 }}>No revenue targets set yet — use Set Target on each team member.</div>
                  )}
                </div>
                {teamRevenueTarget > 0 && (
                  <div className="targets-revenue-banner__pct" style={{ color: teamRevenueActual >= teamRevenueTarget ? GN : RD }}>
                    {Math.round(Math.min(teamRevenueActual / teamRevenueTarget * 100, 999))}%
                  </div>
                )}
              </div>
            )}

            {!enabledMetrics.length && targetView === 'current' && (
              <div style={{ background: '#fff', borderRadius: 18, border: '1px solid #E5E7EB', textAlign: 'center', padding: 48, color: MU }}>
                No target metrics enabled. {isAdmin ? 'Go to Settings → Targets to choose which metrics to track.' : 'Ask your admin to configure targets in Settings.'}
              </div>
            )}

            {targetView === 'current' && enabledMetrics.length > 0 ? (
              <motion.div variants={listContainer} initial="hidden" animate="show" className={'targets-grid' + (isAdmin ? ' is-admin' : ' targets-grid--employee')} style={{ gap: 16 }}>
                {spList.map(sp => {
                  const target = targetsData.find(t => t.salesperson === sp && t.startDate === range.start);
                  const actual = targetsActuals[sp] || { calls: 0, connected: 0, meetings: 0, pipelineValue: 0, revenue: 0, demos: 0 };
                  const tgt = target?.targets || { calls: 0, connected: 0, meetings: 0, pipelineValue: 0, revenue: 0, demos: 0 };
                  const overallPct = calcOverallPct(tgt, actual);
                  const laggingCount = enabledMetrics.filter(m => (Number(tgt[m.key]) || 0) > 0 && (Number(actual[m.key]) || 0) < Number(tgt[m.key])).length;
                  return (
                    <motion.div key={sp} variants={listItem} className="target-person-card">
                      <div className="target-person-card__head">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div className="target-person-card__avatar">{(sp || '?').slice(0, 2).toUpperCase()}</div>
                          <div>
                            <div className="target-person-card__name">{sp}</div>
                            <div className="target-person-card__meta">
                              {target ? (
                                <>
                                  <span style={{ color: overallPct >= 100 ? GN : overallPct >= 60 ? '#F59E0B' : RD, fontWeight: 700 }}>{overallPct}% overall</span>
                                  {laggingCount > 0 && <span style={{ color: RD }}> · {laggingCount} metric{laggingCount > 1 ? 's' : ''} behind</span>}
                                </>
                              ) : 'No target set for this period'}
                            </div>
                          </div>
                        </div>
                        {isAdmin && (
                          <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} className="target-person-card__edit" onClick={() => openTargetForm(sp, tgt)}>Set Target</motion.button>
                        )}
                      </div>
                      <div className="target-metrics-grid">
                        {enabledMetrics.map(def => (
                          <TargetMetricTile key={def.key} def={def} target={tgt[def.key]} actual={actual[def.key]} fmtINR={fmtINR} TX={TX} MU={MU} GN={GN} RD={RD} />
                        ))}
                      </div>
                    </motion.div>
                  );
                })}
              </motion.div>
            ) : targetView === 'history' ? (
              <div style={{ background: '#fff', borderRadius: 18, border: '1px solid #E5E7EB', overflow: 'hidden' }}>
                <div style={{ padding: '16px 20px', borderBottom: '1px solid #F3F4F6', background: '#FAFBFC', display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: TX }}>Target History — Last 12 {targetPeriod === 'monthly' ? 'months' : 'weeks'}</span>
                  {targetHistoryLoading && <span style={{ fontSize: 12, color: LT }}>Loading...</span>}
                </div>
                {targetHistoryLoading ? (
                  <div style={{ padding: 40, textAlign: 'center', color: LT }}>Loading history...</div>
                ) : targetHistory.length === 0 ? (
                  <div style={{ padding: 40, textAlign: 'center', color: MU }}>No historical targets found</div>
                ) : (
                  <div className="table-wrap">
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#F9FAFB' }}>
                          <th style={s.th}>Period</th>
                          <th style={s.th}>Salesperson</th>
                          {enabledMetrics.map(m => <th key={m.key} style={{ ...s.th, textAlign: m.isCurrency ? 'right' : 'center' }}>{m.label}</th>)}
                          <th style={{ ...s.th, textAlign: 'right' }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {targetHistory.filter(h => isAdmin || h.salesperson === user).sort((a, b) => new Date(b.startDate) - new Date(a.startDate)).map((h, i) => {
                          const tgt = h.targets || {};
                          const act = h.actuals || {};
                          const overallPct = calcOverallPct(tgt, act);
                          const achieved = overallPct >= 100;
                          return (
                            <tr key={h._id || i} style={{ borderBottom: '1px solid #F3F4F6', background: i % 2 === 0 ? '#fff' : '#FAFBFC' }}>
                              <td style={s.td}>{h.periodLabel}</td>
                              <td style={s.td}>{h.salesperson}</td>
                              {enabledMetrics.map(m => {
                                const t = Number(tgt[m.key]) || 0;
                                const a = Number(act[m.key]) || 0;
                                const ok = t > 0 && a >= t;
                                return (
                                  <td key={m.key} style={{ ...s.td, textAlign: m.isCurrency ? 'right' : 'center', fontSize: 12 }}>
                                    <span style={{ fontWeight: 600, color: ok ? GN : TX }}>{m.isCurrency ? fmtINR(a) : a}</span>
                                    <span style={{ color: LT }}> / {t > 0 ? (m.isCurrency ? fmtINR(t) : t) : '—'}</span>
                                  </td>
                                );
                              })}
                              <td style={{ ...s.td, textAlign: 'right' }}>
                                <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 99, background: achieved ? '#D1FAE5' : '#F3F4F6', color: achieved ? '#059669' : MU }}>{overallPct}%{achieved ? ' ✓' : ''}</span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ) : null}
            {!spList.length && targetView === 'current' && <div style={{ background: '#fff', borderRadius: 18, border: '1px solid #E5E7EB', textAlign: 'center', padding: 48, color: MU }}>No salespersons configured</div>}
          </>;
        })()}

        {/* ═══ ANALYTICS ═══ */}
        {page==='analytics' && !detailId && (()=>{
          const dateMin = anPeriod!=='all' ? new Date(Date.now()-parseInt(anPeriod)*864e5).toISOString().slice(0,10) : '';
          const fc = calls.filter(c=>{ if(anFilter!=='all'&&c.salesperson!==anFilter)return false; if(dateMin&&c.date<dateMin)return false; if(!matchesAnCity(c.companyId))return false; return true; });
          const fm = meetings.filter(m=>{ if(anFilter!=='all'&&m.salesperson!==anFilter)return false; if(dateMin&&m.date<dateMin)return false; if(!matchesAnCity(m.companyId))return false; return true; });
          const walkInCount = fm.filter(m => m.type === MEETING_TYPE_WALKIN_MET).length;
          const siteVisitCount = fm.filter(m => isNoContactVisit(m)).length;
          const fieldVisitTotal = walkInCount + siteVisitCount;
          const totalCalls = fc.length;
          const connected = fc.filter(c=>c.connected).length;
          const connectRate = totalCalls ? Math.round(connected/totalCalls*100) : 0;
          const mtgCompanies = new Set(fm.filter(m=>!isFieldVisitMeeting(m)).map(m=>String(m.companyId))).size;
          const pipeline = pipelineData.totalPipeline ?? 0;
          const wonVal = pipelineData.wonValue ?? 0;
          const hotLeads = spocs.filter(sp=>{ if(anFilter!=='all'&&companies.find(c=>String(c._id)===String(sp.companyId))?.salesperson!==anFilter)return false; if(!matchesAnCity(sp.companyId))return false; return sp.hot; }).length;
          const crColor = r => r>=50?GN:r>=25?YL:RD;
          const crBg = r => r>=50?GL:r>=25?YLL:RL;

          // Per-person stats from local data
          const persons = (anFilter==='all' ? (config.salespersons||[]) : [anFilter]);
          const personStats = persons.map(sp=>{
            const pCalls = calls.filter(c=>c.salesperson===sp&&(!dateMin||c.date>=dateMin)&&matchesAnCity(c.companyId));
            const pConn = pCalls.filter(c=>c.connected).length;
            const pNotConn = pCalls.length - pConn;
            const pRate = pCalls.length ? Math.round(pConn/pCalls.length*100) : 0;
            const pFollowups = pCalls.filter(c=>c.followUpDate).length;
            const pMeetings = meetings.filter(m=>m.salesperson===sp&&(!dateMin||m.date>=dateMin)&&matchesAnCity(m.companyId));
            const pMtg = new Set(pMeetings.filter(m=>!isFieldVisitMeeting(m)).map(m=>String(m.companyId))).size;
            const pMtgEvents = pMeetings.filter(m=>!isFieldVisitMeeting(m)).length;
            const pWalkins = pMeetings.filter(m=>m.type===MEETING_TYPE_WALKIN_MET).length;
            const pSiteVisits = pMeetings.filter(m=>isNoContactVisit(m)).length;
            const pProposals = proposals.filter(p=>p.salesperson===sp&&(!dateMin||p.date>=dateMin)&&matchesAnCity(p.companyId)).length;
            const pHot = spocs.filter(sp2=>{ const co=companies.find(c=>String(c._id)===String(sp2.companyId)); return co?.salesperson===sp && sp2.hot && matchesAnCity(sp2.companyId); }).length;
            const pWon = companies.filter(c=>c.salesperson===sp&&c.dealStatus==='won'&&(!dateMin||c.wonDate>=dateMin)&&(!anCitySet||anCitySet.has(normalizeCityKey(c.city)))).length;
            const pActive = companies.filter(c=>c.salesperson===sp&&c.dealStatus==='active'&&(!anCitySet||anCitySet.has(normalizeCityKey(c.city)))).length;
            const pApi = personAnalytics.find(p=>p.name===sp)||{};
            const pPipeline = pApi.pipelineValue||0;
            return {
              sp, calls:pCalls.length, connected:pConn, notConnected:pNotConn, rate:pRate, followups:pFollowups,
              meetings:pMtg, meetingEvents:pMtgEvents, walkins:pWalkins, siteVisits:pSiteVisits,
              fieldVisits:pWalkins+pSiteVisits, proposals:pProposals, hot:pHot, won:pWon, active:pActive, pipeline:pPipeline,
            };
          }).sort((a,b)=>b.calls-a.calls);

          const maxCalls = Math.max(...personStats.map(p=>p.calls), 1);
          const maxConn = Math.max(...personStats.map(p=>p.connected), 1);
          const maxNotConn = Math.max(...personStats.map(p=>p.notConnected), 1);
          const maxMtg = Math.max(...personStats.map(p=>p.meetings), 1);
          const maxMtgEv = Math.max(...personStats.map(p=>p.meetingEvents), 1);
          const maxWalkins = Math.max(...personStats.map(p=>p.walkins), 1);
          const maxNoContact = Math.max(...personStats.map(p=>p.siteVisits), 1);
          const maxField = Math.max(...personStats.map(p=>p.fieldVisits), 1);
          const maxProposals = Math.max(...personStats.map(p=>p.proposals), 1);
          const maxFollowups = Math.max(...personStats.map(p=>p.followups), 1);
          const maxHot = Math.max(...personStats.map(p=>p.hot), 1);
          const maxWon = Math.max(...personStats.map(p=>p.won), 1);

          return <>
            {/* ── Header ── */}
            <div className="page-header-row" style={{alignItems:'flex-start',marginBottom:24}}>
              <div>
                <h1 style={{fontSize:26,fontWeight:700,margin:0,letterSpacing:'-0.03em'}}>📈 Analytics</h1>
                <div style={{fontSize:13,color:MU,marginTop:3}}>What's working — and what's not.</div>
              </div>
              <div className="page-header-actions analytics-toolbar">
                <Dropdown style={{...s.sel,width:180}} value={anFilter} onChange={e=>setAnFilter(e.target.value)}>
                  <option value="all">Entire Organisation</option>
                  {config.salespersons?.map(sp=><option key={sp} value={sp}>{sp}</option>)}
                </Dropdown>
                <Dropdown style={{...s.sel,width:160}} value={anPeriod} onChange={e=>setAnPeriod(e.target.value)}>
                  <option value="7">Last 7 Days</option>
                  <option value="30">Last 30 Days</option>
                  <option value="90">Last 90 Days</option>
                  <option value="365">Last Year</option>
                  <option value="all">All Time</option>
                </Dropdown>
                <button style={{...s.btn(),background:'#1a1a2e',color:WH,border:'none',fontWeight:700,fontSize:12,padding:'8px 14px',display:'flex',alignItems:'center',gap:6}}
                  onClick={()=>{
                    const period = anPeriod==='all'?'All Time':'Last '+anPeriod+' days';
                    const fmtINRs = v => !v||v===0?'not set':v>=10000000?'₹'+(v/10000000).toFixed(1)+'Cr':v>=100000?'₹'+(v/100000).toFixed(1)+'L':v>=1000?'₹'+(v/1000).toFixed(0)+'K':'₹'+v;
                    const daysSince = d => { if(!d)return null; try{const diff=Date.now()-new Date(d).getTime();return Math.floor(diff/86400000);}catch{return null;} };
                    const companyMeetingSet2=new Set(meetings.map(m=>String(m.companyId)));
                    const today=new Date().toISOString().slice(0,10);

                    // Per-rep funnel: calls→connect→meeting→proposal→won
                    const teamLines=(config.salespersons||[]).map(sp=>{
                      const pCalls=calls.filter(c=>c.salesperson===sp&&(!dateMin||c.date>=dateMin));
                      const pConn=pCalls.filter(c=>c.connected).length;
                      const pConnR=pCalls.length?Math.round(pConn/pCalls.length*100):0;
                      const spMtgSet=new Set(meetings.filter(m=>m.salesperson===sp&&(!dateMin||m.date>=dateMin)).map(m=>String(m.companyId)));
                      const pMtg=spMtgSet.size;
                      const pProposal=proposals.filter(p=>p.salesperson===sp&&(!dateMin||p.date>=dateMin)).length;
                      const spCompIds=new Set(companies.filter(c=>c.salesperson===sp).map(c=>String(c._id)));
                      const pHot=spocs.filter(sp2=>spCompIds.has(String(sp2.companyId))&&sp2.hot).length;
                      const pWon=companies.filter(c=>c.salesperson===sp&&c.dealStatus==='won').length;
                      const pActive=companies.filter(c=>c.salesperson===sp&&c.dealStatus==='active').length;
                      const pDropped=companies.filter(c=>c.salesperson===sp&&c.dealStatus==='dropped').length;
                      const pProspects=leadBank.filter(l=>l.assignedTo===sp).length;
                      const pApi=personAnalytics.find(p=>p.name===sp)||{};
                      const callToConn=pCalls.length?`${pConnR}%`:'—';
                      const connToMtg=pConn?`${Math.round(pMtg/pConn*100)}%`:'—';
                      const mtgToProposal=pMtg?`${Math.round(pProposal/pMtg*100)}%`:'—';
                      const proposalToWon=pProposal?`${Math.round(pWon/pProposal*100)}%`:'—';
                      return `  ${sp}:
    Funnel: ${pCalls.length} calls → ${pConn} connected (${callToConn}) → ${pMtg} meetings (${connToMtg}) → ${pProposal} proposals (${mtgToProposal}) → ${pWon} won (${proposalToWon})
    Portfolio: ${pActive} active | ${pDropped} dropped | ${pHot} hot leads | ${pProspects} prospects assigned
    Pipeline: ${fmtINRs(pApi.pipelineValue||0)}`;
                    }).join('\n\n');

                    // Open pipeline deals with full detail
                    const pipeComps2=companies.filter(c=>{
                      if(anFilter!=='all'&&c.salesperson!==anFilter)return false;
                      return c.dealStatus==='active'&&(companyMeetingSet2.has(String(c._id))||c.potential>0);
                    });
                    const pipeLines=pipeComps2.map(c=>{
                      const cMtgs=meetings.filter(m=>String(m.companyId)===String(c._id)).sort((a,b)=>(b.date||'').localeCompare(a.date||''));
                      const latestMtg2=cMtgs[0];
                      const tv=latestMtg2?.estimatedValue?Number(String(latestMtg2.estimatedValue).replace(/[^0-9.]/g,'')):0;
                      const proposalSent=proposals.some(p=>String(p.companyId)===String(c._id));
                      const nextAct=latestMtg2?.nextAction||'none set';
                      const nextActDate=latestMtg2?.nextActionDate||'none set';
                      const overdue=latestMtg2?.nextActionDate&&latestMtg2.nextActionDate<today?'⚠️ OVERDUE':'';
                      const daysInStage=daysSince(latestMtg2?.date);
                      const dealAge=daysSince(c.createdAt);
                      const cCalls=calls.filter(cl=>String(cl.companyId)===String(c._id));
                      return `  - ${c.name} (${c.city||'?'}) | Owner: ${c.salesperson||'unassigned'}
      Stage: ${latestMtg2?.stage||'no meeting'} | Days in stage: ${daysInStage!=null?daysInStage+'d':'?'} | Deal age: ${dealAge!=null?dealAge+'d':'?'}
      Ticket: ${fmtINRs(tv)} | Potential: ${c.potential||0}/10 | Proposal sent: ${proposalSent?'YES':'NO'}
      Next action: ${nextAct} (${nextActDate}) ${overdue}
      Calls made: ${cCalls.length} | Connected: ${cCalls.filter(cl=>cl.connected).length}
      Remarks: ${c.remarks||latestMtg2?.summary||'none'}`;
                    }).join('\n\n');

                    // Dropped deals with loss reasons
                    const droppedComps=companies.filter(c=>{
                      if(anFilter!=='all'&&c.salesperson!==anFilter)return false;
                      return c.dealStatus==='dropped';
                    });
                    const dropLines=droppedComps.slice(0,20).map(c=>{
                      const cMtgs=meetings.filter(m=>String(m.companyId)===String(c._id));
                      return `  - ${c.name} (${c.city||'?'}) | Owner: ${c.salesperson||'—'} | Drop reason: ${c.dropReason||'not specified'} | Meetings had: ${cMtgs.length} | Drop date: ${c.dropDate||'?'}`;
                    }).join('\n');
                    const dropReasonSummary={};
                    droppedComps.forEach(c=>{ const r=c.dropReason||'unspecified'; dropReasonSummary[r]=(dropReasonSummary[r]||0)+1; });
                    const dropReasonLines=Object.entries(dropReasonSummary).sort((a,b)=>b[1]-a[1]).map(([r,n])=>`  - ${r}: ${n}`).join('\n');

                    // Source breakdown
                    const srcLines=srcAnalytics.map(d=>`  - ${d.src||'Unknown'}: ${d.total} leads | ${d.connected} connected | ${d.meetings} meetings | ROI: ${d.total?Math.round(d.meetings/d.total*100):0}%`).join('\n');

                    // City-wise prospect breakdown
                    const citiesEx=Object.values(leadBank.reduce((m,x)=>{const k=normalizeCityKey(x.city);if(k&&!m[k])m[k]=x.city.trim();return m;},{})).sort();
                    const cityProspectLines=citiesEx.map(city=>{
                      const cl=leadBank.filter(x=>citiesMatch(x.city,city));
                      const cc=companies.filter(c=>citiesMatch(c.city,city));
                      const cityCalls2=calls.filter(c=>{ const co=companies.find(x=>String(x._id)===String(c.companyId)); return citiesMatch(co?.city,city); });
                      const conn2=cityCalls2.filter(c=>c.connected).length;
                      const pct=cityCalls2.length?Math.round(conn2/cityCalls2.length*100):0;
                      return `  - ${city}: ${cl.length} prospects (${cl.filter(x=>x.status==='unmined').length} unmined / ${cl.filter(x=>x.status==='in-progress').length} in-progress / ${cl.filter(x=>x.status==='done').length} converted) | ${cc.length} companies | ${cityCalls2.length} calls | ${conn2} connected (${pct}%)`;
                    }).join('\n');

                    // Meeting status
                    const mtgTentative=meetings.filter(m=>m.stage==='Tentative').length;
                    const mtgConfirmed=meetings.filter(m=>m.stage==='Confirmed').length;
                    const mtgCompleted=meetings.filter(m=>['Completed','Done'].includes(m.stage)).length;
                    const mtgCancelled=meetings.filter(m=>m.stage==='Cancelled').length;
                    const overdueActions=pipeComps2.filter(c=>{ const lm=meetings.filter(m=>String(m.companyId)===String(c._id)).sort((a,b)=>(b.date||'').localeCompare(a.date||''))[0]; return lm?.nextActionDate&&lm.nextActionDate<today; }).length;

                    const now=new Date().toLocaleString('en-IN',{dateStyle:'medium',timeStyle:'short'});
                    const report=`# Unada Sales Performance Report
Generated: ${now} | Period: ${period} | Filter: ${anFilter==='all'?'Entire Organisation':anFilter}

## Overall KPIs
- Total Calls: ${totalCalls} | Connected: ${connected} | Connect Rate: ${connectRate}%
- Meeting Companies: ${mtgCompanies} | Total Sessions: ${fm.length}
- Hot Leads: ${hotLeads} | Overdue Next Actions: ${overdueActions}
- Pipeline Value: ${fmtINRs(pipeline)} | Won Value: ${fmtINRs(wonVal)}
- Active: ${companies.filter(c=>c.dealStatus==='active').length} | Won: ${companies.filter(c=>c.dealStatus==='won').length} | Dropped: ${companies.filter(c=>c.dealStatus==='dropped').length} | On Hold: ${companies.filter(c=>c.dealStatus==='on-hold').length}

## Per-Rep Funnel (calls→connect→meeting→proposal→won)
${teamLines}

## Open Pipeline Deals (${pipeComps2.length} companies)
${pipeLines||'  No pipeline deals'}

## Dropped Deals — Loss Reason Summary
${dropReasonLines||'  No drops recorded'}

Dropped deals (recent ${Math.min(droppedComps.length,20)}):
${dropLines||'  None'}

## Lead Sources (ROI = meetings÷total)
${srcLines||'  No source data'}
NOTE: Fix source tagging at intake — "Unknown" leads are a black box.

## Prospects (Lead Bank) — City Breakdown
${cityProspectLines||'  No prospect data'}
Total: ${leadBank.length} prospects | ${leadBank.filter(x=>x.status==='unmined').length} unmined | ${leadBank.filter(x=>x.status==='in-progress').length} in-progress | ${leadBank.filter(x=>x.status==='done').length} converted

## Meeting Pipeline Status
- Tentative: ${mtgTentative} | Confirmed: ${mtgConfirmed} | Completed: ${mtgCompleted} | Cancelled: ${mtgCancelled}
- Proposals sent: ${proposals.filter(p=>!dateMin||p.date>=dateMin).length} logged

## Data Quality Flags
- Companies with no ticket size: ${pipeComps2.filter(c=>{ const lm=meetings.filter(m=>String(m.companyId)===String(c._id))[0]; return !lm?.estimatedValue; }).length} of ${pipeComps2.length} pipeline deals
- Dropped without reason: ${droppedComps.filter(c=>!c.dropReason).length} of ${droppedComps.length}
- Calls with unknown source: ${spocs.filter(sp2=>!sp2.leadSource).length} SPOCs have no source tagged
`;
                    navigator.clipboard.writeText(report).then(()=>alert('✅ Copied! Paste into Claude and ask: "Analyze this sales data. Who is performing well, where are the leaks, what should we focus on?"')).catch(()=>{
                      const el=document.createElement('textarea');el.value=report;document.body.appendChild(el);el.select();document.execCommand('copy');document.body.removeChild(el);
                      alert('✅ Copied! Paste into Claude and ask: "Analyze this sales data. Who is performing well, where are the leaks, what should we focus on?"');
                    });
                  }}>
                  📋 Export for Claude
                </button>
              </div>
            </div>

            <div style={{...s.card,marginBottom:16,padding:'14px 16px'}}>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10,gap:12,flexWrap:'wrap'}}>
                <div style={{fontSize:13,fontWeight:600,color:TX}}>Filter by Cities</div>
                <div style={{fontSize:12,color:MU}}>{anCities.length ? `${anCities.length} selected` : 'All cities'}</div>
              </div>
              <div style={{display:'flex',flexWrap:'wrap',gap:8,alignItems:'center'}} className="filter-chip-row">
                {visibleCities.map(c=>(
                  <div key={c} onClick={()=>toggleAnCity(c)} style={{padding:'6px 12px',borderRadius:8,border:'2px solid '+(anCities.includes(c)?P:BD),background:anCities.includes(c)?PL:WH,fontSize:12,fontWeight:600,color:anCities.includes(c)?P:MU,cursor:'pointer',transition:'all .15s',userSelect:'none'}}>{c}</div>
                ))}
                {anCities.length>0&&<motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn('s'),padding:'6px 12px',fontSize:12}} onClick={()=>setAnCities([])}>Clear</motion.button>}
              </div>
            </div>

            {/* ── KPI Strip ── */}
            <div className="grid-6" style={{marginBottom:20}}>
              {[
                {key:'calls',label:'Total Calls',value:totalCalls,sub:`${connected} connected`,icon:'📞',c1:'#6366F1',c2:'#818CF8'},
                {key:'connect',label:'Connect Rate',value:connectRate+'%',sub:connectRate>=50?'On track':connectRate>=25?'Needs push':'Critical',icon:'📶',c1:connectRate>=50?'#10B981':connectRate>=25?'#F59E0B':'#EF4444',c2:connectRate>=50?'#34D399':connectRate>=25?'#FBBF24':'#F87171'},
                {key:'meetings',label:'Meeting Companies',value:mtgCompanies,sub:`${fm.filter(m=>!isFieldVisitMeeting(m)).length} sessions`,icon:'🤝',c1:'#F59E0B',c2:'#FBBF24'},
                {key:'field',label:'Site walk-ins',value:fieldVisitTotal,sub:`${walkInCount} met contact · ${siteVisitCount} no contact`,icon:'🚶',c1:'#059669',c2:'#34D399'},
                {key:'hot',label:'Hot Leads',value:hotLeads,sub:'SPOCs marked hot',icon:'🔥',c1:'#EC4899',c2:'#F472B6'},
                {key:'pipeline',label:'Pipeline',value:fmtINR(pipeline),sub:'Active deals value',icon:'💰',c1:'#8B5CF6',c2:'#A78BFA'},
                {key:'won',label:'Won',value:fmtINR(wonVal),sub:'Closed deals',icon:'🏆',c1:'#10B981',c2:'#6EE7B7'},
              ].map(({key,label,value,sub,icon,c1,c2})=>(
                <div key={key} onClick={()=>setAnDrill(anDrill===key?null:key)} style={{background:`linear-gradient(135deg,${c1},${c2})`,borderRadius:14,padding:'16px 18px',color:WH,position:'relative',overflow:'hidden',cursor:'pointer',outline:anDrill===key?'3px solid rgba(255,255,255,0.7)':'none',transition:'transform .15s,outline .15s'}}
                  onMouseEnter={e=>e.currentTarget.style.transform='translateY(-2px)'}
                  onMouseLeave={e=>e.currentTarget.style.transform='translateY(0)'}>
                  <div style={{fontSize:20,marginBottom:6}}>{icon}</div>
                  <div style={{fontSize:22,fontWeight:800,letterSpacing:'-0.03em',lineHeight:1}}>{value}</div>
                  <div style={{fontSize:11,opacity:0.85,marginTop:4,fontWeight:600}}>{label}</div>
                  <div style={{fontSize:10,opacity:0.65,marginTop:2}}>{sub}</div>
                  <div style={{position:'absolute',bottom:8,right:10,fontSize:9,opacity:0.5}}>tap to drill ↓</div>
                </div>
              ))}
            </div>

            {/* ── Drill-Down Panel ── */}
            {anDrill&&(()=>{
              const fmtD = d => { if(!d)return'—'; try{const dt=new Date(d);return dt.toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'});}catch{return d;} };
              let title='', rows=[], cols=[];

              if(anDrill==='calls'){
                title=`📞 All Calls (${fc.length})`;
                cols=['Date','Salesperson','Company','SPOC','Connected','Notes'];
                rows=fc.slice().sort((a,b)=>(b.date||'').localeCompare(a.date||'')).map(c=>{
                  const sp=spocs.find(s=>String(s._id)===String(c.spocId));
                  const co=companies.find(x=>String(x._id)===String(c.companyId||sp?.companyId));
                  return [fmtD(c.date),c.salesperson||'—',co?.name||'—',sp?.name||'—',c.connected?<span style={{color:GN,fontWeight:700}}>✓ Yes</span>:<span style={{color:RD}}>✗ No</span>,c.notes||'—'];
                });
              } else if(anDrill==='connect'){
                title=`📶 Connected Calls (${fc.filter(c=>c.connected).length} of ${fc.length})`;
                cols=['Date','Salesperson','Company','SPOC','Notes'];
                rows=fc.filter(c=>c.connected).slice().sort((a,b)=>(b.date||'').localeCompare(a.date||'')).map(c=>{
                  const sp=spocs.find(s=>String(s._id)===String(c.spocId));
                  const co=companies.find(x=>String(x._id)===String(c.companyId||sp?.companyId));
                  return [fmtD(c.date),c.salesperson||'—',co?.name||'—',sp?.name||'—',c.notes||'—'];
                });
              } else if(anDrill==='meetings'){
                title=`🤝 Meeting Companies (${mtgCompanies})`;
                cols=['Company','City','Salesperson','Meetings','Last Meeting','Stage'];
                const byComp={};
                fm.forEach(m=>{ const k=String(m.companyId); if(!byComp[k])byComp[k]={meetings:[],co:companies.find(c=>String(c._id)===k)}; byComp[k].meetings.push(m); });
                rows=Object.values(byComp).sort((a,b)=>b.meetings.length-a.meetings.length).map(({meetings:ms,co})=>{
                  const last=ms.slice().sort((a,b)=>(b.date||'').localeCompare(a.date||''))[0];
                  return [co?.name||'Unknown',co?.city||'—',co?.salesperson||'—',ms.length,fmtD(last?.date),last?.stage||'—'];
                });
              } else if(anDrill==='field'){
                title=`🚶 Site walk-ins (${fieldVisitTotal}) — ${walkInCount} met contact · ${siteVisitCount} no contact`;
                cols=['Date','Type','Salesperson','Company','City','Location','Notes'];
                rows=fm.filter(m=>isFieldVisitMeeting(m)).slice().sort((a,b)=>(b.date||'').localeCompare(a.date||'')).map(m=>{
                  const co=companies.find(x=>String(x._id)===String(m.companyId));
                  return [fmtD(m.date),m.type===MEETING_TYPE_WALKIN_MET?<span style={{color:GN,fontWeight:600}}>{WALKIN_MET_SHORT}</span>:<span style={{color:'#D97706',fontWeight:600}}>{WALKIN_NO_CONTACT_SHORT}</span>,m.salesperson||'—',co?.name||'—',co?.city||'—',m.location||'—',m.summary||m.agenda||'—'];
                });
              } else if(anDrill==='hot'){
                const hotSpocs=spocs.filter(sp2=>{ if(anFilter!=='all'&&companies.find(c=>String(c._id)===String(sp2.companyId))?.salesperson!==anFilter)return false; if(!matchesAnCity(sp2.companyId))return false; return sp2.hot; });
                title=`🔥 Hot Leads (${hotSpocs.length})`;
                cols=['SPOC','Company','City','Salesperson','Stage','Phone'];
                rows=hotSpocs.map(sp2=>{ const co=companies.find(c=>String(c._id)===String(sp2.companyId)); return [sp2.name||'—',co?.name||'—',co?.city||'—',co?.salesperson||'—',sp2.stage||'—',sp2.phone||'—']; });
              } else if(anDrill==='pipeline'){
                const companyMeetingSet=new Set(meetings.map(m=>String(m.companyId)));
                const pipeComps=companies.filter(c=>{
                  if(anFilter!=='all'&&c.salesperson!==anFilter)return false;
                  if(!matchesAnCity(c._id))return false;
                  if(c.dealStatus!=='active')return false;
                  // Only real pipeline: has a meeting, OR potential set, OR ticket size on a meeting
                  return companyMeetingSet.has(String(c._id)) || c.potential>0;
                });
                const pipeRows=pipeComps.map(c=>{
                  const latestMtg=meetings.filter(m=>String(m.companyId)===String(c._id)).sort((a,b)=>(b.date||'').localeCompare(a.date||''))[0];
                  const ticketNum=latestMtg?.estimatedValue?Number(String(latestMtg.estimatedValue).replace(/[^0-9.]/g,'')):0;
                  return {c,latestMtg,ticketNum};
                }).sort((a,b)=>b.ticketNum-a.ticketNum||b.c.potential-a.c.potential);
                const pipeTotal=pipeRows.reduce((s,r)=>s+r.ticketNum,0);
                title=`💰 Pipeline — ${pipeComps.length} companies · Total: ${fmtINR(pipeTotal)}`;
                cols=['Company','City','Salesperson','Ticket Size','Potential (0-10)','Stage'];
                rows=pipeRows.map(({c,latestMtg,ticketNum})=>{
                  const ticketDisplay=ticketNum>0?<span style={{fontWeight:700,color:GN}}>{fmtINR(ticketNum)}</span>:<span style={{color:MU,fontSize:11}}>not set</span>;
                  return [c.name,c.city||'—',c.salesperson||'—',ticketDisplay,c.potential>0?<span style={{fontWeight:700,color:c.potential>=7?GN:c.potential>=4?YL:MU}}>{c.potential}/10</span>:<span style={{color:MU}}>—</span>,latestMtg?.stage||<span style={{color:MU,fontSize:11}}>No meeting</span>];
                });
              } else if(anDrill==='won'){
                const wonComps=companies.filter(c=>{ if(anFilter!=='all'&&c.salesperson!==anFilter)return false; if(!matchesAnCity(c._id))return false; return c.dealStatus==='won'&&(!dateMin||c.wonDate>=dateMin); }).sort((a,b)=>(b.wonDate||'').localeCompare(a.wonDate||''));
                title=`🏆 Won Deals (${wonComps.length})`;
                cols=['Company','City','Salesperson','Won Date','Potential'];
                rows=wonComps.map(c=>[c.name,c.city||'—',c.salesperson||'—',fmtD(c.wonDate),c.potential+'/10']);
              }

              return <div style={{...s.card,marginBottom:20,padding:0,overflow:'hidden',border:'2px solid #E0E7FF'}}>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'14px 20px',background:'linear-gradient(135deg,#EEF2FF,#F5F3FF)',borderBottom:'1px solid #E0E7FF'}}>
                  <div style={{fontSize:15,fontWeight:700,color:'#4F46E5'}}>{title}</div>
                  <button onClick={()=>setAnDrill(null)} style={{background:'none',border:'none',cursor:'pointer',fontSize:18,color:MU,lineHeight:1}}>×</button>
                </div>
                {rows.length===0
                  ? <div style={{padding:40,textAlign:'center',color:MU,fontSize:13}}>No data for this period</div>
                  : <div className="table-wrap" style={{maxHeight:380,overflowY:'auto'}}>
                      <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                        <thead style={{position:'sticky',top:0,background:'#F9FAFB',zIndex:1}}>
                          <tr>{cols.map(h=><th key={h} style={{...s.th,fontSize:11,padding:'8px 14px'}}>{h}</th>)}</tr>
                        </thead>
                        <tbody>
                          {rows.map((row,ri)=>(
                            <tr key={ri} style={{background:ri%2===0?WH:'#FAFAFA'}}>
                              {row.map((cell,ci)=><td key={ci} style={{...s.td,padding:'7px 14px',fontSize:12,maxWidth:180,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{cell}</td>)}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                }
              </div>;
            })()}

            {/* ── Salesperson Leaderboard ── */}
            {personStats.length>0&&<div style={{...s.card,marginBottom:20,padding:20}}>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16}}>
                <div>
                  <div style={{fontSize:16,fontWeight:700,letterSpacing:'-0.02em'}}>🏆 Salesperson Leaderboard</div>
                  <div style={{fontSize:12,color:MU,marginTop:2}}>Ranked by calls made · period: {anPeriod==='all'?'All Time':'Last '+anPeriod+' days'}</div>
                </div>
              </div>
              <div className="leaderboard-grid">
                {personStats.map((p,i)=>(
                  <div key={p.sp} style={{background:i===0?'linear-gradient(135deg,#FFFBEB,#FEF3C7)':i===1?'linear-gradient(135deg,#F8FAFC,#F1F5F9)':'#FAFAFA',border:`1px solid ${i===0?'#FCD34D':i===1?'#CBD5E1':'#E5E7EB'}`,borderRadius:14,padding:16,position:'relative'}}>
                    {/* Rank badge */}
                    <div style={{position:'absolute',top:12,right:12,width:28,height:28,borderRadius:999,background:i===0?'#F59E0B':i===1?'#94A3B8':'#D1D5DB',display:'flex',alignItems:'center',justifyContent:'center',fontSize:13,fontWeight:800,color:i===0?WH:i===1?WH:'#6B7280'}}>
                      {i+1}
                    </div>
                    <div style={{fontSize:15,fontWeight:700,marginBottom:2,paddingRight:36}}>{p.sp}</div>
                    <div style={{fontSize:12,color:MU,marginBottom:14}}>{p.calls} calls · {p.rate}% connect</div>

                    {/* Connect rate badge */}
                    <div style={{display:'inline-flex',alignItems:'center',gap:6,background:crBg(p.rate),borderRadius:20,padding:'3px 10px',marginBottom:14}}>
                      <div style={{width:7,height:7,borderRadius:99,background:crColor(p.rate)}}/>
                      <span style={{fontSize:11,fontWeight:700,color:crColor(p.rate)}}>{p.rate}% connect rate</span>
                    </div>

                    {/* All metrics — always visible */}
                    {[
                      {label:'Calls',val:p.calls,max:maxCalls,color:'#6366F1'},
                      {label:'Connected',val:p.connected,max:maxConn,color:'#10B981'},
                      {label:'Not connected',val:p.notConnected,max:maxNotConn,color:'#EF4444'},
                      {label:'Follow-ups set',val:p.followups,max:maxFollowups,color:'#8B5CF6'},
                      {label:'Meetings (companies)',val:p.meetings,max:maxMtg,color:'#F59E0B'},
                      {label:'Meeting sessions',val:p.meetingEvents,max:maxMtgEv,color:'#FB923C'},
                      {label:'Proposals sent',val:p.proposals,max:maxProposals,color:'#7C3AED'},
                      {label:'Walk-ins · Met contact',val:p.walkins,max:maxWalkins,color:'#059669'},
                      {label:'Walk-ins · No contact',val:p.siteVisits,max:maxNoContact,color:'#D97706'},
                      {label:'Site walk-ins total',val:p.fieldVisits,max:maxField,color:'#047857'},
                      {label:'Hot leads',val:p.hot,max:maxHot,color:'#EC4899'},
                      {label:'Won deals',val:p.won,max:maxWon,color:'#16A34A'},
                    ].map(({label,val,max,color})=>(
                      <div key={label} style={{marginBottom:8}}>
                        <div style={{display:'flex',justifyContent:'space-between',fontSize:11,marginBottom:3}}>
                          <span style={{color:MU,fontWeight:500}}>{label}</span>
                          <span style={{fontWeight:700,color:TX}}>{val}</span>
                        </div>
                        <div style={{height:5,background:'#E5E7EB',borderRadius:99,overflow:'hidden'}}>
                          <div style={{height:'100%',width:max?Math.min(val/max*100,100)+'%':'0%',background:color,borderRadius:99,transition:'width .6s'}}/>
                        </div>
                      </div>
                    ))}

                    {/* Portfolio summary — always shown */}
                    <div style={{marginTop:12,paddingTop:12,borderTop:'1px solid #E5E7EB',display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                      <div style={{background:WH,borderRadius:8,padding:'8px 10px',border:'1px solid #E5E7EB'}}>
                        <div style={{fontSize:10,color:MU,fontWeight:600,textTransform:'uppercase',letterSpacing:'.4px'}}>Active companies</div>
                        <div style={{fontSize:16,fontWeight:700,color:TX,marginTop:2}}>{p.active}</div>
                      </div>
                      <div style={{background:WH,borderRadius:8,padding:'8px 10px',border:'1px solid #E5E7EB'}}>
                        <div style={{fontSize:10,color:MU,fontWeight:600,textTransform:'uppercase',letterSpacing:'.4px'}}>Pipeline</div>
                        <div style={{fontSize:16,fontWeight:700,color:p.pipeline>0?GN:MU,marginTop:2}}>{p.pipeline>0?fmtINR(p.pipeline):'—'}</div>
                      </div>
                    </div>

                    {/* Quick badges */}
                    <div style={{display:'flex',gap:6,marginTop:10,flexWrap:'wrap'}}>
                      <span style={{...s.tag('#EEF2FF','#4338CA'),fontSize:10}}>📞 {p.calls}</span>
                      <span style={{...s.tag(GL,GN),fontSize:10}}>✓ {p.connected}</span>
                      <span style={{...s.tag('#D1FAE5','#047857'),fontSize:10}}>🚶 {p.walkins}</span>
                      <span style={{...s.tag('#FFEDD5','#C2410C'),fontSize:10}}>🚪 {p.siteVisits}</span>
                      <span style={{...s.tag('#FEF3C7','#D97706'),fontSize:10}}>🔥 {p.hot}</span>
                      <span style={{...s.tag(GL,GN),fontSize:10}}>🏆 {p.won}</span>
                      <span style={{...s.tag('#F5F3FF','#7C3AED'),fontSize:10}}>📄 {p.proposals}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>}

            {/* ── Company Coverage by Salesperson (from call history) ── */}
            {isAdmin && (()=>{
              const coOf = c => String(c.companyId || spocById[String(c.spocId)]?.companyId || '');
              // Card-local scope (independent of the page filters above): period + person + city.
              let covDateMin='', covDateMax='';
              if(covPeriod==='yesterday'){ covDateMin=yesterday(); covDateMax=yesterday(); }
              else if(covPeriod!=='all'){ covDateMin = covPeriod==='0' ? today() : new Date(Date.now()-parseInt(covPeriod)*864e5).toISOString().slice(0,10); }
              const covScoped = calls.filter(c=>{
                if(covPerson!=='all' && (c.salesperson||'—')!==covPerson) return false;
                if(covDateMin && (c.date||'')<covDateMin) return false;
                if(covDateMax && (c.date||'')>covDateMax) return false;
                if(covCity && !citiesMatch(companyById[coOf(c)]?.city, covCity)) return false;
                return true;
              });
              const covAdvanced = covCity ? 1 : 0;

              const covMap = {};
              covScoped.forEach(c=>{
                const rep = c.salesperson || '—';
                const coId = coOf(c);
                if(!covMap[rep]) covMap[rep] = { sp:rep, calls:0, connected:0, cos:new Set() };
                covMap[rep].calls++;
                if(c.connected) covMap[rep].connected++;
                if(coId) covMap[rep].cos.add(coId);
              });
              const coverageData = Object.values(covMap)
                .map(r=>({ sp:r.sp, calls:r.calls, connected:r.connected, companies:r.cos.size, rate:r.calls?Math.round(r.connected/r.calls*100):0 }))
                .sort((a,b)=>b.calls-a.calls);
              const coverageCompanies = new Set(covScoped.map(coOf).filter(Boolean)).size;
              const covOutcomeMap = {};
              covScoped.forEach(c=>{ const o=c.conversationOutcome||c.outcome||'—'; covOutcomeMap[o]=(covOutcomeMap[o]||0)+1; });
              const covOutcomes = Object.entries(covOutcomeMap).map(([name,value])=>({name,value})).sort((a,b)=>b.value-a.value);
              const maxCovCalls = Math.max(...coverageData.map(d=>d.calls), 1);

              // Company drill (scoped by the same local filters — so the person filter is the rep selector)
              const coOptions = [...new Set(covScoped.map(coOf).filter(Boolean))]
                .map(id=>({ id, name: companyById[id]?.name || 'Unknown', city: companyById[id]?.city || '' }))
                .sort((a,b)=>a.name.localeCompare(b.name));
              const relCalls = anCovCompany
                ? covScoped.filter(c=>coOf(c)===anCovCompany).slice().sort((a,b)=>(b.date||'').localeCompare(a.date||''))
                : [];
              const relConn = relCalls.filter(c=>c.connected).length;
              const relRate = relCalls.length ? Math.round(relConn/relCalls.length*100) : 0;
              const relCo = anCovCompany ? companyById[anCovCompany] : null;

              return <div style={{...s.card,marginBottom:20}}>
                <div style={{fontSize:15,fontWeight:700,marginBottom:4}}>👥 Company Coverage by Salesperson</div>
                <div style={{fontSize:12,color:MU,marginBottom:14}}>From call history · calls vs connected per rep{coverageData.length?` · ${coverageCompanies} companies touched`:''}</div>

                {/* Compact filter bar: period · person · company · More (city) — single row */}
                <div style={{display:'flex',gap:8,flexWrap:'nowrap',alignItems:'center',marginBottom:12}}>
                  <Dropdown style={{...s.sel,flex:'0 0 118px',fontSize:12}} value={covPeriod} onChange={e=>setCovPeriod(e.target.value)}>
                    <option value="0">Today</option>
                    <option value="yesterday">Yesterday</option>
                    <option value="7">7 days</option>
                    <option value="30">30 days</option>
                    <option value="90">90 days</option>
                    <option value="all">All time</option>
                  </Dropdown>
                  <Dropdown style={{...s.sel,flex:'0 0 138px',fontSize:12}} value={covPerson} onChange={e=>{setCovPerson(e.target.value);setAnCovCompany('');}}>
                    <option value="all">Everyone</option>
                    {(config.salespersons||[]).map(sp=><option key={sp} value={sp}>{sp}</option>)}
                  </Dropdown>
                  <Dropdown style={{...s.sel,flex:'1 1 160px',minWidth:140,fontSize:12}} value={anCovCompany} onChange={e=>setAnCovCompany(e.target.value)}>
                    <option value="">All companies</option>
                    {coOptions.map(o=><option key={o.id} value={o.id}>{o.name}{o.city?` · ${o.city}`:''}</option>)}
                  </Dropdown>
                  <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn('s'),flex:'0 0 auto',padding:'8px 12px',fontSize:12,whiteSpace:'nowrap'}} onClick={()=>setCovMore(v=>!v)}>
                    {covMore?'▾ Less':'▸ More'}{covAdvanced>0?` · ${covAdvanced}`:''}
                  </motion.button>
                  {(covPeriod!=='all'||covPerson!=='all'||covCity||anCovCompany)&&<motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn('s'),flex:'0 0 auto',padding:'8px 12px',fontSize:12,color:RD}} onClick={()=>{setCovPeriod('all');setCovPerson('all');setCovCity('');setAnCovCompany('');}}>Clear</motion.button>}
                </div>
                {covMore&&<div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center',marginBottom:14,paddingBottom:12,borderBottom:'1px solid #F3F4F6'}}>
                  <Dropdown style={{...s.sel,width:130,fontSize:12}} value={covCity} onChange={e=>{setCovCity(e.target.value);setAnCovCompany('');}}>
                    <option value="">All cities</option>
                    {visibleCities.map(c=><option key={c} value={c}>{c}</option>)}
                  </Dropdown>
                </div>}

                {!anCovCompany
                  ? (coverageData.length===0
                      ? <div style={{padding:32,textAlign:'center',color:MU,fontSize:13}}>No calls for this selection</div>
                      : <>
                          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12,flexWrap:'wrap',gap:8}}>
                            <div style={{fontSize:11,fontWeight:600,color:MU,textTransform:'uppercase',letterSpacing:'.4px'}}>Calls vs Connected · per rep</div>
                            <div style={{display:'flex',gap:12,fontSize:11,color:MU}}>
                              <span style={{display:'flex',alignItems:'center',gap:5}}><span style={{width:8,height:8,borderRadius:99,background:P}}/>Calls</span>
                              <span style={{display:'flex',alignItems:'center',gap:5}}><span style={{width:8,height:8,borderRadius:99,background:GN}}/>Connected</span>
                            </div>
                          </div>
                          <div style={{display:'flex',flexDirection:'column',gap:12}}>
                            {coverageData.map(d=>{
                              const w=d.calls/maxCovCalls*100, connW=d.calls?d.connected/maxCovCalls*100:0;
                              return <div key={d.sp}>
                                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:5,gap:8}}>
                                  <span style={{fontSize:13,fontWeight:600,color:TX,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{d.sp}</span>
                                  <span style={{fontSize:12,color:MU,flexShrink:0}}><b style={{color:TX,fontSize:13}}>{d.calls}</b> calls · <b style={{color:GN}}>{d.connected}</b> conn ({d.rate}%)</span>
                                </div>
                                <div style={{position:'relative',height:9,background:'#F3F4F6',borderRadius:99,overflow:'hidden'}}>
                                  <div style={{position:'absolute',left:0,top:0,height:'100%',width:w+'%',background:P,borderRadius:99,transition:'width .5s'}}/>
                                  <div style={{position:'absolute',left:0,top:0,height:'100%',width:connW+'%',background:GN,borderRadius:99,transition:'width .5s'}}/>
                                </div>
                              </div>;
                            })}
                          </div>
                          {covOutcomes.length>0 && <>
                            <div style={{fontSize:11,fontWeight:600,color:MU,margin:'20px 0 12px',textTransform:'uppercase',letterSpacing:'.4px'}}>Call Outcomes · from call history</div>
                            <OutcomeBars items={covOutcomes} total={covScoped.length}/>
                          </>}
                        </>)
                  : <div>
                      {/* Relation summary: company × selected person */}
                      <div style={{display:'flex',gap:10,flexWrap:'wrap',marginBottom:12}}>
                        <div style={{...s.tag('#EEF2FF','#4338CA'),fontSize:12,fontWeight:600}}>{relCo?.name||'Company'}{relCo?.city?` · ${relCo.city}`:''}</div>
                        <div style={{...s.tag(PL,TX),fontSize:12}}>{covPerson==='all'?'All reps':covPerson}</div>
                        <div style={{...s.tag(PL,TX),fontSize:12}}>{relCalls.length} call{relCalls.length!==1?'s':''}</div>
                        <div style={{...s.tag(GL,GN),fontSize:12}}>{relConn} connected · {relRate}%</div>
                        {relCo?.salesperson&&<div style={{...s.tag('#FFFBEB','#D97706'),fontSize:12}}>Owner: {relCo.salesperson}</div>}
                      </div>
                      {relCalls.length>0 && (()=>{
                        const om = {};
                        relCalls.forEach(c=>{ const o=c.conversationOutcome||c.outcome||'—'; om[o]=(om[o]||0)+1; });
                        const od = Object.entries(om).map(([name,value])=>({name,value})).sort((a,b)=>b.value-a.value);
                        return <div style={{marginBottom:16}}>
                          <div style={{fontSize:11,fontWeight:600,color:MU,marginBottom:12,textTransform:'uppercase',letterSpacing:'.4px'}}>Outcomes</div>
                          <OutcomeBars items={od} total={relCalls.length}/>
                        </div>;
                      })()}
                      {relCalls.length===0
                        ? <div style={{padding:32,textAlign:'center',color:MU,fontSize:13}}>No calls between this company and {covPerson==='all'?'any rep':covPerson} in scope</div>
                        : <div className="table-wrap" style={{maxHeight:340,overflowY:'auto',border:'1px solid #E5E7EB',borderRadius:8}}>
                            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                              <thead style={{position:'sticky',top:0,background:'#F9FAFB',zIndex:1}}>
                                <tr>{['Date','Salesperson','Connected','Outcome','Notes'].map(h=><th key={h} style={{...s.th,fontSize:11,padding:'8px 14px'}}>{h}</th>)}</tr>
                              </thead>
                              <tbody>
                                {relCalls.map((c,ri)=>(
                                  <tr key={c._id||ri} style={{background:ri%2===0?WH:'#FAFAFA'}}>
                                    <td style={{...s.td,padding:'7px 14px'}}>{fmtDate(c.date)}</td>
                                    <td style={{...s.td,padding:'7px 14px'}}>{c.salesperson||'—'}</td>
                                    <td style={{...s.td,padding:'7px 14px'}}>{c.connected?<span style={{color:GN,fontWeight:700}}>✓ Yes</span>:<span style={{color:RD}}>✗ No</span>}</td>
                                    <td style={{...s.td,padding:'7px 14px'}}>{c.conversationOutcome||c.outcome||'—'}</td>
                                    <td style={{...s.td,padding:'7px 14px',maxWidth:280,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{c.notes||'—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>}
                    </div>}
              </div>;
            })()}

            {/* ── Source ROI + Daily Calls ── */}
            <div className="grid-2" style={{marginBottom:20}}>

              {/* Source ROI */}
              {srcAnalytics.length>0&&<div style={s.card}>
                <div style={{fontSize:15,fontWeight:700,marginBottom:4}}>📡 Lead Source ROI</div>
                <div style={{fontSize:12,color:MU,marginBottom:16}}>Which source is actually converting to meetings?</div>
                <div style={{display:'flex',flexDirection:'column',gap:12}}>
                  {srcAnalytics.slice(0,8).map((d,i)=>{
                    const roi = d.total ? Math.round(d.meetings/d.total*100) : 0;
                    const connPct = d.total ? Math.round(d.connected/d.total*100) : 0;
                    return <div key={d.src} style={{padding:'10px 12px',background:roi>=20?'#F0FDF4':roi>=10?'#FFFBEB':'#F9FAFB',borderRadius:10,border:`1px solid ${roi>=20?'#BBF7D0':roi>=10?'#FCD34D':'#E5E7EB'}`}}>
                      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:6}}>
                        <div style={{fontSize:13,fontWeight:600,color:TX}}>{d.src||'Unknown'}</div>
                        <div style={{display:'flex',gap:6,alignItems:'center'}}>
                          <span style={{fontSize:10,fontWeight:700,color:roi>=20?GN:roi>=10?YL:MU,background:roi>=20?GL:roi>=10?YLL:'#F3F4F6',borderRadius:6,padding:'2px 7px'}}>{roi}% → mtg</span>
                          <span style={{fontSize:10,color:MU,background:'#F3F4F6',borderRadius:6,padding:'2px 7px'}}>{d.total} total</span>
                        </div>
                      </div>
                      <div style={{display:'flex',gap:6,alignItems:'center'}}>
                        <div style={{flex:1,height:6,background:'#E5E7EB',borderRadius:99,overflow:'hidden'}}>
                          <div style={{height:'100%',width:connPct+'%',background:'#6366F1',borderRadius:99}}/>
                        </div>
                        <span style={{fontSize:10,color:MU,width:52,textAlign:'right',flexShrink:0}}>{d.connected} conn · {d.meetings} mtg</span>
                      </div>
                    </div>;
                  })}
                </div>
              </div>}

              {/* Daily Call Trend */}
              <div style={s.card}>
                <div style={{fontSize:15,fontWeight:700,marginBottom:4}}>📞 Daily Call Activity</div>
                <div style={{fontSize:12,color:MU,marginBottom:16}}>Total calls vs connected — last {anPeriod==='all'?'90':anPeriod} days</div>
                {dailyCalls.length>0 ? <>
                  {/* Summary row */}
                  <div style={{display:'flex',gap:12,marginBottom:14}}>
                    {[{label:'Peak Day',val:Math.max(...dailyCalls.map(d=>d.total),0)+' calls',color:P},{label:'Avg/Day',val:Math.round(dailyCalls.reduce((s,d)=>s+d.total,0)/Math.max(dailyCalls.length,1))+' calls',color:MU},{label:'Best Connect',val:Math.max(...dailyCalls.map(d=>d.total?Math.round(d.connected/d.total*100):0),0)+'%',color:GN}].map(({label,val,color})=>(
                      <div key={label} style={{flex:1,textAlign:'center',padding:'8px 0',background:'#F9FAFB',borderRadius:8}}>
                        <div style={{fontSize:14,fontWeight:700,color}}>{val}</div>
                        <div style={{fontSize:10,color:MU,marginTop:1}}>{label}</div>
                      </div>
                    ))}
                  </div>
                  <ResponsiveContainer width="100%" height={200}>
                    <AreaChart data={dailyCalls}>
                      <defs>
                        <linearGradient id="callGrad2" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={P} stopOpacity={0.25}/><stop offset="95%" stopColor={P} stopOpacity={0}/></linearGradient>
                        <linearGradient id="connGrad2" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={GN} stopOpacity={0.25}/><stop offset="95%" stopColor={GN} stopOpacity={0}/></linearGradient>
                      </defs>
                      <XAxis dataKey="date" fontSize={9} tickFormatter={d=>d.slice(5)} tick={{fill:MU}}/>
                      <YAxis fontSize={10} tick={{fill:MU}} width={24}/>
                      <Tooltip cursor={{fill:'rgba(99,102,241,0.04)'}} contentStyle={{borderRadius:10,border:'1px solid #E5E7EB',fontSize:12}}/>
                      <Area type="monotone" dataKey="total" stroke={P} fill="url(#callGrad2)" strokeWidth={2} name="Total Calls"/>
                      <Area type="monotone" dataKey="connected" stroke={GN} fill="url(#connGrad2)" strokeWidth={2} name="Connected"/>
                    </AreaChart>
                  </ResponsiveContainer>
                </> : <div style={{height:200,display:'flex',alignItems:'center',justifyContent:'center',color:MU,fontSize:13}}>No call data for this period</div>}
              </div>
            </div>

            {/* ── Funnel + City ── */}
            <div className="grid-2" style={{marginBottom:20}}>

              {/* Conversion Funnel */}
              <div style={s.card}>
                <div style={{fontSize:15,fontWeight:700,marginBottom:4}}>🔽 Conversion Funnel</div>
                <div style={{fontSize:12,color:MU,marginBottom:16}}>How leads move from cold to won</div>
                {conversionRates.length>0 ? <div style={{display:'flex',flexDirection:'column',gap:10}}>
                  {conversionRates.map((cr,i)=>{
                    const pct = cr.count ? (cr.count/Math.max(...conversionRates.map(x=>x.count))*100) : 0;
                    return <div key={cr.stage}>
                      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:5}}>
                        <div style={{display:'flex',alignItems:'center',gap:8}}>
                          <div style={{width:8,height:8,borderRadius:99,background:COLORS[i%COLORS.length],flexShrink:0}}/>
                          <span style={{fontSize:13,fontWeight:600,color:TX}}>{cr.stage}</span>
                        </div>
                        <div style={{display:'flex',gap:8,alignItems:'center'}}>
                          <span style={{fontSize:13,fontWeight:700,color:TX}}>{cr.count}</span>
                          {cr.rate!==undefined&&<span style={{fontSize:11,color:MU}}>({cr.rate}%)</span>}
                        </div>
                      </div>
                      <div style={{height:8,background:'#F3F4F6',borderRadius:99,overflow:'hidden'}}>
                        <div style={{height:'100%',width:pct+'%',background:COLORS[i%COLORS.length],borderRadius:99,transition:'width .5s'}}/>
                      </div>
                    </div>;
                  })}
                </div> : <div style={{display:'flex',flexDirection:'column',gap:10}}>
                  {(()=>{
                    const totalC=companies.filter(c=>anFilter==='all'||c.salesperson===anFilter).length;
                    const met=new Set(fm.map(m=>String(m.companyId))).size;
                    const won=companies.filter(c=>(anFilter==='all'||c.salesperson===anFilter)&&c.dealStatus==='won').length;
                    return [{stage:'Total Companies',count:totalC,color:'#6366F1'},{stage:'Meeting Done',count:met,color:'#F59E0B'},{stage:'Won',count:won,color:'#10B981'}].map((cr,i)=>(
                      <div key={cr.stage}>
                        <div style={{display:'flex',justifyContent:'space-between',marginBottom:5}}>
                          <span style={{fontSize:13,fontWeight:600}}>{cr.stage}</span>
                          <span style={{fontSize:13,fontWeight:700}}>{cr.count}</span>
                        </div>
                        <div style={{height:8,background:'#F3F4F6',borderRadius:99,overflow:'hidden'}}>
                          <div style={{height:'100%',width:totalC?(cr.count/totalC*100)+'%':'0%',background:cr.color,borderRadius:99,transition:'width .5s'}}/>
                        </div>
                      </div>
                    ));
                  })()}
                </div>}
              </div>

              {/* City Distribution */}
              {cityAnalytics.length>0&&<div style={s.card}>
                <div style={{fontSize:15,fontWeight:700,marginBottom:4}}>🏙 City Coverage</div>
                <div style={{fontSize:12,color:MU,marginBottom:14}}>Company distribution across cities</div>
                <div style={{display:'flex',alignItems:'center',gap:16}}>
                  <ResponsiveContainer width="45%" height={180}>
                    <PieChart>
                      <Pie data={cityAnalytics} dataKey="companies" nameKey="city" cx="50%" cy="50%" innerRadius={40} outerRadius={72} paddingAngle={3}>
                        {cityAnalytics.map((_,i)=><Cell key={i} fill={COLORS[i%COLORS.length]}/>)}
                      </Pie>
                      <Tooltip contentStyle={{borderRadius:10,border:'1px solid #E5E7EB',fontSize:12}}/>
                    </PieChart>
                  </ResponsiveContainer>
                  <div style={{flex:1}}>
                    {cityAnalytics.map((c,i)=>{
                      const maxCity=Math.max(...cityAnalytics.map(x=>x.companies),1);
                      return <div key={c.city} style={{marginBottom:8}}>
                        <div style={{display:'flex',justifyContent:'space-between',fontSize:12,marginBottom:3}}>
                          <div style={{display:'flex',alignItems:'center',gap:6}}><div style={{width:8,height:8,borderRadius:3,background:COLORS[i%COLORS.length],flexShrink:0}}/><span style={{fontWeight:500}}>{c.city}</span></div>
                          <span style={{fontWeight:700}}>{c.companies}</span>
                        </div>
                        <div style={{height:4,background:'#F3F4F6',borderRadius:99}}><div style={{height:'100%',width:(c.companies/maxCity*100)+'%',background:COLORS[i%COLORS.length],borderRadius:99}}/></div>
                      </div>;
                    })}
                  </div>
                </div>
              </div>}
            </div>

            {/* ── Meeting Status Tracker ── */}
            <div style={{...s.card,marginBottom:20}}>
              <div style={{fontSize:15,fontWeight:700,marginBottom:4}}>📅 Meeting Pipeline Status</div>
              <div style={{fontSize:12,color:MU,marginBottom:16}}>Where are your meetings right now?</div>
              <div className="grid-5">
                {[
                  {label:'Tentative',icon:'⏳',filter:'Tentative',bg:'#FFFBEB',border:'#FCD34D',fg:'#D97706'},
                  {label:'Confirmed',icon:'✅',filter:'Confirmed',bg:GL,border:'#BBF7D0',fg:GN},
                  {label:'Completed',icon:'🏁',filter:['Completed','Done'],bg:PL,border:'#C7D2FE',fg:P},
                  {label:'Rescheduled',icon:'🔄',filter:'Rescheduled',bg:'#FFF7ED',border:'#FED7AA',fg:'#EA580C'},
                  {label:'Cancelled',icon:'❌',filter:'Cancelled',bg:RL,border:'#FECACA',fg:RD},
                ].map(({label,icon,filter,bg,border,fg})=>{
                  const cnt = fm.filter(m=>Array.isArray(filter)?filter.includes(m.stage):m.stage===filter).length;
                  const total = fm.length || 1;
                  const pct = Math.round(cnt/total*100);
                  return <div key={label} style={{textAlign:'center',padding:'16px 12px',background:bg,borderRadius:12,border:`1px solid ${border}`}}>
                    <div style={{fontSize:22,marginBottom:4}}>{icon}</div>
                    <div style={{fontSize:26,fontWeight:800,color:fg,lineHeight:1}}>{cnt}</div>
                    <div style={{fontSize:11,fontWeight:600,color:fg,marginTop:4}}>{label}</div>
                    <div style={{fontSize:10,color:MU,marginTop:2}}>{pct}% of meetings</div>
                  </div>;
                })}
              </div>
            </div>

            {/* ── Pipeline by Stage + Dev Category ── */}
            <div className="grid-2" style={{marginBottom:20}}>

              {/* Pipeline by Stage */}
              {Object.entries(funnel).filter(([,v])=>v>0).length>0&&<div style={s.card}>
                <div style={{fontSize:15,fontWeight:700,marginBottom:4}}>🏗 Pipeline by Stage</div>
                <div style={{fontSize:12,color:MU,marginBottom:14}}>Active companies at each SPOC stage</div>
                <div style={{display:'flex',flexDirection:'column',gap:8}}>
                  {Object.entries(funnel).filter(([,v])=>v>0).map(([stage,count],i)=>{
                    const maxStage=Math.max(...Object.values(funnel),1);
                    return <div key={stage} style={{display:'flex',alignItems:'center',gap:10}}>
                      <div style={{width:110,fontSize:11,color:MU,textAlign:'right',flexShrink:0,fontWeight:500,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{stage}</div>
                      <div style={{flex:1,height:20,background:'#F3F4F6',borderRadius:6,overflow:'hidden',position:'relative'}}>
                        <div style={{position:'absolute',inset:0,width:(count/maxStage*100)+'%',background:COLORS[i%COLORS.length]+'CC',borderRadius:6,transition:'width .5s'}}/>
                        <div style={{position:'absolute',inset:0,display:'flex',alignItems:'center',paddingLeft:8}}>
                          <span style={{fontSize:11,fontWeight:700,color:TX}}>{count}</span>
                        </div>
                      </div>
                    </div>;
                  })}
                </div>
              </div>}

              {/* Developer Category Breakdown */}
              {(()=>{
                const cats = config.developerCategories||[];
                const catData=[...cats.map((cat,i)=>{const items=leadBank.filter(x=>x.developerCategory===cat);return{cat,color:COLORS[i%COLORS.length],total:items.length,inProgress:items.filter(x=>x.status==='in-progress').length,done:items.filter(x=>x.status==='done').length};}),(()=>{const u=leadBank.filter(x=>!x.developerCategory);return{cat:'Uncategorized',color:LT,total:u.length,inProgress:u.filter(x=>x.status==='in-progress').length,done:u.filter(x=>x.status==='done').length};})()].filter(x=>x.total>0);
                if(!catData.length) return null;
                return <div style={s.card}>
                  <div style={{fontSize:15,fontWeight:700,marginBottom:4}}>🏷 Developer Category Breakdown</div>
                  <div style={{fontSize:12,color:MU,marginBottom:14}}>Lead Bank conversion by category</div>
                  <div style={{display:'flex',flexDirection:'column',gap:10}}>
                    {catData.map(d=>{
                      const rate=d.total?Math.round(d.done/d.total*100):0;
                      return <div key={d.cat} style={{padding:'10px 12px',background:'#F9FAFB',borderRadius:10,border:'1px solid #E5E7EB'}}>
                        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
                          <span style={{fontSize:12,fontWeight:600,color:TX}}>{d.cat}</span>
                          <div style={{display:'flex',gap:6}}>
                            <span style={{fontSize:10,color:MU}}>{d.total} total</span>
                            <span style={{fontSize:10,fontWeight:700,color:rate>=30?GN:rate>=15?YL:RD}}>{rate}%</span>
                          </div>
                        </div>
                        <div style={{height:6,background:'#E5E7EB',borderRadius:99,overflow:'hidden'}}>
                          <div style={{height:'100%',width:rate+'%',background:d.color,borderRadius:99,transition:'width .5s'}}/>
                        </div>
                        <div style={{display:'flex',gap:12,marginTop:5,fontSize:10,color:MU}}>
                          <span>🔄 {d.inProgress} in progress</span>
                          <span style={{color:GN}}>✅ {d.done} converted</span>
                        </div>
                      </div>;
                    })}
                  </div>
                </div>;
              })()}
            </div>
          </>;
        })()}


        {/* SOPs */}
        {page==='sops' && isAdmin && !detailId && (()=>{
          const isDummy = sops.length === 0;
          const allItems = isDummy ? DUMMY_SOPS : sops;
          const getStage = item => {
            const tags = (item.tags||[]).map(t=>t.toLowerCase());
            const text = [item.title,item.useCaseKey||'',item.whereToUse||'',...tags].join(' ').toLowerCase();
            for(const st of SOP_STAGES) { if(tags.includes(st.key)||text.includes(st.key.replace('-',' '))) return st.key; }
            if(text.includes('meeting')||text.includes('demo')) return 'meeting';
            if(text.includes('proposal')||text.includes('quote')) return 'proposal';
            if(text.includes('follow')||text.includes('nurtur')) return 'follow-up';
            if(text.includes('intro')||text.includes('cold')||text.includes('first')) return 'first-contact';
            if(text.includes('clos')||text.includes('won')||text.includes('deal')) return 'closing';
            return 'general';
          };
          const matchesSearch = item => {
            if(!sopSearch) return true;
            const q = sopSearch.toLowerCase();
            return [item.title,item.useCaseKey||'',item.whereToUse||'',item.category,...(item.tags||[]),...(item.steps||[])].join(' ').toLowerCase().includes(q);
          };
          const matchesFilter = item => {
            if(sopFilter==='all') return true;
            if(SOP_STAGES.some(st=>st.key===sopFilter)) return getStage(item)===sopFilter;
            return item.category===sopFilter;
          };
          const filtered = allItems.filter(x => matchesSearch(x) && matchesFilter(x));
          const grouped = {};
          SOP_STAGES.forEach(st=>{grouped[st.key]=[];});
          filtered.forEach(item=>{const stg=getStage(item);(grouped[stg]||(grouped[stg]=[])).push(item);});
          const copyText = (text) => { navigator.clipboard.writeText(text).then(()=>{}).catch(()=>{}); };
          const toggleCheck = (sopId, stepIdx) => {
            setSopChecked(prev => { const key = sopId+'_'+stepIdx; return {...prev, [key]: !prev[key]}; });
          };
          const isAllChecked = (sopId, steps) => steps.every((_,i)=>sopChecked[sopId+'_'+i]);
          const toggleExpand = (id) => setSopExpanded(prev=>({...prev,[id]:!prev[id]}));
          const totalChecklists = allItems.filter(x=>x.category==='Checklist').length;
          const totalTemplates = allItems.filter(x=>['Email Template','WhatsApp Template'].includes(x.category)).length;
          const totalScripts = allItems.filter(x=>x.category==='Calling Script').length;

          return <>
            {/* Header */}
            <div className="page-header-row" style={{alignItems:'flex-start',marginBottom:20}}>
              <div>
                <h1 style={{fontSize:24,fontWeight:700,margin:0}}>Sales Playbook</h1>
                <div style={{fontSize:13,color:MU,marginTop:4}}>Your checklists, scripts & templates — organized by sales stage</div>
              </div>
              <div style={{display:'flex',gap:8}}>
                <input style={{...s.inp,width:220}} placeholder="Search SOPs..." value={sopSearch} onChange={e=>setSopSearch(e.target.value)}/>
                {isAdmin&&<motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={s.btn()} onClick={()=>openSopForm()}>+ Add SOP</motion.button>}
              </div>
            </div>

            {isDummy&&<div style={{background:'linear-gradient(135deg,#EEF2FF,#F5F3FF)',border:'1px solid #C7D2FE',borderRadius:12,padding:'14px 18px',fontSize:13,color:'#4338CA',marginBottom:16,display:'flex',alignItems:'center',gap:10}}>
              <span style={{fontSize:20}}>💡</span>
              <div><b>Sample playbook loaded.</b> Add your own SOPs and checklists for every step in your sales process.</div>
            </div>}

            {/* Quick stats */}
            <div className="grid-4" style={{marginBottom:20}}>
              {[{label:'Total SOPs',value:allItems.length,icon:'📋',color:P},{label:'Checklists',value:totalChecklists,icon:'✅',color:GN},{label:'Templates',value:totalTemplates,icon:'✉️',color:'#0891B2'},{label:'Scripts',value:totalScripts,icon:'📞',color:RD}].map(st=>
                <div key={st.label} style={{background:WH,border:'1px solid '+BD,borderRadius:12,padding:'14px 18px',display:'flex',alignItems:'center',gap:12}}>
                  <div style={{width:40,height:40,borderRadius:10,background:st.color+'15',display:'flex',alignItems:'center',justifyContent:'center',fontSize:18}}>{st.icon}</div>
                  <div><div style={{fontSize:20,fontWeight:700,color:TX}}>{st.value}</div><div style={{fontSize:11,color:MU}}>{st.label}</div></div>
                </div>
              )}
            </div>

            {/* Filter: by stage or by category */}
            <div style={{display:'flex',gap:6,marginBottom:18,flexWrap:'wrap',alignItems:'center'}}>
              <span style={{fontSize:11,fontWeight:600,color:LT,textTransform:'uppercase',marginRight:4}}>Filter:</span>
              <div style={s.chip(sopFilter==='all')} onClick={()=>setSopFilter('all')}>All</div>
              <span style={{width:1,height:20,background:BD,margin:'0 4px'}}/>
              {SOP_STAGES.map(st=>
                <div key={st.key} style={{...s.chip(sopFilter===st.key),display:'flex',alignItems:'center',gap:4}} onClick={()=>setSopFilter(st.key)}><span style={{fontSize:12}}>{st.icon}</span>{st.label}</div>
              )}
              <span style={{width:1,height:20,background:BD,margin:'0 4px'}}/>
              {[['Email','Email Template'],['WhatsApp','WhatsApp Template'],['Script','Calling Script'],['Checklist','Checklist'],['Process','Process']].map(([label,val])=>
                <div key={val} style={s.chip(sopFilter===val)} onClick={()=>setSopFilter(val)}>{SOP_CAT_ICONS[val]||''} {label}</div>
              )}
            </div>

            {/* ── SOP Grid Layout ── */}
            {(()=>{
              const CAT_META = {
                'Email Template':    {icon:'✉️', color:'#0891B2', bg:'#E0F7FA', label:'Email Template'},
                'WhatsApp Template': {icon:'💬', color:'#25D366', bg:'#E8F5E9', label:'WhatsApp'},
                'Calling Script':    {icon:'📞', color:'#EF4444', bg:'#FEE2E2', label:'Script'},
                'Checklist':         {icon:'✅', color:'#10B981', bg:'#D1FAE5', label:'Checklist'},
                'Process':           {icon:'⚙️', color:'#6366F1', bg:'#EEF2FF', label:'Process'},
              };

              // ── Card renderer ──
              const renderSopCard = (item, stageColor) => {
                const cm = CAT_META[item.category]||{icon:'📋',color:P,bg:PL,label:item.category};
                const isOpen = sopExpanded[item._id];
                const hasSteps = item.steps?.length > 0;
                const hasBody = !!item.body;
                const checkedCount = hasSteps ? item.steps.filter((_,i)=>sopChecked[item._id+'_'+i]).length : 0;
                const allDone = hasSteps && checkedCount===item.steps.length;
                const pct = hasSteps ? Math.round(checkedCount/item.steps.length*100) : 0;

                return <div key={item._id} style={{
                  background:WH, border:`1.5px solid ${allDone?GN+'55':isOpen?stageColor+'44':BD}`,
                  borderRadius:14, overflow:'hidden', transition:'all .2s',
                  display:'flex', flexDirection:'column',
                  boxShadow: isOpen?`0 4px 16px ${stageColor}18`:'0 1px 3px #0000000A'
                }}>
                  {/* Top accent stripe */}
                  <div style={{height:3, background: allDone?GN:stageColor, borderRadius:'14px 14px 0 0'}}/>

                  {/* Card header — always visible */}
                  <div onClick={()=>toggleExpand(item._id)} style={{padding:'14px 16px', cursor:'pointer', flex:1}}>
                    <div style={{display:'flex',alignItems:'flex-start',gap:10,marginBottom:8}}>
                      <div style={{width:36,height:36,borderRadius:10,background:cm.bg,display:'flex',alignItems:'center',justifyContent:'center',fontSize:18,flexShrink:0,border:`1px solid ${cm.color}22`}}>{cm.icon}</div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:13,fontWeight:700,color:TX,lineHeight:1.3,marginBottom:4}}>{item.title}</div>
                        <span style={{fontSize:10,background:cm.bg,color:cm.color,padding:'2px 8px',borderRadius:99,fontWeight:600,border:`1px solid ${cm.color}33`}}>{cm.label}</span>
                        {allDone&&<span style={{fontSize:10,background:GL,color:GN,padding:'2px 8px',borderRadius:99,fontWeight:700,border:'1px solid '+GN+'44',marginLeft:4}}>✓ Done</span>}
                      </div>
                      {!isDummy&&<div style={{display:'flex',gap:3,flexShrink:0}} onClick={e=>e.stopPropagation()}>
                        <button style={{width:24,height:24,borderRadius:6,border:'1px solid '+BD,background:WH,cursor:'pointer',fontSize:10,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={()=>openSopForm(item)}>✏️</button>
                        {isAdmin&&<DeleteIconBtn size={24} onClick={()=>setConfirmDel({fn:async()=>{await api.deleteSop(item._id);loadData();},msg:`Delete "${item.title}"?`})}/>}
                      </div>}
                    </div>

                    {/* Trigger line */}
                    {item.whereToUse&&<div style={{fontSize:12,color:MU,lineHeight:1.4,marginBottom:8}}>{item.whereToUse}</div>}

                    {/* Progress bar for checklists */}
                    {hasSteps&&<div>
                      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}>
                        <span style={{fontSize:10,color:MU,fontWeight:500}}>{checkedCount} of {item.steps.length} steps done</span>
                        <span style={{fontSize:10,fontWeight:700,color:pct===100?GN:stageColor}}>{pct}%</span>
                      </div>
                      <div style={{height:4,background:'#E5E7EB',borderRadius:99,overflow:'hidden'}}>
                        <div style={{height:'100%',width:pct+'%',background:pct===100?GN:stageColor,borderRadius:99,transition:'width .4s'}}/>
                      </div>
                    </div>}

                    {/* Expand toggle */}
                    <div style={{display:'flex',justifyContent:'center',marginTop:10}}>
                      <div style={{display:'flex',alignItems:'center',gap:4,fontSize:11,color:isOpen?stageColor:LT,fontWeight:600,padding:'4px 12px',borderRadius:99,background:isOpen?stageColor+'10':'#F3F4F6',border:`1px solid ${isOpen?stageColor+'33':BD}`,transition:'all .15s'}}>
                        {isOpen?'Hide details':'View details'}
                        <span style={{transform:isOpen?'rotate(180deg)':'none',transition:'transform .2s',fontSize:9}}>▼</span>
                      </div>
                    </div>
                  </div>

                  {/* Expanded body */}
                  {isOpen&&<div style={{borderTop:`1px solid ${stageColor}22`,background:'#F8FAFC'}}>
                    <div style={{padding:'14px 16px',display:'flex',flexDirection:'column',gap:10}}>

                      {item.subject&&<div style={{padding:'10px 12px',background:'#EFF6FF',borderRadius:9,border:'1px solid #BFDBFE'}}>
                        <div style={{fontSize:10,fontWeight:700,color:'#1D4ED8',textTransform:'uppercase',letterSpacing:'.05em',marginBottom:4}}>📨 Subject Line</div>
                        <div style={{fontSize:12.5,color:TX,fontWeight:500,marginBottom:6}}>{item.subject}</div>
                        <button onClick={()=>copyText(item.subject)} style={{fontSize:10,padding:'3px 10px',borderRadius:6,border:'1px solid #BFDBFE',background:WH,cursor:'pointer',color:'#1D4ED8',fontWeight:600}}>Copy subject</button>
                      </div>}

                      {hasBody&&<div>
                        <div style={{fontSize:10,fontWeight:700,color:MU,textTransform:'uppercase',letterSpacing:'.05em',marginBottom:6}}>
                          {item.category==='Calling Script'?'📞 Script':'💬 Template'}
                        </div>
                        <div style={{position:'relative'}}>
                          <pre style={{whiteSpace:'pre-wrap',fontFamily:'inherit',fontSize:12.5,lineHeight:1.8,margin:0,color:TX,background:WH,borderRadius:9,padding:'12px 14px',maxHeight:220,overflowY:'auto',border:'1px solid #E2E8F0'}}>{item.body}</pre>
                          <button onClick={()=>copyText(item.body)} style={{position:'absolute',top:7,right:7,padding:'3px 10px',borderRadius:7,border:'1px solid #CBD5E1',background:WH,cursor:'pointer',fontSize:10,fontWeight:700,color:P}}>📋 Copy</button>
                        </div>
                      </div>}

                      {hasSteps&&<div>
                        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:6}}>
                          <div style={{fontSize:10,fontWeight:700,color:MU,textTransform:'uppercase',letterSpacing:'.05em'}}>✅ Steps</div>
                          {allDone&&<button style={{fontSize:10,padding:'2px 9px',borderRadius:6,border:'1px solid '+BD,background:WH,cursor:'pointer',color:MU}} onClick={()=>{const up={};item.steps.forEach((_,i)=>{up[item._id+'_'+i]=false;});setSopChecked(prev=>({...prev,...up}));}}>Reset</button>}
                        </div>
                        <div style={{display:'flex',flexDirection:'column',gap:3}}>
                          {item.steps.map((st,i)=>{
                            const checked=!!sopChecked[item._id+'_'+i];
                            return <div key={i} onClick={()=>toggleCheck(item._id,i)} style={{display:'flex',alignItems:'flex-start',gap:9,padding:'8px 11px',borderRadius:8,cursor:'pointer',background:checked?GN+'07':WH,border:'1px solid '+(checked?GN+'44':'#E5E7EB'),transition:'all .15s'}}>
                              <div style={{width:18,height:18,borderRadius:5,border:`2px solid ${checked?GN:'#CBD5E1'}`,background:checked?GN:WH,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,marginTop:1,transition:'all .15s'}}>
                                {checked&&<span style={{color:WH,fontSize:10,fontWeight:800}}>✓</span>}
                              </div>
                              <span style={{fontSize:12,color:checked?MU:TX,textDecoration:checked?'line-through':'none',opacity:checked?.6:1,flex:1,lineHeight:1.4}}>{st}</span>
                              <span style={{fontSize:9,color:LT,fontWeight:700,flexShrink:0,marginTop:3}}>#{i+1}</span>
                            </div>;
                          })}
                        </div>
                      </div>}

                      {item.notes&&<div style={{display:'flex',gap:8,padding:'9px 12px',background:'#FFFBEB',borderRadius:8,border:'1px solid #FDE68A'}}>
                        <span style={{fontSize:14,flexShrink:0}}>💡</span>
                        <div style={{fontSize:12,color:'#78350F',lineHeight:1.5}}>{item.notes}</div>
                      </div>}
                    </div>
                  </div>}
                </div>;
              };

              // ── Stage section renderer ──
              const renderStageSection = (stage, items) => {
                const isExp = sopExpanded['stage_'+stage.key] !== false;
                const allStageDone = items.length>0 && items.every(item=>!item.steps?.length||isAllChecked(item._id,item.steps));
                return <div key={stage.key} style={{marginBottom:28}}>
                  {/* Stage header */}
                  <div onClick={()=>setSopExpanded(prev=>({...prev,['stage_'+stage.key]:!isExp}))}
                    style={{display:'flex',alignItems:'center',gap:12,padding:'12px 18px',background:isExp?stage.color+'0C':WH,border:`1.5px solid ${isExp?stage.color+'55':BD}`,borderRadius:13,cursor:'pointer',marginBottom:isExp?14:0,transition:'all .15s',userSelect:'none'}}>
                    <div style={{width:38,height:38,borderRadius:11,background:allStageDone?GL:stage.color+'18',display:'flex',alignItems:'center',justifyContent:'center',fontSize:20,flexShrink:0,border:`1.5px solid ${allStageDone?GN+'44':stage.color+'33'}`}}>
                      {allStageDone?'✅':stage.icon}
                    </div>
                    <div style={{flex:1}}>
                      <div style={{display:'flex',alignItems:'center',gap:8}}>
                        <span style={{fontSize:15,fontWeight:800,color:allStageDone?GN:TX,letterSpacing:'-0.02em'}}>{stage.label}</span>
                        <span style={{fontSize:10,background:stage.color+'18',color:stage.color,padding:'2px 10px',borderRadius:99,fontWeight:700,border:`1px solid ${stage.color}33`}}>{items.length} {items.length===1?'SOP':'SOPs'}</span>
                        {allStageDone&&<span style={{fontSize:10,background:GL,color:GN,padding:'2px 8px',borderRadius:99,fontWeight:700,border:'1px solid '+GN+'44'}}>All done ✓</span>}
                      </div>
                      <div style={{fontSize:12,color:MU,marginTop:2}}>{stage.desc}</div>
                    </div>
                    <div style={{width:28,height:28,borderRadius:8,background:stage.color+'15',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,transition:'transform .2s',transform:isExp?'rotate(180deg)':'none'}}>
                      <span style={{fontSize:11,color:stage.color,fontWeight:700}}>▼</span>
                    </div>
                  </div>

                  {/* Grid of cards */}
                  {isExp&&(items.length
                    ? <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(290px,1fr))',gap:14}}>
                        {items.map(item=>renderSopCard(item, stage.color))}
                      </div>
                    : <div style={{textAlign:'center',padding:'20px',color:LT,fontSize:13,fontStyle:'italic',background:'#FAFAFA',borderRadius:10,border:'1px dashed '+BD}}>
                        No SOPs in this stage yet{isAdmin&&<> — <span style={{color:P,fontStyle:'normal',fontWeight:600,cursor:'pointer'}} onClick={()=>openSopForm()}>+ add one</span></>}
                      </div>
                  )}
                </div>;
              };

              // ── Which stages/items to show ──
              const useStageView = sopFilter==='all' || SOP_STAGES.some(st=>st.key===sopFilter);

              if(useStageView) {
                const visibleStages = SOP_STAGES.filter(st=> sopFilter==='all' ? grouped[st.key]?.length>0 : st.key===sopFilter);
                if(!visibleStages.length) return <div style={{textAlign:'center',padding:48,color:LT,fontSize:13}}>No SOPs match your search.</div>;
                return <div>{visibleStages.map(stage=>renderStageSection(stage, grouped[stage.key]||[]))}</div>;
              } else {
                // Flat filtered by category
                if(!filtered.length) return <div style={{textAlign:'center',padding:48,color:LT,fontSize:13}}>No SOPs match your filter.</div>;
                return <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(290px,1fr))',gap:14}}>
                  {filtered.map(item=>renderSopCard(item, P))}
                </div>;
              }
            })()}
          </>;
        })()}

        {/* ═══ TOP DEVELOPERS ═══ */}
        {page==='topdevelopers' && !detailId && isAdmin && <>
          <div className="page-header-row" style={{alignItems:'center',marginBottom:20}}>
            <div>
              <h1 style={{fontSize:24,fontWeight:700,margin:0}}>⭐ Top Developers</h1>
              <div style={{fontSize:13,color:MU,marginTop:4}}>Mark companies as top developers by city. Employees can see the ⭐ indicator.</div>
            </div>
            <div className="page-header-actions">
              <input style={{...s.inp,width:200}} placeholder="Search developer..." value={tdSearch} onChange={e=>setTdSearch(e.target.value)}/>
              <Dropdown style={{...s.sel,width:160}} value={tdCity} onChange={e=>setTdCity(e.target.value)}><option value="">All Cities</option>{visibleCities.map(c=><option key={c}>{c}</option>)}</Dropdown>
            </div>
          </div>
          {/* ── Prospect: Bulk add & mark as top ── */}
          <div style={{...s.card, marginBottom:20, padding:20}}>
            <div style={{fontWeight:700, fontSize:15, marginBottom:4}}>📋 Prospect — Bulk Mark as Top Developer</div>
            <div style={{fontSize:12, color:MU, marginBottom:14}}>Paste developer names (one per line). Select a city and submit — they'll be added to Lead Bank and marked as ⭐ Top Developer.</div>
            <div className="topdev-bulk-form">
              <textarea
                style={{...s.inp, width:380, minHeight:110, resize:'vertical', fontFamily:'monospace', fontSize:12, padding:10}}
                placeholder={"Prestige Estates\nSobha Limited\nBrigade Group\n..."}
                value={tdProspectText}
                onChange={e=>{setTdProspectText(e.target.value); setTdProspectResult(null);}}
              />
              <div style={{display:'flex', flexDirection:'column', gap:10, minWidth:180}}>
                <Dropdown style={{...s.sel, width:180}} value={tdProspectCity} onChange={e=>setTdProspectCity(e.target.value)}>
                  <option value="">Select City</option>
                  {visibleCities.map(c=><option key={c}>{c}</option>)}
                </Dropdown>
                {(()=>{
                  const lines = tdProspectText.split('\n').map(l=>l.trim().replace(/^["',;]+|["',;]+$/g,'').trim()).filter(l=>l.length>1);
                  return <div style={{fontSize:12, color:MU}}>{lines.length} name{lines.length!==1?'s':''} ready</div>;
                })()}
                <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}}
                  style={{...s.btn(), background:'#F59E0B', border:'none', color:WH, fontWeight:700, fontSize:13, padding:'9px 18px'}}
                  disabled={tdProspectLoading || !tdProspectCity || !tdProspectText.trim()}
                  onClick={async()=>{
                    const names = tdProspectText.split('\n').map(l=>l.trim().replace(/^["',;]+|["',;]+$/g,'').trim()).filter(l=>l.length>1);
                    if(!names.length){ alert('No valid names found'); return; }
                    if(!tdProspectCity){ alert('Please select a city'); return; }
                    setTdProspectLoading(true); setTdProspectResult(null);
                    try{
                      const r = await api.bulkTopDevelopers({ names, city: tdProspectCity });
                      setTdProspectResult(r);
                      setTdProspectText('');
                      const scrollY = window.scrollY;
                      const [leads, cos] = await Promise.all([
                        api.getLeadBank().catch(() => null),
                        api.getCompanies().catch(() => null),
                      ]);
                      if (leads) setLeadBank(leads);
                      if (cos) setCompanies(cos);
                      requestAnimationFrame(() => window.scrollTo(0, scrollY));
                    }catch(e){ alert(e.message); }
                    finally{ setTdProspectLoading(false); }
                  }}>
                  {tdProspectLoading ? 'Marking...' : '⭐ Mark All as Top'}
                </motion.button>
                {tdProspectResult && <div style={{fontSize:12, fontWeight:600, background:'#ECFDF5', border:'1px solid #BBF7D0', borderRadius:8, padding:'10px 12px', lineHeight:1.7}}>
                  <div style={{color:'#059669'}}>✅ Done — {tdProspectResult.total} processed</div>
                  {tdProspectResult.added>0 && <div style={{color:'#047857'}}>➕ {tdProspectResult.added} new added to Prospects</div>}
                  {tdProspectResult.updated>0 && <div style={{color:'#1D4ED8'}}>🔄 {tdProspectResult.updated} existing Prospect{tdProspectResult.updated!==1?'s':''} marked ⭐</div>}
                  {tdProspectResult.skippedCompany>0 && <div style={{color:'#92400E'}}>🏢 {tdProspectResult.skippedCompany} already in Companies — marked ⭐ there</div>}
                </div>}
              </div>
            </div>
          </div>
          {(()=>{
            const shown = buildTopDeveloperEntries(leadBank, companies).filter(x => {
              if (tdCity && !citiesMatch(x.city, tdCity)) return false;
              if (tdSearch && !x.name.toLowerCase().includes(tdSearch.toLowerCase())) return false;
              if (tdStars && topStarsOf(x) !== Number(tdStars)) return false;
              return true;
            }).sort((a, b) => a.name.localeCompare(b.name));
            const topCount = shown.filter(x => topStarsOf(x) > 0).length;
            return <>
              <div style={s.row}>
                <StatCard label="Total Developers" value={shown.length} color={P} icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>}/>
                <div style={{background:WH,borderRadius:14,padding:'16px 18px',flex:1,border:'1px solid #EBEBED'}}>
                  <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:14}}>
                    <div style={{width:34,height:34,borderRadius:10,background:'linear-gradient(135deg,#F59E0B,#F59E0BCC)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,color:'#fff'}}><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg></div>
                    <div style={{fontSize:10,fontWeight:600,color:MU,textTransform:'uppercase',letterSpacing:'.6px',lineHeight:1.3}}>Top Developers</div>
                  </div>
                  <div style={{fontSize:32,fontWeight:700,color:TX,letterSpacing:'-0.04em',lineHeight:1}}>{topCount}</div>
                  <div style={{marginTop:8}}>
                    <Dropdown plain style={{fontSize:11,color:'#F59E0B',fontWeight:600}} value={tdStars} onChange={e=>setTdStars(e.target.value)}>
                      <option value="">All ratings</option>
                      <option value="1">★ 1 star</option>
                      <option value="2">★★ 2 star</option>
                      <option value="3">★★★ 3 star</option>
                    </Dropdown>
                  </div>
                </div>
              </div>
              <div className="table-wrap" style={{marginTop:16}}>
              <table style={{width:'100%',borderCollapse:'collapse',...s.card,padding:0,overflow:'hidden',marginTop:0}}>
                <thead><tr>{['Developer','City','Source','Status','Rating'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
                <tbody>{shown.map(item=>{
                  const isLead=item._src==='lead';
                  const itemId=String(item._id);
                  const stars=topStarsOf(item);
                  const setStars=async(n)=>{
                    const scrollY = window.scrollY;
                    try{
                      if(isLead){await api.toggleTopDeveloperLead(itemId,n);setLeadBank(prev=>prev.map(x=>String(x._id)===itemId?{...x,isTopDeveloper:n>0,topStars:n}:x));}
                      else{await api.toggleTopDeveloperCompany(itemId,n);setCompanies(prev=>prev.map(x=>String(x._id)===itemId?{...x,isTopDeveloper:n>0,topStars:n}:x));}
                    }catch(e){alert(e.message);}
                    finally{requestAnimationFrame(()=>window.scrollTo(0,scrollY));}
                  };
                  return <tr key={itemId} style={{background:stars>0?'#FFFBEB':''}}>
                    <td style={{...s.td,fontWeight:600}}>{stars>0&&<span style={{marginRight:4,color:'#F59E0B',letterSpacing:-1}}>{'★'.repeat(stars)}</span>}{item.name}</td>
                    <td style={s.td}>{item.city}</td>
                    <td style={s.td}><span style={s.tag(PL,P)}>{isLead?'Lead Bank':'Company'}</span></td>
                    <td style={s.td}>{isLead?<span style={s.tag('#F3F4F6',MU)}>{item.status||'unmined'}</span>:<span style={s.tag(GL,GN)}>Active</span>}</td>
                    <td style={s.td}>
                      <div style={{display:'flex',alignItems:'center',gap:2}} title={stars?`${stars} star — click a star to change, or the last lit star to remove`:'Click a star to mark as Top Developer'}>
                        {[1,2,3].map(n=>
                          <span key={n} onClick={(ev)=>{ev.preventDefault();ev.stopPropagation();setStars(stars===n?0:n);}}
                            style={{cursor:'pointer',fontSize:20,lineHeight:1,color:n<=stars?'#F59E0B':'#D1D5DB',userSelect:'none'}}>★</span>
                        )}
                        {stars===0&&<span style={{fontSize:11,color:MU,marginLeft:6}}>Mark as Top</span>}
                      </div>
                    </td>
                  </tr>;
                })}
                {!shown.length&&<tr><td colSpan={5} style={{...s.td,textAlign:'center',padding:40,color:MU}}>No developers found</td></tr>}
                </tbody>
              </table>
              </div>
            </>;
          })()}
        </>}

        {/* ═══ PROBABLE CLIENTS ═══ */}
        {page==='probableclients' && !detailId && isAdmin && (() => {
          const [pcExpanded, setPcExpanded] = [window.__pcExp||null, (v)=>{window.__pcExp=v;setForm(f=>({...f,_pcTick:(f._pcTick||0)+1}));}];
          const [pcFilter, setPcFilter] = [window.__pcFil||'All', (v)=>{window.__pcFil=v;setForm(f=>({...f,_pcTick:(f._pcTick||0)+1}));}];
          const STATUS_STYLE = {
            'In Progress': { bg: '#EEF2FF', color: '#4F46E5', border: '#C7D2FE' },
            'Focus':       { bg: '#EEF2FF', color: '#4F46E5', border: '#C7D2FE' },
            'Contacted':   { bg: '#EEF2FF', color: '#4F46E5', border: '#C7D2FE' },
            Converted:     { bg: GL, color: GN, border: '#BBF7D0' },
            Dropped:       { bg: RL, color: RD, border: '#FECACA' },
          };
          const fmtTrailDate = d => { if(!d) return '—'; try { const dt=new Date(d); if(isNaN(dt.getTime())) return '—'; const dd=String(dt.getDate()).padStart(2,'0'); const mm=String(dt.getMonth()+1).padStart(2,'0'); const yyyy=dt.getFullYear(); return `${dd}/${mm}/${yyyy}`; } catch { return '—'; } };
          return <>
          <div className="page-header-row" style={{alignItems:'center',marginBottom:20}}>
            <div>
              <h1 style={{fontSize:24,fontWeight:700,margin:0,letterSpacing:'-0.03em'}}>Probable Clients</h1>
              <div style={{fontSize:13,color:MU,marginTop:4}}>Focus leads with linked company trail, meetings & call logs.</div>
            </div>
            <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={s.btn()} onClick={()=>{
              setForm({name:'', companyId:'', meetingTrail:'', remarkText:'', status:'In Progress'});
              setFormErrors({});
              setModal({type:'probableClientStandalone'});
            }}>+ Add Lead</motion.button>
          </div>

          {/* Stats row */}
          <div style={s.row}>
            {['In Progress','Converted','Dropped'].map(st => {
              const cnt = probableClients.filter(pc=>(pc.status===st||(st==='In Progress'&&(pc.status==='Focus'||pc.status==='Contacted'||pc.status==='In Progress')))).length;
              const sty = STATUS_STYLE[st];
              return <StatCard key={st} label={st} value={cnt} color={sty.color} icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>}/>;
            })}
            <StatCard label="Total" value={probableClients.length} color={MU} icon={<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>}/>
          </div>

          {!probableClients.length && <div style={{...s.card,textAlign:'center',padding:40,color:MU}}>No probable clients yet. Click "+ Add Lead" to start tracking.</div>}

          {probableClients.length > 0 && <div style={{display:'flex',gap:8,marginTop:20,marginBottom:-4}}>
            {['All', 'In Progress', 'Converted', 'Dropped'].map(f=>(
              <motion.div whileHover={{scale:1.02}} whileTap={{scale:0.98}} key={f} onClick={()=>setPcFilter(f)} style={{padding:'6px 14px',fontSize:12,fontWeight:600,borderRadius:20,cursor:'pointer',border:'1px solid '+(pcFilter===f?P:BD),background:pcFilter===f?P:WH,color:pcFilter===f?WH:MU,transition:'all .15s'}}>
                {f}
              </motion.div>
            ))}
          </div>}

          <div style={{display:'flex',flexDirection:'column',gap:12,marginTop:16}}>
            {probableClients.filter(pc => pcFilter==='All' || (pcFilter==='In Progress' && (pc.status==='Focus'||pc.status==='Contacted'||pc.status==='In Progress')) || pc.status===pcFilter).map(pc => {
              const isOpen = pcExpanded === pc._id;
              const displayStatus = (pc.status==='Focus'||pc.status==='Contacted') ? 'In Progress' : pc.status;
              const sty = STATUS_STYLE[displayStatus] || STATUS_STYLE['In Progress'];
              const linkedCo = pc.companyId ? companies.find(c=>String(c._id)===String(pc.companyId)) : null;
              // Build unified trail: meetings (green), calls (blue), remarks (amber)
              const trail = [];
              (pc.linkedMeetings||[]).forEach(m => trail.push({ type:'meeting', date:m.date, label: m.agenda||'Meeting', detail: `${m.stage||''} · ${m.salesperson||''}`, color:'#059669', bg:'#ECFDF5' }));
              (pc.linkedCalls||[]).forEach(c => trail.push({ type:'call', date:c.date, label: c.connected?'Connected Call':'Call Attempt', detail: `${c.conversationOutcome||c.outcome||''} · ${c.salesperson||''}${c.notes?' — '+c.notes:''}`, color:'#2563EB', bg:'#EFF6FF' }));
              (pc.remarks||[]).forEach(r => trail.push({ type:'remark', date:r.date, label: 'Admin Remark', detail: r.text, by: r.addedBy, color:'#D97706', bg:'#FFFBEB' }));
              trail.sort((a,b)=>new Date(b.date)-new Date(a.date));

              return <motion.div key={pc._id} layout style={{...s.card,marginBottom:0,overflow:'hidden',borderLeft:`4px solid ${sty.color}`,transition:'box-shadow .2s',boxShadow:isOpen?'0 4px 20px rgba(0,0,0,.08)':'0 1px 3px rgba(0,0,0,.04)'}}>
                {/* Header row */}
                <div style={{display:'flex',alignItems:'center',gap:12}}>
                  <div style={{flex:1,minWidth:0,cursor:'pointer'}} onClick={()=>setPcExpanded(isOpen?null:pc._id)}>
                    <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                      <span style={{fontWeight:700,fontSize:15,color:TX}}>{pc.name}</span>
                      {linkedCo && <span style={{fontSize:12,color:P,fontWeight:500}}>@ {linkedCo.name}</span>}
                      {!linkedCo && pc.companyName && <span style={{fontSize:12,color:MU}}>@ {pc.companyName}</span>}
                      <span style={{...s.tag(sty.bg, sty.color),fontSize:10,fontWeight:700,border:'1px solid '+sty.border}}>{displayStatus}</span>
                    </div>
                    {pc.meetingTrail && <div style={{fontSize:12,color:MU,marginTop:3}}>{pc.meetingTrail}</div>}
                  </div>
                  <div style={{display:'flex',alignItems:'center',gap:8,flexShrink:0}}>
                    <div style={{textAlign:'right'}}>
                      <div style={{fontSize:10,color:MU}}>{pc.addedBy || '—'}</div>
                      <div style={{fontSize:10,color:LT}}>{fmtTrailDate(pc.createdAt)}</div>
                    </div>
                    <div style={{display:'flex',gap:4}}>
                      <motion.button whileHover={{scale:1.05}} whileTap={{scale:0.95}} style={{...s.btn('s'),padding:'4px 8px',fontSize:11}} onClick={(e)=>{e.stopPropagation();setForm({...pc, companyId: pc.companyId||'', remarkText:'', status: displayStatus});setFormErrors({});setModal({type:'probableClientStandalone',edit:true,editId:pc._id});}}>Edit</motion.button>
                      <DeleteIconBtn stopPropagation onClick={()=>setConfirmDel({msg:`Delete "${pc.name}"?`,fn:async()=>{await api.deleteProbableClient(pc._id);await loadData();}})}/>
                    </div>
                    <motion.div whileHover={{scale:1.1,background:'#F3F4F6'}} whileTap={{scale:0.9}} onClick={()=>setPcExpanded(isOpen?null:pc._id)} style={{width:32,height:32,borderRadius:8,border:'1.5px solid '+(isOpen?P:BD),display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',background:isOpen?PL:'#fff',transition:'all .15s'}}>
                      <motion.svg animate={{rotate:isOpen?180:0}} transition={{duration:0.2}} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={isOpen?P:MU} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></motion.svg>
                    </motion.div>
                  </div>
                </div>

                {/* Expanded trail */}
                <AnimatePresence initial={false}>
                  {isOpen && <motion.div initial={{height:0,opacity:0}} animate={{height:'auto',opacity:1}} exit={{height:0,opacity:0}} transition={{duration:0.2}} style={{overflow:'hidden'}}>
                    <div style={{marginTop:16,borderTop:'1px solid #E5E7EB',paddingTop:14}}>
                  {/* Add remark inline */}
                  <div style={{display:'flex',gap:8,marginBottom:16}}>
                    <input style={{...s.inp,flex:1,fontSize:12,borderLeft:'3px solid #D97706'}} placeholder="Add a remark to this lead's trail..." value={form['_remark_'+pc._id]||''} onChange={e=>setForm(f=>({...f,['_remark_'+pc._id]:e.target.value}))} onKeyDown={async(e)=>{
                      if(e.key==='Enter'&&(form['_remark_'+pc._id]||'').trim()){
                        await api.addProbableClientRemark(pc._id, form['_remark_'+pc._id]);
                        setForm(f=>({...f,['_remark_'+pc._id]:''}));
                        const fresh = await api.getProbableClients(); setProbableClients(fresh);
                      }
                    }}/>
                    <motion.button whileHover={{scale:1.02}} whileTap={{scale:0.97}} style={{...s.btn(),padding:'6px 14px',fontSize:12}} onClick={async()=>{
                      if(!(form['_remark_'+pc._id]||'').trim()) return;
                      await api.addProbableClientRemark(pc._id, form['_remark_'+pc._id]);
                      setForm(f=>({...f,['_remark_'+pc._id]:''}));
                      const fresh = await api.getProbableClients(); setProbableClients(fresh);
                    }}>+ Remark</motion.button>
                  </div>

                  {trail.length===0 && <div style={{fontSize:12,color:LT,padding:'10px 0'}}>No trail entries yet. Link a company or add remarks.</div>}

                  {/* Trail timeline */}
                  <div style={{display:'flex',flexDirection:'column',gap:0}}>
                    {trail.map((t,i) => (
                      <div key={i} style={{display:'flex',gap:12,paddingBottom:12,borderLeft:'2px solid '+t.color+'66',marginLeft:6,paddingLeft:14,position:'relative'}}>
                        <div style={{position:'absolute',left:-5,top:2,width:10,height:10,borderRadius:5,background:t.color,border:'2px solid #fff',boxShadow:'0 0 0 1px '+t.color+'33'}}/>
                        <div style={{flex:1}}>
                          <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:2}}>
                            <span style={{fontSize:10,fontWeight:700,color:t.color,textTransform:'uppercase',letterSpacing:'.5px',padding:'1px 6px',borderRadius:4,background:t.bg}}>{t.type==='meeting'?'📅 Meeting':t.type==='call'?'📞 Call':'💬 Remark'}</span>
                            <span style={{fontSize:10,color:LT}}>{fmtTrailDate(t.date)}</span>
                            {t.by && <span style={{fontSize:10,color:MU}}>by {t.by}</span>}
                          </div>
                          <div style={{fontSize:13,color:TX,fontWeight:t.type==='remark'?600:400}}>{t.type==='remark'?t.detail:t.label}</div>
                          {t.type!=='remark' && t.detail && <div style={{fontSize:11,color:MU,marginTop:1}}>{t.detail}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                    </div>
                  </motion.div>}
                </AnimatePresence>
              </motion.div>;
            })}
          </div>
        </>})()}

        {/* ═══ SETTINGS ═══ */}
        {page==='settings' && !detailId && isAdmin && <>
          <div className="settings-page">
          <div className="page-header-row" style={{marginBottom:20,alignItems:'flex-start'}}>
            <div>
              <h1 style={{fontSize:26,fontWeight:700,margin:0,letterSpacing:'-0.03em'}}>Settings</h1>
              <div style={{fontSize:13,color:MU,marginTop:4}}>Manage integrations, team access, and app configuration.</div>
            </div>
          </div>

          <div className="settings-layout">
            <nav className="settings-nav">
              {[
                {id:'integrations',label:'Integrations',desc:'Discord reports'},
                {id:'team',label:'Team',desc:'Users & cities'},
                {id:'leads',label:'Lead config',desc:'Sources & lists'},
                {id:'quotes',label:'Motivation',desc:'Top bar quotes'},
                {id:'targets',label:'Targets',desc:'Goals & automation'},
              ].map(t=>
                <button key={t.id} type="button" className={'settings-nav__item'+(settingsTab===t.id?' is-active':'')} onClick={()=>setSettingsTab(t.id)}>
                  <span>{t.label}</span>
                  <span className="settings-nav__item-desc">{t.desc}</span>
                </button>
              )}
            </nav>

            <div className="settings-content">

          {settingsTab==='integrations' && (() => {
            const discordWebhookUrl = (config.discordWebhookUrl || '').trim();
            const discordConfigured = !!(waStatus?.configured || discordWebhookUrl);
            const discordSchedule = waStatus?.schedule || {
              hour: config.discordReportHour ?? 20,
              minute: config.discordReportMinute ?? 0,
              timezone: config.discordReportTimezone || 'Asia/Kolkata',
            };
            const saveDiscordSettings = async () => {
              setDiscordSaveLoading(true);
              setDiscordActionMsg({ type: 'info', text: 'Saving Discord settings…' });
              try {
                const proposalTemplates = mergeWebhookIntoProposalTemplates(config.proposalTemplates, discordWebhookUrl);
                const discordPayload = {
                  discordWebhookUrl,
                  discordReportEnabled: config.discordReportEnabled !== false,
                  discordReportHour: config.discordReportHour ?? 20,
                  discordReportMinute: config.discordReportMinute ?? 0,
                  discordReportTimezone: 'Asia/Kolkata',
                  discordReportUsers: config.discordReportUsers ?? null,
                  proposalTemplates,
                };
                let saved;
                try {
                  saved = await api.updateDiscordSettings(discordPayload);
                } catch {
                  saved = await api.updateSettings(discordPayload);
                }
                setConfig(c => ({
                  ...c,
                  ...saved,
                  proposalTemplates: normalizeProposalTemplates(saved.proposalTemplates || proposalTemplates),
                  discordWebhookUrl: readDiscordWebhookUrl(saved) || discordWebhookUrl,
                }));
                try {
                  const st = await api.getDailyReportStatus();
                  setWaStatus(st);
                } catch {
                  setWaStatus(null);
                }
                setDiscordActionMsg({ type: 'success', text: 'Discord settings saved.' });
              } catch (e) {
                setDiscordActionMsg({ type: 'error', text: e.message });
              } finally {
                setDiscordSaveLoading(false);
              }
            };
            const discordActionColors = {
              info: { bg: '#EFF6FF', border: '#BFDBFE', color: '#1D4ED8' },
              success: { bg: '#ECFDF5', border: '#A7F3D0', color: '#047857' },
              error: { bg: '#FEF2F2', border: '#FECACA', color: '#B91C1C' },
            };
            const loadEodReportsForDate = async (date) => {
              try {
                return await api.getEodReports({ date });
              } catch {
                return [];
              }
            };
            const sendDiscordReport = async (date) => {
              const webhookUrl = (discordWebhookUrl || config.discordWebhookUrl || '').trim();
              const reportConfig = { ...config, discordWebhookUrl: webhookUrl };
              const eodReports = await loadEodReportsForDate(date);

              if (webhookUrl) {
                await sendDailyReportClient({
                  date,
                  config: reportConfig,
                  calls,
                  meetings,
                  companies,
                  eodReports,
                });
                return 'client';
              }

              await api.sendDailyReport({ date });
              return 'api';
            };
            const previewDiscordReport = async (date) => {
              const webhookUrl = (discordWebhookUrl || config.discordWebhookUrl || '').trim();
              const eodReports = await loadEodReportsForDate(date);
              if (webhookUrl) {
                return previewDailyReportClient({
                  date,
                  config: { ...config, discordWebhookUrl: webhookUrl },
                  calls,
                  meetings,
                  companies,
                  eodReports,
                });
              }
              const r = await api.getDailyReportPreview({ date });
              return r.message || '';
            };
            return (
          <SettingsPanel title="Discord Daily Reports" description="Post team activity and EOD notes to your Discord channel. Missing submissions are flagged automatically.">
            <div className="settings-subsection">
              <div className="settings-subsection__title">Webhook</div>
              <Fld label="Discord webhook URL" hint="Discord → Channel → Integrations → Webhooks → Copy URL">
                <input style={s.inp} value={config.discordWebhookUrl || ''} onChange={e=>setConfig(c=>({...c,discordWebhookUrl:e.target.value}))} placeholder="https://discord.com/api/webhooks/…"/>
              </Fld>
              <div style={{marginTop:10}}>
                <span className={'settings-status '+(discordConfigured?'settings-status--ok':'settings-status--warn')}>
                  {discordConfigured ? '● Connected' : '● Not configured'}
                </span>
                {discordConfigured && <span style={{fontSize:12,color:MU,marginLeft:10}}>
                  Auto-send: {(waStatus?.enabled ?? config.discordReportEnabled !== false) ? 'On' : 'Off'} · Daily at {String(discordSchedule.hour ?? 20).padStart(2,'0')}:{String(discordSchedule.minute ?? 0).padStart(2,'0')} IST
                </span>}
              </div>
            </div>

            <div className="settings-subsection">
              <div className="settings-subsection__title">Send report</div>
            <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}}
              style={{...s.btn(),width:'100%',maxWidth:360,padding:'11px 18px',fontSize:13,fontWeight:600,background:'#5865F2',border:'none',color:WH,opacity:waLoading||!discordConfigured?0.7:1,cursor:waLoading||!discordConfigured?'not-allowed':'pointer'}}
              disabled={waLoading||!discordConfigured}
              onClick={async()=>{
                setWaLoading(true);
                setDiscordActionMsg({ type: 'info', text: 'Sending report to Discord…' });
                try{
                  await sendDiscordReport(waPreviewDate);
                  if (discordWebhookUrl) {
                    const proposalTemplates = mergeWebhookIntoProposalTemplates(config.proposalTemplates, discordWebhookUrl);
                    const payload = { discordWebhookUrl, proposalTemplates };
                    try {
                      const saved = await api.updateDiscordSettings(payload);
                      setConfig(c => ({
                        ...c,
                        discordWebhookUrl: readDiscordWebhookUrl(saved) || discordWebhookUrl,
                        proposalTemplates: normalizeProposalTemplates(saved.proposalTemplates || proposalTemplates),
                      }));
                    } catch {
                      try {
                        const saved = await api.updateSettings(payload);
                        setConfig(c => ({
                          ...c,
                          discordWebhookUrl: readDiscordWebhookUrl(saved) || discordWebhookUrl,
                          proposalTemplates: normalizeProposalTemplates(saved.proposalTemplates || proposalTemplates),
                        }));
                      } catch {}
                    }
                  }
                  setDiscordActionMsg({
                    type: 'success',
                    text: 'Report sent to Discord! Check your channel.',
                  });
                  setWaStatus(prev => prev || {
                    configured: true,
                    enabled: config.discordReportEnabled !== false,
                    schedule: discordSchedule,
                  });
                }catch(e){
                  setDiscordActionMsg({ type: 'error', text: e.message || 'Failed to send report.' });
                }
                finally{setWaLoading(false);}
              }}>
              {waLoading ? 'Sending…' : 'Send report to Discord'}
            </motion.button>
            </div>
            {discordActionMsg && (
              <div style={{
                background: discordActionColors[discordActionMsg.type].bg,
                border: `1px solid ${discordActionColors[discordActionMsg.type].border}`,
                borderRadius: 10,
                padding: '10px 12px',
                fontSize: 12,
                fontWeight: 500,
                color: discordActionColors[discordActionMsg.type].color,
                lineHeight: 1.5,
              }}>
                {discordActionMsg.text}
              </div>
            )}

            <div className="settings-subsection">
              <div className="settings-subsection__title">Schedule</div>
              <div style={{display:'flex',flexWrap:'wrap',gap:14,alignItems:'center',marginBottom:12}}>
                <label style={{display:'flex',alignItems:'center',gap:8,fontSize:13,cursor:'pointer'}}>
                  <input type="checkbox" checked={config.discordReportEnabled!==false} onChange={e=>setConfig(c=>({...c,discordReportEnabled:e.target.checked}))}/>
                  Auto-send daily
                </label>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <span style={{fontSize:12,color:MU,fontWeight:500}}>Time</span>
                  <input type="time"
                    style={{...s.inp,padding:'8px 10px',width:130}}
                    value={`${String(config.discordReportHour ?? 20).padStart(2,'0')}:${String(config.discordReportMinute ?? 0).padStart(2,'0')}`}
                    onChange={e=>{const [h,m]=e.target.value.split(':').map(Number);setConfig(c=>({...c,discordReportHour:h,discordReportMinute:m}));}}
                  />
                  <span style={{fontSize:12,color:MU}}>IST</span>
                </div>
              </div>
              <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn('s'),fontSize:12,opacity:discordSaveLoading?0.7:1}} disabled={discordSaveLoading||waLoading} onClick={saveDiscordSettings}>
                {discordSaveLoading ? 'Saving…' : 'Save settings'}
              </motion.button>
            </div>

            <div className="settings-subsection">
              <div className="settings-subsection__title">Report recipients</div>
              <div style={{fontSize:12,color:MU,marginBottom:12,lineHeight:1.5}}>
                Choose which team members appear in the daily Discord EOD report. All employees are included by default.
              </div>
              {(() => {
                const reportUserList = (config.allUsers || []).filter(u => u?.username && u.role !== 'Admin');
                const hasCustomSelection = config.discordReportUsers !== undefined && config.discordReportUsers !== null;
                const isUserInReport = (username) => {
                  if (!hasCustomSelection) return true;
                  if (config.discordReportUsers.length === 0) return false;
                  return config.discordReportUsers.includes(username);
                };
                const selectedCount = reportUserList.filter(u => isUserInReport(u.username)).length;
                const toggleReportUser = (username) => {
                  const allNames = reportUserList.map(u => u.username);
                  const current = hasCustomSelection ? [...config.discordReportUsers] : [...allNames];
                  const next = new Set(current);
                  if (next.has(username)) next.delete(username);
                  else next.add(username);
                  if (next.size === allNames.length) {
                    setConfig(c => ({ ...c, discordReportUsers: null }));
                  } else {
                    setConfig(c => ({ ...c, discordReportUsers: allNames.filter(n => next.has(n)) }));
                  }
                };
                return reportUserList.length === 0 ? (
                  <div style={{fontSize:12,color:MU}}>No employee accounts yet. Add team members below.</div>
                ) : (
                  <>
                    <div style={{display:'flex',flexWrap:'wrap',gap:8,marginBottom:10}}>
                      <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn('s'),fontSize:11,padding:'5px 10px'}}
                        onClick={() => setConfig(c => ({ ...c, discordReportUsers: null }))}>
                        Select all
                      </motion.button>
                      <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn('s'),fontSize:11,padding:'5px 10px',background:WH,color:MU,border:'1px solid #C7D2FE'}}
                        onClick={() => setConfig(c => ({ ...c, discordReportUsers: [] }))}>
                        Deselect all
                      </motion.button>
                      <span style={{fontSize:11,color:MU,alignSelf:'center'}}>{selectedCount}/{reportUserList.length} selected</span>
                    </div>
                    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:8}}>
                      {reportUserList.map(u => (
                        <label key={u._id} style={{display:'flex',alignItems:'center',gap:8,fontSize:13,cursor:'pointer',padding:'8px 10px',borderRadius:8,border:'1px solid '+(isUserInReport(u.username)?'#D1D5DB':'#EBEBED'),background:isUserInReport(u.username)?'#F9FAFB':'#fff'}}>
                          <input type="checkbox" checked={isUserInReport(u.username)} onChange={() => toggleReportUser(u.username)}/>
                          <span style={{fontWeight:600,color:TX}}>{u.username}</span>
                          {(u.assignedCities||[]).length > 0 && <span style={{fontSize:10,color:MU}}>{(u.assignedCities||[]).join(', ')}</span>}
                        </label>
                      ))}
                    </div>
                  </>
                );
              })()}
            </div>

            <div className="settings-subsection">
              <div className="settings-subsection__title">Preview</div>
            <div className="page-header-actions" style={{marginBottom:12}}>
              <input type="date" style={{...s.inp,maxWidth:160}} value={waPreviewDate} onChange={e=>setWaPreviewDate(e.target.value)}/>
              <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={s.btn('s')} disabled={waLoading||!discordConfigured} onClick={async()=>{setWaLoading(true);try{setWaPreview(await previewDiscordReport(waPreviewDate));}catch(e){setDiscordActionMsg({ type: 'error', text: e.message });}finally{setWaLoading(false);}}}>Preview report</motion.button>
            </div>
            {waPreview && <textarea readOnly style={{...s.inp,width:'100%',minHeight:200,fontFamily:'ui-monospace,monospace',fontSize:11,lineHeight:1.5,whiteSpace:'pre-wrap',background:'#FAFBFC'}} value={waPreview}/>}
            </div>
          </SettingsPanel>
            );
          })()}

          {settingsTab==='team' && (() => {
            const activeUsers = (config.allUsers||[]).filter(u=>u&&u.username);
            const deletedHistory = teamHistory.filter(h => h.action === 'deleted');
            const addedHistory = teamHistory.filter(h => h.action === 'added');
            return <>
          <SettingsPanel
            title="Team accounts"
            description="Manage login access, roles, and city assignments for your sales team."
            headerAction={<motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={{...s.btn(),padding:'7px 14px',fontSize:12}} onClick={()=>{setModal({type:'user'});setForm({username:'',password:'',role:'Employee',assignedCities:[],isTopDeveloper:false});setFormErrors({});}}>+ Add user</motion.button>}
          >
            <div style={{...s.card,padding:0,overflow:'hidden',marginBottom:16,border:'1px solid #E5E7EB'}}>
              <div style={{padding:'14px 16px',borderBottom:'1px solid #F3F4F6',display:'flex',justifyContent:'space-between',gap:12,alignItems:'center',flexWrap:'wrap'}}>
                <div>
                  <div style={{fontSize:14,fontWeight:700,color:TX,letterSpacing:'-0.02em'}}>Team Overview</div>
                  <div style={{fontSize:12,color:MU,marginTop:2}}>{activeUsers.length} active · {deletedHistory.length} removed from history</div>
                </div>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))',gap:1,background:'#F3F4F6'}}>
                {[
                  {label:'Current Users',value:activeUsers.length,sub:`${activeUsers.filter(u=>u.role==='Employee').length} employees · ${activeUsers.filter(u=>u.role==='Admin').length} admin`,accent:TX},
                  {label:'Added (logged)',value:addedHistory.length,sub:'Total add events tracked',accent:GN},
                  {label:'Deleted (logged)',value:deletedHistory.length,sub:'Removed team members',accent:deletedHistory.length?RD:MU},
                  {label:'History Entries',value:teamHistory.length,sub:'Add & remove activity',accent:'#6366F1'},
                ].map(({label,value,sub,accent})=>
                  <div key={label} style={{background:WH,padding:'14px 12px',minHeight:86}}>
                    <div style={{fontSize:22,fontWeight:800,color:TX,letterSpacing:'-0.04em',lineHeight:1}}>{value}</div>
                    <div style={{fontSize:11,fontWeight:700,color:MU,marginTop:7}}>{label}</div>
                    <div style={{fontSize:10,color:accent,marginTop:4,fontWeight:600,lineHeight:1.35}}>{sub}</div>
                  </div>
                )}
              </div>
            </div>
            <div className="settings-team-list">
              {activeUsers.map(u => {
                const isAdmin_ = u.role==='Admin';
                const cities = u.assignedCities||[];
                return (
                  <div key={u._id||u.id||u.username} className="settings-team-card">
                    <div className="settings-team-card__main">
                      <UserAvatar username={u.username} avatarColor={u.avatarColor} size={40} />
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                          <span style={{fontWeight:700,fontSize:14,color:TX}}>{u.username}</span>
                          <span style={{padding:'2px 8px',borderRadius:6,fontSize:10,fontWeight:600,background:isAdmin_?'#F3F4F6':'#EFF6FF',color:isAdmin_?'#374151':'#4338CA'}}>{u.role}</span>
                        </div>
                        <div style={{fontSize:11,color:MU,marginTop:3}}>{cities.length>0?cities.join(' · '):'All cities'}</div>
                      </div>
                      {u.username!=='admin'&&<div style={{display:'flex',gap:6,flexShrink:0}}>
                        <motion.button whileHover={{scale:1.02}} whileTap={{scale:0.98}} style={{...s.btn('s'),padding:'4px 10px',fontSize:11}} onClick={()=>{setModal({type:'editUser',user:u});setForm({username:u.username,role:u.role||'Employee',assignedCities:u.assignedCities||[],newPassword:''});setFormErrors({});}}>Edit</motion.button>
                        <motion.button whileHover={{scale:1.02}} whileTap={{scale:0.98}} style={{...s.btn('s'),padding:'4px 10px',fontSize:11}} onClick={()=>{const pw=prompt(`New password for "${u.username}" (min 4 chars):`);if(pw&&pw.length>=4){api.resetUserPassword(String(u._id||u.id),pw).then(()=>alert('Password reset successfully.')).catch(e=>alert('Password reset failed: '+e.message));}else if(pw){alert('Password must be at least 4 characters');}}}>Reset pwd</motion.button>
                        <DeleteIconBtn
                          disabled={removeUserLoadingId === String(u._id || u.id)}
                          title={removeUserLoadingId === String(u._id || u.id) ? 'Loading…' : 'Delete'}
                          onClick={async()=>{
                            const userId = String(u._id || u.id);
                            setRemoveUserLoadingId(userId);
                            try {
                              let apiPreview = null;
                              try {
                                apiPreview = await api.getUserTransferPreview(userId);
                              } catch {
                                // Fall back to client-side preview when API route is missing or outdated.
                              }
                              const clientPreview = buildClientTransferPreview(u.username, {
                                companies,
                                leadBank,
                                spocs,
                                meetings,
                                calls,
                                proposals,
                              });
                              setRemoveUserModal({
                                user: u,
                                preview: mergeTransferPreview(apiPreview, clientPreview),
                              });
                            } finally {
                              setRemoveUserLoadingId(null);
                            }
                          }}
                        />
                      </div>}
                    </div>
                    {u.role==='Employee'&&<div className="settings-team-card__cities">
                      <div className="settings-subsection__title">Assigned cities</div>
                      <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
                        {CITIES.map(c=>{
                          const active=cities.includes(c);
                          return <motion.div key={c} whileHover={{scale:1.02}} whileTap={{scale:0.98}} onClick={async()=>{const arr=new Set(cities);if(arr.has(c))arr.delete(c);else arr.add(c);await api.updateUser(u._id||u.id,{assignedCities:[...arr]});loadData();}}
                            style={{padding:'4px 11px',borderRadius:8,border:'1px solid '+(active?'#111827':BD),background:active?'#111827':WH,fontSize:12,fontWeight:600,color:active?'#fff':MU,cursor:'pointer',userSelect:'none'}}>{c}</motion.div>;
                        })}
                      </div>
                    </div>}
                  </div>
                );
              })}
              {activeUsers.length===0&&<div style={{fontSize:13,color:MU,textAlign:'center',padding:24}}>No team members yet. Add your first user above.</div>}
            </div>
          </SettingsPanel>

          <SettingsPanel
            title="User history"
            description="Track of team members added and removed over time."
          >
            {teamHistory.length === 0 ? (
              <div style={{fontSize:13,color:MU,textAlign:'center',padding:24,background:'#F9FAFB',borderRadius:12,border:'1px solid #E5E7EB'}}>No team changes logged yet.</div>
            ) : (
              <div className="settings-team-history">
                {teamHistory.map(entry => {
                  const isDeleted = entry.action === 'deleted';
                  const when = entry.createdAt ? new Date(entry.createdAt).toLocaleString('en-IN', { day:'numeric', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : entry.date || '—';
                  return (
                    <div key={entry._id} className={'settings-team-history__row'+(isDeleted?' settings-team-history__row--deleted':'')}>
                      <UserAvatar username={entry.username} avatarColor={entry.avatarColor} size={36} style={isDeleted ? { opacity: 0.72 } : undefined} />
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                          <span style={{fontWeight:700,fontSize:13,color:TX}}>{entry.username}</span>
                          <span style={{padding:'2px 8px',borderRadius:999,fontSize:10,fontWeight:700,background:isDeleted?'#FEE2E2':'#DCFCE7',color:isDeleted?'#B91C1C':'#15803D'}}>{isDeleted ? 'Removed' : 'Added'}</span>
                          <span style={{fontSize:10,color:MU,fontWeight:600}}>{entry.role || 'Employee'}</span>
                        </div>
                        <div style={{fontSize:11,color:MU,marginTop:3}}>
                          {(entry.assignedCities||[]).length ? entry.assignedCities.join(' · ') : 'All cities'}
                          {entry.changedBy ? ` · by ${entry.changedBy}` : ''}
                        </div>
                      </div>
                      <div style={{fontSize:11,color:LT,whiteSpace:'nowrap',flexShrink:0}}>{when}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </SettingsPanel>
            </>;
          })()}

          {settingsTab==='leads' && (
          <SettingsPanel title="Lead configuration" description="Dropdown options used across calls, companies, and prospect workflows.">
            {[{k:'leadSources',t:'Lead Sources',p:'Add source…'},{k:'segments',t:'Segments',p:'Add segment…'},{k:'niReasons',t:'Not Interested Reasons',p:'Add reason…'},{k:'strikeoutReasons',t:'Strike-out Reasons',p:'e.g. Wrong number…'},{k:'developerCategories',t:'Developer Categories',p:'e.g. Cat A Developer…'}].map(({k,t,p})=>
              <SettingsListEditor key={k} title={t} items={config[k]||[]} onSave={async v=>{try{const updated={...config,[k]:v};await api.updateSettings(updated);setConfig(updated);}catch(e){alert(e.message);}}} placeholder={p}/>
            )}
            <ProposalTypesSettingsEditor
              title="Proposal Types"
              items={getProposalTemplates(config)}
              onSave={async v=>{try{const proposalTemplates=mergeWebhookIntoProposalTemplates(v, config.discordWebhookUrl);const updated={...config,proposalTemplates};await api.updateSettings({ proposalTemplates });setConfig({...updated, proposalTemplates: normalizeProposalTemplates(proposalTemplates), discordWebhookUrl: readDiscordWebhookUrl({ proposalTemplates }) || config.discordWebhookUrl});}catch(e){alert(e.message);}}}
              placeholder="e.g. Digital tool…"
            />
          </SettingsPanel>
          )}

          {settingsTab==='quotes' && (
          <SettingsPanel title="Motivational quotes" description="Quotes shown in the top bar, rotating at the interval you set.">
            <div className="settings-inline-box">
              <div style={{display:'flex',alignItems:'center',gap:16,flexWrap:'wrap'}}>
                <div style={{fontSize:13,fontWeight:600}}>Refresh interval</div>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <input type="number" min={1} max={1440} value={Math.round((config.quoteIntervalHrs||2)*60)} onChange={async e=>{const mins=Math.max(1,Math.min(1440,+e.target.value||1));const updated={...config,quoteIntervalHrs:mins/60};setConfig(updated);try{await api.updateSettings(updated);}catch{}}} style={{...s.inp,width:72,textAlign:'center',fontWeight:600,fontSize:14}}/>
                  <span style={{fontSize:13,color:MU}}>minutes</span>
                </div>
                <span style={{fontSize:12,color:LT}}>{config.quoteIntervalHrs && config.quoteIntervalHrs >= 1 ? `${Math.round(config.quoteIntervalHrs*10)/10} hr${config.quoteIntervalHrs===1?'':'s'}` : `${Math.round((config.quoteIntervalHrs||2)*60)} min`}</span>
              </div>
            </div>
            <SettingsListEditor title="Quote library" items={config.motivationalQuotes && config.motivationalQuotes.length > 0 ? config.motivationalQuotes : DEFAULT_QUOTES} onSave={async v=>{try{const updated={...config,motivationalQuotes:v};await api.updateSettings(updated);setConfig(updated);}catch(e){alert(e.message);}}} placeholder="Add a motivational quote…"/>
            <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn('s'),fontSize:12,alignSelf:'flex-start'}} onClick={async()=>{try{const updated={...config,motivationalQuotes:DEFAULT_QUOTES};await api.updateSettings(updated);setConfig(updated);}catch(e){alert(e.message);}}}>Reset to 30 default quotes</motion.button>
          </SettingsPanel>
          )}

          {settingsTab==='targets' && (
          <SettingsPanel title="Targets & automation" description="Choose which metrics appear on Sales Targets, plus daily queue defaults.">
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Monthly target metrics</div>
              <div style={{ fontSize: 12, color: MU, marginBottom: 12 }}>Toggle which goals admins can set and employees see on the Sales Targets page.</div>
              <div className="settings-metric-toggles">
                {TARGET_METRIC_DEFS.map(m => {
                  const enabled = { ...DEFAULT_TARGET_METRICS, ...(config.targetMetricsEnabled || {}) }[m.key];
                  return (
                    <div key={m.key} className="settings-metric-toggle" style={{ borderColor: enabled ? m.color + '44' : BD, background: enabled ? m.color + '0D' : '#FAFAFA' }}>
                      <span>{m.icon} {m.label}</span>
                      <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={{ ...s.btn(enabled ? undefined : 's'), padding: '5px 12px', fontSize: 11, background: enabled ? '#111827' : undefined, border: enabled ? 'none' : undefined, color: enabled ? '#fff' : undefined }} onClick={async () => {
                        const next = { ...DEFAULT_TARGET_METRICS, ...(config.targetMetricsEnabled || {}), [m.key]: !enabled };
                        const updated = { ...config, targetMetricsEnabled: next };
                        setConfig(updated);
                        try { await api.updateSettings({ targetMetricsEnabled: next }); } catch (e) { alert(e.message); }
                      }}>{enabled ? 'On' : 'Off'}</motion.button>
                    </div>
                  );
                })}
              </div>
            </div>
            <div style={{display:'flex',gap:14,flexWrap:'wrap'}}>
              {[{t:'Daily call target',k:'callTarget',v:config.callTarget||50,mn:10,mx:150},{t:'Auto-recycle (days)',k:'recycleDays',v:config.recycleDays||90,mn:30,mx:365}].map(n=><div key={n.k} className="settings-slider-card">
                <div style={{fontSize:13,fontWeight:600,marginBottom:12}}>{n.t}</div>
                <div style={{display:'flex',alignItems:'center',gap:12}}><input type="range" min={n.mn} max={n.mx} value={n.v} onChange={async e=>{const updated={...config,[n.k]:+e.target.value};setConfig(updated);try{await api.updateSettings(updated);}catch{}}} style={{flex:1}}/><div style={{background:'#111827',color:'#fff',borderRadius:8,padding:'5px 12px',fontWeight:700,fontSize:14,minWidth:36,textAlign:'center'}}>{n.v}</div></div>
              </div>)}
            </div>
            <div className="settings-toggle-row">
              <div>
                <div style={{fontSize:14,fontWeight:600}}>Show timeline age</div>
                <div style={{fontSize:12,color:MU,marginTop:2}}>Display total days since first meeting on company timelines.</div>
              </div>
              <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={{...s.btn(config.showTimelineMeetingAge?undefined:'s'),padding:'6px 14px',fontSize:12,background:config.showTimelineMeetingAge?'#111827':undefined,border:config.showTimelineMeetingAge?'none':undefined,color:config.showTimelineMeetingAge?'#fff':undefined}} onClick={async()=>{const updated={...config,showTimelineMeetingAge:!config.showTimelineMeetingAge};setConfig(updated);try{await api.updateSettings(updated);}catch{}}}>
                {config.showTimelineMeetingAge ? 'Enabled' : 'Disabled'}
              </motion.button>
            </div>
          </SettingsPanel>
          )}

            </div>{/* settings-content */}
          </div>{/* settings-layout */}

          <div style={{textAlign:'center',padding:'20px 0 4px',color:LT,fontSize:11,fontWeight:500,letterSpacing:'0.04em'}}>
            Unada Sales OS · <span style={{fontWeight:600,color:MU}}>{APP_VERSION}</span>
          </div>
          </div>{/* settings-page */}
        </>}

        </motion.div>
        </AnimatePresence>
      </div>{/* end main */}

      {/* ═══ MODALS ═══ */}

      {/* User Modal - Create */}
      <Modal open={modal?.type==='user'} onClose={closeModal} title="Create User">
        <div style={{display:'flex',flexDirection:'column',gap:14}}>
          {formErrors.api&&<div style={{color:RD,fontSize:13,padding:10,background:RL,borderRadius:8}}>{formErrors.api}</div>}
          <div style={s.row}><Fld label="Username *" half><input style={s.inp} value={form.username||''} onChange={e=>uf('username',e.target.value)}/></Fld><Fld label="Password *" half><input style={s.inp} type="password" value={form.password||''} onChange={e=>uf('password',e.target.value)}/></Fld></div>
          <Fld label="Role"><Dropdown style={s.sel} value={form.role||'Employee'} onChange={e=>uf('role',e.target.value)}><option value="Employee">Employee</option><option value="Admin">Admin</option></Dropdown></Fld>
          <Fld label="Assigned Cities">
            <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
              {CITIES.map(c=><div key={c} onClick={()=>{const arr=new Set(form.assignedCities||[]);if(arr.has(c))arr.delete(c);else arr.add(c);uf('assignedCities',[...arr]);}} style={{padding:'6px 12px',borderRadius:8,border:'2px solid '+(form.assignedCities?.includes(c)?P:BD),background:form.assignedCities?.includes(c)?PL:WH,fontSize:12,fontWeight:600,color:form.assignedCities?.includes(c)?P:MU,cursor:'pointer',transition:'all .15s'}}>{c}</div>)}
            </div>
          </Fld>
          <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}><motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={s.btn('s')} onClick={closeModal}>Cancel</motion.button><motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={s.btn()} onClick={async()=>{try{await api.createUser({username:form.username,password:form.password,role:form.role||'Employee',assignedCities:form.assignedCities||[],isTopDeveloper:!!form.isTopDeveloper});loadData();closeModal();}catch(e){setFormErrors({api:e.message});}}}>Create</motion.button></div>
        </div>
      </Modal>

      {/* User Modal - Edit */}
      <Modal open={modal?.type==='editUser'} onClose={closeModal} title={`Edit User — ${modal?.user?.username||''}`}>
        <div style={{display:'flex',flexDirection:'column',gap:14}}>
          {formErrors.api&&<div style={{color:RD,fontSize:13,padding:10,background:RL,borderRadius:8}}>{formErrors.api}</div>}
          <div style={s.row}><Fld label="Username" half><input style={s.inp} value={form.username||''} onChange={e=>uf('username',e.target.value)}/></Fld><Fld label="Role" half><Dropdown style={s.sel} value={form.role||'Employee'} onChange={e=>uf('role',e.target.value)}><option value="Employee">Employee</option><option value="Admin">Admin</option></Dropdown></Fld></div>
          <Fld label="Assigned Cities">
            <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
              {CITIES.map(c=><div key={c} onClick={()=>{const arr=new Set(form.assignedCities||[]);if(arr.has(c))arr.delete(c);else arr.add(c);uf('assignedCities',[...arr]);}} style={{padding:'6px 12px',borderRadius:8,border:'2px solid '+(form.assignedCities?.includes(c)?P:BD),background:form.assignedCities?.includes(c)?PL:WH,fontSize:12,fontWeight:600,color:form.assignedCities?.includes(c)?P:MU,cursor:'pointer',transition:'all .15s'}}>{c}</div>)}
            </div>
          </Fld>
          <div style={{borderTop:'1px solid '+BD,paddingTop:14}}>
            <Fld label="Reset Password (leave blank to keep current)">
              <input style={s.inp} type="password" value={form.newPassword||''} onChange={e=>uf('newPassword',e.target.value)} placeholder="Enter new password (min 4 chars)"/>
            </Fld>
          </div>
          <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
            <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={s.btn('s')} onClick={closeModal}>Cancel</motion.button>
            <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={s.btn()} onClick={async()=>{
              try{
                const updates={username:form.username,role:form.role,assignedCities:form.assignedCities||[]};
                await api.updateUser(String(modal.user._id),updates);
                if(form.newPassword&&form.newPassword.length>=4){
                  await api.resetUserPassword(String(modal.user._id),form.newPassword);
                }else if(form.newPassword){
                  setFormErrors({api:'Password must be at least 4 characters'});return;
                }
                loadData();closeModal();
              }catch(e){setFormErrors({api:e.message});}
            }}>Save Changes</motion.button>
          </div>
        </div>
      </Modal>

      {/* SOP Modal */}
      <Modal open={modal?.type==='sop'} onClose={closeModal} title={modal?.edit?'Edit SOP':'Add SOP'} wide>
        <div style={{display:'flex',flexDirection:'column',gap:12}}>
          {formErrors.api&&<div style={{color:RD,fontSize:13,padding:10,background:RL,borderRadius:8}}>{formErrors.api}</div>}
          <div style={s.row}>
            <Fld label="Title *" half error={formErrors.title}><input style={{...s.inp,borderColor:formErrors.title?RD:BD}} value={form.title||''} onChange={e=>uf('title',e.target.value)} placeholder="e.g. First Introduction Email"/></Fld>
            <Fld label="Category" half><Dropdown style={s.sel} value={form.category||'Process'} onChange={e=>uf('category',e.target.value)}><option>Email Template</option><option>WhatsApp Template</option><option>Calling Script</option><option>Process</option><option>Checklist</option></Dropdown></Fld>
          </div>
          <div style={s.row}>
            <Fld label="Sales Stage" half hint="Which sales step does this belong to?"><Dropdown style={s.sel} value={(form.tagsText||'').split(',').map(t=>t.trim()).find(t=>SOP_STAGES.some(st=>st.key===t))||''} onChange={e=>{const cur=(form.tagsText||'').split(',').map(t=>t.trim()).filter(t=>!SOP_STAGES.some(st=>st.key===t));if(e.target.value)cur.unshift(e.target.value);uf('tagsText',cur.join(', '));}}><option value="">General</option>{SOP_STAGES.filter(st=>st.key!=='general').map(st=><option key={st.key} value={st.key}>{st.icon} {st.label}</option>)}</Dropdown></Fld>
            <Fld label="Use Case Key *" half error={formErrors.useCaseKey} hint="Short code your team can search for"><input style={{...s.inp,borderColor:formErrors.useCaseKey?RD:BD}} value={form.useCaseKey||''} onChange={e=>uf('useCaseKey',e.target.value.toUpperCase().replace(/\s+/g,'_'))} placeholder="MEETING_CONFIRMATION"/></Fld>
          </div>
          <Fld label="When to use this" hint="Help your team know exactly when to pull this up"><textarea style={{...s.inp,minHeight:54}} value={form.whereToUse||''} onChange={e=>uf('whereToUse',e.target.value)} placeholder="e.g. Send to prospect within 1 hour after a completed meeting"/></Fld>
          {(form.category==='Email Template')&&<Fld label="Email Subject"><input style={s.inp} value={form.subject||''} onChange={e=>uf('subject',e.target.value)} placeholder="Subject line with {{spocName}}, {{companyName}} placeholders"/></Fld>}
          {['Email Template','WhatsApp Template','Calling Script'].includes(form.category)&&<Fld label="Template / Script Body"><textarea style={{...s.inp,minHeight:150,fontFamily:'inherit'}} value={form.body||''} onChange={e=>uf('body',e.target.value)} placeholder="Use placeholders like {{spocName}}, {{companyName}}, {{meetingDate}}, {{salespersonName}}."/></Fld>}
          {['Process','Checklist'].includes(form.category)&&<Fld label={form.category==='Checklist'?'Checklist Items':'Process Steps'} hint="One item per line — these become interactive checkboxes"><textarea style={{...s.inp,minHeight:120}} value={form.stepsText||''} onChange={e=>uf('stepsText',e.target.value)} placeholder={"Step 1: Do this first\nStep 2: Then do this\nStep 3: Finally do this"}/></Fld>}
          {!['Process','Checklist'].includes(form.category)&&<Fld label="Additional Steps (optional)" hint="One per line"><textarea style={{...s.inp,minHeight:80}} value={form.stepsText||''} onChange={e=>uf('stepsText',e.target.value)} placeholder="Optional follow-up steps"/></Fld>}
          <Fld label="Notes (internal)"><textarea style={{...s.inp,minHeight:54}} value={form.notes||''} onChange={e=>uf('notes',e.target.value)} placeholder="Internal notes, tips, or context for the team"/></Fld>
          <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}><motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={s.btn('s')} onClick={closeModal}>Cancel</motion.button><motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={s.btn()} onClick={saveSop}>Save SOP</motion.button></div>
        </div>
      </Modal>

      {/* Company Modal */}
      <Modal open={modal?.type==='company'} onClose={closeModal} title={modal?.edit?'Edit Company':'Add Company'} wide preventClose>
        <div style={{display:'flex',flexDirection:'column',gap:14}}>
          {formErrors.api&&<div style={{color:RD,fontSize:13,padding:10,background:RL,borderRadius:8}}>{formErrors.api}</div>}
          <div style={s.row}><Fld label="Company Name *" half error={formErrors.name}><input style={{...s.inp,borderColor:touched.name&&!form.name?RD:BD}} value={form.name||''} onChange={e=>{uf('name',e.target.value);touch('name');}} onBlur={e=>{touch('name');uf('name',formatProperName(e.target.value));}} maxLength={100}/></Fld><Fld label="City" half><Dropdown style={s.sel} value={form.city||''} onChange={e=>uf('city',e.target.value)}>{CITIES.map(c=><option key={c}>{c}</option>)}</Dropdown></Fld></div>
          <div style={s.row}><Fld label="Segment" half><Dropdown style={s.sel} value={form.segment||''} onChange={e=>uf('segment',e.target.value)}>{(config.segments||[]).map(x=><option key={x}>{x}</option>)}</Dropdown></Fld><Fld label="Lead Source" half><Dropdown style={s.sel} value={form.source||''} onChange={e=>uf('source',e.target.value)}><option value="">Select</option>{(config.leadSources||[]).map(x=><option key={x}>{x}</option>)}</Dropdown></Fld></div>
          <div style={s.row}><Fld label="Salesperson" half><Dropdown style={s.sel} value={form.salesperson||''} onChange={e=>uf('salesperson',e.target.value)}><option value="">Select</option>{(config.salespersons||[]).map(x=><option key={x}>{x}</option>)}</Dropdown></Fld><Fld label="Potential" half><div style={{display:'flex',alignItems:'center',gap:10}}><input type="range" min={1} max={10} value={form.potential||5} onChange={e=>uf('potential',+e.target.value)} style={{flex:1}}/><span style={{fontWeight:700,fontSize:15}}>{form.potential||5}/10</span></div></Fld></div>
          {/* Inline SPOCs */}
          {!modal?.edit&&<div style={{borderTop:'1px solid '+BD,paddingTop:14}}>
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:10}}><div style={{fontSize:12,fontWeight:600,color:P,textTransform:'uppercase'}}>SPOCs ({inlineSps.length})</div><motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={s.btn('s')} onClick={()=>{setShowInlineSp(true);setInlineSpF({name:'',designation:'',phone:'+91',phoneType:'mobile',email:'',leadSource:'',decisionLevel:'Unknown'});}}>+ Add SPOC</motion.button></div>
            {formErrors.spocPhone&&<div style={{fontSize:11,color:RD,marginBottom:6}}>{formErrors.spocPhone}</div>}
            {inlineSps.map((sp,i)=><div key={i} style={{display:'flex',justifyContent:'space-between',padding:'8px 12px',background:'#F9FAFB',borderRadius:8,marginBottom:4,fontSize:13,border:'1px solid '+BD}}><span><b>{sp.name}</b> · {sp.phone||''} · {sp.designation||''}</span><span style={{color:RD,cursor:'pointer',fontWeight:700}} onClick={()=>setInlineSps(prev=>prev.filter((_,j)=>j!==i))}>×</span></div>)}
            {showInlineSp&&<div style={{background:'#F9FAFB',borderRadius:12,padding:14,border:'1px solid '+BD,marginTop:8}}>
              <div style={s.row}><Fld label="Name *" half><input style={s.inp} value={inlineSpF.name||''} onChange={e=>setInlineSpF(prev=>({...prev,name:e.target.value}))} onBlur={e=>setInlineSpF(prev=>({...prev,name:formatProperName(e.target.value)}))}/></Fld>
                <Fld label="Phone (+91...)" half error={validatePhone(inlineSpF.phone, inlineSpF.phoneType)}>
                  <div style={{display:'flex',gap:6,marginBottom:8}}>
                    {['mobile','landline'].map(t=><div key={t} onClick={()=>setInlineSpF(prev=>({...prev,phoneType:t,phone:prev.phone&&prev.phone.length>phoneMaxLength(t)?prev.phone.slice(0,phoneMaxLength(t)):prev.phone}))} style={s.chip((inlineSpF.phoneType||'mobile')===t)}>{t==='mobile'?'📱 Mobile (10)':'☎️ Landline (12)'}</div>)}
                  </div>
                  <input style={{...s.inp,borderColor:inlineSpF.phone&&validatePhone(inlineSpF.phone, inlineSpF.phoneType)?RD:BD}} value={inlineSpF.phone||''} onChange={e=>handlePhoneInput(e.target.value,v=>setInlineSpF(prev=>({...prev,phone:v})), inlineSpF.phoneType||'mobile')} onPaste={e=>handlePhonePaste(e,v=>setInlineSpF(prev=>({...prev,phone:v})), inlineSpF.phoneType||'mobile')} placeholder={phonePlaceholder(inlineSpF.phoneType)}/>
                </Fld>
              </div>
              <div style={{...s.row,marginTop:8}}><Fld label="Designation" half hint="Letters & spaces only"><input style={s.inp} value={inlineSpF.designation||''} onChange={e=>setInlineSpF(prev=>({...prev,designation:e.target.value.replace(/[^a-zA-Z\s.&-]/g,'')}))} onBlur={e=>setInlineSpF(prev=>({...prev,designation:formatProperName(e.target.value.replace(/[^a-zA-Z\s.&-]/g,''))}))}/></Fld><Fld label="Email" half error={validateEmail(inlineSpF.email)}><input type="email" style={{...s.inp,borderColor:inlineSpF.email&&validateEmail(inlineSpF.email)?RD:BD}} value={inlineSpF.email||''} onChange={e=>setInlineSpF(prev=>({...prev,email:e.target.value.trim()}))} placeholder="name@company.com" maxLength={100}/></Fld></div>
              <div style={{display:'flex',gap:8,marginTop:10,justifyContent:'flex-end'}}><motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={s.btn('s')} onClick={()=>setShowInlineSp(false)}>Cancel</motion.button><motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={s.btn()} onClick={()=>{if(!inlineSpF.name)return;const inlineType=resolvePhoneType(inlineSpF.phone, inlineSpF.phoneType);if(inlineSpF.phone&&!phoneValid(inlineSpF.phone, inlineType)){setFormErrors({spocPhone:phoneErrorMsg(inlineType)});return;}if(inlineSpF.email&&!emailValid(inlineSpF.email)){setFormErrors({spocEmail:'Invalid email'});return;}setInlineSps(prev=>[...prev,{...normalizeSpocFields(inlineSpF),phoneType:inlineType,stage:'New'}]);setShowInlineSp(false);setFormErrors({});}}>Add</motion.button></div>
            </div>}
          </div>}
          <div style={{display:'flex',gap:8,justifyContent:'flex-end',borderTop:'1px solid '+BD,paddingTop:14}}>
            <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={s.btn('s')} onClick={closeModal}>Cancel</motion.button>
            <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} disabled={companySaving} style={{...s.btn(),opacity:(companySaving||!form.name)?.5:1,cursor:companySaving?'not-allowed':'pointer'}} onClick={async()=>{const result=await saveCompany();const created=result?.company||result;if(result&&modal?.leadBankId){try{await api.markLeadDone(modal.leadBankId,{companyId:created?._id,companyName:created?.name,assignedTo:form.salesperson});loadData();}catch{}}}}>{companySaving?'Saving...':'Save'+(inlineSps.length?' + '+inlineSps.length+' SPOC'+(inlineSps.length>1?'s':''):'')}</motion.button>
          </div>
        </div>
      </Modal>

      {/* SPOC Modal */}
      <Modal open={modal?.type==='spoc'} onClose={closeModal} title={modal?.edit?'Edit SPOC':'Add SPOC'} preventClose>
        <div style={{display:'flex',flexDirection:'column',gap:12}}>
          {formErrors.api&&<div style={{color:RD,fontSize:13,padding:10,background:RL,borderRadius:8}}>{formErrors.api}</div>}
          {spocPhoneDup&&<div style={{color:spocDupOverridden?'#8a6100':RD,fontSize:13,padding:10,background:spocDupOverridden?'#fff8e6':RL,borderRadius:8,border:`1px solid ${spocDupOverridden?'#e0b34d':RD+'33'}`}}>
            <div style={{fontWeight:600,marginBottom:4}}>{spocDupOverridden?'⚠️ Saving as a duplicate':'⚠️ Duplicate number'}</div>
            <div>{spocPhoneDup.message}</div>
            <div style={{marginTop:6,opacity:.85}}>
              <b>{spocPhoneDup.spocName}</b>{spocPhoneDup.designation?` · ${spocPhoneDup.designation}`:''}<br/>
              Developer: <b>{spocPhoneDup.companyName}</b>{spocPhoneDup.city?` · ${spocPhoneDup.city}`:''}
              {spocPhoneDup.salesperson?<><br/>Owned by: {spocPhoneDup.salesperson}</>:null}
            </div>
            <div style={{marginTop:8,display:'flex',gap:14,alignItems:'center',flexWrap:'wrap'}}>
              {spocPhoneDup.companyId&&<span onClick={()=>{closeModal();setDetailId(spocPhoneDup.companyId);setPage('companies');setDTab('spocs');}} style={{cursor:'pointer',textDecoration:'underline',fontWeight:600}}>View {spocPhoneDup.companyName}</span>}
              {spocDupOverridden
                ? <span onClick={()=>setSpocDupOk('')} style={{cursor:'pointer',textDecoration:'underline',fontWeight:600}}>Undo</span>
                : <span onClick={()=>{setSpocDupOk(spocPhone);setFormErrors(p=>({...p,phone:''}));}} style={{cursor:'pointer',textDecoration:'underline',fontWeight:600}}>Add anyway</span>}
            </div>
            {spocDupOverridden&&<div style={{marginTop:6,fontSize:12,opacity:.8}}>Both contacts will share this number. Change the number to cancel.</div>}
          </div>}
          <div style={s.row}><Fld label="Name *" half error={formErrors.name}><input style={s.inp} value={form.name||''} onChange={e=>uf('name',e.target.value)} onBlur={e=>uf('name',formatProperName(e.target.value))}/></Fld><Fld label="Company *" half error={formErrors.companyId}><Dropdown style={s.sel} value={form.companyId||''} onChange={e=>uf('companyId',e.target.value)}><option value="">Select</option>{companies.map(c=><option key={c._id} value={c._id}>{c.name}</option>)}</Dropdown></Fld></div>
          <div style={s.row}><Fld label="Phone (+91...)" half error={touched.phone?formErrors.phone||validatePhone(form.phone, form.phoneType):''} hint={!touched.phone?phoneFormatHint(form.phoneType):''}>
            <div style={{display:'flex',gap:6,marginBottom:8}}>
              {['mobile','landline'].map(t=><div key={t} onClick={()=>setForm(prev=>({...prev,phoneType:t,phone:prev.phone&&prev.phone.length>phoneMaxLength(t)?prev.phone.slice(0,phoneMaxLength(t)):prev.phone}))} style={s.chip((form.phoneType||'mobile')===t)}>{t==='mobile'?'📱 Mobile (10)':'☎️ Landline (12)'}</div>)}
            </div>
            <input style={{...s.inp,borderColor:touched.phone&&(formErrors.phone||validatePhone(form.phone, form.phoneType))?RD:BD}} value={form.phone||''} onChange={e=>{handlePhoneInput(e.target.value,v=>{uf('phone',v);touch('phone');}, form.phoneType||'mobile');}} onPaste={e=>{handlePhonePaste(e,v=>{uf('phone',v);touch('phone');}, form.phoneType||'mobile');}} onBlur={()=>touch('phone')} placeholder={phonePlaceholder(form.phoneType)}/>
          </Fld><Fld label="Email" half error={touched.email?formErrors.email||validateEmail(form.email):''} hint={!touched.email?"name@company.com":''}><input type="email" style={{...s.inp,borderColor:touched.email&&(formErrors.email||validateEmail(form.email))?RD:BD}} value={form.email||''} onChange={e=>{uf('email',e.target.value.trim());touch('email');}} onBlur={()=>touch('email')} placeholder="name@company.com" maxLength={100}/></Fld></div>
          <div style={s.row}><Fld label="Designation" half hint="Letters & spaces only"><input style={s.inp} value={form.designation||''} onChange={e=>uf('designation',e.target.value.replace(/[^a-zA-Z\s.&-]/g,''))} onBlur={e=>uf('designation',formatProperName(e.target.value.replace(/[^a-zA-Z\s.&-]/g,'')))} maxLength={60}/></Fld><Fld label="Lead Source" half><Dropdown style={s.sel} value={form.leadSource||''} onChange={e=>uf('leadSource',e.target.value)}><option value="">Select</option>{(config.leadSources||[]).map(x=><option key={x}>{x}</option>)}</Dropdown></Fld></div>
          {form.companyId&&(()=>{
            const meId=String(modal?.editId||'');
            const rtOf=x=>{const r=x?.reportsTo?._id||x?.reportsTo;return r?String(r):'';};
            // SPOCs in the same company (active), excluding self and — on edit — this SPOC's own reports (cycle guard)
            const coSps=spocs.filter(x=>String(x.companyId?._id||x.companyId)===String(form.companyId)&&!x.isStrikedOut&&String(x._id)!==meId);
            const blocked=new Set(meId?[meId]:[]);
            if(meId){const addDesc=pid=>{spocs.filter(x=>rtOf(x)===String(pid)).forEach(c=>{if(!blocked.has(String(c._id))){blocked.add(String(c._id));addDesc(c._id);}});};addDesc(meId);}
            const options=coSps.filter(x=>!blocked.has(String(x._id)));
            return <div style={s.row}><Fld label="Reports to" half hint="Manager in the company hierarchy"><Dropdown style={s.sel} value={rtOf(form)} onChange={e=>uf('reportsTo',e.target.value||null)}><option value="">— None (top level) —</option>{options.map(x=><option key={x._id} value={String(x._id)}>{x.name}{x.designation?` (${x.designation})`:''}</option>)}</Dropdown></Fld></div>;
          })()}
          <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}><motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={s.btn('s')} onClick={closeModal}>Cancel</motion.button><motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={{...s.btn(),opacity:(spocSaving||spocPhoneChecking||(spocPhoneDup&&!spocDupOverridden))?.6:1}} disabled={spocSaving||spocPhoneChecking||(!!spocPhoneDup&&!spocDupOverridden)} onClick={saveSpoc}>{spocSaving?'Saving...':spocPhoneChecking?'Checking...':spocDupOverridden?'Save duplicate':spocPhoneDup?'Duplicate number':'Save'}</motion.button></div>
        </div>
      </Modal>

      {/* Call Modal */}
      <Modal open={modal?.type==='call'} onClose={closeModal} title={modal?.edit ? 'Edit Call' : 'Log Call'} preventClose>
        {callStep===1&&<div>
          <Fld label="Search contact"><input style={s.inp} value={callSearch} onChange={e=>setCallSearch(e.target.value)} placeholder="Type name..." autoFocus/></Fld>
          {/* Recent follow-ups first */}
          {!callSearch&&<div style={{fontSize:12,fontWeight:600,color:P,marginTop:14,marginBottom:6}}>📌 Pending Follow-ups</div>}
          <div style={{maxHeight:380,overflowY:'auto',marginTop:callSearch?10:0}}>
            {!callSearch&&spocs.filter(sp=>sp.nextFollowUp&&sp.nextFollowUp<=today()&&!['Won','Dropped','Not interested'].includes(sp.stage)).slice(0,5).map(sp=>{const co=getCo(sp);return<div key={sp._id} onClick={()=>{const h=calls.filter(x=>x.spocId===sp._id);setForm(prev=>({...prev,spocId:sp._id,companyId:sp.companyId,salesperson:activitySalesperson(),callType:h.length?'follow-up':'first call'}));setCallStep(2);}} style={{padding:'10px 12px',cursor:'pointer',borderBottom:'1px solid #F3F4F6',display:'flex',justifyContent:'space-between',fontSize:13,borderRadius:8}} onMouseEnter={e=>{e.currentTarget.style.background=PL}} onMouseLeave={e=>{e.currentTarget.style.background=''}}>
              <div>{sp.hot?'🔥 ':''}<b>{sp.name}</b> · <span style={{color:MU}}>{co?.name}</span></div><div style={{display:'flex',gap:6,alignItems:'center'}}><span style={{fontSize:11,color:sp.nextFollowUp<today()?RD:P}}>{fmtDate(sp.nextFollowUp)}</span><Tag label={sp.stage}/></div>
            </div>})}
            {!callSearch&&<div style={{fontSize:12,fontWeight:600,color:MU,marginTop:14,marginBottom:6}}>All Contacts</div>}
            {companies.map(c=>{const cts=spocs.filter(x=>x.companyId===c._id&&(!callSearch||x.name.toLowerCase().includes(callSearch.toLowerCase())||c.name.toLowerCase().includes(callSearch.toLowerCase())));if(!cts.length)return null;
              return<div key={c._id} style={{marginBottom:10}}><div style={{fontSize:12,fontWeight:600,color:MU,padding:'6px 0',borderBottom:'1px solid '+BD}}>{c.name} · {c.city}</div>
                {cts.map(sp=><div key={sp._id} onClick={()=>{const h=calls.filter(x=>x.spocId===sp._id);setForm(prev=>({...prev,spocId:sp._id,companyId:sp.companyId,salesperson:activitySalesperson(),callType:h.length?'follow-up':'first call'}));setCallStep(2);}} style={{padding:'9px 12px',cursor:'pointer',borderBottom:'1px solid #F3F4F6',display:'flex',justifyContent:'space-between',fontSize:13}} onMouseEnter={e=>{e.currentTarget.style.background=PL}} onMouseLeave={e=>{e.currentTarget.style.background=''}}>
                  <div><b>{sp.name}</b> · {sp.designation||''}</div><Tag label={sp.stage}/>
                </div>)}
              </div>;
            })}
          </div>
        </div>}
        {callStep===2&&(()=>{const sp=spocs.find(x=>String(x._id)===String(form.spocId));const co=getCo(sp);if(!sp)return<div style={{textAlign:'center',padding:20}}><div style={{color:MU,marginBottom:10}}>SPOC not found in loaded data</div><motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={s.btn()} onClick={()=>setCallStep(1)}>← Select a SPOC</motion.button></div>;
          return<div style={{display:'flex',flexDirection:'column',gap:12}}>
            <div style={{padding:14,background:'linear-gradient(135deg,'+PL+',#E0E7FF)',borderRadius:12,display:'flex',justifyContent:'space-between'}}>
              <div><b>{sp.name}</b>{sp.hot&&' 🔥'}<div style={{fontSize:12,color:MU}}>{co?.name} · {sp.designation||''}</div>{sp.lastCallDate&&<div style={{fontSize:11,color:LT,marginTop:2}}>Last call: {fmtDate(sp.lastCallDate)} · {sp.lastOutcome||'—'}</div>}</div>
              <div style={{textAlign:'right',fontSize:11,color:MU}}>Att {sp.totalAttempts||0} · Conn {sp.totalConnected||0}<br/><Tag label={sp.stage}/>{!modal?.hasSpoc&&<div><motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={{...s.btn('s'),padding:'2px 8px',fontSize:11,marginTop:4}} onClick={()=>setCallStep(1)}>Change</motion.button></div>}</div>
            </div>
            <div style={s.row}><Fld label="Date" half><input type="date" style={s.inp} value={form.date||''} onChange={e=>uf('date',e.target.value)}/></Fld><Fld label="Type" half><Dropdown style={s.sel} value={form.callType||''} onChange={e=>uf('callType',e.target.value)}>{['first call','follow-up','reminder','warm reconnect'].map(t=><option key={t}>{t}</option>)}</Dropdown></Fld></div>
            <div style={s.row}><Fld label="Call done by *" half><Dropdown style={{...s.sel,borderColor:!form.salesperson?'#EF4444':undefined}} value={form.salesperson||''} onChange={e=>uf('salesperson',e.target.value)}><option value="">Select</option>{(config.salespersons||[]).map(x=><option key={x}>{x}</option>)}</Dropdown></Fld><Fld label="Lead generated by" half hint="Who added this prospect — not who made this call"><input style={{...s.inp,background:'#F9FAFB',color:MU}} value={co?.salesperson||'—'} readOnly/></Fld></div>
            <div style={{display:'flex',gap:8}}>{[false,true].map(v=><div key={String(v)} onClick={()=>{uf('connected',v);uf('outcome','');uf('conversationOutcome','');uf('connectedVia',v?(form.connectedVia||'Call'):'');}} style={{flex:1,padding:12,borderRadius:10,border:'2px solid '+(form.connected===v?P:BD),cursor:'pointer',textAlign:'center',fontWeight:600,fontSize:13,background:form.connected===v?PL:WH,color:form.connected===v?P:MU,transition:'all .15s'}}>{v?'✅ Connected':'❌ Not Connected'}</div>)}</div>
            {form.connected&&<div><div style={{fontSize:12,color:MU,marginBottom:5}}>Connected via</div><div style={{display:'flex',flexWrap:'wrap',gap:5}}>{CVIA.map(([o,ic])=><div key={o} onClick={()=>uf('connectedVia',o)} style={s.chip(form.connectedVia===o)}>{ic} {o}</div>)}</div></div>}
            {!form.connected&&<div style={{display:'flex',flexWrap:'wrap',gap:5}}>{CNC.map(o=><div key={o} onClick={()=>uf('outcome',o)} style={s.chip(form.outcome===o)}>{o}</div>)}</div>}
            {form.connected&&<div style={{display:'flex',flexWrap:'wrap',gap:5}}>{CCC.map(o=><div key={o} onClick={()=>{uf('conversationOutcome',o);uf('outcome',o);}} style={s.chip(form.conversationOutcome===o)}>{o}</div>)}</div>}
            {(form.outcome||form.conversationOutcome)&&<>
              {form.conversationOutcome==='Not interested'&&<div><div style={{fontSize:12,color:MU,marginBottom:4}}>Reason *</div>{formErrors.ni&&<div style={{fontSize:11,color:RD}}>{formErrors.ni}</div>}<div style={{display:'flex',flexWrap:'wrap',gap:5}}>{(config.niReasons||NIR).map(r=><div key={r} onClick={()=>uf('notInterestedReason',r)} style={s.chip(form.notInterestedReason===r,RD)}>{r}</div>)}</div></div>}
              <div style={s.row}><Fld label={['Follow-up later','Meeting requested','Meeting scheduled'].includes(form.conversationOutcome)?'Follow-up Date *':'Follow-up Date'} half error={formErrors.followUpDate}><input type="date" style={{...s.inp,borderColor:formErrors.followUpDate?RD:BD}} value={form.followUpDate||''} onChange={e=>uf('followUpDate',e.target.value)} min={today()}/></Fld>{form.connected&&<Fld label="Follow-up Time" half><input type="time" style={s.inp} value={form.followUpTime||''} onChange={e=>uf('followUpTime',e.target.value)}/></Fld>}<Fld label="Confidence" half><div style={{display:'flex',alignItems:'center',gap:10}}><input type="range" min={1} max={10} value={form.confidence||5} onChange={e=>uf('confidence',+e.target.value)} style={{flex:1}}/><span style={{fontWeight:700}}>{form.confidence||5}/10</span></div></Fld></div>
              {form.conversationOutcome==='Meeting scheduled'&&<div style={s.row}><Fld label="Meeting Date *" half error={formErrors.meetingDate}><input type="date" style={{...s.inp,borderColor:formErrors.meetingDate?RD:BD}} value={form.meetingDate||''} onChange={e=>uf('meetingDate',e.target.value)} min={today()}/></Fld></div>}
              <Fld label={form.connected?'Notes * (min 10 chars)':'Notes'} error={formErrors.notes}><textarea style={{...s.inp,minHeight:50}} value={form.notes||''} onChange={e=>uf('notes',e.target.value)}/></Fld>
            </>}
            <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}><motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={s.btn('s')} onClick={closeModal}>Cancel</motion.button><motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={{...s.btn(),opacity:callSaving?.6:1}} disabled={callSaving} onClick={saveCall}>{callSaving?'Saving...':(modal?.edit?'Save Changes':'Log Call')}</motion.button></div>
          </div>;
        })()}
      </Modal>

      {/* Meeting Modal */}
      {(()=>{
        const isCompleted=['Completed','Done'].includes(form.stage);
        const isFieldVisitLog=[MEETING_TYPE_WALKIN_MET,MEETING_TYPE_WALKIN_NO_CONTACT,MEETING_TYPE_WALKIN_LEGACY].includes(form.type);
        const isNoContactLog=isNoContactVisit({ type: form.type });
        const mtgTitle = isNoContactLog
          ? (modal?.edit ? `Edit ${WALKIN_NO_CONTACT_LABEL}` : `Log ${WALKIN_NO_CONTACT_LABEL}`)
          : form.type===MEETING_TYPE_WALKIN_MET
            ? (modal?.edit ? `Edit ${WALKIN_MET_LABEL}` : `Log ${WALKIN_MET_LABEL}`)
            : (isCompleted ? 'Post-Meeting Summary' : (modal?.edit ? 'Edit Meeting' : 'Schedule Meeting'));
        return <Modal open={modal?.type==='meeting'} onClose={closeModal} title={mtgTitle} wide preventClose>
          <div style={{display:'flex',flexDirection:'column',gap:12}}>
            {formErrors.api&&<div style={{color:RD,fontSize:13,padding:10,background:RL,borderRadius:8}}>{formErrors.api}</div>}

            {/* ── Always visible: Scheduling Fields ── */}
            <div style={{display:'flex',flexDirection:'column',gap:12}}>
              <div style={s.row}>
                <Fld label="Company *" half><Dropdown style={s.sel} value={form.companyId||''} onChange={e=>{uf('companyId',e.target.value);uf('spocId','');if(!modal?.edit)uf('salesperson',activitySalesperson());}}><option value="">Select</option>{companies.map(c=><option key={c._id} value={c._id}>{c.name}</option>)}</Dropdown></Fld>
                <Fld label="Meeting With" half><Dropdown style={s.sel} value={form.spocId||''} onChange={e=>uf('spocId',e.target.value)}><option value="">Select Person</option>{spocs.filter(ss=>ss.companyId===form.companyId||ss.companyId?._id===form.companyId).map(ss=><option key={ss._id} value={ss._id}>{ss.name}{ss.designation?` (${ss.designation})`:''}</option>)}</Dropdown></Fld>
              </div>
              <div style={s.row}>
                <Fld label="Date" half><input type="date" style={s.inp} value={form.date||''} onChange={e=>uf('date',e.target.value)} min={modal?.edit||isFieldVisitLog?undefined:yesterday()}/></Fld>
                <Fld label="Type" half><Dropdown style={s.sel} value={form.type||''} onChange={e=>{const t=e.target.value;uf('type',t);setFormErrors({});if(!modal?.edit){if(t===MEETING_TYPE_WALKIN_NO_CONTACT){uf('stage','Cancelled');if(!form.agenda)uf('agenda','Walk-in · No contact — person unavailable');}else if(t===MEETING_TYPE_WALKIN_MET){uf('stage','Completed');if(!form.agenda)uf('agenda','Walk-in · Met contact — who you met and outcome');}}}}><option>online</option><option>in-person</option><option>over a call</option><option value={MEETING_TYPE_WALKIN_MET}>{WALKIN_MET_LABEL}</option><option value={MEETING_TYPE_WALKIN_NO_CONTACT}>{WALKIN_NO_CONTACT_LABEL}</option></Dropdown></Fld>
              </div>
              {isNoContactLog&&<div style={{fontSize:12,color:'#92400E',background:'#FFFBEB',border:'1px solid #FCD34D',borderRadius:8,padding:'8px 12px'}}>Site walk-in where you could not meet the contact — e.g. reception said they are out. Note who you spoke to and when to retry.</div>}
              {form.type===MEETING_TYPE_WALKIN_MET&&<div style={{fontSize:12,color:'#047857',background:'#ECFDF5',border:'1px solid #6EE7B7',borderRadius:8,padding:'8px 12px'}}>Site walk-in where you met someone in person. Add who you met and the outcome.</div>}
              {isFieldVisitLog&&<Fld label="Location *"><input style={{...s.inp,borderColor:!form.location?.trim()&&formErrors.api?.includes('Location')?'#EF4444':BD}} value={form.location||''} onChange={e=>uf('location',e.target.value)} placeholder={(()=>{const co=companies.find(c=>String(c._id)===String(form.companyId));return co?.city?`e.g. Main gate, ${co.city}`:'e.g. Reception, Tower B, Whitefield';})()}/></Fld>}
              <div style={s.row}>
                <Fld label="Meeting conducted by *" half hint="Who ran this meeting — not who generated the lead"><Dropdown style={{...s.sel,borderColor:!form.salesperson?'#EF4444':undefined}} value={form.salesperson||''} onChange={e=>uf('salesperson',e.target.value)}><option value="">Select</option>{(config.salespersons||[]).map(x=><option key={x}>{x}</option>)}</Dropdown></Fld>
                <Fld label="Lead generated by" half hint="Who added this prospect — not who ran the meeting"><input style={{...s.inp,background:'#F9FAFB',color:MU}} value={(()=>{const co=companies.find(c=>String(c._id)===String(form.companyId));return co?.salesperson||'—';})()} readOnly/></Fld>
              </div>
              <div style={s.row}>
                <Fld label="Meeting #" half><input type="number" min="1" step="1" style={s.inp} value={form.meetingNumber||''} onChange={e=>uf('meetingNumber',e.target.value.replace(/[^0-9]/g,''))} placeholder="e.g. 1"/></Fld>
              </div>
              <Fld label={isNoContactLog?'Visit notes':form.type===MEETING_TYPE_WALKIN_MET?'Summary / notes':'Agenda'}><textarea style={{...s.inp,minHeight:isNoContactLog?54:36}} value={form.agenda||''} onChange={e=>uf('agenda',e.target.value)} placeholder={isNoContactLog?'e.g. Reception said DM traveling — call back Thursday':form.type===MEETING_TYPE_WALKIN_MET?'Who you met and outcome':'What will be discussed?'}/></Fld>
              <div style={s.row}>
                <Fld label="Estimated Value ₹" half error={touched.estimatedValue?validateAmount(form.estimatedValue):''}><input style={{...s.inp,borderColor:touched.estimatedValue&&validateAmount(form.estimatedValue)?RD:BD}} value={form.estimatedValue||''} onChange={e=>{const v=e.target.value.replace(/[^0-9]/g,'');uf('estimatedValue',v);touch('estimatedValue');}} placeholder="e.g. 1200000" inputMode="numeric"/></Fld>
                <Fld label="Status" half>
                  <Dropdown style={s.sel} value={form.stage||'Tentative'} onChange={e=>uf('stage',e.target.value)}>
                    <option>Tentative</option><option>Confirmed</option><option>Scheduled</option><option>Rescheduled</option><option>Completed</option><option>Cancelled</option>
                  </Dropdown>
                </Fld>
              </div>
            </div>

            {/* ── Prompt to mark completed if not yet ── */}
            {!isCompleted && !isFieldVisitLog && (
              <div style={{background:'#F9FAFB',border:'1px solid #E5E7EB',borderRadius:10,padding:'12px 16px',display:'flex',alignItems:'center',justifyContent:'space-between',gap:12}}>
                <div>
                  <div style={{fontSize:13,fontWeight:600,color:TX}}>Meeting done?</div>
                  <div style={{fontSize:12,color:MU,marginTop:2}}>Mark as Completed to fill in MOM, Key Decisions & Summary</div>
                </div>
                <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn(),padding:'7px 16px',fontSize:12,flexShrink:0}} onClick={()=>uf('stage','Completed')}>
                  Mark as Completed
                </motion.button>
              </div>
            )}

            {/* ── Post-meeting fields: only when Completed ── */}
            {isCompleted && (
              <div style={{display:'flex',flexDirection:'column',gap:12,borderTop:'1px solid #E5E7EB',paddingTop:14,marginTop:2}}>
                <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:2}}>
                  <div style={{width:8,height:8,borderRadius:'50%',background:GN,flexShrink:0}}/>
                  <span style={{fontSize:11,fontWeight:700,color:GN,textTransform:'uppercase',letterSpacing:'.6px'}}>Post-Meeting Summary</span>
                </div>
                <Fld label="Unada Team Present"><input style={s.inp} value={form.attendeesFromUnada||''} onChange={e=>uf('attendeesFromUnada',e.target.value)} placeholder="e.g. Vikram, Srinivas"/></Fld>
                <Fld label="MOM / Summary *"><textarea style={{...s.inp,minHeight:80}} value={form.summary||''} onChange={e=>uf('summary',e.target.value)} placeholder="What was discussed? Key outcomes from the meeting…"/></Fld>
                <Fld label="Key Decisions"><textarea style={{...s.inp,minHeight:50}} value={form.keyDecisions||''} onChange={e=>uf('keyDecisions',e.target.value)} placeholder="Decisions made, commitments given…"/></Fld>
                <div style={s.row}>
                  <Fld label="Next Action" half><input style={s.inp} value={form.nextAction||''} onChange={e=>uf('nextAction',e.target.value)} placeholder="What happens next?"/></Fld>
                  <Fld label="Next Action Date" half><input type="date" style={s.inp} value={form.nextActionDate||''} onChange={e=>uf('nextActionDate',e.target.value)}/></Fld>
                </div>
                <Fld label="Closing %">
                  <div style={{display:'flex',alignItems:'center',gap:12}}>
                    <input type="range" min={0} max={100} step={5} value={form.closingProbability||50} onChange={e=>uf('closingProbability',+e.target.value)} style={{flex:1}}/>
                    <span style={{fontWeight:700,fontSize:14,minWidth:42}}>{form.closingProbability||50}%</span>
                  </div>
                </Fld>
              </div>
            )}

            <div style={{display:'flex',gap:8,justifyContent:'flex-end',paddingTop:4}}>
              <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={s.btn('s')} onClick={closeModal}>Cancel</motion.button>
              <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={s.btn()} onClick={saveMeeting}>{isCompleted?'Save & Complete':'Save Meeting'}</motion.button>
            </div>
          </div>
        </Modal>;
      })()}

      {/* Proposal Modal */}
      {modal?.type==='proposal' && (() => {
        const templates = getProposalTemplates(config);
        return <Modal open onClose={closeModal} title={modal?.edit?'Edit Proposal Log':'Log Proposal Sent'} wide preventClose>
        <div style={{display:'flex',flexDirection:'column',gap:12}}>
          {formErrors.api&&<div style={{color:RD,fontSize:13,padding:10,background:RL,borderRadius:8}}>{formErrors.api}</div>}
          <div style={s.row}>
            <Fld label="Date sent *" half error={formErrors.date}>
              <DateInput value={form.date||''} onChange={e=>uf('date',e.target.value)} error={formErrors.date}/>
            </Fld>
            <Fld label="Next follow-up" half>
              <DateInput value={form.nextFollowUpDate||''} onChange={e=>uf('nextFollowUpDate',e.target.value)} min={form.date||today()}/>
            </Fld>
          </div>
          <Fld label="Sent to (name) *" error={formErrors.sentToName}>
            <input style={{...s.inp,borderColor:formErrors.sentToName?RD:BD}} value={form.sentToName||''} onChange={e=>uf('sentToName',e.target.value)} placeholder="e.g. Giridhar Sharma, CFO"/>
          </Fld>
          <div style={s.row}>
            <Fld label="Sent by" half>
              <Dropdown style={s.sel} value={form.salesperson||''} onChange={e=>uf('salesperson',e.target.value)}>
                <option value="">Select</option>
                {(config.salespersons||[]).map(x=><option key={x} value={x}>{x}</option>)}
              </Dropdown>
            </Fld>
            <Fld label="Proposal type" half>
              <Dropdown style={s.sel} value={form.templateName||''} onChange={e=>uf('templateName',e.target.value)}>
                {templates.map(t=><option key={t.name} value={t.name}>{t.name}</option>)}
              </Dropdown>
            </Fld>
          </div>
          <div style={s.row}>
            <Fld label="Sent via" half>
              <Dropdown style={s.sel} value={form.sentVia||'Email'} onChange={e=>uf('sentVia',e.target.value)}>
                {PROPOSAL_SENT_VIA.map(v=><option key={v} value={v}>{v}</option>)}
              </Dropdown>
            </Fld>
          </div>
          <Fld label="Notes"><textarea style={{...s.inp,minHeight:54}} value={form.notes||''} onChange={e=>uf('notes',e.target.value)} placeholder="Revision details, what was shared, context for follow-up…"/></Fld>
          <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
            <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={s.btn('s')} onClick={closeModal}>Cancel</motion.button>
            <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn(),background:'#7C3AED'}} onClick={saveProposal}>{modal?.edit?'Save Changes':'Log Proposal'}</motion.button>
          </div>
        </div>
        </Modal>;
      })()}

      {/* Visit Modal */}
      {(()=>{
        const tcList = form.targetCompanies||[];
        const addTC = () => {
          const name = (form._tcName||'').trim();
          if(!name) return;
          const matched = companies.find(c=>c.name.toLowerCase()===name.toLowerCase()&&(!form.city||citiesMatch(c.city,form.city)));
          setForm(prev=>({...prev, targetCompanies:[...tcList,{companyName:name,companyId:matched?._id||null,priority:form._tcPriority||'medium',notes:''}], _tcName:'', _tcPriority:'medium'}));
        };
        return <Modal open={modal?.type==='visit'} onClose={closeModal} title={modal?.edit?'Edit Visit':'Plan City Visit'} wide preventClose>
          <div style={{display:'flex',flexDirection:'column',gap:12}}>
            <Fld label={<>Visit Title <span style={{color:RD}}>*</span></>}><input style={{...s.inp,borderColor:form._vErr?.title?RD:undefined}} value={form.title||''} onChange={e=>{uf('title',e.target.value);if(form._vErr)uf('_vErr',{...form._vErr,title:undefined});}} placeholder="e.g. Pune June Visit"/>{form._vErr?.title&&<div style={{fontSize:11,color:RD,marginTop:2}}>{form._vErr.title}</div>}</Fld>
            <div style={s.row}>
              <Fld label={<>City <span style={{color:RD}}>*</span></>} half><Dropdown style={{...s.sel,borderColor:form._vErr?.city?RD:undefined}} value={form.city||''} onChange={e=>{uf('city',e.target.value);if(form._vErr)uf('_vErr',{...form._vErr,city:undefined});}}><option value="">Select City</option>{[...new Set(companies.map(c=>c.city))].sort().map(c=><option key={c}>{c}</option>)}</Dropdown>{form._vErr?.city&&<div style={{fontSize:11,color:RD,marginTop:2}}>{form._vErr.city}</div>}</Fld>
              <Fld label="Status" half><Dropdown style={s.sel} value={form.status||'planning'} onChange={e=>uf('status',e.target.value)}><option value="planning">Planning</option><option value="active">Active</option><option value="completed">Completed</option><option value="cancelled">Cancelled</option></Dropdown></Fld>
            </div>
            <div style={s.row}>
              <Fld label={<>Visit From <span style={{color:RD}}>*</span></>} half><input type="date" style={{...s.inp,borderColor:form._vErr?.visitFrom?RD:undefined}} value={form.visitFrom||''} onChange={e=>{uf('visitFrom',e.target.value);if(form._vErr)uf('_vErr',{...form._vErr,visitFrom:undefined});}}/>{form._vErr?.visitFrom&&<div style={{fontSize:11,color:RD,marginTop:2}}>{form._vErr.visitFrom}</div>}</Fld>
              <Fld label={<>Visit To <span style={{color:RD}}>*</span></>} half><input type="date" style={{...s.inp,borderColor:form._vErr?.visitTo?RD:undefined}} value={form.visitTo||''} onChange={e=>{uf('visitTo',e.target.value);if(form._vErr)uf('_vErr',{...form._vErr,visitTo:undefined});}}/>{form._vErr?.visitTo&&<div style={{fontSize:11,color:RD,marginTop:2}}>{form._vErr.visitTo}</div>}</Fld>
            </div>
            <div style={s.row}>
              <Fld label="Target Meetings" half><input type="number" style={s.inp} value={form.targetMeetings||''} onChange={e=>uf('targetMeetings',+e.target.value)} placeholder="e.g. 5" min={0}/></Fld>
              <Fld label="Assigned Team Members" half>
                <div style={{border:'1px solid '+BD,borderRadius:9,background:WH,padding:'8px 12px',maxHeight:120,overflowY:'auto'}}>
                  {(config.salespersons||[]).length===0&&<div style={{fontSize:12,color:LT}}>No team members found</div>}
                  {(config.salespersons||[]).map(sp=>{
                    const checked=(form.assignedTo||[]).includes(sp);
                    return <label key={sp} style={{display:'flex',alignItems:'center',gap:8,padding:'3px 0',cursor:'pointer',userSelect:'none'}}>
                      <input type="checkbox" checked={checked} onChange={()=>uf('assignedTo',checked?(form.assignedTo||[]).filter(x=>x!==sp):[...(form.assignedTo||[]),sp])} style={{accentColor:TX,width:14,height:14}}/>
                      <span style={{fontSize:13,color:TX}}>{sp}</span>
                    </label>;
                  })}
                </div>
                {(form.assignedTo||[]).length>0&&<div style={{fontSize:11,color:MU,marginTop:4}}>{(form.assignedTo||[]).join(', ')}</div>}
              </Fld>
            </div>
            <Fld label="Visit Notes"><textarea style={{...s.inp,minHeight:48}} value={form.notes||''} onChange={e=>uf('notes',e.target.value)} placeholder="Any context, priorities or instructions for the team…"/></Fld>

            {/* Target companies */}
            {(()=>{
              const tcMode = form._tcMode||'type';
              const cityCompanies = form.city ? companies.filter(c=>citiesMatch(c.city,form.city)&&c.dealStatus==='active') : [];
              const alreadyAdded = new Set(tcList.map(t=>t.companyName.toLowerCase()));
              const browseSel = form._browseSel||{};

              const applyPaste = () => {
                const raw = (form._tcPaste||'').split(/[\n,]+/).map(x=>x.trim()).filter(Boolean);
                const newItems = raw.filter(n=>!alreadyAdded.has(n.toLowerCase())).map(n=>{
                  const matched=companies.find(c=>c.name.toLowerCase()===n.toLowerCase());
                  return {companyName:n,companyId:matched?._id||null,priority:'medium',notes:''};
                });
                setForm(prev=>({...prev,targetCompanies:[...tcList,...newItems],_tcPaste:'',_tcMode:'type'}));
              };

              const applyBrowse = () => {
                const newItems = Object.entries(browseSel).filter(([,v])=>v).map(([id,])=>{
                  const co=companies.find(c=>String(c._id)===id);
                  if(!co||alreadyAdded.has(co.name.toLowerCase()))return null;
                  return {companyName:co.name,companyId:co._id,priority:form['_brPri_'+id]||'medium',notes:''};
                }).filter(Boolean);
                setForm(prev=>({...prev,targetCompanies:[...tcList,...newItems],_browseSel:{},_tcMode:'type'}));
              };

              return <div style={{borderTop:'1px solid #E5E7EB',paddingTop:14}}>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10}}>
                  <div style={{fontSize:13,fontWeight:600,color:TX}}>Target Companies <span style={{fontWeight:400,color:MU,fontSize:12}}>({tcList.length} added)</span></div>
                  {/* Mode switcher */}
                  <div style={{display:'flex',background:'#F3F4F6',borderRadius:8,padding:3,gap:2}}>
                    {[['type','Type'],['paste','Paste List'],['browse','Browse']].map(([k,l])=>
                      <div key={k} style={{padding:'4px 10px',borderRadius:6,fontSize:11,fontWeight:tcMode===k?600:400,background:tcMode===k?WH:'transparent',color:tcMode===k?TX:MU,cursor:'pointer',transition:'all .12s'}} onClick={()=>uf('_tcMode',k)}>{l}</div>
                    )}
                  </div>
                </div>

                {/* Mode: Type one */}
                {tcMode==='type'&&<>
                  <div style={{display:'flex',gap:8,marginBottom:8,flexWrap:'wrap'}}>
                    <input style={{...s.inp,flex:1,minWidth:160}} value={form._tcName||''} onChange={e=>uf('_tcName',e.target.value)} placeholder="Company name…" onKeyDown={e=>e.key==='Enter'&&addTC()}/>
                    <Dropdown style={{...s.sel,width:110}} value={form._tcPriority||'medium'} onChange={e=>uf('_tcPriority',e.target.value)}><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option></Dropdown>
                    <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn('s'),padding:'6px 14px',fontSize:12}} onClick={addTC}>Add</motion.button>
                  </div>
                  {form.city&&cityCompanies.length>0&&<div style={{fontSize:11,color:MU,marginBottom:6}}>
                    Suggestions: {cityCompanies.slice(0,6).map(c=><span key={c._id} style={{cursor:'pointer',color:TX,textDecoration:'underline',marginRight:8,opacity:alreadyAdded.has(c.name.toLowerCase())?0.4:1}} onClick={()=>{if(!alreadyAdded.has(c.name.toLowerCase()))uf('_tcName',c.name);}}>{c.name}</span>)}
                  </div>}
                </>}

                {/* Mode: Paste list */}
                {tcMode==='paste'&&<>
                  <div style={{fontSize:12,color:MU,marginBottom:6}}>Paste company names separated by commas or new lines:</div>
                  <textarea style={{...s.inp,minHeight:90,fontFamily:'monospace',fontSize:12}} value={form._tcPaste||''} onChange={e=>uf('_tcPaste',e.target.value)} placeholder={'Maruti Ventures\nBren Corporation\nLegacy Global, Trifecta Projects'}/>
                  <div style={{display:'flex',justifyContent:'flex-end',marginTop:8}}>
                    <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn(),padding:'7px 16px',fontSize:12}} onClick={applyPaste}>Add All to List</motion.button>
                  </div>
                </>}

                {/* Mode: Browse from city */}
                {tcMode==='browse'&&<>
                  {!form.city&&<div style={{fontSize:12,color:MU,padding:'10px 0'}}>Select a city first to browse companies.</div>}
                  {form.city&&cityCompanies.length===0&&<div style={{fontSize:12,color:MU}}>No active companies found in {form.city}.</div>}
                  {form.city&&cityCompanies.length>0&&<>
                    <div style={{fontSize:11,color:MU,marginBottom:8}}>{cityCompanies.length} active companies in {form.city} — check to add, set priority per row</div>
                    <input style={{...s.inp,marginBottom:8}} value={form._browseSearch||''} onChange={e=>uf('_browseSearch',e.target.value)} placeholder="Search companies…"/>
                    <div style={{maxHeight:220,overflowY:'auto',border:'1px solid #E5E7EB',borderRadius:10,padding:4}}>
                      {cityCompanies.filter(co=>(form._browseSearch||'')?co.name.toLowerCase().includes((form._browseSearch||'').toLowerCase()):true).map(co=>{
                        const id=String(co._id);
                        const checked=!!browseSel[id];
                        const isDone=alreadyAdded.has(co.name.toLowerCase());
                        return <div key={id} style={{display:'flex',alignItems:'center',gap:10,padding:'7px 10px',borderRadius:8,background:checked?'#F0FDF4':isDone?'#F9FAFB':'transparent',opacity:isDone?0.5:1}}>
                          <input type="checkbox" checked={checked||isDone} disabled={isDone} onChange={e=>setForm(prev=>({...prev,_browseSel:{...browseSel,[id]:e.target.checked}}))} style={{width:15,height:15,cursor:isDone?'default':'pointer',flexShrink:0}}/>
                          <span style={{flex:1,fontSize:13,fontWeight:checked?600:400}}>{co.name}</span>
                          {isDone&&<span style={{fontSize:10,color:GN}}>✓ added</span>}
                          {!isDone&&checked&&<Dropdown style={{...s.sel,width:90}} value={form['_brPri_'+id]||'medium'} onChange={e=>uf('_brPri_'+id,e.target.value)}><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option></Dropdown>}
                          {!isDone&&!checked&&<span style={{fontSize:10,color:LT}}>{co.salesperson||''}</span>}
                        </div>;
                      })}
                    </div>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:8}}>
                      <span style={{fontSize:12,color:MU}}>{Object.values(browseSel).filter(Boolean).length} selected</span>
                      <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn(),padding:'7px 16px',fontSize:12}} onClick={applyBrowse}>Add Selected</motion.button>
                    </div>
                  </>}
                </>}

                {/* Added list */}
                {tcList.length>0&&<div style={{marginTop:10,display:'flex',flexDirection:'column',gap:5,maxHeight:180,overflowY:'auto'}}>
                  <div style={{fontSize:11,fontWeight:600,color:MU,textTransform:'uppercase',letterSpacing:'.5px',marginBottom:2}}>Added ({tcList.length})</div>
                  {tcList.map((tc,i)=><div key={i} style={{display:'flex',alignItems:'center',gap:8,padding:'7px 10px',borderRadius:8,background:'#F9FAFB',border:'1px solid #E5E7EB'}}>
                    <div style={{width:8,height:8,borderRadius:'50%',background:tc.priority==='high'?RD:tc.priority==='medium'?YL:GN,flexShrink:0}}/>
                    <span style={{flex:1,fontSize:13}}>{tc.companyName}</span>
                    <Dropdown style={{...s.sel,width:90}} value={tc.priority} onChange={e=>setForm(prev=>({...prev,targetCompanies:tcList.map((t,j)=>j===i?{...t,priority:e.target.value}:t)}))}><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option></Dropdown>
                    <span style={{cursor:'pointer',color:RD,fontSize:13,padding:'0 4px',flexShrink:0}} onClick={()=>setForm(prev=>({...prev,targetCompanies:tcList.filter((_,j)=>j!==i)}))}>✕</span>
                  </div>)}
                </div>}
              </div>;
            })()}

{(()=>{
              const ve={};
              if(!form.title)ve.title='Visit title is required';
              if(!form.city)ve.city='City is required';
              if(!form.visitFrom)ve.visitFrom='Start date is required';
              if(!form.visitTo)ve.visitTo='End date is required';
              if(form.visitFrom&&form.visitTo&&form.visitTo<form.visitFrom)ve.visitTo='End date must be after start date';
              const hasErr=Object.keys(ve).length>0;
              const vErr=form._vErr||{};
              return <>
                {form._vErr&&hasErr&&<div style={{background:'#FEF2F2',border:'1px solid #FECACA',borderRadius:10,padding:'10px 14px',marginBottom:8}}>
                  <div style={{fontSize:12,fontWeight:600,color:RD,marginBottom:4}}>Please fix the following:</div>
                  {Object.values(vErr).filter(Boolean).map((e,i)=><div key={i} style={{fontSize:12,color:RD,display:'flex',alignItems:'center',gap:6}}><span>•</span>{e}</div>)}
                </div>}
                <div style={{display:'flex',gap:8,justifyContent:'flex-end',paddingTop:4}}>
                  <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={s.btn('s')} onClick={closeModal}>Cancel</motion.button>
                  <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={s.btn()} onClick={async()=>{
                    if(hasErr){uf('_vErr',ve);return;}
                    const payload={title:form.title,city:form.city,visitFrom:form.visitFrom,visitTo:form.visitTo,status:form.status||'planning',assignedTo:form.assignedTo||[],targetMeetings:form.targetMeetings||0,notes:form.notes||'',targetCompanies:tcList,createdBy:user};
                    try{
                      if(modal?.edit&&modal?.editId){
                        const updated=await api.updateVisit(modal.editId,payload);
                        setVisits(prev=>prev.map(x=>x._id===modal.editId?updated:x));
                        setVisitDetail(updated);
                        setVisitDetailLoading(true);
                        api.getVisit(modal.editId).then(setVisitDetailData).finally(()=>setVisitDetailLoading(false));
                      } else {
                        await api.createVisit(payload);
                        api.getVisits().then(setVisits);
                      }
                      closeModal();
                    }catch(e){uf('_vErr',{api:e.message});}
                  }}>Save Visit</motion.button>
                </div>
              </>;
            })()}
          </div>
        </Modal>;
      })()}

      {/* Request Drop Modal (non-admin) */}
      <Modal open={modal?.type==='requestDrop'} onClose={closeModal} title={'Request Drop — '+(modal?.companyName||'')}>
        <div style={{display:'flex',flexDirection:'column',gap:14}}>
          <div style={{background:'#FEF3C7',border:'1px solid #FDE68A',borderRadius:10,padding:'10px 14px',fontSize:12,color:'#92400E'}}>
            This request will be sent to the admin for approval. The company will remain active until approved.
          </div>
          <Fld label={<>Reason for dropping <span style={{color:RD}}>*</span></>} error={formErrors.reason}>
            <textarea style={{...s.inp,minHeight:80,borderColor:formErrors.reason?RD:undefined}} value={form.reason||''} onChange={e=>uf('reason',e.target.value)} placeholder="Explain why this company should be dropped (e.g. No budget, not interested, wrong segment…)"/>
          </Fld>
          <Fld label="Reconnect After (optional)">
            <input type="date" style={s.inp} value={form.reconnectDate||''} onChange={e=>uf('reconnectDate',e.target.value)} min={today()}/>
          </Fld>
          <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
            <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={s.btn('s')} onClick={closeModal}>Cancel</motion.button>
            <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn('r')}} onClick={async()=>{
              if(!(form.reason||'').trim()){setFormErrors({reason:'Reason is required'});return;}
              try{
                await api.requestDrop(modal.companyId,{reason:form.reason,reconnectDate:form.reconnectDate||'',requestedBy:user});
                await loadData();
                closeModal();
              }catch(e){setFormErrors({api:e.message});}
            }}>Submit Request</motion.button>
          </div>
          {formErrors.api&&<div style={{fontSize:12,color:RD}}>{formErrors.api}</div>}
        </div>
      </Modal>

      {/* Conclude Modal */}
      <Modal open={modal?.type==='conclude'} onClose={closeModal} title={'Conclude — '+(modal?.companyName||'')}>
        <div style={{display:'flex',flexDirection:'column',gap:16}}>
          <div style={{display:'flex',gap:8}}>
            {[{v:'won',l:'✅ Won',c:GN},{v:'dropped',l:'❌ Dropped',c:RD},{v:'on-hold',l:'⏸️ On Hold',c:YL}].map(({v,l,c})=>
              <div key={v} onClick={()=>uf('status',v)} style={{flex:1,padding:16,borderRadius:12,border:'2px solid '+(form.status===v?c:BD),cursor:'pointer',textAlign:'center',fontWeight:600,background:form.status===v?c+'15':WH,color:form.status===v?c:MU,transition:'all .15s'}}>{l}</div>
            )}
          </div>
          {form.status==='dropped'&&<><Fld label="Drop Reason"><input style={s.inp} value={form.reason||''} onChange={e=>uf('reason',e.target.value)}/></Fld><Fld label="Reconnect After (date)"><input type="date" style={s.inp} value={form.reconnectDate||''} onChange={e=>uf('reconnectDate',e.target.value)} min={today()}/></Fld></>}
          {form.status==='on-hold'&&<Fld label="Reason for Hold"><input style={s.inp} value={form.reason||''} onChange={e=>uf('reason',e.target.value)}/></Fld>}
          <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}><motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={s.btn('s')} onClick={closeModal}>Cancel</motion.button><motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={s.btn()} onClick={concludeDeal}>Confirm</motion.button></div>
        </div>
      </Modal>

      {/* Trail Modal */}
      <Modal open={modal?.type==='trail'} onClose={closeModal} title={'Follow-up Trail — '+(trailSpoc?.name||'')}>
        {trailData.length===0&&<div style={{textAlign:'center',color:LT,padding:30}}>No trail entries yet</div>}
        {trailData.map((entry,i)=><div key={i} style={{display:'flex',gap:12,paddingBottom:16,borderLeft:'2px solid '+P,marginLeft:8,paddingLeft:16}}>
          <div style={{width:8,height:8,borderRadius:4,background:P,marginTop:4,marginLeft:-21}}/>
          <div style={{flex:1}}>
            <div style={{fontSize:12,color:MU}}>{fmtDate(entry.date)}</div>
            <div style={{fontSize:13,fontWeight:500}}>{entry.action} → <Tag label={entry.outcome}/></div>
            {entry.notes&&<div style={{fontSize:12,color:MU,marginTop:2}}>{entry.notes}</div>}
            {entry.confidence&&<div style={{fontSize:11,color:entry.confidence>=7?GN:entry.confidence>=4?YL:RD}}>Confidence: {entry.confidence}/10</div>}
          </div>
        </div>)}
      </Modal>

      {/* Daily Queue SPOC Detail Modal */}
      {modal?.type==='queueSpocDetail' && (()=>{
        const sp=modal.spoc;
        const co=sp?.company||getCo(sp);
        if(!sp)return null;
        const row=(l,v)=><div key={l} style={{display:'flex',padding:'8px 0',borderBottom:'1px solid #F3F4F6',fontSize:13}}><div style={{width:130,color:MU,fontWeight:500,flexShrink:0}}>{l}</div><div style={{fontWeight:500,flex:1}}>{v??'—'}</div></div>;
        return <Modal open onClose={closeModal} title={(sp.hot?'🔥 ':'')+sp.name} wide>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:20}}>
            <div style={s.card}>
              <div style={{fontSize:12,fontWeight:700,color:P,textTransform:'uppercase',letterSpacing:'.5px',marginBottom:10}}>SPOC Details</div>
              {row('Name', sp.name)}
              {row('Designation', sp.designation)}
              {row('Mobile', sp.phone ? `${sp.phone}${sp.phoneType ? ` (${sp.phoneType})` : ''}` : '—')}
              {row('Email', sp.email ? <a href={`mailto:${sp.email}`} style={{color:P}}>{sp.email}</a> : '—')}
              {row('Decision Level', sp.decisionLevel ? <Tag label={sp.decisionLevel}/> : '—')}
              {row('Stage', sp.stage ? <Tag label={sp.stage}/> : '—')}
              {row('Lead Source', sp.leadSource)}
              {row('Hot Lead', sp.hot ? '🔥 Yes' : 'No')}
              {row('Next Follow-up', sp.nextFollowUp ? fmtDate(sp.nextFollowUp) : '—')}
              {row('Last Call', sp.lastCallDate ? `${fmtDate(sp.lastCallDate)} · ${sp.lastOutcome||'—'}` : 'Never')}
              {row('Attempts / Connected', `${sp.totalAttempts||0} / ${sp.totalConnected||0}`)}
              {sp.isStrikedOut&&row('Status', <span style={{color:RD,fontWeight:600}}>✕ {sp.strikeoutReason||'Struck out'}</span>)}
            </div>
            <div style={s.card}>
              <div style={{fontSize:12,fontWeight:700,color:P,textTransform:'uppercase',letterSpacing:'.5px',marginBottom:10}}>Company Details</div>
              {co ? <>
                {row('Company', co.name)}
                {row('City', co.city)}
                {row('Segment', co.segment)}
                {row('Source', co.source)}
                {row('Salesperson', co.salesperson)}
                {row('Potential', co.potential != null ? `${co.potential}/10` : '—')}
                {row('Deal Status', co.dealStatus ? <Tag label={co.dealStatus}/> : '—')}
                {row('Website', co.website ? <a href={co.website.startsWith('http')?co.website:`https://${co.website}`} target="_blank" rel="noopener noreferrer" style={{color:P}}>{co.website}</a> : '—')}
                {row('Remarks', co.remarks)}
                {co.isTopDeveloper&&row('Badge', '⭐ Top Developer')}
                {co.previouslyTapped&&row('Previously Tapped', `Yes (${co.previousTapCount||0} prior attempts)`)}
              </> : <div style={{color:LT,fontSize:13,padding:20,textAlign:'center'}}>Company not found</div>}
            </div>
          </div>
          <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:16,flexWrap:'wrap'}}>
            <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={s.btn('s')} onClick={closeModal}>Close</motion.button>
            {co&&<motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={s.btn('s')} onClick={()=>{closeModal();setDetailId(String(co._id));setPage('companies');setDTab('overview');}}>View Company →</motion.button>}
            <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={s.btn('s')} onClick={()=>openSpocForm(sp)}>Edit SPOC</motion.button>
            {sp.dailyStatus!=='done'&&<motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={s.btn()} onClick={()=>openCallForm(sp)}>📞 Log Call</motion.button>}
          </div>
        </Modal>;
      })()}

      {/* Upload Modal */}
      <Modal open={modal?.type==='upload'} onClose={closeModal} title={modal?.uploadType==='leads'?'Upload Lead Bank CSV':'Upload Companies CSV'} preventClose>
        <div style={{display:'flex',flexDirection:'column',gap:16}}>
          {formErrors.api&&<div style={{color:RD,fontSize:13,padding:10,background:RL,borderRadius:8}}>{formErrors.api}</div>}
          {uploadResult?<div style={{textAlign:'center',padding:20}}>
            <div style={{fontSize:40,marginBottom:8}}>✅</div>
            <div style={{fontSize:16,fontWeight:600}}>Upload Successful!</div>
            <div style={{fontSize:14,color:MU,marginTop:4}}>{uploadResult.companiesCreated!=null?`${uploadResult.companiesCreated} new companies${uploadResult.companiesUpdated?' · '+uploadResult.companiesUpdated+' existing matched':''} · ${uploadResult.spocsCreated} SPOCs added${uploadResult.leadsRemoved?' · '+uploadResult.leadsRemoved+' removed from Lead Bank':''}`:uploadResult.count+' imported'+(uploadResult.skipped?' · '+uploadResult.skipped+' duplicates skipped':'')}</div>
            <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={{...s.btn(),marginTop:16}} onClick={closeModal}>Done</motion.button>
          </div>:<>
            <div style={{textAlign:'center',padding:30,border:'2px dashed '+BD,borderRadius:14,background:'#FAFBFC'}}>
              <div style={{fontSize:32,marginBottom:8}}>📄</div>
              <div style={{fontSize:14,fontWeight:500,marginBottom:4}}>Choose a CSV file to upload</div>
              <div style={{fontSize:12,color:MU,marginBottom:12}}>Max 5MB · Columns: {modal?.uploadType==='leads'?'name, city, assignedTo, notes':modal?.uploadType==='companies-with-spocs'?'Company Name, City, Source, Spoc Name, Spoc Designation, Spoc Contact, Spoc Email Id, Spoc Lead Source (one row per SPOC)':'name, city, segment, source, salesperson, potential'}</div>
              <input type="file" accept=".csv" onChange={handleUpload} style={{fontSize:13}}/>
            </div>
            <div style={{display:'flex',gap:8,justifyContent:'center'}}>
              <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={s.btn('s')} onClick={()=>downloadTemplate(modal?.uploadType==='leads'?'leads':modal?.uploadType==='companies-with-spocs'?'companies-with-spocs':'companies')}>📥 Download Sample Template</motion.button>
            </div>
          </>}
        </div>
      </Modal>

      {/* Probable Client Modal */}
      <Modal open={modal?.type==='probableClient'} onClose={closeModal} title="Add Probable Client" preventClose>
        <div style={{display:'flex',flexDirection:'column',gap:16}}>
          <Fld label={<>Client Name <span style={{color:RD}}>*</span></>} error={formErrors.name}>
            <input style={{...s.inp,borderColor:formErrors.name?RD:undefined}} value={form.name||''} onChange={e=>uf('name',e.target.value)} placeholder="e.g. John Doe"/>
          </Fld>
          <Fld label="Company Name (optional)">
            <input style={s.inp} value={form.companyName||''} onChange={e=>uf('companyName',e.target.value)} placeholder="e.g. Acme Corp"/>
          </Fld>
          <Fld label="Meeting Trail / Notes">
            <textarea style={{...s.inp,minHeight:60}} value={form.meetingTrail||''} onChange={e=>uf('meetingTrail',e.target.value)} placeholder="What did you discuss?"/>
          </Fld>
          <Fld label="Remarks (optional)">
            <input style={s.inp} value={form.remarks||''} onChange={e=>uf('remarks',e.target.value)} placeholder="e.g. Follow up next month"/>
          </Fld>
          <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
            <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={s.btn('s')} onClick={closeModal}>Cancel</motion.button>
            <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={s.btn()} onClick={async () => {
              if (!(form.name||'').trim()) { setFormErrors({name:'Required'}); return; }
              const newClient = { name: form.name.trim(), companyName: (form.companyName||'').trim(), meetingTrail: (form.meetingTrail||'').trim(), remarks: (form.remarks||'').trim() };
              const updatedVisit = { ...visitDetail, probableClients: [...(visitDetail.probableClients||[]), newClient] };
              try {
                const saved = await api.updateVisit(visitDetail._id, updatedVisit);
                setVisitDetail(saved);
                setVisits(prev => prev.map(v => v._id === saved._id ? saved : v));
                closeModal();
              } catch(e) { alert(e.message); }
            }}>Save Client</motion.button>
          </div>
        </div>
      </Modal>

      {/* Probable Client Standalone Modal */}
      <Modal open={modal?.type==='probableClientStandalone'} onClose={closeModal} title={modal?.edit ? "Edit Lead" : "Add Lead"}>
        <div style={{display:'flex',flexDirection:'column',gap:16}}>
          <Fld label={<>Client Name <span style={{color:RD}}>*</span></>} error={formErrors.name}>
            <input style={{...s.inp,borderColor:formErrors.name?RD:undefined}} value={form.name||''} onChange={e=>uf('name',e.target.value)} placeholder="e.g. John Doe"/>
          </Fld>
          <Fld label="Link Company">
            <div style={{position:'relative'}}>
              <input style={s.inp} value={form._coSearch!=null?form._coSearch:(form.companyId?((companies.find(c=>String(c._id)===String(form.companyId))||{}).name+' — '+(companies.find(c=>String(c._id)===String(form.companyId))||{}).city):'')} onChange={e=>{uf('_coSearch',e.target.value);uf('_coDropOpen',true);if(!e.target.value){uf('companyId','');uf('companyName','');}}} onFocus={()=>uf('_coDropOpen',true)} placeholder="Search company..."/>
              {form._coDropOpen && <div style={{position:'absolute',top:'calc(100% + 4px)',left:0,right:0,background:WH,borderRadius:12,border:'1px solid #D1D5DB',boxShadow:'0 8px 24px rgba(0,0,0,.12)',maxHeight:200,overflowY:'auto',zIndex:1000,padding:4}}>
                {companies.filter(co=>{const q=(form._coSearch||'').toLowerCase();return !q||co.name.toLowerCase().includes(q)||(co.city||'').toLowerCase().includes(q);}).slice(0,50).map(co=><div key={co._id} onClick={()=>{uf('companyId',String(co._id));uf('companyName',co.name);uf('_coSearch',null);uf('_coDropOpen',false);}} style={{padding:'8px 12px',fontSize:13,borderRadius:8,cursor:'pointer',fontWeight:form.companyId===String(co._id)?600:400,background:form.companyId===String(co._id)?'#F3F4F6':'transparent',color:TX}}>{co.name} <span style={{color:MU,fontSize:11}}>— {co.city}</span></div>)}
                {companies.filter(co=>{const q=(form._coSearch||'').toLowerCase();return !q||co.name.toLowerCase().includes(q)||(co.city||'').toLowerCase().includes(q);}).length===0 && <div style={{padding:'8px 12px',fontSize:12,color:LT}}>No companies found</div>}
              </div>}
            </div>
          </Fld>
          <Fld label="Meeting Trail / Context">
            <textarea style={{...s.inp,minHeight:60}} value={form.meetingTrail||''} onChange={e=>uf('meetingTrail',e.target.value)} placeholder="How did you meet? Context of discussion..."/>
          </Fld>
          <Fld label="Add Remark (enters into trail)">
            <input style={{...s.inp,borderLeft:'3px solid #D97706'}} value={form.remarkText||''} onChange={e=>uf('remarkText',e.target.value)} placeholder="e.g. Very interested, follow up next month"/>
          </Fld>
          <Fld label="Status">
            <div style={{display:'flex',gap:8}}>
              {[
                {v:'In Progress', icon:'🔄', c:'#4F46E5', bg:'#EEF2FF'},
                {v:'Converted',   icon:'✅', c:GN,        bg:GL},
                {v:'Dropped',     icon:'✕',  c:RD,        bg:RL},
              ].map(st=>
                <div key={st.v} onClick={()=>uf('status',st.v)} style={{flex:1,textAlign:'center',padding:'10px 4px',borderRadius:8,border:'2px solid '+(form.status===st.v?st.c:BD),background:form.status===st.v?st.bg:WH,color:form.status===st.v?st.c:MU,fontSize:12,fontWeight:700,cursor:'pointer',transition:'all .15s'}}>
                  <div style={{fontSize:16,marginBottom:2}}>{st.icon}</div>{st.v}
                </div>
              )}
            </div>
          </Fld>
          <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
            <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={s.btn('s')} onClick={closeModal}>Cancel</motion.button>
            <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={s.btn()} onClick={async () => {
              if (!(form.name||'').trim()) { setFormErrors({name:'Required'}); return; }
              const payload = {
                name: form.name.trim(),
                companyId: form.companyId || null,
                companyName: (form.companyName||'').trim(),
                meetingTrail: (form.meetingTrail||'').trim(),
                status: form.status || 'In Progress'
              };
              try {
                if (modal.edit) {
                  await api.updateProbableClient(modal.editId, payload);
                  // Add remark if provided
                  if ((form.remarkText||'').trim()) {
                    await api.addProbableClientRemark(modal.editId, form.remarkText.trim());
                  }
                } else {
                  const created = await api.createProbableClient({...payload, remarks: (form.remarkText||'').trim()});
                }
                const fresh = await api.getProbableClients(); setProbableClients(fresh);
                closeModal();
              } catch(e) { alert(e.message); }
            }}>Save Lead</motion.button>
          </div>
        </div>
      </Modal>

      {/* Self-Generate Queue Modal (Employee) */}
      <Modal open={modal?.type==='selfGenQueue'} onClose={closeModal} title="Generate My Call List" wide={dqMode==='manual'}>
        <div style={{display:'flex',flexDirection:'column',gap:16}}>
          {formErrors.api&&<div style={{color:RD,fontSize:13,padding:10,background:RL,borderRadius:8}}>{formErrors.api}</div>}
          <div style={{background:'linear-gradient(135deg,'+PL+',#EDE9FE)',borderRadius:10,padding:'12px 16px',fontSize:13,color:P,fontWeight:500}}>
            {dqMode==='auto'?'Generating list for':'Picking SPOCs for'} <strong>{user}</strong>
            {userCities?.length>0&&<span style={{fontWeight:400,color:MU,marginLeft:6}}>· {userCities.join(', ')}</span>}
          </div>
          <div style={{display:'flex',background:'#F3F4F6',borderRadius:8,padding:2}}>
            {[['auto','⚡ Auto Generate'],['manual','🎯 Manual Select']].map(([m,label])=><div key={m} style={{flex:1,padding:'8px 14px',borderRadius:6,fontSize:13,fontWeight:600,cursor:'pointer',textAlign:'center',background:dqMode===m?'#fff':'transparent',color:dqMode===m?P:MU,boxShadow:dqMode===m?'0 1px 3px rgba(0,0,0,.08)':'none',transition:'all .15s'}} onClick={()=>{setDqMode(m);setFormErrors({});}}>{label}</div>)}
          </div>

          {dqMode==='auto'?<>
          <div style={s.row}>
            <Fld label="Date" half>
              <input type="date" style={s.inp} value={dqDate} onChange={e=>setDqDate(e.target.value)}/>
            </Fld>
            <Fld label="City" half>
              <Dropdown style={s.sel} value={selfGenCity} onChange={e=>setSelfGenCity(e.target.value)}>
                {visibleCities.map(c=><option key={c}>{c}</option>)}
              </Dropdown>
            </Fld>
          </div>
          <Fld label={`Target calls: ${selfGenTarget}`}>
            <div style={{display:'flex',alignItems:'center',gap:12}}>
              <input type="range" min={5} max={60} step={5} value={selfGenTarget} onChange={e=>setSelfGenTarget(+e.target.value)} style={{flex:1}}/>
              <div style={{background:P,color:'#fff',borderRadius:8,padding:'4px 12px',fontWeight:700,minWidth:36,textAlign:'center',fontSize:15}}>{selfGenTarget}</div>
            </div>
          </Fld>
          <div style={{display:'flex',alignItems:'center',gap:10}}>
            <div onClick={()=>setSelfGenCarry(v=>!v)} style={{width:40,height:22,borderRadius:11,background:selfGenCarry?P:BD,cursor:'pointer',position:'relative',transition:'background .2s',flexShrink:0}}>
              <div style={{width:18,height:18,borderRadius:9,background:'#fff',position:'absolute',top:2,left:selfGenCarry?20:2,transition:'left .2s',boxShadow:'0 1px 3px rgba(0,0,0,.2)'}}/>
            </div>
            <span style={{fontSize:13}}><b>Carry forward</b> yesterday's pending &amp; skipped items</span>
          </div>
          <div style={{background:'#F9FAFB',borderRadius:8,padding:'10px 14px',fontSize:12,color:MU,lineHeight:1.6}}>
            <div style={{fontWeight:600,color:TX,marginBottom:4}}>Priority order used:</div>
            <div>1. Overdue follow-ups → 2. Today's follow-ups → 3. Retry (Attempted, no follow-up) → 4. Hot leads → 5. Fresh (New)</div>
          </div>
          <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
            <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={s.btn('s')} onClick={closeModal}>Cancel</motion.button>
            <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn(),opacity:selfGenLoading?.6:1}} disabled={selfGenLoading} onClick={async()=>{
              if(!selfGenCity){setFormErrors({api:'Please select a city'});return;}
              setSelfGenLoading(true);setFormErrors({});
              try{
                const res=await api.selfGenerateQueue({date:dqDate,city:selfGenCity,target:selfGenTarget,carryForward:selfGenCarry});
                api.getQueue({date:dqDate}).then(setDailyQueue).catch(()=>{});
                closeModal();
                alert(`✅ Generated ${res.totalAssigned} calls for ${selfGenCity} on ${fmtDate(dqDate)}`);
              }catch(e){setFormErrors({api:e.message});}
              finally{setSelfGenLoading(false);}
            }}>{selfGenLoading?'Generating...':'⚡ Generate My List'}</motion.button>
          </div>
          </>:<>
            <div style={s.row}>
              <Fld label="Date" half><input type="date" style={s.inp} value={dqDate} onChange={e=>setDqDate(e.target.value)}/></Fld>
              <Fld label="Select by" half><Dropdown style={s.sel} value={dqManualView} onChange={e=>setDqManualView(e.target.value)}><option value="company">Company</option><option value="spoc">SPOC</option></Dropdown></Fld>
            </div>
            <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
              <Dropdown style={{...s.sel,width:130}} value={dqManualCity} onChange={e=>setDqManualCity(e.target.value)}><option value="">{userCities?'All my cities':'All cities'}</option>{visibleCities.map(c=><option key={c}>{c}</option>)}</Dropdown>
              <Dropdown style={{...s.sel,width:130}} value={dqManualStage} onChange={e=>setDqManualStage(e.target.value)}><option value="">All stages</option>{['New','Attempted','Connected','Details sent','Meeting scheduled','Proposal stage'].map(st=><option key={st}>{st}</option>)}</Dropdown>
              <input style={{...s.inp,width:160}} placeholder="Search company/SPOC..." value={dqManualSearch} onChange={e=>setDqManualSearch(e.target.value)}/>
              <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn(dqManualHot?undefined:'s'),padding:'6px 12px',fontSize:12,background:dqManualHot?'#EF4444':undefined,border:dqManualHot?'none':undefined,color:dqManualHot?'#fff':undefined}} onClick={()=>setDqManualHot(v=>!v)}>🔥 Hot</motion.button>
              <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn(dqManualTop?undefined:'s'),padding:'6px 12px',fontSize:12,background:dqManualTop?'#F59E0B':undefined,border:dqManualTop?'none':undefined,color:dqManualTop?'#fff':undefined}} onClick={()=>setDqManualTop(v=>!v)}>⭐ Top</motion.button>
            </div>
            {(()=>{
              const STAGE_BL2=['Won','Dropped','Not interested','Invalid data'];
              const doneIds2=new Set(dailyQueue.filter(q=>q.dailyAssignedDate===dqDate&&q.dailyStatus==='done').map(q=>q._id));
              const matchSelfManual=sp=>{
                if(STAGE_BL2.includes(sp.stage))return false;
                if(sp.isStrikedOut)return false;
                if(doneIds2.has(sp._id))return false;
                const co=companies.find(c=>c._id===sp.companyId);
                if(!co)return false;
                if(userCities&&!userCities.includes(co.city))return false;
                if(dqManualCity&&!citiesMatch(co.city,dqManualCity))return false;
                if(dqManualStage&&sp.stage!==dqManualStage)return false;
                if(dqManualHot&&!sp.hot)return false;
                if(dqManualTop&&!co.isTopDeveloper)return false;
                if(dqManualSearch){const q=dqManualSearch.toLowerCase();if(!co.name.toLowerCase().includes(q)&&!sp.name.toLowerCase().includes(q))return false;}
                return true;
              };
              const filteredCount=spocs.filter(matchSelfManual).length;
              return <div style={{display:'flex',alignItems:'center',gap:8,background:'#F9FAFB',border:'1px solid '+BD,borderRadius:8,padding:'8px 12px'}}>
                <span style={{fontSize:12,color:MU,whiteSpace:'nowrap'}}><b style={{color:TX}}>{filteredCount}</b> SPOCs match — select top:</span>
                <input type="number" min={1} max={filteredCount} style={{...s.inp,width:72,padding:'4px 8px',fontSize:12,textAlign:'center'}} placeholder="e.g. 40" value={dqManualQuickN} onChange={e=>setDqManualQuickN(e.target.value)}/>
                <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn(),padding:'5px 14px',fontSize:12,whiteSpace:'nowrap'}} onClick={()=>{
                  const n=Math.max(1,parseInt(dqManualQuickN)||0);
                  const toSelect=spocs.filter(matchSelfManual).slice(0,n);
                  const s2=new Set(dqManualSel);
                  toSelect.forEach(sp=>s2.add(sp._id));
                  setDqManualSel(s2);
                  setDqManualQuickN('');
                }}>Select</motion.button>
              </div>;
            })()}
            {dqManualSel.size>0&&<div style={{background:PL,padding:'8px 14px',borderRadius:8,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <span style={{fontSize:13,fontWeight:600,color:P}}>{dqManualSel.size} SPOC{dqManualSel.size>1?'s':''} selected</span>
              <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn('s'),padding:'4px 10px',fontSize:11}} onClick={()=>setDqManualSel(new Set())}>Deselect All</motion.button>
            </div>}
            <div style={{maxHeight:380,overflowY:'auto',border:'1px solid '+BD,borderRadius:10}}>
              {(()=>{
                const STAGE_BL=['Won','Dropped','Not interested','Invalid data'];
                const doneIds=new Set(dailyQueue.filter(q=>q.dailyAssignedDate===dqDate&&q.dailyStatus==='done').map(q=>q._id));
                const filtered=spocs.filter(sp=>{
                  if(STAGE_BL.includes(sp.stage))return false;
                  if(sp.isStrikedOut)return false;
                  if(doneIds.has(sp._id))return false;
                  const co=companies.find(c=>c._id===sp.companyId);
                  if(!co)return false;
                  if(userCities&&!userCities.includes(co.city))return false;
                  if(dqManualCity&&!citiesMatch(co.city,dqManualCity))return false;
                  if(dqManualStage&&sp.stage!==dqManualStage)return false;
                  if(dqManualHot&&!sp.hot)return false;
                  if(dqManualTop&&!co.isTopDeveloper)return false;
                  if(dqManualSearch){const q=dqManualSearch.toLowerCase();if(!co.name.toLowerCase().includes(q)&&!sp.name.toLowerCase().includes(q))return false;}
                  return true;
                });
                if(!filtered.length)return <div style={{padding:30,textAlign:'center',color:MU,fontSize:13}}>No {dqManualView==='company'?'companies':'SPOCs'} in your assigned cities match filters</div>;
                // Group the matching SPOCs by company — selecting a company selects all of its SPOCs
                const groups=[],gIdx=new Map();
                filtered.forEach(sp=>{const k=String(sp.companyId);if(!gIdx.has(k)){gIdx.set(k,groups.length);groups.push({co:companies.find(c=>String(c._id)===k),spocs:[]});}groups[gIdx.get(k)].spocs.push(sp);});
                return <>
                  <div style={{display:'flex',alignItems:'center',gap:8,padding:'8px 12px',borderBottom:'1px solid '+BD,background:'#FAFBFC',position:'sticky',top:0,zIndex:1}}>
                    <input type="checkbox" checked={filtered.length>0&&filtered.every(sp=>dqManualSel.has(sp._id))} onChange={e=>{if(e.target.checked){const s2=new Set(dqManualSel);filtered.forEach(sp=>s2.add(sp._id));setDqManualSel(s2);}else{const s2=new Set(dqManualSel);filtered.forEach(sp=>s2.delete(sp._id));setDqManualSel(s2);}}}/>
                    <span style={{fontSize:12,fontWeight:600,color:MU}}>Select all ({dqManualView==='company'?`${groups.length} companies · `:''}{filtered.length} SPOCs)</span>
                  </div>
                  {dqManualView==='spoc'&&filtered.map(sp=>{
                    const co=companies.find(c=>c._id===sp.companyId);
                    const checked=dqManualSel.has(sp._id);
                    const inQueue=sp.dailyAssignedDate===dqDate&&sp.dailyAssignedTo;
                    return <div key={sp._id} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 12px',borderBottom:'1px solid #F3F4F6',background:checked?PL:'#fff',cursor:'pointer'}} onClick={()=>{const s2=new Set(dqManualSel);if(checked)s2.delete(sp._id);else s2.add(sp._id);setDqManualSel(s2);}}>
                      <input type="checkbox" checked={checked} onChange={()=>{}} style={{cursor:'pointer'}}/>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:13,fontWeight:600}}>{sp.hot?'🔥 ':''}{sp.name} <span style={{fontWeight:400,color:MU,fontSize:11}}>({sp.designation||'—'})</span></div>
                        <div style={{fontSize:11,color:MU}}>{co?.name} · {co?.city}{co?.isTopDeveloper?' ⭐':''}</div>
                      </div>
                      <Tag label={sp.stage}/>
                      {inQueue&&<span style={{fontSize:10,color:'#0284C7',fontWeight:600,background:'#E0F2FE',padding:'2px 6px',borderRadius:4}}>In Queue · {sp.dailyAssignedTo}</span>}
                    </div>;
                  })}
                  {dqManualView==='company'&&groups.map(({co,spocs:gs})=>{
                    const selCount=gs.filter(sp=>dqManualSel.has(sp._id)).length;
                    const checked=selCount===gs.length;
                    const hot=gs.filter(sp=>sp.hot).length;
                    const inQueue=gs.filter(sp=>sp.dailyAssignedDate===dqDate&&sp.dailyAssignedTo).length;
                    return <div key={co?._id||'?'} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 12px',borderBottom:'1px solid #F3F4F6',background:selCount?PL:'#fff',cursor:'pointer'}} onClick={()=>{const s2=new Set(dqManualSel);gs.forEach(sp=>checked?s2.delete(sp._id):s2.add(sp._id));setDqManualSel(s2);}}>
                      <input type="checkbox" checked={checked} ref={el=>{if(el)el.indeterminate=selCount>0&&!checked;}} onChange={()=>{}} style={{cursor:'pointer'}}/>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:13,fontWeight:600}}>{co?.name||'—'}{co?.isTopDeveloper?' ⭐':''}</div>
                        <div style={{fontSize:11,color:MU}}>{co?.city} · {gs.length} SPOC{gs.length>1?'s':''}{hot?` · ${hot} 🔥`:''}</div>
                      </div>
                      {selCount>0&&!checked&&<span style={{fontSize:10,color:P,fontWeight:600,background:'#fff',padding:'2px 6px',borderRadius:4}}>{selCount}/{gs.length} selected</span>}
                      {inQueue>0&&<span style={{fontSize:10,color:'#0284C7',fontWeight:600,background:'#E0F2FE',padding:'2px 6px',borderRadius:4}}>{inQueue} in queue</span>}
                    </div>;
                  })}
                </>;
              })()}
            </div>
            <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
              <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={s.btn('s')} onClick={closeModal}>Cancel</motion.button>
              <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={s.btn()} disabled={dqGenerating||!dqManualSel.size} onClick={async()=>{setDqGenerating(true);try{const res=await api.manualQueue({date:dqDate,spocIds:[...dqManualSel],assignTo:user});alert(`Added ${res.assigned} SPOCs to your queue`);api.getQueue({date:dqDate}).then(setDailyQueue).catch(()=>{});setDqManualSel(new Set());closeModal();}catch(e){setFormErrors({api:e.message});}finally{setDqGenerating(false);}}}>{dqGenerating?'Adding...':'📋 Add to My Queue ('+dqManualSel.size+')'}</motion.button>
            </div>
          </>}
        </div>
      </Modal>

      {/* Generate Queue Modal */}
      <Modal open={modal?.type==='generateQueue'} onClose={closeModal} title="Generate Daily Queue" wide={dqMode==='manual'}>
        <div style={{display:'flex',flexDirection:'column',gap:14}}>
          {formErrors.api&&<div style={{color:RD,fontSize:13,padding:10,background:RL,borderRadius:8}}>{formErrors.api}</div>}
          {/* Mode tabs */}
          <div style={{display:'flex',background:'#F3F4F6',borderRadius:8,padding:2}}>
            {[['auto','⚡ Auto Generate'],['manual','🎯 Manual Select']].map(([m,label])=><div key={m} style={{flex:1,padding:'8px 14px',borderRadius:6,fontSize:13,fontWeight:600,cursor:'pointer',textAlign:'center',background:dqMode===m?'#fff':'transparent',color:dqMode===m?P:MU,boxShadow:dqMode===m?'0 1px 3px rgba(0,0,0,.08)':'none',transition:'all .15s'}} onClick={()=>{setDqMode(m);setFormErrors({});}}>{label}</div>)}
          </div>

          {dqMode==='auto'?<>
            <div style={s.row}>
              <Fld label="Date" half><input type="date" style={s.inp} value={dqDate} onChange={e=>setDqDate(e.target.value)}/></Fld>
              <Fld label="City" half><Dropdown style={s.sel} value={dqCity} onChange={e=>setDqCity(e.target.value)}>{visibleCities.map(c=><option key={c}>{c}</option>)}</Dropdown></Fld>
            </div>
            <Fld label={`Target per person: ${dqTarget}`}>
              <div style={{display:'flex',alignItems:'center',gap:10}}>
                <input type="range" min={5} max={60} step={5} value={dqTarget} onChange={e=>setDqTarget(+e.target.value)} style={{flex:1}}/><div style={{background:P,color:'#fff',borderRadius:8,padding:'3px 10px',fontWeight:700,minWidth:30,textAlign:'center'}}>{dqTarget}</div>
              </div>
            </Fld>
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              <div onClick={()=>setDqCarryForward(!dqCarryForward)} style={{width:40,height:22,borderRadius:11,background:dqCarryForward?P:BD,cursor:'pointer',position:'relative',transition:'background .2s'}}>
                <div style={{width:18,height:18,borderRadius:9,background:'#fff',position:'absolute',top:2,left:dqCarryForward?20:2,transition:'left .2s',boxShadow:'0 1px 3px rgba(0,0,0,.2)'}}/>
              </div>
              <span style={{fontSize:13}}><b>Carry forward</b> yesterday's unfinished items</span>
            </div>
            <div style={{background:'#F9FAFB',borderRadius:8,padding:'10px 14px',fontSize:12,color:MU}}>
              Salespeople for <b>{dqCity}</b>: {config.allUsers?.filter(u=>u.role==='Employee'&&(!u.assignedCities?.length||u.assignedCities.includes(dqCity))).map(u=>`${u.isTopDeveloper?'⭐ ':''}${u.username}`).join(', ')||'None assigned'}
            </div>
            <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
              <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={s.btn('s')} onClick={closeModal}>Cancel</motion.button>
              <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={s.btn()} disabled={dqGenerating} onClick={async()=>{setDqGenerating(true);try{const sps=config.allUsers.filter(u=>u.role==='Employee'&&(!u.assignedCities?.length||u.assignedCities.includes(dqCity))).map(u=>({username:u.username,isTopDeveloper:u.isTopDeveloper}));if(!sps.length){setFormErrors({api:'No salespeople assigned to '+dqCity});return;}const res=await api.generateQueue({date:dqDate,city:dqCity,salespersons:sps,carryForward:dqCarryForward,targetPerPerson:dqTarget});alert(`Generated ${res.totalAssigned} assignments for ${dqCity}`);api.getQueue({date:dqDate}).then(setDailyQueue).catch(()=>{});closeModal();}catch(e){setFormErrors({api:e.message});}finally{setDqGenerating(false);}}}>{dqGenerating?'Generating...':'⚡ Generate'}</motion.button>
            </div>
          </>:<>
            {/* Manual Select Mode */}
            <div style={s.row}>
              <Fld label="Date" half><input type="date" style={s.inp} value={dqDate} onChange={e=>setDqDate(e.target.value)}/></Fld>
              <Fld label="Assign to" half><Dropdown style={s.sel} value={dqManualAssign} onChange={e=>setDqManualAssign(e.target.value)}><option value="">Select Salesperson</option>{(config.allUsers||[]).filter(u=>u.role==='Employee').map(u=><option key={u.username} value={u.username}>{u.username}</option>)}</Dropdown></Fld>
              <Fld label="Select by" half><Dropdown style={s.sel} value={dqManualView} onChange={e=>setDqManualView(e.target.value)}><option value="company">Company</option><option value="spoc">SPOC</option></Dropdown></Fld>
            </div>
            {/* Filters */}
            <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
              <Dropdown style={{...s.sel,width:130}} value={dqManualCity} onChange={e=>setDqManualCity(e.target.value)}><option value="">All Cities</option>{CITIES.map(c=><option key={c}>{c}</option>)}</Dropdown>
              <Dropdown style={{...s.sel,width:130}} value={dqManualStage} onChange={e=>setDqManualStage(e.target.value)}><option value="">All Stages</option>{['New','Attempted','Connected','Details sent','Meeting scheduled','Proposal stage'].map(st=><option key={st}>{st}</option>)}</Dropdown>
              <input style={{...s.inp,width:160}} placeholder="Search company/SPOC..." value={dqManualSearch} onChange={e=>setDqManualSearch(e.target.value)}/>
              <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn(dqManualHot?undefined:'s'),padding:'6px 12px',fontSize:12,background:dqManualHot?'#EF4444':undefined,border:dqManualHot?'none':undefined,color:dqManualHot?'#fff':undefined}} onClick={()=>setDqManualHot(v=>!v)}>🔥 Hot</motion.button>
              <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn(dqManualTop?undefined:'s'),padding:'6px 12px',fontSize:12,background:dqManualTop?'#F59E0B':undefined,border:dqManualTop?'none':undefined,color:dqManualTop?'#fff':undefined}} onClick={()=>setDqManualTop(v=>!v)}>⭐ Top</motion.button>
            </div>
            {/* Quick select top N */}
            {(()=>{
              const STAGE_BL2=['Won','Dropped','Not interested','Invalid data'];
              const doneIds2=new Set(dailyQueue.filter(q=>q.dailyAssignedDate===dqDate&&q.dailyStatus==='done').map(q=>q._id));
              const filteredCount=spocs.filter(sp=>{
                if(STAGE_BL2.includes(sp.stage))return false;
                if(sp.isStrikedOut)return false;
                if(doneIds2.has(sp._id))return false;
                const co=companies.find(c=>c._id===sp.companyId);
                if(!co)return false;
                if(dqManualCity&&!citiesMatch(co.city,dqManualCity))return false;
                if(dqManualStage&&sp.stage!==dqManualStage)return false;
                if(dqManualHot&&!sp.hot)return false;
                if(dqManualTop&&!co.isTopDeveloper)return false;
                if(dqManualSearch){const q=dqManualSearch.toLowerCase();if(!co.name.toLowerCase().includes(q)&&!sp.name.toLowerCase().includes(q))return false;}
                return true;
              }).length;
              return <div style={{display:'flex',alignItems:'center',gap:8,background:'#F9FAFB',border:'1px solid '+BD,borderRadius:8,padding:'8px 12px'}}>
                <span style={{fontSize:12,color:MU,whiteSpace:'nowrap'}}><b style={{color:TX}}>{filteredCount}</b> SPOCs match filters — select top:</span>
                <input type="number" min={1} max={filteredCount} style={{...s.inp,width:72,padding:'4px 8px',fontSize:12,textAlign:'center'}} placeholder="e.g. 40" value={dqManualQuickN} onChange={e=>setDqManualQuickN(e.target.value)}/>
                <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn(),padding:'5px 14px',fontSize:12,whiteSpace:'nowrap'}} onClick={()=>{
                  const n=Math.max(1,parseInt(dqManualQuickN)||0);
                  const STAGE_BL3=['Won','Dropped','Not interested','Invalid data'];
                  const doneIds3=new Set(dailyQueue.filter(q=>q.dailyAssignedDate===dqDate&&q.dailyStatus==='done').map(q=>q._id));
                  const toSelect=spocs.filter(sp=>{
                    if(STAGE_BL3.includes(sp.stage))return false;
                    if(sp.isStrikedOut)return false;
                    if(doneIds3.has(sp._id))return false;
                    const co=companies.find(c=>c._id===sp.companyId);
                    if(!co)return false;
                    if(dqManualCity&&!citiesMatch(co.city,dqManualCity))return false;
                    if(dqManualStage&&sp.stage!==dqManualStage)return false;
                    if(dqManualHot&&!sp.hot)return false;
                    if(dqManualTop&&!co.isTopDeveloper)return false;
                    if(dqManualSearch){const q=dqManualSearch.toLowerCase();if(!co.name.toLowerCase().includes(q)&&!sp.name.toLowerCase().includes(q))return false;}
                    return true;
                  }).slice(0,n);
                  const s2=new Set(dqManualSel);
                  toSelect.forEach(sp=>s2.add(sp._id));
                  setDqManualSel(s2);
                  setDqManualQuickN('');
                }}>Select</motion.button>
              </div>;
            })()}
            {/* Selection bar */}
            {dqManualSel.size>0&&<div style={{background:PL,padding:'8px 14px',borderRadius:8,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <span style={{fontSize:13,fontWeight:600,color:P}}>{dqManualSel.size} SPOC{dqManualSel.size>1?'s':''} selected</span>
              <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn('s'),padding:'4px 10px',fontSize:11}} onClick={()=>setDqManualSel(new Set())}>Deselect All</motion.button>
            </div>}
            {/* SPOC list */}
            <div style={{maxHeight:380,overflowY:'auto',border:'1px solid '+BD,borderRadius:10}}>
              {(()=>{
                const STAGE_BL = ['Won','Dropped','Not interested','Invalid data'];
                const doneIds=new Set(dailyQueue.filter(q=>q.dailyAssignedDate===dqDate&&q.dailyStatus==='done').map(q=>q._id));
                const filtered = spocs.filter(sp=>{
                  if(STAGE_BL.includes(sp.stage))return false;
                  if(sp.isStrikedOut)return false;
                  if(doneIds.has(sp._id))return false;
                  const co=companies.find(c=>c._id===sp.companyId);
                  if(!co)return false;
                  if(dqManualCity&&!citiesMatch(co.city,dqManualCity))return false;
                  if(dqManualStage&&sp.stage!==dqManualStage)return false;
                  if(dqManualHot&&!sp.hot)return false;
                  if(dqManualTop&&!co.isTopDeveloper)return false;
                  if(dqManualSearch){
                    const q=dqManualSearch.toLowerCase();
                    if(!co.name.toLowerCase().includes(q)&&!sp.name.toLowerCase().includes(q))return false;
                  }
                  return true;
                });
                if(!filtered.length)return <div style={{padding:30,textAlign:'center',color:MU,fontSize:13}}>No {dqManualView==='company'?'companies':'SPOCs'} match filters</div>;
                // Group the matching SPOCs by company — selecting a company selects all of its SPOCs
                const groups=[],gIdx=new Map();
                filtered.forEach(sp=>{const k=String(sp.companyId);if(!gIdx.has(k)){gIdx.set(k,groups.length);groups.push({co:companies.find(c=>String(c._id)===k),spocs:[]});}groups[gIdx.get(k)].spocs.push(sp);});
                return <>
                  <div style={{display:'flex',alignItems:'center',gap:8,padding:'8px 12px',borderBottom:'1px solid '+BD,background:'#FAFBFC',position:'sticky',top:0,zIndex:1}}>
                    <input type="checkbox" checked={filtered.length>0&&filtered.every(sp=>dqManualSel.has(sp._id))} onChange={e=>{if(e.target.checked){const s2=new Set(dqManualSel);filtered.forEach(sp=>s2.add(sp._id));setDqManualSel(s2);}else{const s2=new Set(dqManualSel);filtered.forEach(sp=>s2.delete(sp._id));setDqManualSel(s2);}}}/>
                    <span style={{fontSize:12,fontWeight:600,color:MU}}>Select all ({dqManualView==='company'?`${groups.length} companies · `:''}{filtered.length} SPOCs)</span>
                  </div>
                  {dqManualView==='spoc'&&filtered.map(sp=>{
                    const co=companies.find(c=>c._id===sp.companyId);
                    const checked=dqManualSel.has(sp._id);
                    const inQueue=sp.dailyAssignedDate===dqDate&&sp.dailyAssignedTo;
                    return <div key={sp._id} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 12px',borderBottom:'1px solid #F3F4F6',background:checked?PL:'#fff',cursor:'pointer'}} onClick={()=>{const s2=new Set(dqManualSel);if(checked)s2.delete(sp._id);else s2.add(sp._id);setDqManualSel(s2);}}>
                      <input type="checkbox" checked={checked} onChange={()=>{}} style={{cursor:'pointer'}}/>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:13,fontWeight:600}}>{sp.hot?'🔥 ':''}{sp.name} <span style={{fontWeight:400,color:MU,fontSize:11}}>({sp.designation||'—'})</span></div>
                        <div style={{fontSize:11,color:MU}}>{co?.name} · {co?.city}{co?.isTopDeveloper?' ⭐':''}</div>
                      </div>
                      <Tag label={sp.stage}/>
                      {inQueue&&<span style={{fontSize:10,color:'#0284C7',fontWeight:600,background:'#E0F2FE',padding:'2px 6px',borderRadius:4}}>In Queue · {sp.dailyAssignedTo}</span>}
                    </div>;
                  })}
                  {dqManualView==='company'&&groups.map(({co,spocs:gs})=>{
                    const selCount=gs.filter(sp=>dqManualSel.has(sp._id)).length;
                    const checked=selCount===gs.length;
                    const hot=gs.filter(sp=>sp.hot).length;
                    const inQueue=gs.filter(sp=>sp.dailyAssignedDate===dqDate&&sp.dailyAssignedTo).length;
                    return <div key={co?._id||'?'} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 12px',borderBottom:'1px solid #F3F4F6',background:selCount?PL:'#fff',cursor:'pointer'}} onClick={()=>{const s2=new Set(dqManualSel);gs.forEach(sp=>checked?s2.delete(sp._id):s2.add(sp._id));setDqManualSel(s2);}}>
                      <input type="checkbox" checked={checked} ref={el=>{if(el)el.indeterminate=selCount>0&&!checked;}} onChange={()=>{}} style={{cursor:'pointer'}}/>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:13,fontWeight:600}}>{co?.name||'—'}{co?.isTopDeveloper?' ⭐':''}</div>
                        <div style={{fontSize:11,color:MU}}>{co?.city} · {gs.length} SPOC{gs.length>1?'s':''}{hot?` · ${hot} 🔥`:''}</div>
                      </div>
                      {selCount>0&&!checked&&<span style={{fontSize:10,color:P,fontWeight:600,background:'#fff',padding:'2px 6px',borderRadius:4}}>{selCount}/{gs.length} selected</span>}
                      {inQueue>0&&<span style={{fontSize:10,color:'#0284C7',fontWeight:600,background:'#E0F2FE',padding:'2px 6px',borderRadius:4}}>{inQueue} in queue</span>}
                    </div>;
                  })}
                </>;
              })()}
            </div>
            <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
              <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={s.btn('s')} onClick={closeModal}>Cancel</motion.button>
              <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={s.btn()} disabled={dqGenerating||!dqManualSel.size||!dqManualAssign} onClick={async()=>{setDqGenerating(true);try{const res=await api.manualQueue({date:dqDate,spocIds:[...dqManualSel],assignTo:dqManualAssign});alert(`Added ${res.assigned} SPOCs to ${dqManualAssign}'s queue`);api.getQueue({date:dqDate}).then(setDailyQueue).catch(()=>{});setDqManualSel(new Set());closeModal();}catch(e){setFormErrors({api:e.message});}finally{setDqGenerating(false);}}}>{dqGenerating?'Adding...':'📋 Add to Queue ('+dqManualSel.size+')'}</motion.button>
            </div>
          </>}
        </div>
      </Modal>

      {/* Target Modal */}
      <Modal open={modal?.type==='target'} onClose={closeModal} title={'Set Target — '+(form.salesperson||'New')}>
        <div style={{display:'flex',flexDirection:'column',gap:14}}>
          {formErrors.api&&<div style={{color:RD,fontSize:13,padding:10,background:RL,borderRadius:8}}>{formErrors.api}</div>}
          <div style={{fontSize:12,color:MU,background:'#F9FAFB',padding:'8px 12px',borderRadius:8}}>{form.period==='weekly'?'Weekly':'Monthly'} target · {fmtDate(form.startDate)} — {fmtDate(form.endDate)}</div>
          <Fld label="Salesperson *"><Dropdown style={s.sel} value={form.salesperson||''} onChange={e=>uf('salesperson',e.target.value)} disabled={!!form.isEdit}><option value="">Select Team Member</option>{(config.allUsers||[]).filter(u=>u.role!=='Admin').map(u=><option key={u.username} value={u.username}>{u.username}</option>)}</Dropdown></Fld>
          {isAdmin && (
            <Fld label="Period"><Dropdown style={s.sel} value={form.period||targetPeriod} onChange={e=>uf('period',e.target.value)}><option value="monthly">Monthly</option><option value="weekly">Weekly</option></Dropdown></Fld>
          )}
          {getEnabledTargetMetrics(config, form.period || targetPeriod).length === 0 && (
            <div style={{ fontSize: 12, color: RD, background: RL, padding: 10, borderRadius: 8 }}>Enable at least one metric in Settings → Targets first.</div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {getEnabledTargetMetrics(config, form.period || targetPeriod).map(m => (
              <Fld key={m.key} label={`${m.label} Target${m.isCurrency ? ' ₹' : ''}`}>
                <input style={s.inp} type="number" value={form[m.key] ?? ''} onChange={e => uf(m.key, e.target.value)} placeholder={m.isCurrency ? 'e.g. 500000' : 'e.g. 50'} />
              </Fld>
            ))}
          </div>
          <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}><motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={s.btn('s')} onClick={closeModal}>Cancel</motion.button><motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={s.btn()} onClick={async()=>{try{const metrics=getEnabledTargetMetrics(config, form.period||targetPeriod);const targets={calls:0,connected:0,meetings:0,pipelineValue:0,revenue:0,demos:0};metrics.forEach(m=>{targets[m.key]=Number(form[m.key])||0;});await api.createTarget({salesperson:form.salesperson,period:form.period||targetPeriod,startDate:form.startDate,endDate:form.endDate,targets,createdBy:user});closeModal();setTargetRefresh(r=>r+1);}catch(e){setFormErrors({api:e.message});}}}>Save Target</motion.button></div>
        </div>
      </Modal>

      {/* Confirm Delete Dialog */}
      <ConfirmDialog open={!!confirmDel} onClose={()=>setConfirmDel(null)} onConfirm={()=>{const fn=confirmDel?.fn;setConfirmDel(null);fn?.();}} title={confirmDel?.title||'Confirm Delete'} message={confirmDel?.msg||'Are you sure?'} confirmLabel={confirmDel?.confirmLabel} confirmKind={confirmDel?.confirmKind}/>

      {/* Follow-up reminder toasts (5 min prior) */}
      {fuReminders.length>0 && <div style={{position:'fixed',bottom:20,right:20,zIndex:100000,display:'flex',flexDirection:'column',gap:10,maxWidth:340}}>
        {fuReminders.map(r=>(
          <motion.div key={r.spocId} initial={{opacity:0,x:40}} animate={{opacity:1,x:0}} exit={{opacity:0,x:40}} style={{background:WH,border:'1px solid '+BD,borderLeft:'4px solid '+P,borderRadius:12,boxShadow:'0 8px 32px rgba(0,0,0,0.16)',padding:'14px 16px'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
              <span style={{fontSize:12,fontWeight:700,color:P}}>⏰ Follow-up in 5 min</span>
              <button type="button" onClick={()=>dismissReminder(r.spocId)} style={{border:'none',background:'transparent',cursor:'pointer',color:MU,fontSize:16,lineHeight:1}}>×</button>
            </div>
            <div style={{fontSize:14,fontWeight:600,color:TX}}>{r.name} <span style={{fontWeight:400,color:MU,fontSize:12}}>· {r.company}</span></div>
            <div style={{fontSize:12,color:MU,marginTop:2}}>Scheduled at {r.time}{r.reason?` · ${r.reason}`:''}</div>
            <div style={{display:'flex',gap:8,marginTop:12,justifyContent:'flex-end'}}>
              <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn('s'),padding:'5px 12px',fontSize:12}} onClick={()=>dismissReminder(r.spocId)}>Dismiss</motion.button>
              <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn(),padding:'5px 12px',fontSize:12}} onClick={()=>{const sp=spocs.find(x=>String(x._id)===String(r.spocId));dismissReminder(r.spocId);if(sp)openCallForm(sp);}}>📞 Log Call</motion.button>
            </div>
          </motion.div>
        ))}
      </div>}

      <RemoveUserModal
        open={!!removeUserModal}
        user={removeUserModal?.user}
        preview={removeUserModal?.preview}
        recipients={(config.salespersons || []).filter(sp => sp !== removeUserModal?.user?.username)}
        onClose={() => setRemoveUserModal(null)}
        onRemoved={() => {
          setRemoveUserModal(null);
          loadData();
          api.getTeamHistory().then(setTeamHistory).catch(() => {});
        }}
      />

      {/* Reconcile Prospects Modal */}
      <Modal open={!!reconcileModal} onClose={()=>setReconcileModal(null)} title="Sync Prospects → Companies" wide>
        {reconcileModal&&<div style={{display:'flex',flexDirection:'column',gap:16}}>
          {/* Debug info */}
          {process.env.NODE_ENV==='development'&&<div style={{fontSize:10,color:MU,background:'#F9FAFB',padding:8,borderRadius:6,fontFamily:'monospace'}}>
            Debug: exact={Array.isArray(reconcileModal.exact)?reconcileModal.exact.length:'N/A'}, fuzzy={Array.isArray(reconcileModal.fuzzy)?reconcileModal.fuzzy.length:'N/A'}
          </div>}
          
          {/* Exact matches — auto approved */}
          {Array.isArray(reconcileModal.exact)&&reconcileModal.exact.length>0&&<div>
            <div style={{fontSize:12,fontWeight:700,color:GN,textTransform:'uppercase',letterSpacing:'.05em',marginBottom:8}}>
              ✅ Exact matches — will be auto-marked Done ({reconcileModal.exact.length})
            </div>
            <div style={{background:'#F0FDF4',border:'1px solid #BBF7D0',borderRadius:10,overflow:'hidden'}}>
              {reconcileModal.exact.map((m,i)=><div key={i} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 14px',borderBottom:i<reconcileModal.exact.length-1?'1px solid #D1FAE5':'none',fontSize:12.5}}>
                <span style={{flex:1,color:TX,fontWeight:500}}>{m.leadName}</span>
                <span style={{fontSize:11,color:MU}}>→</span>
                <span style={{flex:1,color:GN,fontWeight:600}}>{m.companyName}</span>
                <span style={{fontSize:11,color:MU,flexShrink:0}}>{m.city}</span>
              </div>)}
            </div>
          </div>}

          {/* Fuzzy matches — user reviews */}
          {Array.isArray(reconcileModal.fuzzy)&&reconcileModal.fuzzy.length>0&&<div>
            <div style={{fontSize:12,fontWeight:700,color:'#D97706',textTransform:'uppercase',letterSpacing:'.05em',marginBottom:4}}>
              ⚠️ Fuzzy matches — names differ slightly, review each ({reconcileModal.fuzzy.length})
            </div>
            <div style={{fontSize:11,color:MU,marginBottom:8}}>Toggle off any you do NOT want merged.</div>
            <div style={{border:'1px solid #FDE68A',borderRadius:10,overflow:'hidden'}}>
              {reconcileModal.fuzzy.map((m,i)=>{
                const on=reconcileModal.approved.has(i);
                return <div key={i} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',borderBottom:i<reconcileModal.fuzzy.length-1?'1px solid #FEF3C7':'none',background:on?'#FFFBEB':WH,transition:'background .15s',fontSize:12.5}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:11,color:MU,marginBottom:1}}>Prospect</div>
                    <div style={{fontWeight:600,color:TX}}>{m.leadName}</div>
                  </div>
                  <span style={{fontSize:11,color:MU}}>→</span>
                  <div style={{flex:1}}>
                    <div style={{fontSize:11,color:MU,marginBottom:1}}>Company</div>
                    <div style={{fontWeight:600,color:on?'#92400E':MU}}>{m.companyName}</div>
                  </div>
                  <span style={{fontSize:11,color:MU,flexShrink:0,marginRight:8}}>{m.city}</span>
                  <div onClick={()=>setReconcileModal(prev=>{const s=new Set(prev.approved);on?s.delete(i):s.add(i);return{...prev,approved:s};})}
                    style={{width:40,height:22,borderRadius:11,background:on?GN:'#E5E7EB',cursor:'pointer',position:'relative',transition:'background .2s',flexShrink:0}}>
                    <div style={{width:18,height:18,borderRadius:9,background:WH,position:'absolute',top:2,left:on?20:2,transition:'left .2s',boxShadow:'0 1px 3px #0003'}}/>
                  </div>
                </div>;
              })}
            </div>
          </div>}

          {/* No matches message */}
          {(!Array.isArray(reconcileModal.exact)||reconcileModal.exact.length===0) && (!Array.isArray(reconcileModal.fuzzy)||reconcileModal.fuzzy.length===0) && (
            <div style={{textAlign:'center',padding:40,color:LT,fontSize:13,background:'#FAFAFA',borderRadius:10,border:'1px dashed '+BD}}>
              No matching prospects found to sync.
            </div>
          )}

          {/* Summary + actions */}
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',borderTop:'1px solid '+BD,paddingTop:12}}>
            <div style={{fontSize:12,color:MU}}>
              Will mark <b style={{color:TX}}>{(Array.isArray(reconcileModal.exact)?reconcileModal.exact.length:0) + (reconcileModal.approved?.size||0)}</b> prospect{((Array.isArray(reconcileModal.exact)?reconcileModal.exact.length:0)+(reconcileModal.approved?.size||0))===1?'':'s'} as Done
              {(Array.isArray(reconcileModal.fuzzy)&&reconcileModal.fuzzy.length-(reconcileModal.approved?.size||0)>0)&&<span style={{color:MU}}> · {reconcileModal.fuzzy.length-(reconcileModal.approved?.size||0)} skipped</span>}
            </div>
            <div style={{display:'flex',gap:8}}>
              <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={s.btn('s')} onClick={()=>setReconcileModal(null)}>Cancel</motion.button>
              <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} disabled={reconcileSaving} style={{...s.btn(),opacity:reconcileSaving?.6:1}} onClick={async()=>{
                if(reconcileSaving)return;
                setReconcileSaving(true);
                try{
                  const approved=[...reconcileModal.approved].map(i=>reconcileModal.fuzzy[i]);
                  const merges=[...reconcileModal.exact,...approved];
                  const r=await api.reconcileLeads(merges);
                  await loadData();
                  setReconcileModal(null);
                  alert(`${r.updated||0} prospect${r.updated===1?'':'s'} marked as Converted.`);
                }catch(e){alert('Error: '+e.message);}
                finally{setReconcileSaving(false);}
              }}>{reconcileSaving?'Syncing...':'Confirm Sync'}</motion.button>
            </div>
          </div>
        </div>}
      </Modal>

      {/* Company pre-create check Modal — duplicate companies + matching prospects */}
      <Modal open={!!companyCheck} onClose={()=>setCompanyCheck(null)} title="Before you create…" wide>
        {companyCheck&&<div style={{display:'flex',flexDirection:'column',gap:16}}>
          <div style={{fontSize:13,color:MU}}>You're adding <b style={{color:TX}}>{companyCheck.name}</b>{companyCheck.city?<span> in <b style={{color:TX}}>{companyCheck.city}</b></span>:null}. We found possible matches — please review.</div>

          {/* Duplicate companies */}
          {companyCheck.dupCompanies.length>0&&<div>
            <div style={{fontSize:12,fontWeight:700,color:RD,textTransform:'uppercase',letterSpacing:'.05em',marginBottom:8}}>
              ⚠️ Possible duplicate compan{companyCheck.dupCompanies.length===1?'y':'ies'} ({companyCheck.dupCompanies.length})
            </div>
            <div style={{background:'#FEF2F2',border:'1px solid #FECACA',borderRadius:10,overflow:'hidden'}}>
              {companyCheck.dupCompanies.map((c,i)=><div key={c.id} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 14px',borderBottom:i<companyCheck.dupCompanies.length-1?'1px solid #FEE2E2':'none',fontSize:12.5}}>
                <span style={{flex:1,color:TX,fontWeight:600}}>{c.name}</span>
                <span style={{fontSize:11,padding:'1px 8px',borderRadius:10,background:companyCheck.dupExact.some(x=>x.id===c.id)?'#FCA5A5':'#FED7AA',color:'#7F1D1D',fontWeight:600}}>{companyCheck.dupExact.some(x=>x.id===c.id)?'EXACT':'SIMILAR'}</span>
                <span style={{fontSize:11,color:MU,flexShrink:0}}>{c.city}{c.salesperson?' · '+c.salesperson:''}</span>
              </div>)}
            </div>
            <div style={{fontSize:11,color:MU,marginTop:6}}>If this is the same company, click <b>Cancel</b> and edit the existing one instead of creating a duplicate.</div>
          </div>}

          {/* Matching prospects */}
          {companyCheck.prospects.length>0&&<div>
            <div style={{fontSize:12,fontWeight:700,color:GN,textTransform:'uppercase',letterSpacing:'.05em',marginBottom:8}}>
              🔗 Matching prospect{companyCheck.prospects.length===1?'':'s'} — link & mark Done ({companyCheck.prospects.length})
            </div>
            <div style={{border:'1px solid #BBF7D0',borderRadius:10,overflow:'hidden'}}>
              {companyCheck.prospects.map((p,i)=>{
                const on=companyCheck.linkLeadId===p.id;
                return <div key={p.id} onClick={()=>setCompanyCheck(prev=>({...prev,linkLeadId:on?null:p.id}))} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',borderBottom:i<companyCheck.prospects.length-1?'1px solid #DCFCE7':'none',background:on?'#F0FDF4':WH,cursor:'pointer',fontSize:12.5}}>
                  <div style={{width:18,height:18,borderRadius:9,border:'2px solid '+(on?GN:'#CBD5E1'),background:on?GN:WH,flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center',color:WH,fontSize:11}}>{on?'✓':''}</div>
                  <span style={{flex:1,color:TX,fontWeight:600}}>{p.name}</span>
                  <span style={{fontSize:11,padding:'1px 8px',borderRadius:10,background:companyCheck.proExact.some(x=>x.id===p.id)?'#BBF7D0':'#FEF3C7',color:companyCheck.proExact.some(x=>x.id===p.id)?'#166534':'#92400E',fontWeight:600}}>{companyCheck.proExact.some(x=>x.id===p.id)?'EXACT':'SIMILAR'}</span>
                  <span style={{fontSize:11,color:MU,flexShrink:0}}>{p.status}{p.city?' · '+p.city:''}</span>
                </div>;
              })}
            </div>
            {companyCheck.linkLeadId&&(()=>{const p=companyCheck.prospects.find(x=>x.id===companyCheck.linkLeadId);const pName=p?.name||'';const same=pName.trim().toLowerCase()===companyCheck.name.trim().toLowerCase();return !same&&<div style={{marginTop:10,padding:'10px 12px',background:'#F9FAFB',border:'1px solid '+BD,borderRadius:10}}>
              <div style={{fontSize:11,fontWeight:700,color:MU,textTransform:'uppercase',letterSpacing:'.04em',marginBottom:8}}>Names differ — keep which one for both?</div>
              <div style={{display:'flex',gap:8}}>
                {[{k:'company',label:companyCheck.name},{k:'prospect',label:pName}].map(opt=>{const active=companyCheck.keepName===opt.k;return <div key={opt.k} onClick={()=>setCompanyCheck(prev=>({...prev,keepName:opt.k}))} style={{flex:1,padding:'8px 12px',borderRadius:8,border:'1.5px solid '+(active?GN:BD),background:active?'#F0FDF4':WH,cursor:'pointer'}}>
                  <div style={{fontSize:10,color:MU,marginBottom:2}}>{opt.k==='company'?'Company name':'Prospect name'}</div>
                  <div style={{fontSize:12.5,fontWeight:600,color:active?'#166534':TX}}>{opt.label}</div>
                </div>;})}
              </div>
            </div>;})()}
          </div>}

          {formErrors.api&&<div style={{color:RD,fontSize:12.5}}>{formErrors.api}</div>}

          {/* Actions */}
          <div style={{display:'flex',gap:8,justifyContent:'flex-end',borderTop:'1px solid '+BD,paddingTop:14}}>
            <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={s.btn('s')} onClick={()=>setCompanyCheck(null)}>Cancel</motion.button>
            <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} disabled={companySaving} style={{...s.btn(),opacity:companySaving?.6:1}} onClick={()=>{
              const opts={};
              if(companyCheck.dupCompanies.length) opts.allowDuplicate=true; // user acknowledged the duplicate-company warning
              if(companyCheck.linkLeadId){
                opts.linkLeadId=companyCheck.linkLeadId;
                if(companyCheck.keepName==='prospect'){
                  const p=companyCheck.prospects.find(x=>x.id===companyCheck.linkLeadId);
                  if(p?.name) opts.nameOverride=p.name;
                  opts.unifyLeadName=false;
                } else { opts.unifyLeadName=true; }
              }
              performCreateCompany(opts);
            }}>{companySaving?'Saving…':(companyCheck.linkLeadId?'Link & Create':'Create Anyway')}</motion.button>
          </div>
        </div>}
      </Modal>

      {/* Find Duplicate Companies Modal */}
      <Modal open={!!dupModal} onClose={()=>setDupModal(null)} title="Duplicate Companies" wide>
        {dupModal&&<div style={{display:'flex',flexDirection:'column',gap:16}}>
          <div style={{fontSize:13,color:MU}}>Found <b style={{color:TX}}>{dupModal.groupCount}</b> group{dupModal.groupCount===1?'':'s'} with <b style={{color:RD}}>{dupModal.totalDuplicates}</b> extra duplicate{dupModal.totalDuplicates===1?'':'s'}. Choose which name to keep, merge all SPOCs into one company, and remove duplicate contacts automatically.</div>
          <div style={{display:'flex',flexDirection:'column',gap:12,maxHeight:'52vh',overflowY:'auto'}}>
            {(Array.isArray(dupModal.groups)?dupModal.groups:[]).map((g,gi)=>{
              const items=Array.isArray(g)?g:[];
              if(!items.length) return null;
              const selection=dupModal.selections?.[gi]||{keepId:items[0]?.id,keepName:items[0]?.name||''};
              const keepId=selection.keepId||items[0]?.id;
              const keepName=selection.keepName||items.find(c=>c.id===keepId)?.name||items[0]?.name||'';
              const mergeIds=items.filter(c=>c.id!==keepId).map(c=>c.id);
              const totalSpocs=items.reduce((sum,c)=>sum+(c.spocCount||0),0);
              const nameOptions=[...new Set(items.map(c=>c.name).filter(Boolean))];
              const isMerging=dupMergingGroup===gi;
              return <div key={gi} style={{border:'1px solid '+BD,borderRadius:10,overflow:'hidden',flexShrink:0}}>
                <div style={{padding:'10px 14px',background:'#F3F4F6',borderBottom:'1px solid '+BD}}>
                  <div style={{fontSize:13,fontWeight:700,color:TX}}>{items[0].city||'—'} · {items.length} copies · {totalSpocs} total SPOCs</div>
                  <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center',marginTop:8}}>
                    <span style={{fontSize:11,fontWeight:600,color:MU}}>Keep name as</span>
                    <Dropdown style={{...s.sel,minWidth:180,maxWidth:260,fontSize:12,padding:'6px 10px'}} value={keepName} onChange={e=>setDupModal(prev=>{if(!prev)return prev;const selections=[...(prev.selections||[])];selections[gi]={...selections[gi],keepName:e.target.value};return{...prev,selections};})}>
                      {nameOptions.map(n=><option key={n} value={n}>{n}</option>)}
                    </Dropdown>
                    <input style={{...s.inp,flex:'1 1 160px',minWidth:140,padding:'6px 10px',fontSize:12}} value={keepName} onChange={e=>setDupModal(prev=>{if(!prev)return prev;const selections=[...(prev.selections||[])];selections[gi]={...selections[gi],keepName:e.target.value};return{...prev,selections};})} onBlur={e=>setDupModal(prev=>{if(!prev)return prev;const selections=[...(prev.selections||[])];selections[gi]={...selections[gi],keepName:formatProperName(e.target.value)};return{...prev,selections};})} placeholder="Company name to keep"/>
                    <motion.button whileHover={{scale:1.02}} whileTap={{scale:0.98}} disabled={!keepId||!mergeIds.length||isMerging} style={{...s.btn(),padding:'6px 14px',fontSize:12,opacity:(!keepId||!mergeIds.length||isMerging)?0.55:1}} onClick={async()=>{
                      if(!keepId||!mergeIds.length) return;
                      const mergeNames=items.filter(c=>mergeIds.includes(c.id)).map(c=>c.name).join(', ');
                      if(!window.confirm(`Merge ${mergeIds.length} duplicate${mergeIds.length===1?'':'s'} into "${formatProperName(keepName)}"?\n\nRemoving: ${mergeNames}\n\nAll SPOCs, calls, and meetings will move to the kept company. Duplicate SPOCs (same phone/email/name) will be combined.`)) return;
                      setDupMergingGroup(gi);
                      try {
                        const result=await api.mergeDuplicateCompanies({ keepCompanyId: keepId, mergeCompanyIds: mergeIds, keepName: formatProperName(keepName) });
                        await loadData();
                        setDupModal(prev=>{
                          if(!prev) return prev;
                          const groups=prev.groups.filter((_,idx)=>idx!==gi);
                          const selections=prev.selections.filter((_,idx)=>idx!==gi);
                          return {...prev,groups,selections,groupCount:groups.length,totalDuplicates:groups.reduce((sum,grp)=>sum+(grp.length-1),0)};
                        });
                        alert(`Merged successfully.\n\nKept: ${result.keepName}\nSPOCs after dedupe: ${result.spocCount}${result.duplicateSpocsRemoved?`\nDuplicate SPOCs removed: ${result.duplicateSpocsRemoved}`:''}`);
                      } catch(e) {
                        alert('Merge failed: '+e.message);
                      } finally {
                        setDupMergingGroup(null);
                      }
                    }}>{isMerging?'Merging…':`Merge ${mergeIds.length} into one`}</motion.button>
                  </div>
                </div>
                {items.map((c,ci)=><div key={c.id||ci} style={{display:'flex',alignItems:'center',gap:10,padding:'9px 14px',borderBottom:ci<items.length-1?'1px solid #F1F5F9':'none',fontSize:13,background:c.id===keepId?'#F0FDF4':'WH'}}>
                  <label style={{display:'flex',alignItems:'center',gap:8,flex:1,minWidth:0,cursor:'pointer'}}>
                    <input type="radio" name={`dup-keep-${gi}`} checked={c.id===keepId} onChange={()=>setDupModal(prev=>{if(!prev)return prev;const selections=[...(prev.selections||[])];selections[gi]={keepId:c.id,keepName:c.name||selections[gi]?.keepName||''};return{...prev,selections};})} style={{accentColor:TX,flexShrink:0}}/>
                    <span style={{color:TX,fontWeight:c.id===keepId?700:500,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{c.name||'(no name)'}</span>
                  </label>
                  <span title="SPOCs linked to this company" style={{fontSize:11,fontWeight:600,padding:'2px 8px',borderRadius:10,background:(c.spocCount>0)?'#DBEAFE':'#F1F5F9',color:(c.spocCount>0)?'#1E40AF':MU,flexShrink:0}}>{c.spocCount||0} SPOC{(c.spocCount||0)===1?'':'s'}</span>
                  <span style={{fontSize:11,color:MU,minWidth:48,textAlign:'right'}}>{c.salesperson||''}</span>
                  {ci===0&&<span style={{fontSize:10,padding:'2px 8px',borderRadius:10,background:'#DCFCE7',color:'#166534',fontWeight:600,flexShrink:0}}>oldest</span>}
                  {c.id===keepId&&<span style={{fontSize:10,padding:'2px 8px',borderRadius:10,background:'#DBEAFE',color:'#1E40AF',fontWeight:600,flexShrink:0}}>keep</span>}
                  {c.id!==keepId&&<motion.button whileHover={{scale:1.03}} whileTap={{scale:0.97}} style={{...s.btn('s'),color:RD,borderColor:'#FECACA',padding:'4px 12px',fontSize:12,flexShrink:0}} onClick={()=>setConfirmDel({title:'Delete duplicate company',msg:`Delete "${c.name}" (${c.city})?\n\nThis company has ${c.spocCount||0} SPOC${(c.spocCount||0)===1?'':'s'}${(c.spocCount||0)>0?' which will be ORPHANED':''}.\n\nTip: use Merge instead to keep SPOCs.\n\nThis action cannot be undone.`,confirmLabel:'Delete',confirmKind:'danger',fn:async()=>{try{await api.deleteCompany(c.id);await loadData();setDupModal(prev=>{if(!prev)return prev;const groups=prev.groups.map(grp=>grp.filter(x=>x.id!==c.id)).filter(grp=>grp.length>1);return buildDuplicateModalState({groups,groupCount:groups.length,totalDuplicates:groups.reduce((s,grp)=>s+(grp.length-1),0)});});}catch(e){alert('Error: '+e.message);}}})}>Delete</motion.button>}
                </div>)}
              </div>;
            })}
            {(!Array.isArray(dupModal.groups)||dupModal.groups.length===0)&&<div style={{textAlign:'center',padding:30,color:LT,fontSize:13,background:'#FAFAFA',borderRadius:10,border:'1px dashed '+BD}}>No more duplicates 🎉</div>}
          </div>
          <div style={{display:'flex',justifyContent:'flex-end',borderTop:'1px solid '+BD,paddingTop:12}}>
            <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={s.btn('s')} onClick={()=>setDupModal(null)}>Done</motion.button>
          </div>
        </div>}
      </Modal>

      {/* Strike-out Modal */}
      {strikeModal&&(()=>{
        const sp=strikeModal.spoc;
        const reasons=config.strikeoutReasons||[];
        const [selReason,setSelReason]=[strikeModal.reason||'',r=>setStrikeModal(m=>({...m,reason:r}))];
        return <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:110,backdropFilter:'blur(2px)'}} onClick={()=>setStrikeModal(null)}>
          <motion.div initial={{opacity:0,scale:0.95,y:10}} animate={{opacity:1,scale:1,y:0}} style={{background:WH,borderRadius:16,padding:28,width:'92%',maxWidth:420,boxShadow:'0 20px 60px rgba(0,0,0,.15)'}} onClick={e=>e.stopPropagation()}>
            <h3 style={{margin:'0 0 4px',fontSize:16}}>Strike Out SPOC</h3>
            <p style={{color:MU,fontSize:13,margin:'0 0 16px'}}>Strike out <strong>{sp.name}</strong>? Select a reason — it will be shown on the SPOC row.</p>
            <div style={{marginBottom:16}}>
              <div style={{fontSize:12,fontWeight:600,color:MU,marginBottom:8,textTransform:'uppercase',letterSpacing:'0.04em'}}>Reason *</div>
              <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
                {reasons.map(r=>{const active=selReason===r;return <div key={r} onClick={()=>setSelReason(r)} style={{padding:'6px 14px',borderRadius:20,border:'1.5px solid '+(active?RD:BD),background:active?'#FEF2F2':WH,color:active?RD:MU,fontSize:12,fontWeight:600,cursor:'pointer',transition:'all .15s'}}>{r}</div>;})}
              </div>
              {!reasons.length&&<div style={{fontSize:12,color:LT}}>No reasons configured. Add them in Settings → Lead Configuration → Strike-out Reasons.</div>}
              <input style={{...s.inp,marginTop:10,fontSize:13}} placeholder="Or type a custom reason..." value={selReason} onChange={e=>setSelReason(e.target.value)}/>
            </div>
            <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
              <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={s.btn('s')} onClick={()=>setStrikeModal(null)}>Cancel</motion.button>
              <motion.button whileHover={{scale:1.01}} whileTap={{scale:0.985}} style={{...s.btn('r'),opacity:selReason?1:0.5}} onClick={async()=>{if(!selReason)return;await api.strikeoutSpoc(sp._id,selReason);setStrikeModal(null);loadData();}}>Strike Out</motion.button>
            </div>
          </motion.div>
        </div>;
      })()}

    </div>
  );
}

// Settings panel wrapper
function SettingsPanel({ title, description, headerAction, children }) {
  return (
    <div className="settings-panel">
      {(title || description || headerAction) && (
        <div className="settings-panel__header">
          <div>
            {title && <h2 className="settings-panel__title">{title}</h2>}
            {description && <p className="settings-panel__desc">{description}</p>}
          </div>
          {headerAction}
        </div>
      )}
      <div className="settings-panel__body">{children}</div>
    </div>
  );
}

// Settings List Editor (redesigned)
function SettingsListEditor({ title, items, onSave, placeholder }) {
  const [val, setVal] = useState('');
  const [confirmRemove, setConfirmRemove] = useState(null);
  return (
    <div className="settings-list-editor" style={{paddingBottom:16,borderBottom:'1px solid #F3F4F6',marginBottom:16}}>
      {title&&<div className="settings-subsection__title">{title}</div>}
      <div style={{display:'flex',flexWrap:'wrap',gap:6,marginBottom:10}}>
        {items.length===0&&<span style={{fontSize:12,color:'#9CA3AF',fontStyle:'italic'}}>No items yet</span>}
        {items.map((item,i) => <div key={i} style={{display:'flex',alignItems:'center',gap:6,padding:'6px 12px',background:'#F9FAFB',borderRadius:8,fontSize:13,border:'1px solid #E5E7EB'}}>
          {item}
          <span onClick={()=>setConfirmRemove(i)} style={{cursor:'pointer',color:'#EF4444',fontWeight:700,fontSize:14,lineHeight:1,opacity:.55}} onMouseEnter={e=>{e.currentTarget.style.opacity='1'}} onMouseLeave={e=>{e.currentTarget.style.opacity='.55'}}>×</span>
        </div>)}
      </div>
      {confirmRemove!==null&&<div style={{background:'#FEF2F2',padding:'8px 14px',borderRadius:8,marginBottom:8,display:'flex',justifyContent:'space-between',alignItems:'center',fontSize:13,border:'1px solid #FECACA'}}>
        <span>Remove &ldquo;<b>{items[confirmRemove]}</b>&rdquo;?</span>
        <div style={{display:'flex',gap:6}}><motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={{padding:'3px 10px',borderRadius:6,border:'1px solid #E5E7EB',background:'#fff',cursor:'pointer',fontSize:12}} onClick={()=>setConfirmRemove(null)}>Cancel</motion.button><motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={{padding:'3px 10px',borderRadius:6,border:'none',background:'#EF4444',color:'#fff',cursor:'pointer',fontSize:12,fontWeight:600}} onClick={()=>{onSave(items.filter((_,j)=>j!==confirmRemove));setConfirmRemove(null);}}>Remove</motion.button></div>
      </div>}
      <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
        <input style={{...s.inp,flex:'1 1 200px',maxWidth:360,padding:'9px 12px',fontSize:13}} value={val} onChange={e=>setVal(e.target.value)} placeholder={placeholder||'Add new item…'} onKeyDown={e=>{if(e.key==='Enter'&&val.trim()){onSave([...items,val.trim()]);setVal('');}}}/>
        <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={{...s.btn(),padding:'8px 16px',fontSize:13}} onClick={()=>{if(val.trim()){onSave([...items,val.trim()]);setVal('');}}}>Add</motion.button>
      </div>
    </div>
  );
}

function ProposalTypesSettingsEditor({ title, items, onSave, placeholder }) {
  const [val, setVal] = useState('');
  const [confirmRemove, setConfirmRemove] = useState(null);
  const addItem = () => {
    const name = val.trim();
    if (!name) return;
    if (items.some(t => t.name.toLowerCase() === name.toLowerCase())) {
      alert('That proposal type already exists.');
      return;
    }
    onSave([...items, { name, url: '' }]);
    setVal('');
  };
  return (
    <div className="settings-list-editor" style={{paddingBottom:0,borderBottom:'none',marginBottom:0}}>
      {title&&<div className="settings-subsection__title">{title}</div>}
      <div style={{display:'flex',flexWrap:'wrap',gap:6,marginBottom:10}}>
        {items.length===0&&<span style={{fontSize:12,color:'#9CA3AF',fontStyle:'italic'}}>No proposal types yet</span>}
        {items.map((item,i) => <div key={i} style={{display:'flex',alignItems:'center',gap:6,padding:'6px 12px',background:'#F9FAFB',borderRadius:8,fontSize:13,border:'1px solid #E5E7EB'}}>
          {item.name}
          <span onClick={()=>setConfirmRemove(i)} style={{cursor:'pointer',color:'#EF4444',fontWeight:700,fontSize:14,lineHeight:1,opacity:.55}} onMouseEnter={e=>{e.currentTarget.style.opacity='1'}} onMouseLeave={e=>{e.currentTarget.style.opacity='.55'}}>×</span>
        </div>)}
      </div>
      {confirmRemove!==null&&<div style={{background:'#FEF2F2',padding:'8px 14px',borderRadius:8,marginBottom:8,display:'flex',justifyContent:'space-between',alignItems:'center',fontSize:13,border:'1px solid #FECACA'}}>
        <span>Remove &ldquo;<b>{items[confirmRemove]?.name}</b>&rdquo;?</span>
        <div style={{display:'flex',gap:6}}><motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={{padding:'3px 10px',borderRadius:6,border:'1px solid #E5E7EB',background:'#fff',cursor:'pointer',fontSize:12}} onClick={()=>setConfirmRemove(null)}>Cancel</motion.button><motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={{padding:'3px 10px',borderRadius:6,border:'none',background:'#EF4444',color:'#fff',cursor:'pointer',fontSize:12,fontWeight:600}} onClick={()=>{onSave(items.filter((_,j)=>j!==confirmRemove));setConfirmRemove(null);}}>Remove</motion.button></div>
      </div>}
      <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
        <input style={{...s.inp,flex:'1 1 200px',maxWidth:360,padding:'9px 12px',fontSize:13}} value={val} onChange={e=>setVal(e.target.value)} placeholder={placeholder||'Add proposal type…'} onKeyDown={e=>{if(e.key==='Enter')addItem();}}/>
        <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={{...s.btn(),padding:'8px 16px',fontSize:13}} onClick={addItem}>Add</motion.button>
        <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.985 }} style={{...s.btn('s'),fontSize:12}} onClick={async()=>{try{onSave(DEFAULT_PROPOSAL_TEMPLATES);}catch(e){alert(e.message);}}}>Reset defaults</motion.button>
      </div>
    </div>
  );
}
